export const WassceExamInfo = {
  examName: 'WASSCE Mock Exam',
  totalQuestions: 60,
  durationSeconds: 60 * 60, // 1 hour default
  passMarkPercent: 50,
  notes: `Simulated West African Senior School Certificate Examination (WASSCE) objective paper. Single-paper CBT style with randomized past questions. Each item carries 1 mark, no negative marking. Time is strictly enforced.`
};

export const WassceSubjects = [
  {
    subject: 'English Language',
    timeAlloted: 60 * 60,
    description: `Core language paper assessing candidates' ability to understand and use English accurately in speech and writing across lexis, structure, comprehension, summary and orals/listening.

        Syllabus Focus:
        - Communicative competence in English (spoken and written) across everyday, academic and civic contexts.
        - Accurate control of grammar, lexis and sentence patterns (including concord, sequence of tenses and prepositions).
        - Paragraph organization and development: logical, chronological and spatial coherence.
        - Reading and listening comprehension: understanding explicit information, implied meaning, tone and attitude.
        - Summary skills: identifying main points and expressing them concisely in candidates' own words.
        - Sound system of English: consonants, vowels/diphthongs, stress (word/sentence), rhythm, intonation and basic phonetic symbols.

        Exam Structure:
        - Paper 1: Objective (Lexis & Structure)
          • Multiple-choice questions on vocabulary and structural patterns.
          • Tests idioms/collocations, figurative language, synonyms/antonyms/homonyms and basic grammar control.

        - Paper 2: Essay, Comprehension & Summary
          • Section A – Essay Writing (letters, speeches, narratives, descriptions, arguments/debates, reports, articles, expositions, creative writing).
          • Section B – Comprehension (one prose passage testing understanding, inference, vocabulary in context, grammar and basic literary awareness).
          • Section C – Summary (one prose passage testing selection of relevant points and clear, concise restatement).

        - Paper 3: Orals / Listening
          • Test of orals or listening comprehension depending on country.
          • Focus on segmental and suprasegmental features (sounds, stress, rhythm, intonation) and listening skills.

        Guidelines for Candidates:
        - Build a wide active vocabulary by reading across subjects (literature, science, social issues, etc.).
        - Practise sentence construction, concord, tenses and correct use of pronouns, articles and prepositions.
        - In essays, focus on content, organization (introduction, well-linked paragraphs, conclusion) and mechanical accuracy.
        - In comprehension and summary, read passages carefully, answer in your own words where required and respect length limits.
        - For orals/listening, train your ear for English sounds, stress and intonation; listen to model recordings and practise aloud.
        - Manage time across all papers and leave a little time to review answers.`,
  },
  {
    subject: 'Mathematics',
    timeAlloted: 90 * 60,
    description: `Compulsory core Mathematics paper testing computational skills, conceptual understanding and problem-solving across number and numeration, algebra, geometry, trigonometry, statistics, probability, vectors and introductory calculus.

        Syllabus Focus:
        - Number & Numeration: number bases, modular arithmetic, fractions/decimals, approximations and significant figures, indices, standard form, logarithms, sequences/series, sets, logic, integers/rationals, surds, matrices/determinants, ratio/proportion/rates, percentages, variation and basic financial arithmetic.
        - Algebraic Processes: algebraic expressions, expansion and factorization, binary operations, linear and simultaneous equations, change of subject of formula, quadratic equations, functions/relations, graphs of linear and quadratic functions and simple inequalities.
        - Mensuration & Plane Geometry: lengths, perimeters, areas and volumes of 2D and 3D shapes (triangles, quadrilaterals, circles, sectors/segments, prisms, cylinders, cones, pyramids, spheres) and related applications.
        - Geometry of Angles & Circles: angle properties on lines and at a point, triangles and polygons, similar/congruent figures, circle theorems, simple constructions and loci.
        - Coordinate Geometry & Trigonometry: coordinates and equations of lines, gradients, midpoints; trigonometric ratios, graphs, bearings, heights and distances.
        - Introductory Calculus: basic differentiation and integration of simple algebraic functions, with applications to rates of change and maxima/minima.
        - Statistics & Probability: data representation, measures of central tendency and dispersion, cumulative frequency and ogives, experimental/theoretical probability and simple combinations of events.
        - Vectors & Transformations: vectors in the plane, translations, reflections, rotations and enlargements on the Cartesian plane.

        Exam Structure:
        - Paper 1: Objective (Common Areas)
          • 50 multiple-choice questions drawn from the common/core areas of the syllabus.
          • Duration: about 1½ hours.

        - Paper 2: Essay / Structured Questions
          • Questions split into Sections A and B, to be answered in about 2½ hours.
          • Section A: compulsory, relatively elementary questions from the common areas.
          • Section B: longer, more demanding questions (including items from non-common areas); candidates answer a subset.

        Guidelines for Candidates:
        - Master core skills in arithmetic and algebraic manipulation.
        - Practise translating word problems into mathematical form and selecting appropriate methods.
        - Show clear workings; partial credit depends heavily on method.
        - Use diagrams for geometry, bearings, loci, vectors and transformations.
        - Work many past questions to build speed, accuracy and familiarity with question styles.
        - Use approved calculators, tables and drawing instruments correctly and keep to required accuracy.`,
  },
  {
    subject: 'Physics',
    timeAlloted: 75 * 60,
    description: `Senior Secondary Physics paper built around broad concepts: interaction of matter, space and time; energy; waves; fields; and atomic & nuclear physics/electronics.

        Preamble & Syllabus Focus:
        - Conceptual approach: matter, position, motion and time; energy; waves; fields; atomic and nuclear physics, with electronics integrated.
        - Matter: particle nature of matter (Brownian motion, kinetic theory), states of matter, pressure in gases, evaporation/boiling, cohesion/adhesion, capillarity, crystalline vs amorphous materials.
        - Interaction of matter, space & time: fundamental/derived quantities and units; position, distance, displacement and direction; mass vs weight; time and its measurement; fluids at rest (density, relative density, pressure, Archimedes’ principle, flotation); linear and circular motion; speed/velocity; acceleration; scalars/vectors and equilibrium of forces; simple harmonic motion.
        - Energy (Mechanical & Heat): forms and sources of energy, conservation of energy; work, power, potential and kinetic energy, machines; thermal concepts (temperature, heat capacity, specific heat, expansion, heat transfer, gas laws, latent heat, evaporation/boiling, humidity and weather).
        - Waves: generation and propagation of mechanical waves; types of waves (transverse/longitudinal); wave properties and relationships; reflection, refraction, diffraction, interference and standing waves; sound (production, propagation, speed, echoes, characteristics, vibrations in strings and air columns, resonance, harmonics).
        - Light & Electromagnetic Waves: rectilinear propagation, reflection (plane/curved mirrors), refraction (blocks/prisms), lenses and optical instruments; dispersion and spectrum; electromagnetic spectrum and applications.
        - Fields: gravitational, electric and magnetic fields; electrostatics, capacitors and current electricity; magnetic fields around magnets and currents; electromagnetic induction, transformers and power transmission; simple a.c. circuits (R, L, C; reactance, impedance, resonance, power).
        - Atomic & Nuclear Physics/Electronics: models of the atom, energy levels, line spectra, photoelectric effect, thermionic emission, X‑rays, nuclear composition, radioactivity, fission/fusion, nuclear energy, radiation hazards/safety; basic semiconductor ideas where harmonised topics apply (e.g. diodes, LASER, fibre optics, etc.).

        Assessment Objectives:
        - Knowledge & Understanding: phenomena, facts, laws, definitions, concepts and theories; correct symbols, quantities and SI units; experimental apparatus, techniques and safety; physical quantities and their determination; basic applications and implications of physics in society.
        - Information Handling & Problem-Solving: select, organize and present information (verbal, symbolic, graphical and numerical); analyse/evaluate data; identify patterns and trends; draw inferences; explain natural occurrences and make predictions from given data.
        - Experimental Skills & Techniques: follow instructions; set up and use apparatus; make, record and estimate measurements with attention to precision, accuracy and units; interpret and evaluate experimental data; plan and improve investigations; state and justify precautions.

        Exam Structure:
        - Paper 1: Objective (Multiple Choice)
          • ~50 multiple-choice questions covering common areas of the syllabus.
          • Duration: 1¼ hours (75 minutes) for 50 marks.

        - Paper 2: Theory (Short Structured + Essays)
          • Section A: about 7 short-structured questions; answer any 5 for 15 marks.
          • Section B: 5 longer essay questions; answer any 3 for 45 marks.
          • Duration: 1½ hours (90 minutes) for 60 marks.

        - Paper 3: Practical / Alternative to Practical
          • School candidates: 3 practical questions, answer any 2 in 2¾ hours for 50 marks.
          • Private candidates: written Alternative to Practical with equivalent coverage and weighting.

        Guidelines for Candidates:
        - Build solid conceptual understanding first, then practise quantitative problem solving (including multi-step and data-interpretation questions).
        - Memorise key definitions, laws and relationships (e.g. equations of motion, Newton’s laws, work/energy/power relationships, gas laws, wave and optical formulas, field and nuclear relations) and practise applying them to real situations.
        - Keep strict track of units, significant figures and vector directions; use SI units unless otherwise stated.
        - Draw clear, labelled diagrams for mechanics (forces, motion), optics (rays, images), electricity (circuits), fields (lines of force) and atomic/nuclear processes.
        - Engage in regular practical work to strengthen intuition about measurement, error, graphs and experimental design.
        - Practise past questions across all parts of the syllabus (mechanics, heat, waves, optics, electricity, fields, modern physics) to improve speed, accuracy and exam technique.
        - Manage time across Papers 1 and 2: attempt straightforward questions first, show working clearly, and check answers for physical reasonableness.

        Practical & Safety Notes:
        - Be familiar with common laboratory apparatus (balances, thermometers, stopwatches, lenses, ray boxes, power supplies, meters, bridges, ripple tanks, etc.) and safe handling practices.
        - Observe safety protocols when working with electricity, heat sources, glassware, X‑rays and radioactive sources.
        - Understand common sources of experimental error and how to minimise them (e.g. parallax, zero error, reaction time, heat loss).`,
  },
  {
    subject: 'Biology',
    timeAlloted: 50 * 60,
    description: `Senior Secondary Biology paper covering the structure and function of living organisms, laboratory and field skills, basic genetics and evolution, ecology and human/plant biology.

        Preamble & Aims:
        - Built from member countries’ curricula; to be read alongside each country’s teaching syllabus.
        - Assesses understanding of how living organisms are structured and function, appreciation of nature, and the interrelationships between biology and other sciences.
        - Emphasizes laboratory and field skills, scientific attitudes (observation, classification, interpretation), problem‑solving and the application of biological principles to health, environment, community and economic issues.

        Syllabus Focus (Section A – Common to All Countries):
        - Concept of Living Things & Organization of Life:
          • Classification of living and non‑living things; five kingdoms (Monera, Protoctista, Fungi, Plantae, Animalia) and major phyla/divisions.
          • Levels of organization: cell → tissue → organ → system; forms of cellular existence (single cells, colonies, filaments, tissues).
          • Cell structure and organelles; similarities/differences between plant and animal cells; cell and its environment (diffusion, osmosis, active transport).
          • Basic life processes at cellular level: nutrition, respiration (aerobic/anaerobic), excretion, growth and development, movement, and reproduction (asexual and sexual).
        - Support & Locomotion:
          • Skeleton and supporting systems in animals (bones, cartilage, chitin; exoskeleton, endoskeleton, hydrostatic skeleton) and their functions.
          • Supporting tissues in plants (turgid parenchyma, collenchyma, sclerenchyma, xylem) and how they confer strength, rigidity and flexibility.
        - Transport & Respiration:
          • Need for transport; surface area/volume relationships.
          • Transport media and systems in animals (blood, lymph; heart, vessels) and in plants (xylem, phloem; water and mineral uptake, translocation, transpiration).
          • Respiratory systems: body surface, gills, lungs and stomata; gaseous exchange in fish, toad, mammals and plants.
        - Excretion & Homeostasis:
          • Excretory structures and products in animals and plants (kidney, skin, stomata, lenticels; CO₂, water, nitrogenous wastes, plant exudates).
          • Regulation of internal environment (kidney, liver, skin) and basic homeostatic functions.
        - Coordination & Sensory Biology:
          • Hormonal coordination (endocrine glands, animal hormones; basic plant hormones/auxins).
          • Nervous coordination (central and peripheral nervous systems, neurones, reflex/voluntary actions, conditioned reflexes).
          • Sense organs (eye, ear, skin, tongue, nose): structure and function.
        - Reproduction & Life Cycles:
          • Human reproductive systems and gametes; fertilization, embryo development, birth and birth control.
          • Metamorphosis and life histories of selected insects (e.g. butterfly, cockroach), comparison across vertebrate groups.
          • Flowering plant reproduction: flower structure, pollination types and agents, placentation, fertilization, fruit/seed types and dispersal.
        - Nutrition & Energy in Living Systems:
          • Plant nutrition and photosynthesis (light and dark phases, conditions, evidence, fate of products).
          • Mineral nutrition (macro‑ and micro‑nutrients; soil/atmosphere as sources).
          • Animal nutrition: classes of food, balanced diet and malnutrition; food tests; digestive enzymes and modes of nutrition; structure and function of alimentary systems.
        - Ecology & Environment:
          • Ecosystem concepts and components (biotic/abiotic); ecological factors in aquatic and terrestrial systems.
          • Food chains, food webs, trophic levels, energy flow, pyramids of numbers/energy; decomposition.
          • Biological associations (parasitism, symbiosis, commensalism, saprophytism); adaptation of organisms to habitats.
          • Pollution (air, water, soil, noise), public health, and ecological management.
          • Population ecology: succession, population size/density, limiting factors, and simple pest biology/control.
        - Microorganisms & Health:
          • Roles of microorganisms in nature, industry and health (beneficial and harmful); carriers, common microbial diseases and control.
          • Basic public health measures: hygiene, refuse/sewage disposal, immunization/vaccination.
        - Conservation, Variation & Heredity:
          • Conservation of natural resources (soil, water, wildlife, forest, minerals).
          • Morphological and physiological variation in populations.
          • Genetics basics: terms (gene, genotype, phenotype, dominance, etc.), simple Mendelian inheritance, chromosomes as basis of heredity, simple linkage and sex‑linked traits, and practical applications (agriculture, medicine).
          • Adaptation and evolution: behavioural and structural adaptations; evidence and simple theories of evolution.

        Country‑Specific Extensions (Sections B & C – Ghana / Nigeria, Sierra Leone, The Gambia, Liberia):
        - Cell biology extensions (endocytosis/exocytosis, nucleic acids, DNA/RNA, protein synthesis, cell cycle).
        - Detailed diversity and life processes in selected organisms (protozoa, algae, fungi, mosses, ferns, insects).
        - Additional human anatomy/physiology (dissection, detailed heart, muscles, skeletal tissues, antenatal care, extra excretory and sensory topics).
        - Advanced ecology, evolution and applied biology topics (soil, population sampling, nutrient cycles, integrated water resources management, biotechnology, industrial biology, crime detection, paternity, etc.).

        Exam Structure:
        - Paper 1: Objective (Multiple Choice)
          • 50 multiple-choice questions from Section A (common core).
          • Duration: 50 minutes for 50 marks.

        - Paper 2: Theory / Essays (Sections A, B & C)
          • Section A: 4 essay questions from Section A; answer 2 (20 marks each).
          • Section B: short-structured questions from Section B (Ghana only), 30 marks.
          • Section C: short-structured questions from Section C (Nigeria, Sierra Leone, The Gambia, Liberia), 30 marks.
          • Duration: about 1 hour 40 minutes; total 70 marks.

        - Paper 3: Practical / Test of Practical Work
          • Section A: 2 compulsory questions from Section A (25 marks each).
          • Section B: 1 question from Section B (Ghana) for 30 marks.
          • Section C: 1 question from Section C (Nigeria, Sierra Leone, The Gambia, Liberia) for 30 marks.
          • Duration: 2 hours; total 80 marks.

        Guidelines for Candidates:
        - Practise drawing and labelling clear biological diagrams (cells, tissues, organs, life cycles, ecological diagrams) and learn to interpret them.
        - Master key definitions, processes and cycles (photosynthesis, respiration, nitrogen and carbon cycles, reproduction, inheritance, succession).
        - Build confidence in basic lab skills: handling apparatus, preparing slides, making observations, recording data, plotting/reading graphs and writing concise reports.
        - Use examples from local environments and agriculture to ground abstract concepts in real contexts.
        - Approach questions by linking structure to function (in cells, tissues, organs and ecosystems) and by explaining biological phenomena logically.
        - Revise with past questions from all three papers, paying attention to command words (describe, explain, compare, state, outline, etc.) and mark allocations.
        - Manage exam time carefully across objective, essay and practical components, and show working or reasoning where applicable.`,
  },
  {
    subject: 'Chemistry',
    timeAlloted: 60 * 60,
    description: `Senior Secondary Chemistry paper assessing basic concepts in physical, inorganic and organic chemistry, laboratory skills, and links between chemistry, industry, environment and everyday life.

        Preamble & Aims:
        - Assumes prior Integrated/Basic/General Science and Mathematics at JSS/JHS level and access to a functional laboratory.
        - Tests understanding of core chemical concepts, safe and efficient lab skills, relationships between chemistry and other disciplines, links to industry/environment (benefits and hazards) and critical/logical thinking.
        - Requires familiarity with SI units, significant figures and conventional/IUPAC nomenclature.

        Syllabus Focus (Section A – Common to All Countries):
        - Introduction to Chemistry:
          • Measurement of physical quantities (mass, length, time, temperature, volume), SI units, significant figures, precision and accuracy.
          • Scientific method: observation, hypothesis, experiment, formulation of laws and theories.
        - Atomic Structure & Periodic Chemistry:
          • Historical models (Dalton, Thomson, Rutherford, Bohr); sub‑atomic particles; atomic/mass numbers, isotopes, relative atomic/molecular masses (C‑12 scale).
          • Electron configuration (s, p, d up to Z = 30), orbitals, filling rules (Aufbau, Hund, Pauli).
          • Periodic law and trends (atomic/ionic size, ionisation energy, electron affinity, electronegativity); periodic gradation across period 3 and group 7; first‑row transition elements and their typical properties (variable oxidation states, coloured complexes, catalysis, magnetism).
        - Separation & Purity:
          • Types of mixtures and standard separation techniques (crystallisation, distillation, chromatography, sublimation, precipitation, magnetisation, etc.).
          • Criteria of purity: melting/boiling points.
        - Chemical Bonding & Structure:
          • Ionic, covalent (including dative), metallic and intermolecular forces (H‑bonding, van der Waals) and how they control physical properties (solubility, mp/bp, conductivity, hardness).
          • Simple molecular shapes (linear, trigonal planar, tetrahedral, etc.).
        - Stoichiometry & States of Matter:
          • Symbols, formulae, equations; empirical/molecular formulae; laws of chemical combination.
          • The mole, mass/volume/concentration relationships; preparation and dilution of standard solutions.
          • Kinetic theory, changes of state, Brownian motion and diffusion; gas laws and the ideal gas equation; basic ideas of real gases.
          • Nature and preparation of common gases (H₂, NH₃, CO₂) and their key reactions.
        - Energy & Energetics:
          • Energy vs enthalpy; exothermic/endothermic processes; simple enthalpy changes (combustion, dissolution, neutralisation).
        - Acids, Bases & Salts:
          • Arrhenius definitions; strong/weak electrolytes; behaviour of acids/bases on indicators and with metals, oxides and carbonates.
          • pH concept and scale; practical pH measurement and simple real‑world relevance (acid rain, soil, biological fluids).
          • Types and preparation of salts; hydrolysis and pH of salt solutions; deliquescent/efflorescent/hygroscopic substances.
          • Indicators and acid–base titrations (strong/weak combinations) for concentration, purity, composition and water of crystallisation.
        - Solubility & Equilibria:
          • Solubility, saturated solutions, solubility curves, effect of temperature and crystallisation as purification.
          • Simple solubility rules for qualitative analysis.
          • Rates of reaction (factors, collision/transition state ideas) and simple rate graphs.
          • Reversible reactions, dynamic equilibrium and qualitative Le Chatelier predictions.
        - Redox & Electrochemistry:
          • Oxidation/reduction in terms of O/H transfer, electrons and oxidation numbers; balancing redox equations.
          • Electrochemical cells, standard electrode potentials, simple cell notation, e.m.f. and applications (primary/secondary cells, fuel cells).
          • Electrolysis (cells, discharge of ions, Faraday’s laws) and applications (electroplating, extraction/purification of metals, corrosion and protection).
        - Basic Organic & Industrial Chemistry:
          • Classification of organic compounds, functional groups and IUPAC naming (up to C₅ for key series).
          • General properties: homologous series, isomerism.
          • Alkanes, alkenes, alkynes and benzene: sources, structures, principal reactions and simple detection tests.
          • Alkanols, alkanoic acids and alkanoates (esters): preparation, key reactions and uses.
          • Chemistry, industry and environment: key local raw materials, chemical industries, petrochemicals, pollution (air, water, soil), green‑house and ozone issues, and basic biotechnology/food processing.
          • Introductory biochemistry and polymers: proteins, amino acids, fats/oils, carbohydrates, synthetic polymers and their uses.

        Country‑Specific Extensions (Sections B & C):
        - Ghana (Section B): nuclear chemistry and radioactivity/half‑life; advanced periodic gradation, bonding (hybridisation, sigma/pi), solution concentration/dilution; Hess’s law and bond energies; extended acid–base and solubility equilibria; detailed kinetics/equilibrium constants; deeper organic reactivity; extra industrial chemistry (mining, cement, etc.).
        - Nigeria, Sierra Leone, The Gambia, Liberia (Section C): additional non‑metals and their compounds (C, O, H, halogens, N, S, noble gases, water/hardness); metals and their compounds (Na, Ca, Al, Fe, Cu, Sn, alloys) and more applied practical skills.

        Exam Structure:
        - Paper 1: Objective (Multiple Choice)
          • 50 questions from Section A (common core).
          • Duration: 1 hour for 50 marks.

        - Paper 2: Theory / Essay
          • 2‑hour paper (100 marks) with Sections A and B.
          • Section A: 10 short‑structured questions from Section A of syllabus (25 marks total).
          • Section B: 4 longer questions – 2 from Section A and 2 from the country‑specific section (B or C); answer any 3 (25 marks each, 75 marks total).

        - Paper 3: Practical / Alternative to Practical
          • School candidates: 2‑hour practical (3 compulsory questions) for 50 marks.
          • Private candidates: 1½‑hour alternative‑to‑practical with parallel coverage.
          • Typical components: one quantitative analysis, one qualitative analysis and one question based on prescribed practical activities.

        Guidelines for Candidates:
        - Build a strong foundation in core concepts (structure, bonding, stoichiometry, energetics, equilibrium, redox, organic) before tackling past questions.
        - Practise writing balanced equations, doing mole and titration calculations and interpreting graphs/tables.
        - Develop good lab habits: accurate measurement, neat titration technique, careful observation/recording, and awareness of hazards and safety.
        - Relate chemistry to familiar industrial and environmental issues (water treatment, corrosion, fuels, plastics, pollution control, food processing).
        - Use past WASSCE questions (Papers 1–3) to refine speed, accuracy and exam technique, paying attention to IUPAC naming and required units in numerical answers.`,
  },
  {
    subject: 'Agricultural Science',
    timeAlloted: 50 * 60,
    description: `Applied science paper built around crop and animal production, soil and environmental management, agricultural engineering, economics and extension, with strong emphasis on practical skills and farm records.

        Preamble & Aims:
        - Presents Agriculture as an applied science drawing from biology, chemistry, physics and economics.
        - Builds on basic agriculture from junior secondary level and expects schools to operate functional school farms (crops, at least one ruminant and one non‑ruminant, and a fish pond where possible).
        - Emphasizes continuous assessment, students’ practical notebooks/records and regular visits to research institutes and commercial farms.
        - Aims to sustain interest in agriculture and equip candidates with knowledge and skills for further studies and careers in the sector.

        Syllabus Focus (Theory – Papers 1 & 2):
        - Basic Concepts in Agriculture:
          • Meaning, branches and importance of agriculture to individuals, communities and national development.
          • Problems limiting agricultural development (land tenure, poor infrastructure, finance, transport, storage/processing, education/extension, inputs, marketing, environmental degradation) and broad solutions.
          • Subsistence versus commercial agriculture: characteristics, advantages, disadvantages and challenges.
          • Roles of government and NGOs in agricultural development (policies, programmes, credit, subsidies, research and extension).
          • Agricultural laws and reforms, especially land tenure systems and land use policies.
        - Agricultural Ecology & Soil Science:
          • Farm ecosystem and interactions among biotic and abiotic components in terrestrial and aquatic systems.
          • Land: meaning, characteristics, agricultural and non‑agricultural uses; physical, economic and socio‑cultural factors affecting land availability.
          • Agro‑allied industries and linkages between agriculture and industry.
          • Environmental factors affecting crops/animals: climate, biotic factors (pests, pathogens, weeds) and edaphic factors (soil texture, structure, pH, type).
          • Rock types and soil formation (weathering, soil profile, horizons).
          • Soil types, composition (water, air, mineral and organic matter, organisms), soil properties and pH management.
          • Plant nutrients (macro/micro), nutrient cycles (nitrogen, carbon, water, phosphorus), nutrient depletion and methods of replenishment (fertilizers, manures, crop rotation, fallow, cover crops, liming), plus organic agriculture.
          • Irrigation and drainage systems: types, advantages/limitations, importance and associated problems.
          • Agricultural pollution: causes (chemicals, oil spills, wastes), effects and control.
        - Agricultural Engineering/Mechanization:
          • Simple farm tools: types, uses and routine care.
          • Farm machinery and tractor‑coupled implements: identification, basic functions, simple maintenance and safety (engineering detail not required).
          • Agricultural mechanization: meaning, operations, advantages/limitations and future prospects.
          • Farm power sources (human, animal, mechanical and others) with their merits and drawbacks.
          • Farm surveying: basic instruments, uses, maintenance and importance.
          • Farm planning and farmstead layout: factors in site selection and arrangement of buildings, fields and facilities.
        - Crop Production & Related Areas:
          • Classification of crops by use, life cycle and morphology.
          • Husbandry of representative crops from major groups (cereals, pulses, roots/tubers, vegetables, fruits, beverages, spices, oil crops, fibres, latex crops and sugar cane), including land preparation, propagation, planting, cultural practices, pest/disease control, harvesting, processing and storage.
          • Pasture and forage crops: meaning, uses, types of pasture, common grasses/legumes, establishment, management and factors affecting productivity.
          • Crop improvement: aims, methods (introduction, selection, breeding), basic genetic concepts and Mendel’s laws.
        - Forestry, Ornamental Horticulture & Agro‑forestry:
          • Forestry: meaning, importance, regulations, forest management practices and consequences of deforestation.
          • Agro‑forestry practices (taungya, alley cropping, ley farming, etc.) and suitable tree species.
          • Ornamental plants: meaning, types (bedding, hedging, lawn grasses), settings, propagation methods and maintenance.
        - Crop Protection:
          • Crop diseases: general effects and specific examples across cereals, legumes, beverages, tubers, fruits, fibres, vegetables and stored produce; causal organisms, symptoms, transmission, prevention and control.
          • Crop pests: insect and non‑insect pests, classification by mouthparts and habitat; life cycles, damage, economic importance and control of key field and storage pests.
          • Weeds: meaning, types, characteristics, effects on crops/economy and control methods (cultural, biological, chemical, physical/mechanical).

        - Animal Production:
          • Types and classification of farm animals by habitat and uses.
          • Anatomy and physiology: major external parts, internal organs and organ systems (digestive, respiratory, circulatory, excretory, reproductive).
          • Reproduction in farm animals: reproductive organs, hormones, oestrus cycle, mating, gestation, parturition, lactation, colostrum and egg formation in poultry.
          • Environmental physiology: effects of temperature, humidity and light on growth, reproduction and production.
          • Livestock management systems (extensive, intensive, semi‑intensive), housing, feeding, hygiene and finishing of at least one ruminant and one non‑ruminant.
          • Animal nutrition: classes of feeds, nutrients and functions, rations/diets (maintenance and production), malnutrition (causes, symptoms, correction).
          • Rangeland and pasture management: importance, common grasses/legumes, productivity factors and improvement methods.
          • Animal improvement: aims, methods (introduction, selection, breeding), artificial insemination and its advantages/disadvantages.
          • Animal health: disease concepts, causal organisms, predisposing factors, resistance/susceptibility; major viral, bacterial, fungal and protozoan diseases; parasites (endo‑ and ectoparasites), their life cycles, economic importance and control.
          • General disease and parasite control measures (hygiene, quarantine, vaccination, breeding for resistance).
          • Aquaculture: meaning and types (fish, shrimp, crab); siting, establishment and maintenance of fish ponds; fishery regulations, fishing methods and tools.
          • Apiculture: meaning, types of bees, importance, traditional/modern bee keeping, equipment and safety precautions.

        - Agricultural Economics & Extension:
          • Basic economic principles (scarcity, choice, scale of preference, law of diminishing returns) and factors of production (land, labour, capital, management).
          • Demand and supply of agricultural produce: laws, determinants, shifts/movements and implications (price support, control, subsidy).
          • Farm management: functions of the farm manager, decision‑making and problems faced.
          • Agricultural finance: meaning, importance, sources and types of credit; challenges for farmers and lending institutions; capital market institutions and roles.
          • Farm records and accounts: types of records and basic accounts (income, expenditure, profit and loss, balance sheet, simple depreciation and budgeting concepts).
          • Marketing of agricultural produce: marketing channels, agents and functions; export crops, export procedures, cooperative and corporate exporters; benefits and problems in marketing.
          • Agricultural insurance: meaning, importance, major policy types, premiums and typical challenges.
          • Agricultural extension: meaning, importance, individual/group extension methods, major programmes in West Africa, constraints and qualities of an effective extension worker.

        Practical Agricultural Science (Paper 3):
        - Agricultural Ecology & Soils: identification of soil types, profiles and rocks; simple lab work on soil texture, bulk density, moisture content, water‑holding capacity, capillarity and pH; recognition and use of common fertilizers and organic manures; basic irrigation and drainage equipment.
        - Agricultural Engineering Tools & Equipment: identification, uses and care of simple farm tools, tractor and animal‑drawn implements, harvesting/processing/storage equipment, horticultural tools, livestock and fishing equipment, and basic surveying instruments.
        - Crop Production Practicals: identification of seeds, seedlings, fruits and storage organs; main pests and diseases of crops; planting dates, seed rates, plant population and germination/seed quality tests; bed preparation, fertilizer/manure application, mulching, watering, vegetative propagation techniques; common weeds and control.
        - Animal Production Practicals: identification and handling of common breeds; major internal organs; animal by‑products; feeds and feedstuffs; routine management practices (e.g. selection, culling, marking, dehorning, debeaking, castration); pests, parasites and disease control; fish harvesting, processing and preservation.

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple‑choice questions drawn from the entire syllabus.
          • Duration: 50 minutes for 50 marks.

        - Paper 2: Essay/Theory
          • 6 essay questions covering at least two major themes; candidates answer 5.
          • Duration: about 2 hours 10 minutes for 90 marks.

        - Paper 3: Practical / Alternative to Practical
          • 4 compulsory practical‑based questions taken in 1½ hours for 60 marks.

        Guidelines for Candidates:
        - Treat agriculture as both theoretical and practical: keep detailed farm and practical notebooks with observations, measurements and records.
        - Link topics across the syllabus (e.g. soil fertility with crop yields, pasture quality with animal performance, finance/marketing with farm management decisions).
        - Learn local examples of crops, animals, agro‑industries, government programmes and extension activities to enrich answers.
        - Practise drawing and labelling diagrams (tools, farm structures, soil profiles, animals’ organs, farm layouts) and presenting data in tables/charts.
        - Revise with past questions for all three papers, paying attention to command words and mark allocation, and practise calculations in farm management, finance and records.
        - Observe safe, responsible behaviour on school farms, laboratories and during field trips, and understand environmental impacts of farming practices.`,
  },
  {
    subject: 'Geography',
    timeAlloted: 60 * 60,
    description: `Evaluates candidates’ understanding of physical and human geography, map work, regional geography (home country, Africa and the wider world), and basic fieldwork skills, with strong emphasis on man–environment relationships and spatial thinking.

        Preamble & Aims:
        - Focuses on the spatial distribution of physical and human features of the earth’s surface and their interactions.
        - Emphasizes man–environment relations, environmental issues and sustainable use of resources.
        - Develops candidates’ ability to explain geographical concepts, analyze spatial problems and communicate findings using maps, diagrams, graphs and reports.
        - Assumes a regular teaching schedule (about three hours per week) and encourages classroom and local-environment investigations.

        Syllabus Focus – Elements of Practical & Physical Geography:
        - Map Work & Interpretation:
          • Meaning, types and uses of maps.
          • Reading and interpretation of contoured topographic maps of parts of West Africa.
          • Scale, measurement of distance, direction and bearing; map reduction and enlargement.
          • Identification of physical features (spurs, valleys, hills, plains, drainage patterns) and cultural features (settlements, routes, land use, city walls, etc.).
          • Measurement of gradients, drawing and interpretation of cross profiles, inter-visibility.
          • Description and explanation of drainage, settlement and communication patterns.
        - Elementary Surveying (*for some countries):
          • Basic concepts, instruments (chain, tapes, prismatic compass), simple traverse and plotting, avoiding obstacles.
        - Geographic Information Systems – GIS (**for Nigeria):
          • Basic concepts and components (hardware, software, data, procedures, people).
          • Data sources (surveying, remote sensing, digitizing, scanning, fieldwork, tabular data).
          • Uses (defence, agriculture, urban planning, transportation, census, mapping, etc.) and challenges (power, personnel, capital).
        - Statistical Maps and Diagrams:
          • Graphical presentation of geographical data: bar and line graphs, flow charts, dot maps, proportional circles, density and isopleth maps.

        - Elements of Physical Geography:
          • The Earth as a Planet: earth–sun relationships, latitude/longitude, distance and time, rotation and revolution and their effects; internal and external structure of the earth.
          • Hydrosphere: ocean basins, salinity, ocean currents and their effects on adjacent coasts, lakes, rivers, lagoons and water as an environmental resource.
          • Rocks: types (igneous, sedimentary, metamorphic), characteristics, formation and uses.
          • Tectonic & Denudational Processes: vulcanicity, earthquakes, mountain building, plains, karst and coastal landforms; agents of denudation (weathering, mass movement, running and underground water, wind, waves) and resulting landforms.
          • Weather & Climate: simple weather observations, Stevenson’s screen and common instruments (rain gauge, thermometer, barometer, wind vane, etc.).
          • Climate Elements & Classification: rainfall, temperature, pressure, wind, humidity, sunshine and cloud; controls such as latitude, altitude, ocean currents, continentality, aspect; interpretation of climatic graphs and tables; major climate types (equatorial, tropical continental, desert, warm and cool temperate) and basic Greek/Köppen classification.
          • Climate Change (**for some countries): meaning, causes, effects and remedies.
          • Vegetation (*): major world vegetation types (tropical rainforest, tropical grassland/savanna, cool/warm temperate woodland); characteristics, distribution, controlling factors, plant communities and conservation; vegetation as an environmental resource.
          • Soils (*): local soil types and characteristics, factors and processes of soil formation, soil profile and importance to man; human impacts on soil, erosion types and control, conservation.
          • Environmental Resources & Problems: renewable and non‑renewable resources (vegetation, water, minerals, atmosphere, etc.), their importance; environmental hazards (erosion, drought, desert encroachment, flooding, pollution) – causes, effects and prevention; environmental conservation – meaning, methods, problems and solutions.

        Syllabus Focus – Economic & Human Geography:
        - World Population: factors and patterns of growth, distribution and movement; growth rates and related problems.
        - Settlement: types (rural/urban), patterns and locational factors; growth, size, functions and land use; interaction patterns (rural–urban, urban–urban, rural–rural) and migration.
        - Transportation: modes (road, rail, water, air, pipeline, cable, ropeways); role in economic development (movement of people/commodities, trade, diffusion of ideas and technology, national integration); problems and solutions.
        - Industry: classification (primary, secondary, tertiary), heavy/light industries, factors of industrial location, contributions to development, problems and solutions.
        - Trade: meaning, types (national and international), reasons and importance.
        - Tourism: meaning, main centres, reasons (leisure, recreation, education, etc.), importance, problems and remedies.

        Syllabus Focus – Regional Geography:
        - Regional Geography of the Candidate’s Home Country (e.g. Nigeria, Ghana, Sierra Leone, Liberia, The Gambia/Senegambia):
          • Location, size, position and political divisions.
          • Physical setting: relief, drainage, climate, vegetation and soils.
          • Population: size, distribution, density, structure/quality, migration; data sources, problems and solutions.
          • Resources: mineral, power, water and vegetation resources, their distribution, exploitation, importance and associated problems/solutions.
          • Agriculture: major systems and practices, food and cash crops, importance, constraints and remedies.
          • Transportation and Communication: networks, advantages/limitations, influence on human activities, problems and solutions.
          • Industry, Trade & Commerce: types and distribution of industries, location factors, major commercial centres and stock exchanges where applicable.
          • Tourism: key attractions, development, socio‑economic importance, problems and solutions.
          • Issues of Development & Environment: rural and regional development, resource management, pollution (air, water, soil, noise), waste disposal and conservation.
          • Sub‑regional integration where relevant (e.g. ECOWAS): membership, objectives, benefits, problems and solutions.
        - Regional Geography of Africa:
          • Africa in outline: location, size, position, islands, physical features and their economic importance; basic mineral distribution.
          • Selected topics: irrigation agriculture (Nile, Niger basins), plantation agriculture in West/East Africa, oil production (Nigeria, Ghana, Libya), lumbering in Equatorial Africa, gold mining (e.g. South Africa), copper mining (e.g. Zambia, DR Congo), population distribution in West Africa, ECOWAS.

        Fieldwork & Local Geography:
        - Fieldwork on local topics such as land use (rural or urban), market surveys, traffic flow, journey‑to‑work patterns, erosion studies, etc.
        - Based on the candidate’s home area and examined mainly through school‑based continuous assessment (typically about 25% of CA marks).
        - Emphasizes planning investigations, data collection, analysis and report writing.

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple‑choice questions from common topics for all member countries.
          • Duration: 1 hour for 50 marks.

        - Paper 2: Essay / Theory
          • 9 essay questions split into sections: A (Economic & Human Geography), B (Regional Geography of candidate’s home country) and C (Regional Geography of Africa, for some countries).
          • Candidates answer 4 questions in 2 hours for 80 marks, with required spread across sections depending on country.

        - Paper 3: Elements of Practical & Physical Geography
          • 8 questions; candidates answer 4 in 1 hour 50 minutes for 70 marks.
          • Question 1 (map reading and interpretation) is compulsory and carries 25 marks; others carry 15 marks each.
          • Candidates bring simple non‑programmable calculators, mathematical sets, graduated rulers and string.

        Guidelines for Candidates:
        - Practise map reading regularly: scales, bearings, gradients, profiles, settlement/land‑use patterns and drainage interpretation.
        - Learn to present and interpret data using appropriate graphs, maps and diagrams.
        - Relate physical processes (tectonics, denudation, climate, hydrology) to resulting landforms, resources and human activities.
        - Use local and regional examples when discussing population, settlement, transport, industry, trade and environmental issues.
        - Revise case studies for regional geography of home country, Africa and selected economic activities.
        - Undertake simple fieldwork projects and write concise, well‑structured reports linking observations to geographical concepts.
        - Manage exam time across all three papers and follow command words (describe, explain, analyze, compare, account for, etc.) carefully.`,
  },
  {
    subject: 'Economics',
    timeAlloted: 60 * 60,
    description: `Assesses candidates’ understanding of basic economic principles and tools, the structure and functioning of economic institutions, and the major issues in West African and international economic development.

        Preamble & Objectives:
        - Provides the economic reasoning needed for rational decision making by individuals, firms, government and society.
        - Helps candidates appreciate government economic policies, implementation challenges and their impact on the economy.
        - Emphasizes that economics is both an academic discipline and a practical subject related to everyday choices.
        - Tests candidates’ ability to:
          • Understand key concepts, principles and tools of economic analysis.
          • Describe how commercial, agricultural, industrial and financial institutions function.
          • Explain the structure of West African economies and the roles of agriculture, industry and mining in national income.
          • Follow West Africa’s position in international economic relations.
          • Recognize major development problems facing West African countries.

        Syllabus Focus – Microeconomic Foundations:
        - Definition & Scope of Economics:
          • Scarcity, choice, scale of preference, opportunity cost and the production possibility curve.
          • Economic activities (production, distribution, consumption) and their classification (primary, secondary, tertiary) with contributions to output, employment, savings, investment and foreign exchange.
        - Factors of Production:
          • Land, labour, capital and entrepreneurship – meanings, characteristics and importance.
        - Economic Systems:
          • Types: capitalism, socialism and mixed economy.
          • Basic features, advantages and disadvantages of each system.
          • Fundamental economic problems (what, how, for whom to produce) and how each system addresses them.
        - Basic Tools of Economic Analysis:
          • Use of tables, graphs and charts.
          • Simple statistical measures: arithmetic mean, median, mode and basic applications.
        - Demand:
          • Concept and law of demand; schedules and curves; exceptional demand curves and reasons.
          • Types of demand (derived, composite, joint, competitive) and determinants (own price, prices of related goods, income, tastes, expectations, etc.).
          • Distinction between movement along and shift of the demand curve.
          • Elasticity of demand: price, income and cross elasticities – measurement and importance to consumers, producers and government.
        - Supply:
          • Concept and law of supply; schedules and curves.
          • Types of supply (composite, complementary, competitive) and determinants (input prices, technology, prices of other goods, climate, etc.).
          • Distinction between movement along and shift of the supply curve.
          • Elasticity of supply: measurement and significance for producers and government.
        - Theory of Consumer Behaviour:
          • Utility concepts (total, average, marginal); utility schedules and the law of diminishing marginal utility.
          • Relationships among TU, AU and MU; consumer equilibrium and the effect of price changes on equilibrium.
          • Link between marginal utility and the demand curve.
        - Price Determination:
          • Concept of a market; interaction of demand and supply in product and factor markets.
          • Equilibrium price and quantity; effects of shifts in demand or supply.
          • Algebraic introduction to equilibrium (simple linear demand and supply functions).
          • Price controls (maximum/minimum prices), rationing and black/parallel markets.
        - Theory of Production:
          • Production, division of labour and specialization.
          • Scale of production (internal and external economies) and their advantages.
          • Total, average and marginal product; law of variable proportions.
        - Theory of Cost & Revenue:
          • Cost concepts: total, average, marginal, fixed and variable costs; short-run vs long-run costs.
          • Economist’s vs accountant’s cost (opportunity vs money cost).
          • Revenue concepts: total, average and marginal revenue; marginal revenue product.
        - Market Structures:
          • Characteristics of key market types: perfect competition, monopoly and monopolistic competition.
          • Determination of price and output under different structures (basic diagrams and logic).
          • Price discrimination: meaning and simple illustrations.

        Syllabus Focus – Firms, Trade & Factor Markets:
        - Business Organizations:
          • Forms and features: sole proprietorship, partnership, joint-stock companies (private/public), co-operatives, statutory corporations and joint ventures.
          • Sources of business finance; general problems of enterprises.
          • Privatization and commercialization as responses to public enterprise problems; indigenization and nationalization policies.
        - Distributive Trade:
          • Process of moving goods from producers to final consumers.
          • Roles of producers, wholesalers, retailers, co-operatives and government agencies.
          • Problems of distribution and suggested solutions.
        - Population & Labour Market:
          • Population size, growth and structure; rural-urban migration and Malthusian theory.
          • Geographical, age, sex and occupational distributions; census, its importance and problems.
          • Under-, optimum and over-population in relation to economic development.
          • Labour force and human capital: efficiency, mobility and factors affecting labour supply.
          • Demand and supply of labour and wage determination; unemployment and underemployment.
          • Role of trade unions, employers’ associations and government labour/wage policies.
        - Agriculture:
          • Structure of agriculture (food crops, export crops, livestock, fisheries).
          • Systems: peasant, commercial, co-operative and state farming.
          • Importance of agriculture, marketing of produce (e.g. commodity boards), agricultural policies (minimum prices), problems and remedies.
        - Industrialization:
          • Meaning, types of industry, and related concepts (plant, firm, industry, industrial estates).
          • Industrial location and localization; role of industry in development.
          • Industrialization strategies, problems and links between agriculture and industry.

        Syllabus Focus – Macroeconomics & Development:
        - National Income:
          • Concepts: GDP, GNP, NNP and related measures.
          • Methods of measurement (output, income, expenditure) and limitations.
          • Uses of national income data; trends and sectoral structure of national income.
        - Money & Inflation:
          • Money: definition, historical development (barter and its problems), types, characteristics and functions.
          • Supply and demand for money; value of money and price level.
          • Inflation: meaning, types, causes, effects and control measures.
        - Financial Institutions:
          • Types: traditional institutions, central bank, commercial banks, development and merchant banks, insurance companies, building societies, etc.
          • Development and functions of financial institutions.
          • Money and capital markets: meaning, types and roles.
        - Public Finance:
          • Fiscal policy and objectives of public finance.
          • Sources of government revenue, especially taxation (direct/indirect): objectives, merits, demerits, incidence.
          • Canons of taxation; tax rate structures (proportional, progressive, regressive).
          • Public expenditure (recurrent and capital): composition, structure and effects.
          • Government budgets and national debt.
        - Economic Development & Planning:
          • Meaning of economic development; distinction between growth and development.
          • Characteristics and problems of developing countries.
          • Elements and objectives of development planning; planning problems.
          • Types of plans: short-term, medium-term, long-term/perspective and rolling plans.

        International Economics, Integration & Resources:
        - International Trade & Balance of Payments:
          • Domestic vs international trade; basis of trade (absolute and comparative advantage).
          • Terms of trade: definition and simple measurement.
          • Commercial policy objectives and instruments: tariffs (types) and quantitative controls; structure and trends of West African external trade.
          • Balance of payments: meaning, components, causes of disequilibrium; adjustment and financing (exchange rate policy, exchange control, monetary/fiscal policies, use of reserves and borrowing).
        - Economic Integration:
          • Objectives, levels and key features of economic integration.
          • Development and problems of integration efforts in West Africa, especially ECOWAS.
        - International Economic Organizations:
          • Origin and roles of organizations such as OPEC, ECA, IMF, IBRD/World Bank, AfDB, UNCTAD, etc.
          • Relevance of these bodies to West African economies.
        - Major Natural Resources:
          • Development of key resources (petroleum, gold, diamond, timber, groundnut, etc.).
          • Positive and negative effects of resource exploitation on West African economies.

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple-choice questions covering the whole syllabus.
          • Duration: 1 hour for 50 marks.

        - Paper 2: Essay / Data Response
          • 8 questions divided into Sections A and B.
          • Section A: 2 data-response questions; Section B: 6 free-response/essay questions.
          • Candidates answer 4 questions in total: 1 from Section A and any 3 from Section B.
          • Duration: 2 hours for 80 marks.

        Guidelines for Candidates:
        - Build strong command of basic concepts, diagrams and simple algebra in demand/supply, elasticity, costs, revenues, market structures and macro indicators.
        - Regularly relate theory to West African examples (agriculture, industry, trade, public finance, monetary policy).
        - Practise interpreting tables, charts and data-response questions.
        - Use precise economic terminology in explanations and diagrams, and pay attention to command words (define, explain, distinguish, illustrate, evaluate, etc.).
        - Attempt past questions to improve speed, clarity of written answers and exam technique.`,
  },
  {
    subject: 'Government',
    timeAlloted: 60 * 60,
    description: `Assesses candidates' understanding of core concepts, principles, institutions and processes of government, with emphasis on citizenship, political and constitutional development in West Africa, and international relations.

        Aims & Objectives:
        - Enable candidates to define government in its various senses (institution, process and field of study) and understand how it works.
        - Help them recognize their roles as informed citizens and how they can contribute to national development.
        - Develop the ability to evaluate the successes, failures and problems of governments in West Africa.
        - Build capacity to analyze colonial and post‑independence constitutional developments.
        - Foster appreciation of their country's role in the international community.

        Syllabus Focus – Section A: Elements of Government
        - Meaning & Scope of Government:
          • Government as an institution of the state: meaning of state, its features, structure and functions.
          • Government as a process or art of governing: decision‑making, policy formulation and implementation.
          • Government as an academic field of study: reasons for studying government and its relevance.
        - Basic Concepts & Principles:
          • Concepts: state, society, nation, power, authority, legitimacy, sovereignty, democracy, political culture and socialization.
          • Systems and ideologies: communalism, feudalism, oligarchy, liberalism, socialism, communism, capitalism, fascism, aristocracy, totalitarianism – meanings and key features.
          • Principles: rule of law, fundamental human rights, separation of powers and checks and balances, constitutionalism, political participation, representative government, centralization and decentralization (delegated legislation, devolution).
        - Constitutions:
          • Definition, sources (custom, conventions, legislation, judicial precedents, written documents, etc.).
          • Functions of a constitution.
          • Types and features: written vs unwritten; rigid vs flexible.
        - Organs of Government:
          • Executive, legislature and judiciary: structure, composition/membership, functions, powers and limitations.
          • Judicial independence: meaning and safeguards.
        - State Structure & Forms of Government:
          • Types: unitary, federal, confederal, presidential (non‑parliamentary), parliamentary (cabinet), monarchical and republican.
          • Meanings, essential features, merits and demerits of each.
        - Citizenship:
          • Meaning and modes of acquiring citizenship (birth, descent, registration, naturalization, etc.).
          • Rights, duties and obligations of citizens; ways of safeguarding rights.
        - Political Parties & Party Systems:
          • Political parties: definition, structure/organization and functions.
          • Party systems: one‑party, two‑party and multi‑party systems – definitions, advantages and disadvantages.
        - Pressure Groups, Public Opinion & Mass Media:
          • Pressure groups: meaning, types, methods (lobbying, protests, media campaigns) and functions.
          • Public opinion: meaning, formation, measurement and importance.
          • Mass media: meaning, roles and impact on politics and governance.
        - Electoral Systems, Processes & Electoral Management Body:
          • Elections: meaning and purposes.
          • Franchise: meaning, qualifications and limitations.
          • Electoral systems: types (e.g. simple majority, proportional representation, second ballot) with advantages and disadvantages.
          • Electoral management body: meaning, functions, constraints and problems.
        - Public/Civil Service Administration:
          • Public/civil service: meaning, structure, characteristics and functions.
          • Public/civil service commission: meaning and functions.
          • Public corporations: definition, purposes, functions, control, challenges, and reasons for commercialization/privatization.
          • Local government: meaning, structure, purposes, functions, sources of revenue, control and typical problems.

        Syllabus Focus – Section B: Political & Constitutional Developments in West Africa and International Relations
        - Pre‑colonial Political Systems (by Country):
          • Structural organization of major pre‑colonial polities, e.g.: Hausa/Fulani, Yoruba and Igbo (Nigeria); Akan and Talensi (Ghana); Mende and Temne (Sierra Leone); Wolof, Mandingo and Jola (The Gambia); Vai and Kru (Liberia).
        - Colonial Administration:
          • British colonial structures: crown colony, protectorate, indirect rule.
          • French colonial policies: assimilation and association (including Loi Cadre).
          • Impacts, advantages and disadvantages of colonial rule.
        - Nationalism:
          • Meaning, factors promoting nationalism and its effects.
          • Key nationalist leaders and movements in each country and their contributions.
        - Constitutional Developments (Pre‑ and Post‑Independence):
          • Major pre‑independence constitutions in candidates' countries (e.g. Clifford, Richards, Macpherson, Lyttleton, Burns, Arden‑Clarke, etc.): main features, merits and demerits.
          • Post‑independence constitutions: independence and republican constitutions, later constitutional changes and reforms; their origins, features, strengths and weaknesses.
        - Development of Major Political Parties:
          • Origins, objectives, sources of finance, achievements and failures of key parties in each country.
        - Military Rule in West Africa:
          • Causes and effects of military intervention in politics.
          • Overview of major military regimes in relevant countries (Nigeria, Ghana, Sierra Leone, The Gambia, Liberia).
        - Federal & Unitary Arrangements:
          • Experience of federal and unitary systems in West African states (origins, structures, features, problems).
        - Foreign Policy of West African States:
          • Meaning of foreign policy; factors influencing it (history, geography, ideology, economy, personality of leaders, etc.).
          • Objectives, advantages and disadvantages of the foreign policies of candidates' home countries.
        - International & Regional Organizations:
          • Origins, aims, achievements and problems of: United Nations Organization (UNO); Commonwealth of Nations; African Union (AU) and NEPAD; Economic Community of West African States (ECOWAS).

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple‑choice questions drawn from the entire syllabus.
          • Duration: 1 hour for 40 marks.

        - Paper 2: Essay
          • 2‑hour paper with two sections.
          • Section A (Elements of Government): 5 questions; candidates answer any 2.
          • Section B (Political & Constitutional Developments in West Africa and International Relations): country‑specific set of 5 questions; candidates answer any 2 from their own country's set.
          • Total: 60 marks.

        Guidelines for Candidates:
        - Master key concepts, principles and institutions of government and illustrate them with West African examples.
        - Study pre‑colonial systems, nationalist movements and constitutional changes for the candidate's country in detail.
        - Follow contemporary political developments and relate them to syllabus themes (citizenship, rights, parties, elections, public service, foreign policy).
        - Practise writing clear, well‑structured essays that define terms, analyze issues and evaluate successes/failures.
        - Use past WASSCE Government questions to build familiarity with question styles and to improve exam technique.`,
  },
  {
    subject: 'History',
    timeAlloted: 60 * 60,
    description: `Assesses candidates’ knowledge of their national histories and the wider West African and global context from earliest times to about 2000, and their ability to interpret, analyze and relate past events to present realities.

        Preamble & Aims:
        - Builds understanding of national history (The Gambia, Ghana, Liberia, Nigeria or Sierra Leone) from earliest times to 2000, with focus on relations between peoples and states.
        - Develops intellectual skills of historical interpretation, analysis and argumentation.
        - Encourages use of historical insight to relate the past to current issues and to appreciate factors that promote national unity and global understanding.
        - Exposes candidates to similarities and differences in national, social and political institutions across West Africa.
        - Strengthens knowledge of main developments in West African and world history and their interconnections.

        Syllabus Focus – Paper 1: West Africa & the Wider World (Earliest Times–2000):
        - Historiography & Historical Skills:
          • What history is and why we study it.
          • Sources of history (oral, written, archaeological, linguistic, artistic, etc.).
          • Historical methods and skills (ancient and modern approaches) and the prospects of ICT in historical studies.
        - Trans‑Saharan Trade:
          • Origin, organization and major routes.
          • Role in the rise and development of West African states and civilizations.
        - Islam in West Africa:
          • Introduction, spread and religious, social, cultural and political effects.
        - Early European Contact with West Africa:
          • Motives for European arrival; nature of early trade and interaction.
          • Immediate effects and African responses.
        - Trans‑Atlantic Slave Trade:
          • Origin, organization and main actors.
          • Economic, social and political effects on West Africa and the wider world.
          • Suppression and its consequences.
        - Christian Missionary Activities:
          • Linkages between suppression of the slave trade and missionary work.
          • Educational, medical, religious and socio‑cultural impact on West Africa.
        - Scramble for & Partition of West Africa:
          • Industrial Revolution and scramble for colonies.
          • Partition, colonial conquest and subjugation; African reactions.
        - Colonial Rule in West Africa:
          • Patterns of rule and colonial administration.
          • Consolidation of European culture; colonial economy and underdevelopment.
          • Colonial Africa and the First and Second World Wars.
        - Problems of Independent West African States:
          • Nature of post‑independence politics; neo‑colonialism and economic underdevelopment.
          • Uneven development, instability, military intervention, boundary disputes and threats to unity.
        - West Africa & International Organizations:
          • Membership, aims, achievements and challenges of organizations such as the UN, OAU/AU, ECOWAS, etc., and their relevance for West Africa.

        Syllabus Focus – Paper 2: National Histories (Earliest Times–2000):
        - Structure by Country & Period:
          • Separate sets of questions for The Gambia, Ghana, Liberia, Nigeria and Sierra Leone.
          • Each set organized chronologically (for most: earliest times–c.1800; 19th century; 1900–2000; for Ghana: landmarks of African history, Ghana and the wider world, and Ghana 1900–1991).
        - Core Themes (illustrated with country‑specific topics):
          • Land and People: major geographical zones, demographic/ethnic and linguistic groups; impact of environment on economic activities.
          • Early Civilizations, States & Societies: centres of ancient civilization; centralized and non‑centralized states; kingdoms, chiefdoms and confederacies; political, social, religious and economic organization.
          • Indigenous Crafts & Industries: technologies such as pottery, iron working, weaving, carving, metal casting, boat building and their social/economic importance.
          • Migrations & Inter‑group Relations: causes and effects of migrations; trade, intermarriage, bilingualism and political relations.
          • External Influences: Trans‑Saharan trade and Islam; early European contact (trade, Christianity and impacts); the trans‑Atlantic slave trade at country level.
          • 19th‑Century Transformations: jihads and the Sokoto Caliphate, Borno under the Shehus, Yoruba politics, Benin, Soninke‑Marabout and religious wars, resistance to colonial intrusion, creation of colonies and protectorates.
          • Colonial Rule & Economy: patterns of British or other colonial administration; indirect rule; economic and social developments (agriculture, mining, transport, education, health, urbanization).
          • Nationalism & Decolonization: early and later nationalist organizations and leaders; constitutional changes; negotiations and attainment of independence.
          • Post‑Independence Developments to 2000: political regimes (civilian and military), coups, civil wars and reconstruction; constitutional changes; social and economic development issues; attempts at national integration and democratization.
          • Country in the International Community: participation in UN, Commonwealth, OAU/AU, ECOWAS and other regional bodies; contributions to peacekeeping and diplomacy.
          • Global Issues: West African diaspora, racism, debt relief, international aid, peace and security, development goals.

        Exam Structure:
        - Paper 1: Objective (Common to All Candidates)
          • 50 multiple‑choice questions on West Africa and the wider world.
          • Duration: 1 hour for 40 marks.

        - Paper 2: Essay (National History)
          • 2‑hour paper with country‑specific sets split into 3 chronological sections.
          • For Nigeria, Sierra Leone, The Gambia and Liberia: Section A (earliest times–c.1800), Section B (19th century), Section C (1900–2000).
          • For Ghana: Section A (Landmarks of African history), Section B (Ghana and the wider world to c.1900), Section C (Ghana 1900–1991).
          • Each section has 3 questions; candidates answer 4 questions in total with at least one from each section.
          • Total: 60 marks.

        Guidelines for Candidates:
        - Build a clear chronological framework of major events, personalities and processes in both West African and national history.
        - Practise source‑based and essay questions that require explanation, analysis and evaluation rather than rote listing.
        - Use maps, timelines and thematic charts (e.g. trade, religion, politics) to organize knowledge.
        - Regularly connect national history topics with wider West African and global developments (trade, colonization, wars, international organizations).
        - Use precise historical terms, dates (where important) and place‑names, and structure essays with introductions, well‑argued bodies and concise conclusions.
        - Review past WASSCE History questions for both Paper 1 and Paper 2 to strengthen exam technique and time management.`,
  },
  {
    subject: 'Christian Religious Studies',
    timeAlloted: 60 * 60,
    description: `Explores key Old and New Testament themes and passages to build candidates’ understanding of the biblical, historical and social context, moral lessons, and their relevance to personal life, peaceful coexistence and national development.

        Aims & Objectives:
        - Provide knowledge, understanding and appreciation of the religious, cultural, historical and social background of prescribed biblical materials.
        - Help candidates grasp the nature of biblical moral lessons and evaluate their relevance and application to contemporary social, political, religious and cultural issues.
        - Enable candidates to identify difficulties in applying healthy biblical attitudes and concepts to ensure peaceful coexistence and national development.
        - Develop critical thinking and independent study skills as preparation for higher education.

        Syllabus Focus – Section A: Themes from the Old Testament:
        - The Sovereignty of God:
          • God as Creator and Sustainer of the universe (creation narratives and God’s control over all things).
        - Leadership Roles:
          • Joseph, Moses, Joshua and Deborah – their call, roles, challenges and models of faithful leadership.
        - Parental Responsibility:
          • Eli and Samuel – failures in parenting, consequences and lessons on family responsibility.
        - Consequences of Disobedience:
          • Saul’s disobedience, rejection by God and the implications of failing to obey divine instructions.
        - Submission to the Will of God:
          • David’s submission to God’s will in times of trial.
          • David’s sin, repentance and forgiveness – God’s mercy and the seriousness of sin.
        - Making Decisions:
          • Solomon’s wisdom, wise judgment and the blessings of seeking God’s guidance.
          • Unwise decisions by Solomon and Rehoboam and their impact on the nation.
        - Supremacy of God:
          • Religious tensions under Ahab; Elijah at Mount Carmel; God’s power over false gods and the call to true worship.
        - Greed and Its Effects:
          • Ahab and Naboth’s vineyard; Gehazi’s greed – personal and national consequences of covetousness and corruption.
        - Religious Reforms:
          • King Josiah’s reforms and the importance of returning to God’s word.
        - Concern for One’s Nation:
          • Nehemiah and Ezra – concern for the condition of the nation, prayer, leadership and sacrificial service in rebuilding.
        - Faith in God:
          • Faith and courage in the face of persecution (e.g. the three Hebrews in the furnace; Daniel in the lions’ den).
        - Nature of God:
          • True religion and social justice in Amos – God’s concern for righteousness, justice and the poor.
          • God’s steadfast love and faithfulness in Hosea – covenant love, repentance and restoration.

        Syllabus Focus – Section B: Themes from the Synoptic Gospels & Acts:
        - Baptism & Temptation of Jesus:
          • Jesus’ baptism – significance of his identity and mission.
          • Temptation narratives – nature of temptation, victory over sin and reliance on Scripture.
        - Call & Demands of Discipleship:
          • Call of the first disciples and Matthew; conditions and cost of following Christ.
        - Teaching on Forgiveness:
          • Lord’s Prayer, parable of the unforgiving servant and other passages emphasizing forgiving others as God forgives.
        - Trials of Jesus:
          • Gethsemane, Peter’s denial, Jewish and Roman trials, injustice and Jesus’ submission to the Father’s will.
        - Crucifixion, Burial & Resurrection:
          • Accounts of the crucifixion and burial; theological meaning of Jesus’ death.
          • Resurrection appearances and their implications for faith, hope and mission.
        - Fellowship in the Early Church:
          • Life of the earliest Christians: prayer, teaching, breaking of bread, sharing of possessions, discipline and service.
        - Holy Spirit & Mission to the Gentiles:
          • Coming of the Holy Spirit at Pentecost; empowerment for witness.
          • Mission to the Gentiles (e.g. Paul’s call and ministry) and expansion of the church beyond Judaism.
        - Opposition to the Gospel Message:
          • Persecution of apostles and early believers; God’s deliverance, steadfastness in suffering and growth of the church.

        Syllabus Focus – Section C: Themes from Selected Epistles:
        - Epistle of James:
          • Faith and works – relationship between genuine faith and good deeds.
          • Impartiality – warning against discrimination and class prejudice in the Christian community.
          • Effective prayer – conditions for answered prayer, persistence and faith in times of trial.
        - First Epistle of Peter:
          • Good citizenship – responsibilities to governing authorities.
          • Christian living among non‑Christians – witness through holy conduct, endurance in suffering and hope.
          • Interpersonal relationships among Christians – humility, mutual care, leadership responsibilities and resisting the devil.

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple‑choice questions on all prescribed themes.
          • Duration: 1 hour for 40 marks.

        - Paper 2: Essay
          • 9 essay questions in three sections (A: Old Testament, B: Synoptic Gospels & Acts, C: Selected Epistles).
          • Candidates answer 4 questions in 2 hours: at least 1 from each section, and the 4th from either Section A or B.
          • Total: 60 marks.

        Guidelines for Candidates:
        - Read all prescribed passages carefully in the Revised Standard Version (RSV) and know key stories, teachings and characters.
        - Focus on the historical and social context as well as the moral and spiritual lessons.
        - Be ready to apply biblical teachings to contemporary issues such as leadership, justice, family life, forgiveness, corruption, nation‑building and peaceful coexistence.
        - Practise answering past CRS questions with clear, organized essays that accurately reference passages and draw practical applications.
        - Cultivate critical reflection on challenges of living out biblical values in modern society.`,
  },
  {
    subject: 'Islamic Religious Studies',
    timeAlloted: 50 * 60,
    description: `Covers the historical and contemporary development of Islam, key Qurʼanic passages, Hadith, and core concepts in Tawhid and Fiqh, with emphasis on how Islamic teachings guide spiritual, moral, social and economic life.

        Aims & Objectives:
        - Assess candidates’ knowledge of the historical and contemporary development of Islam and the lessons derived from them.
        - Highlight the spiritual, moral, socio‑economic and intellectual roles of Islam in society.
        - Emphasize the practical application of Islamic teachings in daily life.
        - Organize content into four main parts: Tarikh (history), Qurʼan, Hadith, and Tawhid & Fiqh (theology and jurisprudence).

        Syllabus Focus – Part 1: Tarikh (Historical Development of Islam):
        - The Jahiliyyah (Pre‑Islamic Arabia):
          • Description and location of Arabia before Islam.
          • Social life: marriage, divorce and inheritance practices.
          • Religious life: idol worship and major deities.
          • Political organization and systems of government.
          • Economic life: main sources of livelihood.
          • Reforms introduced by Islam to correct Jahiliyyah practices.
        - Life of Prophet Muhammad (S.A.W.):
          • Birth, early life and marriage.
          • Call to prophethood and the core of his mission.
          • Hijrah (migration): causes, course and significance.
          • Formation of the Ummah and the Madinan Constitution.
          • Leadership qualities of the Prophet.
          • Major battles (Badr, Uhud, Khandaq): causes, courses and effects.
          • Treaty of Hudaybiyyah: terms and outcomes.
          • Conquest of Makkah.
          • Farewell Pilgrimage: rites, sermon and lessons.
          • Death of the Prophet.
        - The Khulafāʼu r‑Rāshidīn (Rightly Guided Caliphs):
          • Biographies of Abū Bakr, ʿUmar, ʿUthmān and ʿAlī.
          • Their major contributions to Islam and lessons from their lives.
        - Islam in West Africa:
          • Roles of traders, teachers, murābiṭūn, Sufi orders, mujaddidūn (reformers) and Hajj in spreading Islam.
          • Impact of Islam on the socio‑political, economic and linguistic life of peoples of Ghana, Mali, Songhai, Kanem‑Bornu and other empires.

        Syllabus Focus – Part 2: Qurʼan:
        - Revelation of the Qurʼan:
          • Prophet’s visits to Cave Hirāʼ; first revelation and his reaction.
          • Modes of revelation and the wisdom of gradual (piecemeal) revelation.
        - Preservation of the Qurʼan:
          • Makki and Madani Suwar; recording, compilation and standardization.
          • Roles of Companions in preserving and transmitting the Qurʼan.
        - Importance of the Qurʼan:
          • As primary source of guidance in social, political, spiritual and moral matters.
        - Selected Suwar:
          • Reading (Arabic or transliteration), memorization and meaning of short surahs (e.g. Fātiḥah, Ḍuḥā, Tīn, ʿAlaq 1‑5, Qadr, Kāfirūn, Naṣr, Ikhlāṣ, Falaq, Nās).
          • Identification of key themes and practical lessons (guidance, worship, sincerity, trust in Allah, etc.).
        - Moral Lessons from the Qurʼan:
          • Attitude to parents; honesty in words and deeds; prohibition of gambling and related vices.

        Syllabus Focus – Part 3: Hadith:
        - Definition & Nature:
          • Meanings of Ḥadīth and Sunnah; similarities and differences.
          • Importance of Ḥadīth and Sunnah as sources of Islamic guidance.
        - Structure & Classification:
          • Parts of a Ḥadīth: isnād, matn and rāwī.
          • Criteria for authenticity; major classifications: Ṣaḥīḥ, Ḥasan and Ḍaʿīf.
          • The Six Sound Collections (Ṣiḥāḥ as‑Sittah) and their compilers.
        - Selected Ḥadīth Texts:
          • Reading, memorization, meaning and lessons from selected aḥādīth (e.g. from an‑Nawawī’s collection: nos. 1, 5, 7, 9, 13, 15, 16, 18, 34, 41).
          • Application of these teachings to personal conduct and community life.

        Syllabus Focus – Part 4: Tawhid & Fiqh (Islamic Theology & Jurisprudence):
        - Īmān (Faith):
          • Meaning, significance and articles of faith: belief in Allah (Tawḥīd, Kalimat ash‑Shahādah, rejection of Trinity), angels, books, prophets (with emphasis on Ulu l‑ʿAzm: Ibrāhīm, Nūḥ, Mūsā, ʿĪsā (A.S.), Muḥammad (S.A.W.)), the Last Day and Qadar (destiny).
        - Shirk (Associating Partners with Allah):
          • Forms such as idol worship, ancestor and hero worship; Qurʼanic condemnations and implications for belief and practice.
        - Ṭahārah (Purification):
          • Importance of purity.
          • Practices: al‑istinjāʼ, wuḍūʼ, tayammum and ghusl – conditions and procedures.
        - Ṣalāt (Prayer):
          • Meaning, importance and acts that invalidate ṣalāt.
          • Congregational prayer, sujūd as‑sahw (prostration of forgetfulness).
          • Types: obligatory prayers (farḍ), recommended/nafl prayers, and special prayers (Jumuʿah, ʿĪdayn, Janāzah, Istisqāʼ, Tarāwīḥ, Kusūfayn, Istikhārah, travel prayers).
        - Ṣawm (Fasting):
          • Definition, types (compulsory and voluntary), examples of each.
          • How fasting is observed, those exempted, things that invalidate the fast.
          • Spiritual, social and moral objectives and benefits.
        - Zakāt (Almsgiving/Charity):
          • Definition of Zakāt and Ṣadaqah and distinction between them.
          • Properties on which Zakāt is due, niṣāb and categories: livestock, money, agricultural produce, Zakāt al‑Fiṭr.
          • Beneficiaries (Q.9:60) and spiritual, socio‑economic and moral roles of Zakāt.
        - Ḥajj & ʿUmrah (Pilgrimage):
          • Definition, origin and prerequisites.
          • Rites of ʿUmrah and Ḥajj; types of Ḥajj (Ifrād, Tamattuʿ, Qirān).
          • Acts that affect validity; spiritual, social and economic significance.
        - Sharīʿah:
          • Definition, main sources (Qurʼan, Sunnah, Ijmāʿ, Qiyās) and significance for Muslim life and society.
        - Nikāḥ (Marriage):
          • Concept and virtues of marriage; prohibited categories; conditions for a valid marriage.
          • Rights and duties of husband and wife and Qurʼanic protection against ill‑treatment.
        - Ṭalāq (Divorce):
          • Islamic attitude to divorce and efforts at reconciliation.
          • Types (Ṭalāq, Khulʿ, Faskh, Mubārāʼah, Ẓihār, Liʿān) and Iddah (waiting period) – meaning, duration and implications.

        Exam Structure:
        - Paper 1: Objective
          • 50 multiple‑choice questions covering Tarikh, Qurʼan, Hadith, Tawhid and Fiqh.
          • Duration: 50 minutes for 50 marks.

        - Paper 2: Essay
          • 6 essay questions, of which 1 is compulsory and 5 optional.
          • Candidates answer the compulsory question and any 3 of the others in 2 hours for 100 marks.

        Guidelines for Candidates:
        - Study key historical events and personalities in early Islam and its spread to West Africa, focusing on lessons for today.
        - Memorize and understand selected Qurʼanic verses and aḥādīth and be ready to apply their teachings to contemporary issues.
        - Master core beliefs and basic acts of worship (Imān, Ṣalāt, Ṣawm, Zakāt, Ḥajj) and their purposes.
        - Practise writing organized, well‑referenced essays that combine accurate information with reflection on practical application in modern life.
        - Use recommended Islamic Studies texts alongside the Qurʼan and authentic Ḥadīth collections for deeper understanding.`,
  },
  {
    subject: 'Civic Education',
    timeAlloted: 60 * 60,
    description: `Civic Education prepares candidates to understand their role as responsible citizens, appreciate democratic values and engage meaningfully in national development.

      Aims and Focus:
      - Understand the importance of citizenship and citizenship education in nation-building.
      - Explain the structure, functions and responsibilities of government to the people.
      - Recognize emerging social issues and how they affect individuals and communities.
      - Know the rights, duties and obligations of citizens to government and society.
      - Appreciate and uphold democratic values, the rule of law and accountability.

      Examination Structure:
      - Paper 1: 1-hour multiple-choice paper with 50 questions drawn from the whole syllabus, carrying 40 marks.
      - Paper 2: 2-hour essay paper with three sections (A, B, C). Each section contains three questions.
      - Candidates answer four questions in all, choosing at least one from each section, for 60 marks.
      - Sections cover: A – National ethics, discipline, rights and obligations; B – Emerging issues in the society; C – Governmental system and processes.

      Section A – National Ethics, Discipline, Rights and Obligations:
      - Values: meaning, types (moral, social, cultural, political, economic etc.) and importance of values to individuals and society.
      - Citizenship and Nationalism: meaning of citizenship and citizenship education; goals of citizenship education; duties and obligations of citizens; meaning of nationalism; ways of promoting national consciousness, integrity and unity; nationalistic roles of individuals and groups; identification of local and global civic problems.
      - Human Rights: meaning, categories and characteristics of human rights; historical background and meaning of the Universal Declaration of Human Rights (UDHR); the seven core freedoms; importance of the UDHR; roles of individuals, groups and government in promoting human rights; limitations to the exercise of rights.
      - Law and Order: definition and features of law and order; importance of law and order in society; meaning, types and roles of constituted authority.
      - Responsible Parenthood: meaning, roles of responsible parents and importance of responsible parenthood in national development.
      - Traffic Regulations: meaning and importance of traffic regulations; roles of individuals and government in maintaining traffic regulations.
      - Inter-personal and Inter-communal Relationships: meaning, types and skills that promote interpersonal relationships; meaning and importance of inter-communal relationships; meaning of inter-communal conflicts and skills for resolving them.

      Section B – Emerging Issues in the Society:
      - Cultism: meaning and origin; different cult groups and their symbols; reasons for joining cults; consequences of cultism; ways of preventing cultism in schools and the wider society.
      - Drugs and Drug Abuse: meaning of drug abuse; types of drugs commonly abused; how drugs are abused; symptoms and behaviours of drug addicts; prevention of drug abuse; government agencies working against drug abuse and trafficking and their activities; laws against drug abuse.
      - Human Trafficking: meaning, causes, effects and consequences of human trafficking; efforts of government, organizations and individuals to curb human trafficking.
      - HIV/AIDS: meaning, causes, symptoms and effects; prevention strategies; stigmatization of people living with HIV/AIDS and ways of reducing stigma.
      - Youth Empowerment: meaning, types of empowerment skills (vocational, entrepreneurial, ICT, civic etc.); importance and benefits of youth empowerment; efforts of government and other bodies to empower the youth.

      Section C – Governmental System and Processes:
      - Structure and Functions of Government: meaning of government; structures/tiers of government; basic functions and responsibilities of each tier towards citizens.
      - Democracy, Rule of Law and National Development: meaning, types and features of democracy; importance and pillars of democracy; problems and challenges; meaning and features of the rule of law; importance and problems of enforcing rule of law; meaning of national development and how democracy and rule of law promote good governance, employment creation and poverty alleviation.
      - Political Apathy: meaning, causes and consequences; why leaders sometimes fail to protect the interests of their followers; ways leaders can protect citizens’ interests; strategies for discouraging political apathy and promoting participation.
      - Civil Society and Popular Participation: meaning and types of popular participation; need for public involvement in governance; traditional and modern modes of participation; how to achieve broad-based participation; meaning, functions and qualities of civil society organizations (accountability, fairness, justice, equity etc.); problems facing civil society such as corruption, inadequate funding and undue foreign influence.
      - Public Service in Democracy: meaning and functions of the public service; problems and reasons for shortcomings in performance; ways of improving efficiency, accountability and service delivery in a democratic setting.

      Guidelines for Candidates:
      - Master key concepts such as values, citizenship, nationalism, human rights, democracy, rule of law and popular participation and link them to real-life situations.
      - Use current affairs, local examples and case studies to understand emerging issues like cultism, drug abuse, trafficking, HIV/AIDS and youth unemployment.
      - Study how Nigerian governmental institutions and civil society organisations work, including their roles in promoting accountability and development.
      - Practise answering both objective and essay questions that require explanation, analysis, evaluation and practical suggestions for improving civic life.
      - Read widely from recommended Civic Education and Government texts and follow civic-related news to keep examples up to date.`,
  },
  {
    subject: 'Literature in English',
    timeAlloted: 60 * 60,
    description: `Literature in English assesses candidates’ knowledge and critical appreciation of prose, drama and poetry, including set texts and unseen passages.

        Examination Structure:
        - Paper 1: 1-hour multiple-choice objective paper of 50 questions for 50 marks, covering:
          • General Knowledge of Literature (20 questions on literary terms, genres, devices and background).
          • An unseen prose passage (about 120–150 words) with 5 questions testing comprehension and literary appreciation.
          • An unseen poem with 5 questions focusing on meaning, mood, tone and poetic devices.
          • Shakespeare: 20 context questions on the prescribed Shakespearean text testing theme, characterization, style and setting (no essay questions are set on Shakespeare).

        - Paper 2: Prose (Essay) – African and Non-African Prose.
          • Essay paper of 1 hour 15 minutes for 50 marks.
          • Two sections: Section A – African Prose; Section B – Non-African Prose.
          • Two essay questions are set on each prescribed novel.
          • Candidates answer one question only from each section (one African, one Non-African novel).

        - Paper 3: Drama and Poetry (Essay).
          • 2 hours 30 minutes, carrying 100 marks.
          • Four sections:
            Section A – African Drama; Section B – Non-African Drama;
            Section C – African Poetry; Section D – Non-African Poetry.
          • Two essay questions are set on each prescribed drama text in Sections A and B.
          • Two questions are set for each poetry section (C and D).
          • Candidates answer one question from each section, making a total of four questions.

        Skills and Emphases:
        - Demonstrating understanding of plot, theme, characterization, setting and style in drama, prose and poetry.
        - Identifying and explaining literary devices (imagery, symbolism, irony, satire, rhythm, rhyme, etc.) and their effects.
        - Responding critically to unseen prose and poetry passages, showing sensitivity to language and tone.
        - Writing well-organized essays that analyze issues in the prescribed texts with relevant textual support.

        Guidelines for Candidates:
        - Study the prescribed prose, drama and poetry texts in depth, noting themes, characters, structure and language.
        - Practise answering objective questions on literary terms, genres and devices for Paper 1.
        - Regularly attempt timed essays on the set texts, following WAEC-style questions for Papers 2 and 3.
        - Read a wide range of additional literary works to deepen appreciation of different styles and traditions.
        - Pay particular attention to the prescribed Shakespearean text for context questions, focusing on key scenes and speeches.`,
  },
  {
    subject: 'Financial Accounting',
    timeAlloted: 60 * 60,
    description: `Financial Accounting assesses candidates’ understanding of basic accounting principles and their ability to record, summarize and interpret business transactions for different types of organizations.

        Aims and Objectives:
        - Understand the nature, history, functions and role of accounting in business and society.
        - Apply the rules and principles of double-entry bookkeeping to record transactions accurately.
        - Prepare, analyse and interpret financial statements for sole traders, partnerships, companies and not-for-profit organisations.
        - Provide a solid foundation for further studies and careers in accounting and related fields.

        Examination Scheme:
        - Composite paper made up of Paper 1 and Paper 2, taken at one sitting.
        - Paper 1: 50 multiple-choice questions to be answered in 1 hour for 50 marks, drawn from the entire syllabus.
        - Paper 2: 2½-hour essay/practical paper for 100 marks, with two sections:
          • Section A – Theory of Financial Accounting: five essay questions on concepts and principles; candidates answer two questions (15 marks each).
          • Section B – Financial Accounting Practice: five essay/practical questions; candidates answer three questions (15 marks each).

        Major Syllabus Areas:
        - Introduction to Financial Accounting: history, nature and functions of accounting; users of accounting information; stages in the accounting process; characteristics of useful accounting information.
        - Accounting Equation and Double Entry Principles: the accounting equation; source documents and their purposes; subsidiary books; ledger classification; cash book including analytical and petty cash book; preparation of trial balance; bank reconciliation statements; correction of errors and suspense accounts.
        - Accounting Concepts: meaning, types (e.g. business entity, going concern, accrual, consistency, prudence etc.), significance and limitations.
        - Final Accounts of a Sole Trader: trading, profit and loss (income statement); balance sheet/statement of financial position; adjustments to final accounts (accruals, prepayments, bad and doubtful debts, depreciation etc.).
        - Provisions and Reserves: provision for doubtful debts and discounts; reasons for and methods of depreciation (straight-line, reducing balance, sum-of-years-digits, revaluation); accounting treatment of depreciation; revenue and capital reserves.
        - Manufacturing Accounts: purpose of manufacturing accounts; classification of costs (direct/indirect, fixed/variable); preparation of manufacturing accounts and final accounts of manufacturing concerns.
        - Control Accounts and Self-balancing Ledgers: meaning, uses and types (sales ledger control, purchases ledger control); preparation and reconciliation of control accounts.
        - Single Entry and Incomplete Records: meaning and limitations; computation of profit or loss from opening and closing balance sheets; conversion of single entry to double entry; preparation of final accounts from incomplete records; mark-up and margin.
        - Accounts of Not-for-Profit Making Organisations: key terms; receipts and payments account; subscriptions account; income and expenditure account; accumulated fund; balance sheet; profit or loss from income-generating activities.
        - Partnership Accounts: nature and formation; partnership agreement/deed; profit and loss appropriation account; partners’ capital/current accounts and balance sheet; admission of a new partner; treatment of goodwill and revaluation of assets; dissolution of partnership (excluding Garner v. Murray and piecemeal realization).
        - Company Accounts: nature and formation of companies; types of companies and shares; issue of shares; loan capital (debentures/loan notes, mortgages); preparation of final accounts for internal use; simple interpretation of accounts using basic ratios; purchase of business account; statement of cash flows (direct and indirect methods, where applicable).
        - Accounting for Value Added Tax (where applicable): purpose and characteristics of VAT; computation of input and output VAT; preparation of VAT returns; exempt goods and services.
        - Departmental and Branch Accounts: meaning and importance; differences between departments and branches; preparation of departmental accounts; branch accounts (excluding foreign branches); inter-branch transactions.
        - Public Sector Accounting: meaning and differences between public and private sector accounts; sources of public revenue; capital and recurrent expenditure; preparation of simple government accounts.
        - Information Technology in Accounting: manual vs computerized accounting systems; stages of data processing; basic computer hardware and software; merits and demerits of manual and computerized systems.
        - Miscellaneous Accounts: meaning, key terms and preparation of simple joint venture, consignment, contract and hire purchase accounts.
        - Financial System: meaning and components; money, capital and insurance markets; methods of raising funds from the capital market (offer for sale, offer for subscription, rights issue, private placement); requirements for accessing the market; benefits of the capital market to investors, government, the economy and companies; reasons for and features of regulation.

        Guidelines for Candidates:
        - Practise setting up books of original entry, ledgers, trial balances and full final accounts for different entities.
        - Work many past questions on adjustments, control accounts, incomplete records, partnership and company accounts.
        - Memorise key accounting concepts, terms and formats while understanding the reasoning behind them.
        - Use recommended texts and real-life business examples to see how accounting information supports decision making.
        - Manage time carefully in both objective and essay sections, showing clear workings and neat layouts in calculations.`,
  },
  {
    subject: 'Commerce',
    timeAlloted: 50 * 60,
    description: `Commerce examines how goods and services move from producers to consumers, the institutions that facilitate this process, and the legal and managerial framework of business.

        Examination Scheme:
        - Composite paper made up of Paper 1 and Paper 2 taken at one sitting.
        - Paper 1: 50 multiple-choice questions to be answered in 50 minutes for 50 marks, drawn from the whole syllabus.
        - Paper 2: Eight essay-type questions; candidates answer any five in 2 hours for 100 marks.

        Major Syllabus Areas:
        - Introduction to Commerce and E-commerce: meaning and definitions; history/background of commerce; scope of commerce (trade and aids to trade) including electronic commerce; functions and importance of commerce and e-commerce in modern economies.
        - Occupation: meaning and types of occupation – industrial, commercial and service; factors that determine choice of occupation/employment; career opportunities in different sectors.
        - Production: meaning and purpose; factors of production – land, labour, capital and entrepreneurship; types of production – primary, secondary and tertiary; division of labour and specialization (meaning, types, advantages, disadvantages and limitations); inter-relationship between production and exchange.
        - Business Units: meaning and objectives of business; forms of business units – sole proprietorship, partnership, co-operative societies, credit unions and thrift societies, public enterprises and companies; types, formation, characteristics, advantages and disadvantages of each; sources of capital; meaning and purpose of amalgamations, mergers and acquisitions, trusts, holding companies, subsidiaries, consortium and cartel; dissolution/liquidation of companies and partnerships.
        - Trade Associations: aims and functions of trade associations; chambers of commerce, employers’ associations and consumer associations/consumerism.
        - Business Capital and Profits: meaning and types of capital – authorised/registered, called-up, paid-up, owned capital, liquid/circulating capital; credit – meaning, sources, instruments and functions; calculation and importance of working capital; profit – meaning, types and computation; turnover – meaning, calculation and factors affecting turnover.
        - Trade – Home and Foreign: meaning and purpose of trade; home vs foreign trade and their differences; retail trade – functions of the retailer, factors to consider in starting a retail business, reasons for success/failure; small-scale and large-scale retailing (unit shops, stalls, hawkers, kiosks, mobile shops, supermarkets, chain stores, department stores, shopping malls, hypermarkets, mail order); characteristics, advantages and disadvantages; modern trends such as branding, self-service, vouchers, vending machines and credit cards; wholesale trade – functions and types of wholesalers (merchant and agents); factors affecting elimination or survival of middlemen; channels of distribution, types and factors influencing choice.
        - Foreign Trade in Detail: meaning and types – import, export and entrepôt; basic concepts – terms of trade, balance of trade, balance of payments (favourable/unfavourable), visible and invisible items, bilateral and multilateral agreements, counter trade; advantages and disadvantages of foreign trade; barriers to trade; tariffs – meaning, reasons and effects; roles of port and airport authorities, customs and excise, shipping companies, clearing and forwarding agents, export promotion councils.
        - Purchase and Sale of Goods in Home and Foreign Trade: procedures and documents used – order, indent, invoices (ordinary, consular, proforma), credit/debit notes, letter of hypothecation, documentary credit, certificates of origin and inspection, insurance certificate; price quotations and trade/cash/quantity discounts; common trade terms (C.O.D., C.W.O., C.I.F., F.O.B., E. & O.E., ex-works, loco, F.A.S., F.O.R., franco); terms of payment – cash/spot, deferred and credit; means of payment – legal tender, cheques, standing orders, bank drafts, stamps, postal and money orders, bills of exchange, promissory notes, mail and telegraphic transfers, travellers’ cheques, electronic payment.
        - Finance and Financial Institutions:
          • Money: meaning, evolution, forms, qualities and functions.
          • Banks: types – central bank, commercial banks and other specialised institutions such as development banks, mortgage banks, building societies, microfinance institutions and bureaux-de-change; their features and functions; e-banking – meaning and forms (ATMs, transfers, online payments).
          • Insurance: meaning and basic principles – utmost good faith, insurable interest, indemnity, subrogation, contribution and proximate cause; types (life and non-life: motor, fire, fidelity, burglary/robbery/theft, accident, consequential loss, marine); types of risk (insurable vs uninsurable); importance to business and individuals; procedures for taking a policy; underwriting and reinsurance.
          • Capital Market: meaning and functions; methods of raising funds (offer for sale, offer for subscription, rights issue, private placement, issue by tender); second-tier securities market – meaning, functions, advantages and listing requirements.
          • Stock Exchange (first-tier): meaning, functions, importance; transactions and types of speculators; types of securities (shares, stocks, bonds, debentures, gilt-edged securities, convertible loans).
          • Commodity Exchange: meaning and purposes; types of tradable commodities; requirements for trading (grading, standardising, warehousing, clearing systems); open outcry and electronic trading methods; benefits.
        - Transport, Tourism, Communication and Warehousing:
          • Transport: meaning, importance, forms (land, water, air, pipeline); factors influencing choice; advantages and disadvantages; key documents (waybills, consignment notes, tickets, manifests).
          • Tourism: meaning, advantages and disadvantages.
          • Communication: meaning, types (oral, written, visual, non-verbal, traditional and modern); advantages and disadvantages; importance and services of the post office; courier services and electronic communication (telephone, satellite, internet/email); basic computer appreciation.
          • Warehousing: meaning, importance, functions, types and advantages.
        - Advertising and Marketing:
          • Advertising: meaning; roles, advantages and disadvantages; types (informative, persuasive, competitive, mass/specific); methods (direct and indirect); media – types, choice and effectiveness.
          • Marketing: meaning, functions and importance; differences between market and marketing, and between market and marketing research; marketing mix (product, price, place, promotion).
          • Customer Service and Sales Promotion: meaning, importance and types of pre- and after-sales services; sales promotion methods – trade fairs, exhibitions, gifts, demonstrations, personal selling.
        - Legal Aspects of Business and Consumer Protection: basic areas of law affecting business – contract (meaning, elements, discharge), agency (meaning, creation, duties and termination), sale of goods, hire purchase; rights and obligations of employer and employee; government regulation of business (patents, copyrights, trademarks, business registration); consumer protection – meaning, need, means and consumerism; key legislation (e.g. food and drugs, standards, price control, factory and shops, product quality).
        - Government Policies Relating to Business: meaning, reasons, advantages, disadvantages and differences between commercialisation, privatisation and deregulation.
        - Introduction to Business Management: meaning and objectives of business; meaning and functions of management; business resources (man, money, materials, opportunities/goodwill); structure of business organisation (organisation charts, departments, authority, delegation, responsibility, span of control); business and its environment (economic, political, technological and competitive factors); social responsibility of business; importance of intra- and inter-departmental communication.
        - Economic Groupings: ECOWAS, Niger Basin Commission, Lake Chad Basin Commission, Mano River Union, European Union, West African Clearing House – history, membership, objectives, achievements and problems.

        Guidelines for Candidates:
        - Build strong conceptual understanding of key commercial terms, documents, institutions and laws, and connect them to real-life business situations.
        - Practise both objective and essay questions across all syllabus areas, especially trade, finance, transport, insurance and legal aspects.
        - Use current examples from local and international commerce (e-commerce, modern retail formats, digital payments) to enrich answers.
        - Draw diagrams and flowcharts where helpful (e.g. channels of distribution, organisational charts, capital market structure).
        - Read widely from recommended Commerce and Marketing texts and follow business news to keep up with emerging trends.`,
  },
  {
    subject: 'Business Management',
    timeAlloted: 50 * 60,
    description: `Business Management introduces candidates to the principles, functions and environment of managing business organizations, including legal and financial frameworks, information technology, globalization and entrepreneurship.

        Aims and Focus:
        - Understand and appreciate basic concepts, principles and practices of business management.
        - Recognize the role of business management in the economy and in society.
        - Relate management concepts and principles to real-life business situations.
        - Build a foundation for further studies in business and management-related disciplines.

        Examination Scheme:
        - Paper 1: 50 compulsory multiple-choice questions covering the entire syllabus, taken in 1 hour for 30 marks.
        - Paper 2: Essay paper consisting of one compulsory case study question (200–250 words) worth 25 marks and seven other essay questions of 15 marks each.
          • Candidates answer the compulsory case study plus any three of the remaining questions.
          • Total marks for Paper 2: 70.

        Major Syllabus Areas:
        - Nature of Management:
          • Meaning of management and administration; similarities and differences.
          • Basic functions of management (planning, organizing, directing, controlling, coordinating, etc.).
          • Levels of management (top, middle, lower) and their roles.
          • Types and roles of managers in organizations.
          • Meaning of business organization; major forms – sole proprietorship, partnership, limited liability company, public corporations, co-operative societies.
          • Formation procedures, characteristics, advantages and disadvantages of each form; causes of business failure.
          • Business and society: meaning of business environment, forces in the environment, ethical, legal and social responsibilities of business, primary and secondary stakeholders and responsibilities toward them.

        - Functions of Management:
          • Planning and Decision Making: meaning, importance and steps in planning; types of plans; limitations of planning; meaning and importance of decision making; steps in the decision-making process; types of decisions (programmed and non-programmed); levels of management decisions.
          • Organizing: meaning, importance and main activities; principles of organizing; organizational structures and charts; centralization and decentralization – meaning, differences, advantages and disadvantages; meaning and benefits of delegation; reasons why managers do not delegate and why subordinates may not accept delegation; guidelines for effective delegation.
          • Directing: meaning and elements; motivation (meaning, intrinsic and extrinsic factors; Maslow’s hierarchy of needs); leadership (importance of good leadership, leadership styles and sources of power); communication (meaning, importance, formal and informal channels); communication process, barriers and guidelines for effective communication.
          • Controlling: meaning, reasons for control, steps in the control process and basic tools of control.

        - Management Information Technology:
          • Concept of ICT; distinction between data and information; sources of business information.
          • Functions and applications of computers in business (data processing, record keeping, communication, decision support, e-commerce, etc.).
          • Advantages and challenges of using computers and ICT in business management.

        - Legal Environment of Business:
          • Law of Contract: meaning and essential elements; classification of contracts; vitiating factors (void and voidable contracts); ways of discharging a contract.
          • Agency: creation of agency; rights and duties of principals and agents; termination of agency.
          • Negotiable Instruments: meaning, characteristics and types (cheques, bills of exchange, promissory notes); classes and types of cheques; advantages, disadvantages and ways of avoiding cheque fraud.

        - Finance and Financial Institutions:
          • Money: meaning, characteristics and functions; meaning and causes of inflation.
          • Banking: types of banks; functions of the central bank; functions of commercial and other types of banks; types of bank accounts; tools of monetary control.
          • Risk Management and Insurance: meaning of risk and risk management; types of business risks; meaning, importance and principles of insurance; types of insurance and policies relevant to business.
          • Stock Exchange: meaning, functions and types of securities traded.

        - Role of Government in the Economy:
          • Role of government in economic management.
          • Sources and types of government revenue; purpose of taxation; types of taxes and challenges of tax collection.
          • Types and items of government expenditure.

        - International Trade and Problems of Developing Economies:
          • Meaning of trade; differences between domestic and international trade.
          • Documents used in domestic and international trade; reasons for international trade.
          • Theories of absolute and comparative cost advantage.
          • Restrictions on international trade; balance of payments and measures for correcting BOP deficits.
          • Challenges facing developing economies in relation to international trade; ways governments can improve developing economies.
          • Roles of IMF, IBRD and UNCTAD in international trade and development.

        - Globalization and Economic Integration:
          • Meaning, causes, advantages and disadvantages of globalization.
          • Meaning of economic integration; roles of ECOWAS, AU, EU and other regional groupings in promoting integration.

        - Functional Management:
          • Human Resource Management: meaning, functions, recruitment and selection processes, sources of recruitment; types of training; job analysis (job description and job specification); performance appraisal – meaning and importance.
          • Labour Relations: meaning of labour relations, trade unions and industrial relations; functions of trade unions; employers’ associations; collective bargaining – process, importance and rights of parties.
          • Production Management: meaning of production and production management; forms of production (extractive, manufacturing, construction, commercial and personal services); factors of production; specialization and exchange – advantages and disadvantages; production processes (job, batch, flow); steps in production planning and control.
          • Marketing: meaning and activities; market segmentation – meaning and bases; elements of the marketing mix (product, price, promotion and place); steps in new product development; stages of the product life cycle; functions of packaging and branding; factors affecting pricing and pricing objectives; promotion – meaning, importance and types; channels of distribution; functions of wholesalers and retailers.
          • E-commerce and Business Finance: meaning and importance of e-commerce; meaning of business finance; short-term and long-term sources of finance; government lending programmes and venture capital funds.

        - Entrepreneurship and Small Business:
          • Entrepreneurship: meaning, role of entrepreneurs, characteristics of an entrepreneur; business plan – content and use; reasons for business success and failure; record keeping (meaning, importance and types); time management – meaning, importance and techniques.
          • Small Business: meaning, categories and procedures for formation; importance of small businesses in the economy; challenges facing small businesses; role of government in promoting small businesses; strategies for sustaining established businesses.

        Guidelines for Candidates:
        - Study key management concepts and be prepared to apply them to practical case studies, especially in Paper 2.
        - Use real organizational examples to illustrate issues like leadership, motivation, communication, globalization and entrepreneurship.
        - Practise both objective and essay questions, paying attention to clear structure and logical argument in written answers.
        - Follow business news and developments in technology and regional integration to enrich exam responses with up-to-date illustrations.
        - Read recommended Business Management texts for deeper understanding of core functional areas and the business environment.`,
  },
  {
    subject: 'Data Processing',
    timeAlloted: 60 * 60,
    description: `Data Processing tests candidates’ basic knowledge and practical skills in handling data and using ICT tools, with emphasis on concepts that support everyday life, entrepreneurship and further study.

        Aims and Objectives:
        - Acquire foundational skills in data processing and management.
        - Develop competence in ICT applications that support work, business and education.
        - Understand how ICT facilitates business transactions, communication and learning.
        - Build preparedness for further studies in Data Processing, ICT and related fields.

        Examination Scheme:
        - Paper 1: 40 multiple-choice objective questions on the entire syllabus, to be answered in 1 hour for 40 marks.
        - Paper 2: Two sections (A and B), taken as part of a composite paper:
          • Section A – Theory: 4 essay questions; candidates answer any 3 in 1 hour for 30 marks.
          • Section B – Practical: 2 compulsory practical-oriented essay questions to be answered in 1 hour for 40 marks.

        Major Syllabus Areas:
        - Information Age and History of Computers: evolution of computing devices; number systems and their importance in computing; generations of computers (first to fifth) and their key characteristics (technology, speed, storage, size and examples).
        - Data and Information: definitions of data and information; types of data; ways of handling data; digitalization; clear distinction between data and information and their roles in processing.
        - Basic Computing: classification of computers by type, size and usage; common examples used at home, school and in business.
        - Application of ICT in Everyday Life: uses of ICT in communication, education, business, health, governance and entertainment; impact of ICT on society and development; links between ICT skills and entrepreneurship.
        - Components of Computers: input, output, storage devices and system unit; examples of each and how they are used in data processing.
        - Information Processing: meaning of information processing; steps in the information processing cycle (input, processing, output, storage, feedback); simple real-life illustrations.
        - Information Transmission: meaning of information transmission; methods and modes of transmission (visual, audio, audio-visual); media such as radio, television, newspapers, GSM/phones and the internet; advantages, limitations and appropriate use.
        - Networking and Internet: meaning of networking, internet and intranet; types of networks (LAN, MAN, WAN); benefits and significance of networking; definition and benefits of the internet; browsers and basic services; internet security issues; common abuses of the internet and responsible use.

        Tools for Processing Information:
        - Operating Systems: meaning, types (text/character-based and graphical user interface), examples; key functions of an operating system.
        - Word Processing: meaning and uses; examples of word processors; starting, loading and exiting the program; creating, saving and retrieving documents; basic editing and formatting for simple documents.
        - Spreadsheets: meaning and uses; examples of spreadsheet applications; loading and exiting; creating, saving and retrieving worksheets; basic data entry, simple formulas and formatting.
        - Database Management Systems (DBMS): meaning and uses; examples (e.g. Microsoft Access); loading and exiting; creating, saving and retrieving database files; basic operations on tables and records.
        - Presentation Packages: definition, uses and examples; loading and exiting; creating, saving and retrieving presentation files; basics of slide design and sequencing.
        - Web Design Packages: basic concept, uses, components and examples (designing full web pages is not required).
        - Graphic Packages: meaning and uses of graphics software; examples such as CorelDRAW; simple applications to create and manipulate basic graphics.

        Computer Maintenance, Ethics and Human Issues:
        - Computer Maintenance: general cleaning; battery charging and replacement for portable systems and UPS; DVD drive lens cleaning; basic hardware and software maintenance; understanding computer crashes, data integrity and simple data recovery strategies.
        - Computer Ethics: laboratory rules and regulations; responsible use of computers, networks and data; security and privacy considerations.
        - Safety Measures: correct sitting posture; proper positioning of monitor, keyboard, mouse, CPU and peripherals; adequate lighting and dust control; keeping liquids away from equipment; importance of ergonomics and safe environments.
        - Career Opportunities in Data Processing: professions in computing and data management; qualities of a good data processing professional; major professional bodies and their roles.
        - Computer Virus: definition; types and examples; sources of infection; warning signs; prevention, detection and removal; dangers of virus attacks on systems and data.

        Data Management Concepts:
        - Relational Model: creating databases and tables using application packages; establishing relationships between tables; concepts of entities, attributes, primary and foreign keys; creating forms, queries and reports; basic use of query languages (e.g. query-by-example, simple SQL) provided by the DBMS.
        - File Organization: meaning; types of file organisation (sequential, random, indexed, etc.) and simple comparison of their uses and advantages.
        - Database Security: concepts of data security, access control and data encryption; role and responsibilities of a database administrator.
        - Parallel and Distributed Databases: basic concepts and significance of storing data in distributed database systems.

        Guidelines for Candidates:
        - Practise hands-on work with standard office applications (word processor, spreadsheet, DBMS, presentation and graphics packages) to build real skills.
        - Understand concepts, definitions and examples well enough to answer theory questions and interpret simple scenarios.
        - Use everyday examples (school records, shops, banks, social media, online forms) to relate data processing ideas to real life.
        - Observe good laboratory ethics and safety procedures during practical work.
        - Read recommended ICT texts and stay familiar with current tools and common internet services.`,
  },
  {
    subject: 'Computer Studies',
    timeAlloted: 60 * 60,
    description: `Computer Studies introduces candidates to computer fundamentals, hardware, software, basic operations, applications, problem-solving with programming, ICT and ethics.

        Aims and Focus:
        - Understand basic concepts of computers and how they operate.
        - Develop manipulative, computational and problem-solving skills using computers.
        - Apply common software packages and operate simple computer-related devices.
        - Build online skills and good attitudes for safe and effective computer use.
        - Prepare for higher studies in computer-related disciplines.

        Examination Scheme:
        - Paper 1: 50 multiple-choice questions in 1 hour for 25 marks, covering the entire syllabus.
        - Paper 2: 5 essay questions; candidates answer any 3 in 1 hour for 30 marks.
        - Paper 3: Practical paper with 3 questions testing actual skills (or knowledge of practical work for private candidates), taken in 2 hours for 45 marks.

        Major Thematic Areas:
        - Computer Fundamentals and Evolution:
          • Evolution of computing devices from early tools (abacus, slide rule, Napier’s bones, Pascal’s machine, Leibniz multiplier, Jacquard loom, Babbage’s engines, Hollerith machine, Burroughs machine) to modern systems (ENIAC, EDVAC, UNIVAC I, desktops, laptops, notebooks, palmtops).
          • Contributions of key inventors; comparison of devices by size, components and capability.
          • Definition of a computer; hardware vs software; functional parts of a computer; key characteristics (electronic, speed, accuracy, diligence, interactivity, etc.).
          • Data and information – definitions, examples and differences.

        - Computer Hardware:
          • Input devices (keyboard, mouse, scanner, joystick, light pen, etc.) – features, uses and keyboard key groups.
          • Output devices (monitors, printers, speakers, plotters) – types, features, uses and differences between input and output devices; comparison of inkjet, laser and line printers.
          • Central Processing Unit (CPU) – ALU and control unit, their functions; system unit concept.
          • Memory: primary (RAM, ROM) and secondary storage (floppy, hard disk, CD, DVD, flash drive); units of storage (bit, byte, KB, MB, GB, TB) and simple conversions; comparative study of storage devices by size, speed and technology.
          • Logic circuits: basic logic gates (AND, OR, NOT, NAND, NOR, XOR), symbols, truth tables, simple logic equations and uses; simple comparators.

        - Computer Software:
          • System vs application software; operating systems (GUI and command line) on computers and mobile devices; translators (assembler, compiler, interpreter) and utility programs (editor, antivirus, etc.).
          • Application software: common packages such as word processing, spreadsheets, databases, graphics, accounting/payroll, banking and educational software; differences between user programs and application packages.

        - Computer Applications:
          • Word processing: definition, examples (MS Word, WordStar, WordPerfect); features and uses; activating and exiting; creating, editing, saving, retrieving, printing and closing documents; formatting, fonts, justification, spell-checking, search, file merging.
          • Spreadsheets: definition, examples (VisiCalc, MS Excel, SuperCalc, etc.); environment (menu bar, formula bar, status bar); basic concepts (worksheet, workbook, cell, ranges); data types (numbers, labels, formulas); basic operations (data entry, save, retrieve, copy, move); simple calculations with formulas and built-in functions (e.g. SUM, AVERAGE); creating charts (bar, pie, etc.).
          • Databases: definitions of database, file, record, field, key; examples (dBASE, FoxBase, MS Access, Oracle); hierarchical, network and relational database models; table structure (rows and columns); creating and operating on databases (define structure, choose field types, enter, save, search, sort, modify, report).
          • Graphics: definition and examples (Paint, Harvard Graphics, Photoshop, CorelDRAW); simple designs (logos, flags, cards, certificates) using CorelDRAW.
          • Presentation packages: definition and examples (PowerPoint); environment features; creating, editing and running slide shows; inserting text, pictures, charts; saving, printing and closing presentations.

        - Managing Computer Files:
          • Concepts: file, record, field, data item; types of data items (numeric, alphabetic, alphanumeric).
          • File organization structure (data item → record → file → database) and methods (serial, sequential, indexed, random); methods of file access.
          • File classification (master, transaction, reference files) and criteria for classification.
          • Basic file operations (create, delete, retrieve, insert, copy, view, update, open, close) and consequences of file insecurity (data loss, corruption, unreliability); causes of data loss and methods of securing files (backups, antivirus, passwords, labelling, etc.); differences between computer and manual files.

        - Basic Computer Operations:
          • Booting and shutting down: meaning, types (cold and warm booting), steps in starting up and shutting down, identifying desktop features.
          • Computer data conversion: registers, addresses and buses; basic registers (MDR, CIR, SCR) and their functions; simple explanation of how input–process–output works; factors affecting data transfer speed (bus speed and width).

        - Information and Communication Technology (ICT):
          • Communication systems and types of ICT (broadcasting, telecommunications, data networks, information systems, satellite communication) with real-world examples.
          • ICT-based gadgets (computers, mobile phones, ATMs, POS machines, ACR, radios, TVs, dispensers) and how they are used.
          • Internet: key terms (homepage, browser, URL, ISP, HTTP, HTML, website, webpage, cybercafe, etc.); browsers and their features; common Internet services (email, messaging, FTP, web, chat, discussion groups, etc.); email creation, sending and receiving; chatting.
          • Networking: definition; types (PAN, LAN, MAN, WAN, Internet); topologies (star, bus, ring); network devices (hubs, switches, routers, modems, NICs, bridges); advantages of networking.
          • World Wide Web: meaning, brief history, terminologies (website, webpage, homepage, protocol), HTTP/HTML; benefits and basic navigation; distinction between email and website addresses; simple web development tools.
          • Cables and connectors: network and computer cables (twisted pair, coaxial, fibre optic, USB, printer, power, etc.) and connectors (RJ45, RJ11, T-connectors, male/female plugs).

        - Developing Problem-Solving Skills:
          • Programming languages: definition, levels (machine, low-level, high-level), features, examples (BASIC, C, FORTRAN, COBOL, Java, Python etc.); advantages and disadvantages of each level.
          • High-level languages: categories (scientific, general-purpose, business, AI, string-processing) and characteristics of languages like BASIC, C, PASCAL, COBOL.
          • Algorithms and flowcharts: definitions, functions, characteristics (finite, effective, unambiguous); writing simple algorithms; standard flowchart symbols and diagrams for basic problems.
          • BASIC programming: data types (variables, constants, numeric and string); BASIC statements (INPUT, PRINT, LET, END, REM, READ, DATA); arithmetic operators and expressions; built-in functions (SQR, INT, SIN, COS, TAN, ABS, LOG, EXP, RND); simple programs and iterations; arrays and basic operations on array elements; writing and running small programs for everyday tasks.
          • Systems development cycle: stages (preliminary study, feasibility, investigation, analysis, design, implementation, maintenance, review) and their descriptions.
          • Program development cycle: characteristics of a good program; precautions in development; stages (problem definition, analysis, flowcharting, desk checking, coding, compilation, testing/debugging, documentation); interpreted vs compiled programs.

        - Computer Ethics and Human Issues:
          • Security and ethics: sources of security breaches (viruses, worms, Trojans, poor policies, carelessness, hackers, spammers); effects of malware.
          • Preventive measures: antivirus software, firewalls, encryption, safe online behaviour, proper policy implementation, careful handling of email attachments, use of trusted sites.
          • Legal issues: software copyright and ownership; rights to text, images, audio and video; privacy of audio/video content; cybercrimes (identity theft, internet fraud, hacking) and their implications.

        Guidelines for Candidates:
        - Combine conceptual understanding with hands-on practice using real computers and standard software packages.
        - Practise theory, short-structured and practical-type questions regularly, especially for programming, spreadsheets and file management.
        - Observe all lab safety, security and ethical rules when working with ICT.
        - Explore simple real-world tasks (documents, charts, databases, programs) to consolidate skills for Paper 3 practicals.
        - Use recommended Computer Studies textbooks and reliable online resources to stay current with tools and terminology.`,
  },
  {
    subject: 'Technical Drawing',
    timeAlloted: 60 * 60,
    description: `Technical Drawing develops candidates’ ability to communicate ideas visually using accepted drawing principles, standards and conventions for both building and mechanical fields.

        Examination Scheme:
        - Paper 1: 40 multiple-choice questions in 1 hour for 40 marks, in two sections:
          • Section A – 30 questions on general principles, techniques and uses of plane and solid geometry.
          • Section B – 10 questions from one chosen option only: Part I (Building Drawing) or Part II (Mechanical Drawing).
        - Paper 2: 5 essay questions on plane, solid and vector geometry; candidates answer any 3 in 1¾ hours for 60 marks.
        - Paper 3: 2¾-hour drawing/practical paper for 100 marks, in two sections:
          • Section A – 3 sketching questions on objects, components, symbols and tools used in building and mechanical industries; 1 compulsory question (20 marks) and 1 other question (10 marks) in 45 minutes.
          • Section B – 2 structured drawing questions, one on Building Drawing and one on Mechanical Drawing; candidates answer 1 in 2 hours for 70 marks.

        Plane Geometry (BS 1192 / BS 308A conventions):
        - Drawing Materials and Equipment: standard ISO A2 board and drawing sheet, T-square, set squares, protractor, compasses, pencils and related instruments; correct handling and care.
        - Lines, Lettering and Dimensioning: types and uses of lines; lettering styles, title blocks and border lines; dimensioning principles according to standard conventions (e.g. BS 1192, BS 308A).
        - Division of Lines and Scales: dividing a line into equal parts or given proportions; plain and diagonal scales, construction and applications; scale of chords and use in angle construction.
        - Angles, Triangles, Quadrilaterals and Polygons: classification, properties and construction of angles; regular and irregular polygons and their constructions.
        - Circles and Tangency: parts and properties of circles; constructions involving circles; tangents and tangency principles; applications in tools such as spanners, anchors and similar components.
        - Inscribed, Circumscribed and Escribed Figures: definitions, identification and construction around and within circles and polygons.
        - Similar Figures and Areas: enlargement and reduction of shapes by scale of sides and by area.
        - Loci: definition and construction of loci of points – straight line, circle, ellipse, parabola, hyperbola, helix, trochoids (inferior and superior), spirals, cycloids, hypocycloids, involutes and simple link mechanisms.

        Solid Geometry:
        - Lines in Space and Planes: locating points and lines in space; true length and inclination of a line; intersecting lines; plane relationships and traces; types of planes and edge views.
        - Pictorial Drawing: isometric, oblique and perspective drawings (one- and two-point); isometric and oblique scales and circles; freehand sketching of simple objects.
        - Orthographic Projection: use of multi-plane systems; first-angle and third-angle projection; interpreting and producing front, plan and side views.
        - Auxiliary Projections: first and second auxiliary views of solids such as prisms, cones, cylinders, cubes and pyramids (right and oblique).
        - Sections and True Shapes: sectional views and true shapes of sections through prisms, cylinders, pyramids and cones, yielding shapes such as ellipses, parabolas, hyperbolas, circles and triangles.
        - Development of Surfaces: development of surfaces of right and oblique pyramids, prisms, cylinders and cones, including truncated forms and frusta; simple transition pieces.
        - Intersection and Interpenetration: intersections of cylinders, cones, prisms and pyramids and determination of curves of interpenetration.

        Symbols and Conventions:
        - Use of standard drawing symbols and conventions for plane and solid geometry, including diameter and radius symbols, section lines and other notations.

        Vector Geometry (where applicable):
        - Forces: coplanar, concurrent and parallel forces; graphical determination of resultants (using Bow’s notation), equilibrants and reactions using triangle and polygon of forces.
        - Simple Framed Structures: determination of reactions and internal member forces; nature of forces (tension/compression).
        - Shear Force and Bending Moment Diagrams: for simply supported beams and cantilevers under concentrated and uniformly distributed loads.
        - Centre of Gravity of Lamina: finding centres of gravity of simple geometrical shapes.

        Building Drawing (BS 1192 conventions):
        - Freehand Sketching and Building Tools: pictorial and orthographic sketches of basic building tools and components.
        - Foundations and Floors: types and parts of foundations and floors and their representation.
        - Openings: doors, windows and arches – types, parts and standard representations.
        - Stairs and Staircases: basic parts and types of staircases.
        - Roofs: common roof types and parts, their symbols and conventions.
        - Constructional Details: walls, columns, beams, lintels and other structural elements.
        - Working Drawings: orthographic projections and sectional views of simple buildings; use of building symbols, hatching and notations.
        - Building Materials and Fittings: symbols and freehand sketches for common building materials and services, including electrical and plumbing fittings.

        Mechanical Drawing (BS 308A conventions):
        - Freehand Sketching and Mechanical Tools: pictorial and orthographic sketches of mechanical tools and machine components.
        - Dimensioning: limits and fits, allowances and tolerances; correct dimensioning techniques following standards.
        - Screw Threads, Fasteners and Locking Devices: conventional representation, basic construction and uses in assemblies.
        - Sectioning: full, half, part, offset, broken, removed, aligned and revolved sections and their application in mechanical drawings.
        - Pictorial Drawing: isometric and oblique drawings of mechanical parts, including freehand sketches.
        - Working and Assembly Drawings: orthographic and sectional views of parts and assemblies; interpreting and producing exploded and assembled views.
        - Symbols, Conventions and Abbreviations: standard mechanical, electrical, plumbing and welding symbols as used in mechanical drawing practice.

        Guidelines for Candidates:
        - Practise accurate use of drawing instruments and freehand sketching for both building and mechanical objects.
        - Follow BS 1192 and BS 308A (or equivalent) standards for line types, lettering, dimensioning and symbols.
        - Master geometric constructions, developments and projections as they form the basis for both options.
        - Choose either the Building or Mechanical option early and become familiar with its tools, symbols and conventions for Paper 1 Section B and Paper 3.
        - Work under exam timing conditions to improve speed, neatness and clarity of drawings.`,
  },
  {
    subject: 'Visual Arts',
    timeAlloted: 60 * 60,
    description: `Visual Arts assesses candidates’ aesthetic awareness, powers of observation and expressive skills through theory, appreciation and studio-based creative work in two- and three-dimensional media.

        Aims and Focus:
        - Develop deep observation, analytical and expressive skills through self-participatory art activities.
        - Build knowledge and skills in the use and maintenance of art tools, equipment and materials.
        - Foster appreciation of values and qualities in different artworks, including indigenous material culture.
        - Understand and apply the elements and principles of design in creative work.
        - Gain knowledge of the history and branches of art in Africa and the wider world.
        - Appreciate relationships between African and Western art and the role of art in socio-economic development and self-reliance.
        - Encourage improvisation with local materials and understanding of art-related careers.

        Examination Scheme:
        - Paper 1: 40 multiple-choice objective questions in 50 minutes for 40 marks.
        - Paper 2: Essay paper (2 hours, 60 marks) with 3 sections:
          • Section A – General knowledge in art.
          • Section B – Art of West Africa.
          • Section C – Pre-historic art, Ancient Egyptian art and Western art.
          Candidates answer 4 questions: 1 from Section A, 2 from Section B and 1 from Section C.
        - Papers 1 and 2 together focus on general art knowledge, elements and principles, branches and processes, art history and appreciation, and use/care of tools and materials.
        - Paper 3: Practical paper with 3 sections (A, B and C), emphasising drawing and creative design in 2D and 3D:
          • Section A – Drawing (objects, nature or life): candidates answer 1 question in 3 hours for 100 marks.
          • Section B – Creative Design (2D): 6 questions (2 each in graphic design, textile design and picture-making); candidates answer 1 in 3 hours for 100 marks.
          • Section C – Creative Design (3D): 4 questions (sculpture, product design/modeling, ceramics, crafts); candidates answer 1 within a long project window (up to 6 months) for 100 marks.

        Theory Content (Papers 1 and 2):
        - General Knowledge in Art:
          • Nature and branches of art and related careers.
          • Elements of art – line, colour, space, shape, form, texture, etc., their forms, characteristics and functions.
          • Principles of art and design – balance, rhythm, proportion, harmony, emphasis, variety and unity, and their creative application.
          • Perspective – meaning and types (aerial, linear: angular/parallel, foreshortening).
          • Production, use and care of art/craft tools and materials (brushes, pencils, colours, palettes, easels, etc.).
          • Art and craft terms (e.g. tie and dye, batik, biscuit ware, silhouette, relief, chiaroscuro, sfumato, aperture, etc.).
          • Interrelationships among the arts – music, visual art, dance, drama and literary arts.

        - Art of West Africa:
          • Traditional art of West African peoples – basic concepts (animism, fetishism, mythology), major art forms, locations, characteristics, beliefs and sacred/secular functions.
          • Ancient traditional art centres and cultures (e.g. Benin, Ife, Nok, Igbo-Ukwu, Esie, Owo; Ashanti, Ewe, Frafra; Bamileke, Fon, Senufo, Baule, Dan, Dogon, Bambara, Mende, Mossi and others).
          • Modern traditional art forms – Oshogbo art, Kente weaving (Ashanti, Ewe), modern Benin art, Winneba and Abuja pottery, Ntoso Adinkra, Bida brass works, calabash carving, Mbari mud sculpture, weaving, leatherwork, wall decoration and decorative motifs.
          • Contemporary West African art – art institutions (schools, galleries, museums, art councils and associations), cultural festivals and their artistic significance (costumes, masks, body and wall decorations, symbols, objects).
          • Outstanding contemporary artists and art educators: training, styles, media, materials and contributions to art development.

        - Pre-historic, Ancient Egyptian and Western Art:
          • Pre-historic art (Palaeolithic, Mesolithic, Neolithic periods): beliefs, materials, characteristics and functions.
          • Ancient Egyptian art (Old, Middle and New Kingdoms): beliefs, styles and functions.
          • Greek art: periods (Archaic, Classical, Hellenistic), characteristics and key artists.
          • Medieval art: main features, media and characteristics.
          • Renaissance art: early, low and high Renaissance periods; major artists (e.g. Giotto, Donatello, Masaccio).
          • 19th-century art: Impressionism and Post-Impressionism (e.g. Cezanne, Monet, Manet, Seurat, Van Gogh, Gauguin).
          • 20th-century art: Cubism, influence of African art and artists such as Picasso and Braque.

        Practical Content (Paper 3):
        - Part A – Drawing:
          • Drawing from Objects – still-life compositions using man-made objects arranged as a total composition, including part of the surroundings.
          • Drawing from Nature – studies of natural objects (branches, flowers, foliage, fruits, rocks, shells, bones, fish, birds, insects, etc.) to reveal form and growth.
          • Drawing from Life – observational drawing of the human figure, showing structure and form from a posed model.
          • Media include pencil, charcoal, pastel, pen and ink, poster or water colour.

        - Part B – Creative Design (2D):
          • Graphic Design – lettering and poster design (formal lettering, notices, pictorial posters, greeting cards, invitations, book jackets, logos, emblems, labels, symbols); book illustration; printmaking (linocut, woodcut, yam print, stencil, screen printing); package design; computer graphics using software such as CorelDRAW and Photoshop.
          • Textile Design – batik, tie and dye, block and screen printing on fabric; appliqué; tapestry and other surface decoration approaches.
          • Picture Making – painting and composition from life or imagination (including murals); basic photography (portraits, landscapes, seascapes, nature, pets and experimental techniques like photograms); mosaic and collage using varied materials.

        - Part C – Creative Design (3D):
          • Sculpture – works in clay, metal, wood, papier-mâché, cement, etc. (clay works must be fired).
          • Product Design/Modeling – models of everyday industrial products (e.g. automobiles, phones, bottles, cosmetics).
          • Ceramics – functional and decorative wares (jugs, vases, bowls, toys, figures) produced by hand-building, moulding or wheel throwing (and fired).
          • Crafts – basketry, jewellery, calabash decoration and other utilitarian or decorative objects using traditional and improvised materials.
          • Project work for 3D design is executed over several months; notes, diagrams and working drawings contribute to the total mark.

        Guidelines for Candidates:
        - Combine theoretical knowledge of art history and appreciation with regular studio practice across different media.
        - Visit galleries, museums, cultural festivals and local craft centres to deepen understanding of indigenous and contemporary art.
        - Practise drawing from observation frequently to improve proportion, structure, shading and composition.
        - Explore both traditional and modern materials, including improvisation with local resources.
        - Build a portfolio of works (drawings, designs, paintings, textiles, crafts) that reflects creativity, originality and technical control.`,
  },
  {
    subject: 'Music',
    timeAlloted: 60 * 60,
    description: `Music assesses candidates’ understanding of music theory, composition, aural skills, performance, and the history and literature of Western and African music, including Black music in the diaspora.

        Aims and Focus:
        - Explore basic elements of music through reading, writing, listening, aural recognition, improvisation and composition.
        - Develop skills and confidence in performing music, including solo performance on voice or instrument.
        - Appreciate historical, social and economic factors influencing composers and musical styles in West Africa and globally.
        - Compose short vocal and instrumental pieces and explore basic computer applications in music.
        - Build a foundation for further study and possible careers in music (performance, composition, production, sound engineering, music education, etc.).

        Examination Scheme:
        - Paper 1: 1-hour multiple-choice paper with 40 questions from the entire syllabus for 40 marks.
        - Paper 2: 2-hour essay paper with 5 questions; candidates answer 3 for 60 marks.
          • Question 1 (Theory/Composition – melody writing, harmony, counterpoint) and Question 2 (analysis of prescribed set-works) are compulsory.
          • Remaining questions cover form and analysis, history and literature of Western and African music, and Black music in the diaspora.
        - Paper 3A: Aural Test – 45-minute listening test for 50 marks, covering rhythmic and melodic dictation, two-part phrases, chords, cadences, modulations and theme identification.
        - Paper 3B: Performance Test – 30-minute practical exam for 50 marks, testing performance on an approved instrument or voice, including sight reading, technical exercises, scales and arpeggios.

        Theory of Music and Composition:
        - Rudiments of Music:
          • Staff notation and clefs (C, G, F).
          • Western scales: diatonic (major and minor – natural, harmonic, melodic) and chromatic.
          • African modes: pentatonic (5-tone), hexatonic (6-tone), heptatonic (7-tone).
          • Keys and key signatures; simple and compound time signatures.
          • Intervals; transcription between staff notation and solfa; transposition including for transposing instruments.
          • Musical terms, signs, ornaments and abbreviations.
        - Elementary Harmony:
          • Chords/triads: primary (I/i, IV/iv, V) and secondary (ii, iii, vi), chord vii° and dominant 7th and their inversions.
          • Chord progressions and cadences: perfect, plagal, interrupted, imperfect in major and minor keys.
          • Harmonising a given melody using primary and secondary triads, including cadential and passing six-four chords and non-harmonic tones (passing, neighbouring, anticipation).
          • Modulation to closely related keys (dominant, subdominant, relative major/minor).
          • Four-part harmony (SATB) and two-part free counterpoint (adding a line above or below a given melody).
        - Composition:
          • Continuing a given melodic phrase in major or minor to form a 12–16 bar melody, possibly with modulation to a related key.
          • Setting a short English text to music.
        - Form and Analysis:
          • Simple forms (binary, ternary, rondo) and extended forms (overture, oratorio, opera, cantata, suite, sonata, symphony, concerto, etc.).
          • Form in traditional African music (call-and-response, cantor/chorus, refrain, cyclic/repetitive forms, etc.).
          • Form and techniques in contemporary African art music (use of melody, rhythm, harmony, instrumentation, through-composed structures, etc.).
        - Set Works:
          • Study of selected Western and African set-works (e.g. concertos, symphonic movements, songs and African art music pieces) for detailed analysis in Paper 2.

        Aural Tests (Paper 3A):
        - Rhythmic dictation of short melodies (up to 4 bars) in simple and compound time.
        - Melodic dictation (including modal and African-inflected melodies).
        - Writing the upper or lower part of a short two-part phrase.
        - Identification of chord progressions using Roman numerals or functional names.
        - Recognition and naming of cadences (perfect, imperfect, interrupted, plagal).
        - Recognition of simple modulations to closely related keys.
        - Identification/description of short themes or excerpts (form, style/genre, main instruments, scale/mode, metre), including at least one African example.

        Performance Tests (Paper 3B):
        - Performance on one approved instrument or voice (e.g. voice types, pianoforte/electronic keyboard, violin, guitar, recorder, atenteben, flute, clarinet, saxophone, trumpet, trombone, euphonium, tuba).
        - Assessment of technical control (tone production, intonation, articulation, phrasing), musical expression and stylistic awareness.
        - Performance of set pieces and technical exercises (scales, arpeggios, studies) according to the instrument’s prescribed list.
        - Sight-reading tests in simple and compound time.

        History and Literature of Music:
        - Traditional African Musicians/Composers: major traditional performers from Nigeria, Ghana and other West African countries and their contributions.
        - Popular African Musicians: highlife, juju, afrobeat, fuji, waka, afro-pop, reggae, hip-hop and related styles, with representative artists in Nigeria, Ghana and beyond.
        - Contemporary African Art Musicians: key African art music composers, their training, styles, important works and roles in music development.
        - Western Music History:
          • Medieval, Renaissance, Baroque, Classical, Romantic and 20th-century periods.
          • Representative composers in each era and their major contributions.
        - Black Music in the Diaspora:
          • Important artists and genres such as spirituals, jazz, blues, rock, gospel, soul, calypso, reggae, Afrobeat and their influence on global music.

        Traditional and Contemporary African Music:
        - Role of music in traditional African society (ceremonial, social, religious, communicative and recreational functions).
        - African musical instruments: names, descriptions, classification and musical/non-musical functions.
        - General musical characteristics: scales and modes; rhythm (metrical, non-metrical, cross-rhythm, hemiola, syncopation, polyrhythm); polyphony; forms (antiphony, strophic, through-composed, etc.); vocal styles (recitative, yodelling, ululation, holler, nasalisation); textures (monophonic, homophonic, polyphonic); instrumentation.
        - Traditional vocal genres (dirges, cradle songs/lullabies, ritual songs, folk songs, satirical songs and other song types) and their functions.
        - Relationship of music to other arts such as dance, drama and festivals; examples of major traditional dances and their ethnic/cultural contexts.

        Guidelines for Candidates:
        - Practise theory (rudiments, harmony, composition) regularly alongside listening and aural training.
        - Build strong performance skills on at least one instrument or voice, including scales, studies and sight-reading.
        - Listen widely to traditional, popular, art and Western music and study scores and recordings of set works.
        - Use basic music software for notation and simple audio work where possible.
        - Engage with local musical practices (choirs, bands, ensembles) to connect examination topics with real performance contexts.`,
  },
  {
    subject: 'French',
    timeAlloted: 60 * 60,
    description: `French assesses candidates’ communicative competence in the four language skills – listening, speaking, reading and writing – for effective use of French in personal, educational and regional contexts.

        Aims and Focus:
        - Build practical ability to understand spoken and written French on everyday topics.
        - Develop clear and accurate expression in spoken and written French.
        - Strengthen vocabulary and control of key grammatical structures within communicative situations.
        - Encourage positive attitudes towards French for participation in affairs of the sub-region and the wider Francophone world.

        Examination Scheme:
        - Paper 1 (Objective – 1 hour, 40 marks):
          • Section A – Comprehension: two short passages (about 150 words each) with multiple-choice questions testing reading comprehension.
          • Section B – Structure: 30 multiple-choice items testing appropriate use of French words, expressions and structural patterns in context.
        - Paper 2 (Essay and Letter Writing – 1 hour 15 minutes, 40 marks):
          • Section A – Essay: three questions; candidates answer one, writing about 100 words on general-interest topics.
          • Section B – Letter Writing: three questions; candidates answer one formal or informal letter (about 100 words).
        - Paper 3 (Oral – about 40 minutes, 50 marks):
          • Listening Comprehension: a straightforward passage (~200 words) read aloud twice; candidates answer 10 questions in writing.
          • Reading Aloud: candidates read a simple contemporary passage (~100 words) to test pronunciation and fluency.
          • Conversation: about 10 minutes per candidate, including dialogue and short exposition on everyday themes (sports, health, leisure, education, politics, culture, etc.).

        Language Content and Skills:
        - Comprehension and Reading:
          • Understanding standard written French in short texts such as narratives, descriptions, dialogues and simple informational passages.
          • Answering questions on main ideas, specific details and inferred meaning, mainly via multiple-choice items.
        - Structure and Grammar in Context:
          • Parts of speech and grammatical categories.
          • Articles – definite, indefinite, partitive (with correct gender and number).
          • Verbs – regular and irregular; common moods and tenses in everyday communication; standard expressions (e.g. "avoir + nom", "venir de + infinitif").
          • Nouns – complements, gender and number.
          • Pronouns – personal, possessive, indefinite, demonstrative, relative and impersonal, with correct agreement.
          • Adjectives – possessive, demonstrative, indefinite and interrogative (agreement in gender/number and correct position).
          • Adverbs – formation and position, including special adverbs (mal, vite, fort, tout, etc.).
          • Negation – common patterns (ne…pas, ne…rien, ne…jamais, etc.).
          • Conjunctions in common use (et, mais, donc, ou, parce que, etc.).
          • Prepositions (à, de, sur, en, devant, etc.).
          • Sentence types – declarative, interrogative, exclamative; active/passive voice; direct and reported speech.
          • Compound and complex sentences; idiomatic expressions, proverbs and basic figures of speech (simile, metaphor, hyperbole) in simple contexts.
          • Communicative acts (“actes de parole”) – expressing politeness, disappointment, approval, disgust, surprise, pleasure, regret, etc.

        Productive Skills (Writing and Speaking):
        - Writing short essays and letters on familiar topics using appropriate registers, tenses and connectors.
        - Organising ideas clearly in paragraphs with correct spelling, accents and punctuation.
        - Participating in guided dialogues, responding to at least ten questions on everyday situations (and, where applicable, simple literature texts).
        - Giving short expositions on given topics, producing at least about five well-formed sentences.

        Oral and Listening Skills:
        - Understanding spoken French in simple narratives and expository passages related to daily life and contemporary issues.
        - Demonstrating accurate pronunciation, rhythm and intonation when reading aloud.
        - Engaging in simple but coherent conversation on themes such as school, family, hobbies, health, environment, culture and current events.

        Guidelines for Candidates:
        - Practise all four skills regularly: listen to spoken French, read short texts, write short compositions and speak as often as possible.
        - Learn core grammar and vocabulary in context rather than in isolation, focusing on accuracy and communicative use.
        - Use past questions, dialogues and role-plays to prepare for comprehension, structure, essay and oral components.
        - Read simple French books, magazines, websites and graded readers to build fluency.
        - Make use of audio resources (radio, songs, podcasts) and conversation practice to strengthen listening and speaking confidence.`,
  },
  {
    subject: 'Yoruba',
    timeAlloted: 60 * 60,
    description: `Indigenous language paper based on the NERDC 2007 Yoruba curriculum, assessing candidates’ ability to speak, read and write standard Yoruba, and their appreciation of Yoruba language, literature, customs and institutions.

        Preamble & Aims:
        - Entirely aligned with the senior secondary Yoruba curriculum developed by NERDC (2007); all rubrics are written in Yoruba and answers are expected in official Yoruba orthography and metalanguage.
        - Enable candidates to speak, read and write competently in Yoruba and communicate effectively with other speakers of the language.
        - Familiarize candidates with the sound system, grammatical structures and other key features of Yoruba.
        - Provide tools for a thorough appreciation of Yoruba speech usage, oral and written literature, and culture (customs and institutions).

        Scheme of Examination:
        - Paper 1: Objective (Language, Literature and Culture)
          • 60 multiple-choice questions to be taken in 1 hour for 60 marks.
          • Section A – Language (30 items): comprehension, composition, sound system, grammar and translation.
          • Section B – Literature (22 items): basic principles of literary appreciation plus oral and written literature (prose, poetry, drama) based on the set books.
          • Section C – Culture (8 items): questions on customs and institutions.

        - Paper 2: Essay (Language, Literature and Culture)
          • 2-hour essay paper carrying 100 marks in three sections: A (Language), B (Literature), C (Culture).
          • Candidates answer six questions in all: three from Section A, two from Section B and one from Section C.
          • All answers are written in Yoruba using the official orthography and approved metalanguage.

        Syllabus Focus – Section A: Language
        - Composition:
          • Continuous writing of about 300 words in not more than 45 minutes.
          • Essay types include: narrative, descriptive, argumentative, expository, dialogue, debate and letter writing (formal and informal).

        - Sound System (Fonoloji):
          • Yoruba vowels, consonants and syllabic nasal – place/manner of articulation; basic phonetic and phonemic classification.
          • Syllable structure and common sound processes such as vowel harmony, assimilation and elision.
          • Sound-system aspects of the orthography (grapheme–phoneme correspondence).
          • Tones and tone notation.

        - Grammar (Girama):
          • Word structure and word formation processes.
          • Numerals – basic and derived forms and their usage.
          • Word classes: nouns, verbs, adverbs, pronouns, adjectives and others in Yoruba.
          • Grammatical categories: subject, object, complement, predicate, number, person, tense, aspect, mood, etc.
          • Phrases and clauses; structure and types of sentences in standard Yoruba.

        - Translation & Comprehension:
          • Short passages testing understanding, inference and vocabulary in context.
          • Translation between Yoruba and English (or other language as specified) to test accurate grasp of meaning, structure and usage.

        Syllabus Focus – Section B: Literature
        - Basic Principles of Literary Appreciation:
          • Literary terms and concepts applied to Yoruba texts (theme, plot, characterization, setting, style, figurative language, etc.).
          • Simple criticism and appreciation of given extracts.

        - Oral Literature (Litireso Abe-enu):
          • Prose, poetry and drama in the oral tradition, studied through selected set books.
          • Questions on content, form, performance context and moral/cultural values.

        - Written Literature (Litireso Atelewe):
          • Prose, poetry and drama in written Yoruba, also based on set texts.
          • One question set on each prescribed prose, poetry and drama text; candidates answer according to exam instructions.

        Syllabus Focus – Section C: Culture (Asa ati Ile-Ise)
        - Customs (Asa):
          • Make-up and adornments.
          • Indoor and outdoor games.
          • Pregnancy and its care; naming ceremonies.
          • Home training: greetings, respect for elders and superiors, cleanliness, tolerance, integrity, hard work and related virtues.
          • Yoruba foods and food customs.
          • Non-verbal communication (gestures, facial expressions, body language, etc.).
          • Funerals – traditional and modern practices.
          • Inheritance – traditional and modern methods.

        - Institutions (Ile-Ise ati Eto Awujọ):
          • Traditional occupations.
          • Marriage practices and ceremonies.
          • Politics and social organization: family structure, age grades, chieftaincy, Obaship and related institutions.
          • Child care and upbringing.
          • Ancestors and beliefs about life after death.
          • Yoruba beliefs about Olodumare (God), deities/divinities and other invisible forces.
          • Modern religions – Islam, Christianity and their place in Yoruba society.
          • Economic life: advertising, money lending, co-operative efforts and similar practices.

        Guidelines for Candidates:
        - Read, speak and write Yoruba regularly to build fluency and confidence in the standard orthography and tone marking.
        - Revise key areas of fonoloji and girama (sound system and grammar) using approved language texts and past questions.
        - Study all prescribed oral and written literature texts carefully, paying attention to plot, themes, characters, style and cultural background.
        - Observe Yoruba customs and institutions in the home and community and relate them to the culture topics in the syllabus.
        - Practise objective and essay-type questions across the three sections (Language, Literature and Culture), managing time so that composition, language questions and literary/culture essays are all adequately covered.`,
  },
  {
    subject: 'Igbo',
    timeAlloted: 60 * 60,
    description: `Indigenous language paper designed for candidates studying Igbo, assessing their command of the sound system, grammar, composition, translation, oral and written literature, and Igbo culture/worldview.

        Preamble & Objectives:
        - Assumes that sound knowledge of Igbo language, literature and culture is essential for understanding the Igbo people.
        - Enables candidates to speak, read and write Igbo properly and communicate effectively in it.
        - Familiarizes candidates with the sound system, grammatical structures and other key features of the Igbo language.
        - Exposes candidates to Igbo culture – customs, institutions and worldview.
        - Provides tools needed for creative writing and appreciation of Igbo language and literature.

        Scheme of Examination:
        - Composite paper made up of Papers 1 and 2, taken at one sitting.
        - All rubrics are written in Igbo; candidates answer all questions in Igbo, using the standard orthography and approved metalanguage.

        - Paper 1: Objective (Language, Literature and Culture)
          • 60 multiple-choice questions to be answered in 1 hour for 60 marks.
          • Section A – Language (30 items):
            - Comprehension (2 short passages) – 10 items.
            - Composition/letter writing – 4 items.
            - Sound system – 6 items.
            - Grammar – 6 items.
            - Translation – 4 items.
          • Section B – Literature (22 items):
            - Basic principles of literary appreciation – 4 items.
            - Oral literature – prose (3), poetry (3), drama (3).
            - Written literature – prose (3), poetry (3), drama (3).
            - All questions except those on basic principles are set on the prescribed texts.
          • Section C – Culture (8 items):
            - Customs – 4 items.
            - Institutions – 4 items.

        - Paper 2: Essay / Composition (Language, Literature and Culture)
          • 2-hour paper for 100 marks with three sections: A (Language), B (Literature), C (Culture).
          • Thirteen questions in all; candidates answer six: drawn across the three sections as specified in the rubric.

        Syllabus Focus – Section A: Language
        - Part I – Composition / Letter Writing:
          • Five topics are set; candidates choose one and write about 300 words in not more than 45 minutes.
          • Essay types include: narrative, descriptive, argumentative, expository, dialogue, debate and letter writing (formal/informal).

        - Part II – Sound System (Phonology):
          • Igbo phonemes: vowels, consonants and syllabic nasal – their production and classification.
          • Syllable structure in Igbo.
          • Sound processes: vowel harmony, vowel assimilation, vowel elision and consonant elision.
          • Tone and tone notation.
          • The Igbo alphabet and its use in standard orthography.

        - Part III – Grammar:
          • Spelling rules in standard Igbo.
          • Word formation processes.
          • Igbo dialects and Standard Igbo.
          • Loan words and Igbo metalanguage (new technical terms).
          • Word classes: noun, verb, pronoun, conjunction, preposition and others.
          • Grammatical categories: tense, number and person.
          • Phrases and clauses – their types and functions.
          • Sentences: constituents and types (including direct and indirect speech).
          • Lexis and structure; correct use of punctuation marks.

        Syllabus Focus – Section B: Literature
        - Part I – Oral Literature (Prose, Poetry and Drama):
          • Questions set on prescribed oral literature texts.
          • Candidates are expected to know: definition and characteristics of oral literature; classification, functions and major literary devices.
          • Genres and forms include: folktales (ifo), anecdotes (ụkabụilu), legends (nkọkịrịkọ / akụkọ dike), myths (akụkọ okike / mmalite), poems (abụ), songs (uri), proverbs (ilu), riddles (agwụgwa), chants (mbem), oral drama (ejije ọnụ) and tongue twisters (okwuntụhi).

        - Part II – Written Literature (Prose, Poetry and Drama):
          • Questions based on the prescribed written prose, poetry and drama texts.
          • Candidates demonstrate understanding of plot, characterization, themes, setting, language/style and cultural background.
          • Recommended texts are prescribed in cycles (e.g. 2016–2020) for both oral and written literature components.

        Syllabus Focus – Section C: Culture (Omenala na Ewumewu)
        - Customs (Omenala):
          • Igbo identity and ways of life: language, dress, etiquette, taboos and social values.
          • Greetings and forms of address in different contexts.
          • Ceremonies and observances: kola nut rituals, morning rites, libation, welcoming visitors.
          • Birth and child‑related customs: pregnancy care, purification, naming, initiation, socialization of children.
          • Beliefs and practices: reincarnation, ọgbanje, dreams and divination-related practices.
          • Festivals and communal events: harvest and yam festivals, ofala and other title/festival ceremonies, mourning and funeral rites, age‑grade and women’s ceremonies, games and children’s play.

        - Institutions (Ewumewu):
          • Political and social institutions: the family, kindred and lineage, traditional rulers, title holders (nze na ọzọ, etc.), age grades, women’s groups, secret societies and village/ town structures.
          • Marriage and kinship institutions: forms of marriage, bride‑wealth, in‑law relations and kinship ties.
          • Economic institutions: land tenure, communal ownership, farming groups and other work organizations; occupations such as hunting, fishing, farming, carving, pottery and crafts.
          • Religious institutions: shrines, priests/diviners, sacred objects and patterns of worship; veneration of ancestors and deities.

        Guidelines for Candidates:
        - Practise speaking, reading and writing standard Igbo regularly, observing correct spelling, tone marking and punctuation.
        - Study the sound system and grammar topics systematically and apply them in composition, translation and objective questions.
        - Read all prescribed oral and written literature texts in depth and be able to discuss content, form, devices and cultural significance.
        - Pay close attention to Igbo customs, institutions and worldview in real life and in texts, relating them to the culture section of the syllabus.
        - Use past WASSCE Igbo questions to build speed and familiarity with the structure of Papers 1 and 2 and to balance time between language, literature and culture components.`,
  },
  {
    subject: 'Hausa',
    timeAlloted: 60 * 60,
    description: `Indigenous language paper assessing candidates’ ability to use standard Hausa in speech and writing, and their understanding of Hausa sound system, grammar, literature and culture.

        Scheme of Examination:
        - Paper 1: Objective (Language, Literature and Culture)
          • 60 multiple-choice questions in 1 hour for 60 marks.
          • Section A – Language (30 items):
            - Comprehension (2 short passages) – 10 items.
            - Composition/letter writing – 4 items.
            - Sound system – 6 items.
            - Grammar – 6 items.
            - Translation – 4 items.
          • Section B – Literature (22 items):
            - Basic principles of literary appreciation – 4 items.
            - Oral literature – prose (3), poetry (3), drama (3) – 13 items.
            - Written literature – prose (3), poetry (3), drama (3) – 9 items.
            - All questions other than those on basic principles are based on the prescribed set books for Paper 2.
          • Section C – Culture (8 items):
            - Customs – 4 items.
            - Institutions – 4 items.

        - Paper 2: Essay (Language, Literature and Culture)
          • 2-hour essay paper for 100 marks in three sections: A (Language), B (Literature), C (Culture).
          • Candidates answer six questions in all: three from Section A, two from Section B and one from Section C.
          • Rubrics are written in Hausa; candidates answer in the official Hausa orthography using appropriate metalanguage.

        Syllabus Focus – Section A: Language (Harshe)
        - Composition / Letter Writing (Insha’i / Rubutun Wasika):
          • One extended essay of about 300 words; recommended time not more than 45 minutes.
          • Topics may be any of: narrative (Insha’i na Labari), descriptive (Insha’i na Siffance), argumentative (Insha’i na Muhawara), expository (Insha’i na Bayani), dialogue (Muhawara ta kai tsaye), proverbial (Karin Magana) and letter writing (Rubutun Wasika).

        - Sound System – Tsarin Sauti (Phonology):
          • Consonants, vowels and syllable structure (furucin bakake da wasula da kirar gaba):
            - Production in terms of place, manner and glottal status (e.g. bilabials, nasals, voiced/voiceless sounds).
            - Syllable structure: how consonants and vowels combine in syllables.
            - Main patterns such as CV (BW), CVV (BWW) and CVC (BWB).
            - Types of syllable: open (budaddiyar gaba) and close (rufaffiyar gaba).
          • Vowel production (furucin wasula):
            - Short and long vowels, plus diphthongs (gajeru, dogaye da tagwan wasula).
          • Tone (Karin sauti):
            - High tone, low tone and falling tone (karin sautin sama, kasa da mai faduwa).

        - Grammar – Nahawu:
          • Word structure and word formation (ginin kalmomi):
            - Root (saiwa), affix (dafi) – prefix (dafa goshi), infix (dafa ciki), suffix (dafa keya).
          • Word classes (azuzuwan kalmomi):
            - Noun (suna), verb (aikatau), adjective (sifa), adverb (bayanau), pronoun (wakilin suna), conjunction (mahadi), specifier (mafayyaci), quantifier (ma’auni), demonstrative (nunau), ideophone (amsa-kama), diminutive (tsigilau), stabilizer (dirka) and referential (madanganci).
          • Grammatical categories (rukunonin nahawu):
            - Tense and aspect (lokutan aiki): general past, relative past, general continuous, relative continuous, first and second future, habitual, subjunctive, negative.
            - Gender (jinsi) and number (adadi).
          • Structure and types of sentences (tsari da ire-iren jumloli):
            - Subject and predicate (yankin suna da na aikatau).
            - Simple, complex and compound sentences.

        Syllabus Focus – Section B: Literature (Adabi)
        - Oral Literature (Adabin Baka):
          • Prose: traditional tales and historical narratives (tatsuniya, tarihihi), proverbial narratives and puzzles (karin magana, ka-cinci-ka-cinci).
          • Poetry: oral poems and songs (wakokin baka).
          • Drama: traditional plays (wasannin gargajiya).
          • Three set books (one per genre); one question is set on each, candidates answer any one.

        - Written Literature (Rubutaccen Adabi):
          • Prose (zube), written poetry (rubutattun wakoki) and drama (wasan kwaikwayo).
          • Three set books (one per genre); one question on each, candidates answer any one.
          • Emphasis on plot, characterization, themes, setting, language and cultural values.

        Syllabus Focus – Section C: Culture (Al’adu da Tsare-tsare)
        - Customs and Social Behaviour:
          • Greeting (gaisuwa): greetings of respect (ban girma), congratulations (taya murna), greetings for times and seasons (safiya, rana, damina, rani, etc.), condolence (jaje, ta’aziyya).
          • Tarbiyya (upbringing and discipline): methods such as scolding, warnings and correction; respect for parents and elders, peers and authority.
          • Hospitality (karimci): honouring visitors, gifts, feeding others, charity (sadaka).
          • Family (iyali): marriage customs (al’adun aure), childbirth and naming (haihuwa, radin suna), upbringing and weaning (reno, yaye), circumcision (kaciya), kinship ties (zumunci), and death/funeral customs (al’adun mutuwa).
          • Traditional beliefs and practices: practices contrary to formal religion such as superstition (camfi), spirit possession (bori), divination and magic (duba, tsibbu), witchcraft (maita), belief in spirits/ghosts (fatalwa).

        - Institutions, Occupations and Recreation:
          • Traditional occupations (sana’o’in gargajiya): farming (noma), blacksmithing (kira), weaving (saka), leather work (jima), barbering/surgery (wanzanci), hair plaiting (kitso), carving (sassaka), herding (kiwo), butchering (fawa), dyeing (rini), trading and brokerage (kasuwanci, dillanci), and others.
          • Traditional medicine (magungunan gargajiya): practitioners such as magori, ’yar maiganye, bokaye, wanzamai and their roles.
          • Traditional authority and office holding (sarautun gargajiya): offices such as sarki, hakimi, dagaci, mai unguwa, shamaki, shantali, sarkin zagi, liman, sarkin fada and others.
          • Traditional recreation (wasannin dandali): dambe, kokawa, kalankuwa, shanci, sukuwa, hawan kaho and similar games and sports.

        Guidelines for Candidates:
        - Build active proficiency in spoken and written Hausa using the official orthography and recommended metalanguage.
        - Revise the sound system (tsarin sauti) and nahawu regularly and apply them in composition, objective items and translation.
        - Study all prescribed oral and written literature texts carefully, noting themes, characters, style and cultural context.
        - Observe Hausa customs, institutions and everyday practices and relate them to the culture topics in Section C.
        - Practise past WASSCE Hausa questions for both Papers 1 and 2 to develop speed, accuracy and good exam technique across language, literature and culture components.`,
  },
  {
    subject: 'Further Mathematics',
    timeAlloted: 90 * 60,
    description: `Advanced Mathematics / Mathematics (Elective) paper for candidates who need a deeper, more rigorous treatment of Mathematics beyond the core syllabus, bridging to higher-level work in Mathematics, Engineering and the Sciences.

        Aims of the Syllabus:
        - Develop further conceptual and manipulative skills in Mathematics.
        - Provide an intermediate course of study that bridges the gap between Elementary (Core) Mathematics and Higher Mathematics.
        - Equip potential Mathematicians, Engineers, Scientists and other professionals with additional mathematical tools.
        - Build the ability to analyse quantitative data and draw valid conclusions.
        - Strengthen logical, abstract and precise reasoning skills.

        Examination Scheme:
        - Paper 1: Objective
          • 40 multiple-choice questions covering the entire syllabus.
          • Duration: 1½ hours (90 minutes) for 40 marks.
          • Question distribution:
            - Pure Mathematics – 30 questions.
            - Statistics & Probability – 4 questions.
            - Vectors & Mechanics – 6 questions.

        - Paper 2: Theory / Problems
          • 2½‑hour written paper for 100 marks with two sections (A and B).
          • Section A – 8 short, compulsory questions (elementary in style) for 48 marks:
            - Pure Mathematics – 4 questions.
            - Statistics & Probability – 2 questions.
            - Vectors & Mechanics – 2 questions.
          • Section B – 7 longer, more demanding questions (52 marks total), in three parts:
            - Part I – Pure Mathematics: 3 questions.
            - Part II – Statistics & Probability: 2 questions.
            - Part III – Vectors & Mechanics: 2 questions.
          • Candidates answer 4 questions from Section B with at least one from each part.

        Major Syllabus Areas – Pure Mathematics:
        - Sets & Logic:
          • Set notation, universal set, subsets, complements, Venn diagrams and De Morgan’s laws.
          • Binary operations and their properties (closure, commutativity, associativity, identity, inverses, distributivity).
          • Logical reasoning: statements, connectives, implications, truth tables and validity of arguments.

        - Algebra & Functions:
          • Surds and operations on surds; rationalising denominators.
          • Functions, domain/range, one‑to‑one/onto, inverse and composite functions.
          • Polynomial functions (linear, quadratic, cubic), factor and remainder theorems; simultaneous linear–quadratic equations; inequalities and graphing.
          • Rational functions and partial fractions (simple linear/quadratic denominators).
          • Indices and logarithms; laws of indices/logs; solving equations involving indices/logs; linearising power relations.
          • Permutation and combination; binomial expansion for positive integer index and simple approximations.
          • Sequences and series: arithmetic and geometric progressions, sums (finite/infinite), simple recurrence relations.

        - Matrices, Linear Transformations & Coordinate Geometry:
          • Matrices up to 3×3, matrix operations, determinants (2×2 and, where applicable, 3×3) and inverses (2×2); solving simultaneous linear equations.
          • Linear transformations in the plane: image of points, matrices of basic transformations (reflections, rotations, enlargements), composition and inverse transformations.
          • Coordinate geometry: distance, midpoint, gradient, equations of lines, conditions for parallel/perpendicular lines; simple loci and equations of circles and basic parabolas.

        - Trigonometry & Calculus:
          • Trig ratios for general angles; compound and multiple angles; trigonometric identities and equations; graphs of trig functions and simple combinations (asinx + bcosx).
          • Limits (intuitive idea) and derivatives from first principles for simple functions.
          • Differentiation of polynomials and standard trig functions; product/quotient rules; simple implicit differentiation; second derivatives and applications.
          • Applications of differentiation: tangents/normals, maxima/minima, rates of change and basic kinematics.
          • Integration of polynomials (including x⁻¹), simple trig forms and basic substitutions; definite integrals, area under curves and simple volumes of revolution; trapezium rule approximations.

        Major Syllabus Areas – Statistics & Probability:
        - Descriptive Statistics:
          • Data presentation: frequency tables, histograms (including unequal class widths), ogives, cumulative frequency and scatter diagrams.
          • Measures of location: mean, median, mode, quartiles and percentiles (for grouped and ungrouped data).
          • Measures of dispersion: range, inter‑quartile range, semi‑inter‑quartile range, mean deviation, variance and standard deviation.
          • Correlation: scatter plots, positive/negative/zero correlation, line of best fit (by eye) and Spearman’s rank correlation (without ties).

        - Probability & Distributions:
          • Basic probability concepts: sample spaces, equally likely outcomes, relative frequency; independent and mutually exclusive events; simple conditional probability.
          • Addition and multiplication rules for probability.
          • Discrete distributions: binomial distribution for simple problems; Poisson distribution (where specified) as an approximation when n is large and p is small.

        Major Syllabus Areas – Vectors & Mechanics:
        - Vectors:
          • Scalars vs vectors; vector representation in component form; addition, subtraction and scalar multiplication.
          • Resultants using triangle, parallelogram and polygon laws; unit vectors; position vectors and division of a line in a given ratio.
          • Scalar (dot) product and angle between vectors; basic applications.

        - Statics:
          • Concept and representation of forces; composition and resolution of coplanar forces (particles and rigid bodies).
          • Conditions for equilibrium; Lami’s theorem; moments of forces and simple applications.
          • Friction on rough and smooth planes; coefficient of friction and limiting equilibrium.

        - Dynamics & Kinematics:
          • Displacement, velocity, acceleration and speed; rectilinear motion under constant acceleration.
          • Newton’s laws of motion; motion on horizontal and inclined planes (including resolving components and frictional forces).
          • Equations of motion (v = u + at, s = ut + ½at², v² = u² + 2as) and applications.
          • Impulse and linear momentum; conservation of linear momentum in simple systems.
          • Projectiles launched at an angle to the horizontal (where specified).

        Guidelines for Candidates:
        - Master the Core/General Mathematics syllabus first, then use this course to extend techniques in algebra, calculus, statistics, vectors and mechanics.
        - Practise plenty of multi‑step problems from both Papers 1 and 2, paying special attention to showing clear working and using correct mathematical notation.
        - Use approved tables and non‑programmable calculators effectively, but also be prepared for questions that restrict their use.
        - Build speed and accuracy in routine manipulations (algebra, trig, differentiation/integration) so that more time is available for longer Section B questions.
        - Regularly attempt past Further Mathematics / Mathematics (Elective) papers under timed conditions to strengthen exam technique and topic coverage.`,
  },
  {
    subject: 'Animal Husbandry (Alt A)',
    timeAlloted: 60 * 60,
    description: `Animal Husbandry (Alternative A) assesses candidates’ understanding of the production, management, processing and marketing of farm animals and their products, with strong emphasis on practical skills and environmentally sustainable practices.

        Preamble & Aims:
        - Provides basic knowledge and skills in the production, processing, preservation, packaging, storage and marketing of farm animals and animal products.
        - Prepares candidates for further training in agriculture and animal science at tertiary level.
        - Emphasizes efficient and effective management of animal enterprises using environmentally friendly practices.
        - Develops problem‑solving skills for identifying and addressing constraints in the livestock industry.

        School Requirements & Practical Orientation:
        - Schools are expected to keep at least one species from each group:
          • Monogastrics: poultry, pigs, rabbits.
          • Ruminants: goats, sheep, cattle.
          • Non‑traditional animals: grasscutters, snails, etc.
        - Schools are encouraged to maintain an agriculture laboratory for specimens, equipment and chemicals.
        - Candidates should keep detailed practical notebooks with records of farm activities, field trips and collected specimens.

        Examination Scheme (for Ghana and similar pattern in other member countries):
        - Paper 1: Objective
          • 50 multiple‑choice questions to be answered in 1 hour for 50 marks.
        - Paper 2: Essay
          • 6 essay‑type questions; candidates answer 4 in 2 hours for 80 marks.
        - Paper 3: Practical / Test of Practical Work
          • 4 questions; all must be answered in 2 hours for 60 marks.

        Major Syllabus Areas:
        - A. Introduction to Animal Husbandry
          • Meaning, scope and importance of animal husbandry in food supply, employment and income generation.
          • Problems of animal production in West Africa and possible solutions.
          • Quality and safety standards for animal‑source foods and roles of regulatory agencies.
          • Classification of farm animals by digestive system and by traditional vs non‑traditional species.

        - B. Anatomy and Physiology of Farm Animals
          • Meaning of anatomy and physiology; external parts and orientation of farm animals.
          • Digestive systems of ruminants, non‑ruminant herbivores and monogastrics; digestion and absorption of nutrients.
          • Central and autonomic nervous systems and their functions; nerve impulses and homeostasis.
          • Endocrine glands and hormones and their roles in body regulation and reproduction.
          • Integumentary, skeletal and muscular systems – structure and functions.
          • Male and female reproductive systems in livestock and poultry; reproductive terms and processes from puberty to parturition.
          • Circulatory, respiratory and excretory systems – structures, functions and key parameters (heart rate, pulse, respiration, excretion and excretory products).

        - C. Animal Nutrition
          • Meaning and importance of animal nutrition and basic terms (nutrients, diet, ration, roughage, concentrates, malnutrition).
          • Modes of feeding in different classes of animals and relation to dentition.
          • Feedstuffs and supplements: types, major nutrient composition and classification.
          • Simple food tests for carbohydrates, fats/oils and proteins.
          • Producers and suppliers of feedstuffs and supplements.
          • Common terms in feed formulation (dry matter, metabolizable energy, etc.); factors to consider when formulating rations.
          • Simple ration formulation using Pearson’s Square; preparation of feeds.
          • Handling and storage of feeds: importance, storage structures, factors affecting quality and good storage practices.
          • Record keeping and estimation of cost of prepared feeds; pests of stored feeds and their control.

        - D. Animal Health
          • Meaning of disease, pests and parasites; signs of good and ill‑health in farm animals.
          • Factors predisposing animals to diseases (overcrowding, poor nutrition, poor housing, stress) and how to reduce stress.
          • Effects of diseases, pests and parasites on productivity, product quality and income.
          • Zoonotic diseases: meaning, importance to human health and prevention.
          • Pathogens (viruses, bacteria, fungi, protozoa), major pathogenic diseases and their economic importance.
          • Principles and practices for prevention and control of diseases: nutrition, sanitation, vaccination, prophylactic drugs and appropriate drug administration.
          • Ectoparasites and endoparasites: types and examples, structure, life cycles, modes of feeding, conditions influencing parasite populations and their control.

        - E. Genetic Principles and Animal Improvement
          • Basic genetic concepts: heredity, gene, genotype, phenotype, sex‑linkage, meiosis and mitosis.
          • Mendel’s first law and simple monohybrid crosses.
          • Meaning, objectives and benefits of animal improvement (e.g. better growth rate, disease resistance).
          • Performance traits of improved vs unimproved animals and simple data analysis to identify superior stock.
          • Methods of animal improvement: introduction, selection and breeding (inbreeding, line‑breeding, cross‑breeding) – advantages and disadvantages.
          • Nutritional and environmental factors affecting expression of genetic potential.
          • Artificial insemination (A.I.): meaning, process, equipment, advantages, signs of heat and proper semen handling.

        - F. Environmental Physiology
          • Internal and external environments of animals (e.g. body temperature, blood composition vs ambient temperature, humidity, wind).
          • Role of nervous and endocrine systems in homeostasis and response to stimuli.
          • Measurement and interpretation of room temperature, body temperature, respiration and heart rate.
          • Effects of climate and extreme weather on health, growth and reproduction.
          • Modification of animal environment (housing design, shade, cooling, watering, timing of grazing and feeding) and management practices to reduce stress.

        - G. Monogastric and Domestic Pet Production
          • Poultry production: definition, characteristics, classes and breeds/types of poultry (chicken, turkey, duck, guinea fowl, ostrich).
          • Importance of poultry; systems of production (extensive, intensive, semi‑intensive) – merits and demerits.
          • Incubation and hatchery practices: terminology, natural vs artificial incubation, egg selection, incubation conditions, process, candling and marketing of day‑old chicks.
          • Poultry management practices: housing, brooding, feeding, sanitation, pest/disease control, identification and record keeping.
          • Processing and marketing of poultry: slaughtering and dressing, quality control, preservation, storage, packaging and marketing outlets.
          • Pig production: types and breeds of pigs, characteristics, importance, systems of keeping pigs, management practices and slaughtering/processing/marketing.
          • Domestic pets: meaning, importance (security, companionship, shepherding, income), management, training and marketing of pets.

        - H. Ruminant Production
          • Sheep and goats: common breeds, distribution, distinguishing features and importance (social, economic and nutritional).
          • Management practices: housing, feeding, hoof trimming, sanitation, pest/disease control, age determination by dentition.
          • Common parasites and diseases of small ruminants and their control; processing and marketing of sheep and goats for meat.
          • Beef cattle: common breeds and characteristics, importance, production systems (extensive, semi‑intensive, intensive), planning and management for beef and draught.
          • General management practices: housing, breeding, feeding, fattening, training of work cattle; slaughtering, processing and marketing of beef; major production constraints and possible solutions.
          • Dairy cattle: dairy breeds and features, composition and importance of milk, requirements for dairy production, major diseases (e.g. mastitis, tuberculosis, ketosis) and their control; processing and marketing of milk and dairy products; problems of dairy production in warm climates.

        - I. Range and Pasture Management
          • Basic terms (pasture, range, stocking rate, rotation) and types of pasture (natural/range and artificial/cultivated).
          • Importance of pastures and forage crops; factors affecting productivity (rainfall, soil fertility, temperature, weeds).
          • Major forage crops and poisonous plants of pastures.
          • Qualities of good pasture species; establishment of pastures (site selection, land preparation, propagation methods); improving natural pastures.
          • Management of cultivated pastures, harvesting of forages and determination of yield.
          • Forage conservation: meaning and importance; hay and silage – preparation, properties and differences.
          • Use of crop residues in ruminant feeding – advantages and limitations.

        - J. Non‑Traditional Animal Production
          • Rabbits and grasscutters: common breeds, importance, value‑chain approach to production, management practices (housing, feeding, breeding, sanitation, disease control, record keeping), and processing/marketing.
          • Snail farming: meaning, importance, external features and species, site selection, management (housing, feeding, sanitation, breeding, pest/disease control, records), processing and marketing.
          • Small‑scale animal enterprises: types of animal production enterprises, factors to consider when establishing an enterprise and basic procedures for planning and managing small livestock businesses.

        Practical Animal Husbandry (Paper 3 Emphasis):
        - Identification and handling of farm animals, breeds, external parts and major organs.
        - Demonstration of management practices: housing, feeding, breeding, brooding, identification, debeaking, castration, dehorning, vaccination and simple health routines.
        - Feedstuffs identification, simple food tests and use of Pearson’s Square for ration formulation; feed mixing and cost estimation; records.
        - Identification and control of pests and parasites of animals and stored feeds; recognition of common diseases and basic control measures.
        - Pasture and forage identification, poisonous plants, conserved forages (hay, silage) and simple pasture operations.

        Guidelines for Candidates:
        - Link theoretical concepts to hands‑on work on school or home farms and keep accurate practical notebooks.
        - Master key terms, diagrams and processes in anatomy, physiology, nutrition, health, genetics, reproduction and environmental physiology.
        - Practise classification and identification of animals, feedstuffs, parasites, diseases and forage species using local examples.
        - Work past questions across all three papers (objective, essay and practical) and pay attention to command words, calculations (rations, costs) and data interpretation.
        - Emphasize safe, hygienic and welfare‑friendly practices when handling animals, equipment, chemicals and animal products.`,
  },
  {
    subject: 'Animal Husbandry (Alt B)',
    timeAlloted: 40 * 60,
    description: `Animal Husbandry (Alternative B) presents animal husbandry as a trade for livelihood, with strong emphasis on practical skills and entrepreneurial competence in raising, managing and marketing farm animals and their products.

        Preamble & Aims:
        - Treats animal husbandry as a vocational subject that can provide employment, income and food security.
        - Assesses candidates’ knowledge of basic production practices such as feeding, housing, breeding and health care.
        - Emphasizes efficient and effective management of animal enterprises, including record keeping and simple accounts.
        - Develops skills in processing, preservation, packaging, storage and marketing of animal products and by‑products.
        - Provides basic knowledge in animal improvement and health to support sustainable livestock production.

        School Requirements & Practical Orientation:
        - Schools offering Animal Husbandry should raise at least one species from each group:
          • Monogastrics: poultry, pigs, snails, camel, donkey, horse, rabbit, bees.
          • Ruminants: cattle, sheep, goats.
        - Schools are encouraged to have an agricultural laboratory for tools, preserved specimens, charts and simple equipment.
        - Candidates should keep practical notebooks and specimen albums with records of farm activities, field trips and collected specimens.
        - Visits to livestock and poultry farms, abattoirs, feed mills and animal‑product based industries are strongly recommended.

        Examination Scheme (Nigeria – Alternative B):
        - Paper 1: Objective
          • 40 multiple‑choice questions covering the whole syllabus.
          • Duration: 40 minutes for 40 marks.

        - Paper 2: Essay
          • 6 structured/essay questions drawn from across the syllabus.
          • Candidates answer 4 questions in 2 hours for 80 marks.

        - Paper 3: Practical / Test of Practical Work
          • 4 questions based on practical animal husbandry.
          • All four are answered in 1½ hours for 60 marks.

        Major Syllabus Areas:

        A. Livestock Production
        - Importance of Farm Animals:
          • Food (meat, milk, eggs, honey, etc.); raw materials (hides and skins, bones, hooves, hair/fur, shells, feathers); manure and bio‑energy; feed ingredients (blood meal, bone meal, snail/egg shells, feathers); animal power; research materials; employment and income; social, cultural and security roles; pets and sports.
        - Classification of Farm Animals:
          • By stomach type: monogastrics (simple stomach – pigs, rabbits, horses, donkeys, snails, bees, poultry, grasscutters) and ruminants (complex stomach – cattle, sheep, goats).
          • Identification of common ruminants and non‑ruminants based on external features, type of stomach and feed.
        - Internal Organs and Body Systems:
          • Identification and functions of major internal organs (liver, lungs, heart, kidneys, spleen, pancreas, stomach, crop, gizzard, intestines, etc.).
          • Body systems and their functions: digestive, respiratory, circulatory, nervous, skeletal, muscular, integumentary and reproductive systems.
        - Reproduction in Farm Animals:
          • Key reproductive terms (ovulation, oestrus cycle, heat, mating, gestation, parturition, lactation, colostrum, flushing, steaming‑up, dystocia, prolapse) and their importance.
          • Reproduction in mammals and poultry, including egg formation; detection of heat, mating systems, pregnancy diagnosis and signs of parturition.
          • Roles of reproductive hormones (oestrogen, progesterone, relaxin, oxytocin, testosterone/androgens).
        - Management Systems and Practices:
          • Management systems: intensive, semi‑intensive and extensive – features, advantages and disadvantages.
          • Housing requirements for major classes of animals, including use of local materials.
          • Routine management practices: feeding and watering, sanitation and hygiene, castration, dehorning, deworming, vaccination and inoculation, culling, debeaking, smoking (in bees), docking, identification (tattooing, branding, ear‑notching, rings), isolation, weaning and care of young.
          • Simple record keeping (growth, production, health, income and expenditure) and management from birth to maturity for at least one large ruminant, small ruminant, poultry, pig, grasscutter, bee and snail.

        B. Animal Nutrition
        - Meaning and Classes of Animal Feeds:
          • Meaning of animal nutrition and nutrients (carbohydrates, proteins, fats/oils, vitamins, minerals and water) – functions, sources and deficiency signs.
          • Classification of feeds into concentrates, roughages, supplements and feed additives.
        - Animal Feeds and Feeding:
          • Meaning and types of rations (balanced, maintenance and production rations).
          • Malnutrition: causes, symptoms and preventive measures (balanced diets, separate feeding of weak animals, deworming, supplementation, avoiding toxic plants, proper stocking rates, adequate water).
        - Formulation of Livestock Rations:
          • Practical diet formulation for different classes of animals (starter, grower and finisher rations).
          • Identification of common feed ingredients (blood meal, fish meal, cotton seed cake, oyster shell, groundnut cake, maize, salt, premix, etc.).
          • Factors to consider in ration formulation: species, age, body weight, physiological status, production target, feed acceptability, nutrient composition, ingredient availability and cost.
        - Processing and Marketing of Animal Products:
          • Pre‑slaughter, slaughter and post‑slaughter operations; hygienic handling of carcasses.
          • Processing of animal products and by‑products (meat, milk, eggs, skins, wool, honey, shells, feathers, hooves, horns, blood, dung) to add value.
          • Marketing channels and agents (producers, wholesalers, retailers, consumers); advantages and disadvantages of different channels.

        C. Pasture and Range Management
        - Pasture Management:
          • Meaning and importance of pasture and forage crops; types and characteristics of common pasture species.
          • Basic terminology in pasture management (stocking rate, grazing pressure, carrying capacity, etc.).
        - Rangeland Improvement:
          • Meaning and characteristics of rangelands.
          • Methods of rangeland improvement: reseeding, rotational/deferred grazing, controlled stocking, controlled burning, fertilizer use, pest and disease control.
          • Importance of rangelands for grazing, exercise, hay and silage production.

        D. Animal Improvement
        - Meaning and Aims of Animal Improvement:
          • Meaning of animal improvement and related terms.
          • Aims such as improved reproductive efficiency, mothering ability, temperament, libido, resistance to pests/diseases and tolerance of harsh environments.
        - Methods of Farm Animal Improvement:
          • Introduction, selection and breeding (inbreeding, cross‑breeding, line‑breeding) – merits and demerits.
        - Artificial Insemination (A.I.):
          • Meaning of A.I.; identification of basic materials and equipment; simple steps and precautions.
          • Advantages of A.I. in improving stock and disseminating superior genes.

        E. Animal Health
        - Farm Animal Diseases and Pathogens:
          • Meaning of disease; causal agents (bacteria, viruses, fungi, protozoa) and common examples.
          • General signs of ill‑health (loss of appetite, weight loss, diarrhoea, high temperature, blood‑stained urine or faeces, discharges, ruffled feathers/fur, standing hair, unusual noise, excessive salivation, anaemia, staggering, breathing difficulty, coma, sudden death).
          • Major diseases of farm animals, their causal agents, transmission, symptoms and simple preventive/control measures (including antibiotics and ethnoveterinary practices).
          • Factors that predispose animals to disease: poor nutrition, poor health status, poor sanitation, inadequate bio‑security, overcrowding, unfavourable weather, low immunity.
        - Livestock Parasites and Pests:
          • Meaning, classes and effects of parasites; life cycles, prevention and control.
          • Identification and economic importance of ectoparasites (ticks, lice, mites, fleas, etc.) and endoparasites (tapeworms, roundworms, hookworms, pinworms, liver flukes, trypanosomes, etc.).
          • Common livestock pests (rodents, snakes, soldier ants, birds, weevils, termites, flies, etc.), their effects on animals and stored products, and methods of control (including acaricides, dewormers, pesticides and ethnoveterinary remedies).

        Practical Animal Husbandry & Facilities (Paper 3 Focus):
        - Identification of animal products and by‑products (meat, eggs, milk, honey, hides, skins, hair, wool, feathers, horns, hooves, bones, shells, dung) and their uses.
        - Identification and description of external parts and major internal organs of selected farm animals; drawing and labelling of organs and systems.
        - Identification and use of common tools and equipment for housing, brooding, feeding, identification, debeaking, castration, handling, dehorning, incubation, milking, slaughtering and pasture management; simple maintenance of these tools.
        - Identification and uses of feed ingredients, crop residues, by‑products and non‑conventional feeds; basic knowledge of pasture and forage crops, hay and silage making.
        - Simple demonstration of artificial insemination tools and basic steps.
        - Identification of key pests and parasites (storage and field pests, ecto‑ and endoparasites) and understanding their economic importance and control.
        - Awareness of required farm structures, live animals, specimens, charts, equipment, drugs/vaccines and feed samples typically listed for school practical work.

        Guidelines for Candidates:
        - Treat the subject as both theoretical and practical: participate actively in farm work, keep detailed practical notebooks and relate concepts to observations.
        - Master key terms, diagrams and processes in livestock production, nutrition, health, improvement and pasture management.
        - Practise classification and identification of animals, organs, feeds, parasites, pests and pasture species, using examples from your local environment.
        - Work past WAEC Animal Husbandry (Alt B) questions for all three papers, paying attention to command words, calculations (rations, feed costs) and data interpretation.
        - Emphasize safe, hygienic and welfare‑friendly practices when handling animals, tools, chemicals and animal products, and appreciate animal husbandry as a viable small‑scale enterprise.`,
  },
  {
    subject: 'Applied Electricity or Basic Electricity',
    timeAlloted: 60 * 60,
    description: `Basic/Applied Electricity assesses candidates’ understanding of fundamental electrical and electronic principles, D.C. and A.C. circuit theory, machines, wiring and safety, as well as the maintenance and repair of domestic and industrial electrical equipment.

        Preamble & Objectives:
        - Derived from the Senior Secondary School Electricity curriculum; intended to support both academic and vocational pathways.
        - Tests grasp of basic concepts and laws of electricity and electronics.
        - Builds ability to use tools and instruments for installation, maintenance and repair.
        - Emphasizes understanding of the operation and applications of common electrical/electronic devices.
        - Promotes safe working practices in domestic and industrial electrical installations.

        Examination Scheme:
        - Paper 1: Objective
          • 50 multiple‑choice questions from the entire syllabus.
          • Duration: 1 hour for 50 marks.

        - Paper 2: Theory
          • Three sections (A, B, C).
          • Section A (Common to all candidates): 4 questions – answer any 3.
          • Section B (Ghana only): 3 questions – answer any 2.
          • Section C (Nigeria, Sierra Leone, The Gambia): 3 questions – answer any 2.
          • Total: 5 questions in 1 hour for 50 marks.

        - Paper 3: Practical
          • 2 compulsory experiments.
          • Duration: 3 hours for 100 marks.

        Section A – Common Core Topics:

        1. Direct‑Current (D.C.) Circuit Theory
        - Structure of Matter and Electric Current:
          • Qualitative treatment of atomic structure (electrons, protons, neutrons) in relation to current flow.
          • Conductors and insulators – definitions, examples and characteristics.
        - Resistors and Resistance:
          • Types of resistors, resistor colour code, power rating and practical identification.
          • Resistors in series and parallel – equivalent resistance and simple calculations.
        - Ohm’s Law and Kirchhoff’s Laws:
          • Relationship between voltage, current and resistance; V = IR.
          • Kirchhoff’s current and voltage laws applied to simple D.C. networks.
        - Power and Energy in D.C. Circuits:
          • P = VI, P = I²R, P = V²/R and E = Pt; basic calculations involving power and electrical energy.

        2. Magnetic Field, Electric Field & Electromagnetism
        - Fundamentals of Magnetism:
          • Types of magnets; magnetic materials; concepts of magnetic flux, flux density, permeability, magnetomotive force and reluctance.
        - Electric Field Concepts:
          • Electric flux, flux density, electric field strength, permittivity, dielectric constant and potential gradient.
          • Comparison between magnetic and electric circuits.
        - Capacitors:
          • Types and construction; capacitance and dielectric materials.
          • Charge/voltage relationship (Q = CV); energy stored in a capacitor E = ½QV = ½CV².
          • Series/parallel connection, voltage rating and simple applications (filtering, smoothing, timing, power factor correction).
        - Electromagnetic Field & Induction:
          • Magnetic field around a current‑carrying conductor; Fleming’s Right‑Hand Rule.
          • Force on a conductor in a magnetic field (F = BIL sinθ) – basic calculations.
          • Lenz’s and Faraday’s laws; induced e.m.f. in a moving conductor (E = BLV sinθ).
          • Self and mutual induction; energy stored in an inductor (E = ½LI²) – qualitative treatment.
          • Applications of electromagnetism in devices (electric bell, alarm systems, solenoids, loudspeakers, buzzers, moving‑coil instruments).

        3. Measuring Instruments
        - Moving‑Coil Instruments:
          • Construction, principle of operation, advantages and disadvantages.
          • Conversion to ammeter and voltmeter; calculation of shunts and multipliers.
        - Moving‑Iron Instruments:
          • Construction, principle of operation, advantages and disadvantages.
        - Digital Instruments:
          • Multimeters, digital voltmeters and ammeters – uses, advantages and limitations.

        4. Digital Electronics
        - Binary Number System:
          • Conversion between decimal and binary numbers and vice versa.
        - Logic Gates and Boolean Concepts:
          • Series connection of switches as AND function; parallel switches as OR; inverter as NOT.
          • Truth tables and standard symbols for AND, OR, NOT, NAND and NOR gates.
          • Simple Boolean expressions and logic combinations.

        5. Alternating Current (A.C.) Circuit Theory
        - Generation of A.C. and Waveforms:
          • E.m.f. induced in a single‑turn coil; plotting a labelled sinusoidal waveform for one cycle.
        - A.C. Quantities:
          • r.m.s., peak and average values; form factor, cycle, period and frequency.
        - RLC Circuits:
          • Behaviour of R, L and C in series circuits; impedance and phase relationships.
          • Series resonance – conditions and phasor diagram.

        6. Transformers
        - Types and Construction:
          • Shell and core types; current and voltage transformers; basic parts and functions.
        - Principle of Operation:
          • Transformation ratio, ideal transformer relationships and simple calculations.
        - Losses, Temperature Rise and Cooling:
          • Copper and iron losses, qualitative effect on temperature rise and efficiency.
          • Methods and need for cooling (oil, air, radiators, fans).

        7. Power Supply
        - Power Sources:
          • Dry cells, solar cells, nickel‑cadmium cells, accumulators.
        - Block Diagram of D.C. Power Supply:
          • Components: transformer, rectifier, filter and stabilizer; functions of each block.
        - Rectification and Smoothing:
          • Half‑wave and full‑wave rectification; filtration and voltage stabilization concepts.

        8. Electrical Machines
        - Single‑Phase A.C. Motors:
          • Types: split‑phase, capacitor‑start, capacitor‑run; main parts, principle of operation and applications.
        - Alternators:
          • Parts and principle of operation; relationship between speed, number of poles and frequency (f = PN/120).
        - Three‑Phase A.C. Motors:
          • Basic principles, parts, types and applications.
          • Starting methods: direct‑on‑line (DOL), star–delta and auto‑transformer starting.
        - D.C. Generators and Motors:
          • Construction, parts and methods of field winding connections.
          • Types of D.C. motors (series, shunt, compound) – principles and applications.

        9. Electrical Energy Supply & Wiring Practice
        - Generating Stations:
          • Methods of generating electrical power – diesel, steam, hydro‑electric, nuclear, gas turbine.
        - Electrical Installation & Wiring:
          • Types of wiring: surface, conduit, trunking, ducting.
          • Selection of materials, tools and accessories; application of basic IEE regulations to domestic installations.
          • Cables and accessories; current‑carrying capacity, lighting and socket circuits, plug connection.
        - Protection & Earthing:
          • Protective devices: fuses, circuit breakers and discrimination.
          • Reasons for earthing, methods of earthing and earth‑loop impedance.
        - Maintenance, Fault Diagnosis & Repairs:
          • Types of faults (short circuit, open circuit, earth leakage) and methods for diagnosing and repairing faults in common appliances (fluorescent fittings, irons, fans, blenders, kettles, etc.).
        - Testing of Installations & Safety:
          • Continuity, insulation resistance, earth leakage and polarity tests.
          • Application of electrical safety regulations and general workshop safety.

        Section B – Ghana Only (Outline):
        - Electron emission (thermionic, photo, secondary, field emission), thermionic valves and cathode ray tubes.
        - Semiconductor theory: intrinsic and extrinsic semiconductors; p‑type and n‑type materials.
        - Diodes and transistors: pn‑junction diodes, biasing and characteristics; bipolar transistor configurations (CE, CB, CC); JFET principles; other semiconductor devices (DIAC, TRIAC, SCR, LED, Zener, photo‑devices) and their applications.
        - Communication: electromagnetic waves, amplitude and frequency modulation, basic radio communication blocks.
        - Amplifiers and Operational Amplifiers: classes of operation (A, B, AB, C), efficiency, inverting and non‑inverting op‑amp circuits, and simple integrated circuits and uses.

        Section C – Nigeria, Sierra Leone & The Gambia (Outline):
        - Transmission of Electricity:
          • Layout of high‑voltage overhead transmission systems; main components (towers, insulators, conductors) and their functions.
          • Operating voltage levels (e.g. 132 kV, 330 kV) and reasons for high‑voltage transmission.
        - Distribution of Electricity:
          • Layout and components of distribution networks; functions of substations, transformers, feeders and distribution lines.
        - Maintenance & Repair of Electrical Appliances:
          • Types of maintenance (predictive, preventive, corrective).
          • Common faults (short circuit, open circuit, earth faults) in household appliances (blenders, irons, kettles, toasters, fluorescent lamps, etc.) and basic repair procedures.

        Guidelines for Candidates:
        - Strengthen basic Physics and Mathematics skills, especially in algebraic manipulation and simple circuit calculations.
        - Practise drawing and interpreting circuit diagrams, phasor diagrams and block diagrams of machines and power supplies.
        - Gain hands‑on familiarity with tools, measuring instruments and common appliances under supervision.
        - Pay close attention to safety rules in workshops, laboratories and during installations.
        - Work through past Basic/Applied Electricity WASSCE questions for all three papers to build speed, accuracy and confidence in both theory and practical work.`,
  },
  {
    subject: 'Auto Body Repairs and Spray Painting',
    timeAlloted: 60 * 60,
    description: `Auto Body Repairs and Spray Painting prepares candidates for practical work in vehicle body repair, refinishing and small workshop management, with strong emphasis on safety, correct use of tools and high‑quality finishes.

        Examination Scheme:
        - Paper 1: Objective
          • 40 multiple‑choice questions in 1 hour.
          • Covers safety, tools/equipment, materials, basic repair techniques and spray‑painting principles.

        - Paper 2: Essay
          • 5 structured/essay questions.
          • Candidates answer any 4 in 1½ hours for 60 marks.
          • Questions test understanding of theory, processes, fault causes, corrective methods and basic business issues.

        - Paper 3: Practical or Alternative to Practical
          • Practical: 1 compulsory job in 3 hours 10 minutes for 100 marks, based on real repair/spray‑painting tasks; list of materials is released to schools at least two weeks in advance.
          • Alternative to Practical: where materials cannot be procured, one theory‑based question in 3 hours assessing candidates’ grasp of practical skills and procedures.

        Major Syllabus Areas:

        1. Safety Rules and Regulations
        - Meaning and importance of safety rules in auto body repairs and spray painting.
        - General workshop rules and safe working procedures; how they prevent accidents and protect health, equipment and the environment.
        - Safety tools, equipment and machines for auto‑body work and how to use them correctly.
        - Personal protective equipment (PPE) and dress code: nose masks/respirators, goggles, safety boots, overalls, gloves, ear defenders, etc.
        - Sources of accidents (human factors, machine/equipment faults, poor housekeeping) and preventive measures.
        - Basic knowledge of relevant factory and environmental regulations (e.g. hazardous waste handling, solvent recovery, environmental protection agency rules) and correct identification of containers for hazardous waste.

        2. Tools, Equipment and Maintenance
        - Basic Hand Tools:
          • Types and uses of hammers, dollies, spanners, files, ratchets, body spoons and other panel beating tools.
        - Power Tools and Machines:
          • Drills, body jacks, sanders, air compressors, shaping and cutting machines – advantages, disadvantages and safety precautions.
        - Spray‑Painting Tools and Equipment:
          • Scrapers, brushes, funnels, paint pots, spray guns, filters, compressors, ovens and spray booths – identification and correct use.
        - Care and Maintenance:
          • Meaning and types of maintenance (predictive, preventive and corrective), manufacturer’s maintenance guides and schedules.
          • Cleaning, dismantling and assembling tools (e.g. spray guns), lubrication and safe storage.

        3. Materials for Auto‑Body Repairs and Spray Painting
        - Body Repair Materials:
          • Types of body fillers (plastic fillers, fibreglass fillers, body solder – 30/70 and 40/60); abrasives (wet/dry sandpapers, sanding discs); sealers (rubber/seam sealers, undercoating); rivets (pop and solid rivets).
        - Paints and Coatings:
          • Types of paints: enamels, lacquers, acrylics, metallic paints.
          • Undercoats: surface primers, putty/body filler, sealers.
          • Top coats: lacquers, enamel and other finishing coats.
        - Paint Selection and Mixing:
          • Manufacturer’s paint guides: paint formulae, mixing equipment, custom‑mix base colours, labels and colour charts.
          • Basic paint mixing techniques, identification of colours using colour charts and correct use of thinners.

        4. Metals, Properties and Heat Treatment
        - Types of Metals:
          • Ferrous and non‑ferrous metals used on auto bodies: mild steel, cast iron, aluminium, brass, etc.
        - Properties of Metals:
          • Fusibility, malleability, ductility, weldability and their relevance to body repair and forming.
        - Heat Treatment:
          • Meaning and main processes: hardening, annealing, normalising, case‑hardening and tempering.
          • Importance of heat treatment in improving properties, relieving stresses and preparing panels for repair.

        5. Oxy‑Acetylene Welding and Equipment
        - Definition and basic principles of oxy‑acetylene welding.
        - Safety precautions in gas welding operations.
        - Identification and functions of oxy‑acetylene welding equipment.
        - Types of gas systems (high‑ and low‑pressure), cylinders, regulators, hoses and nozzles.
        - Installation and assembly of welding equipment; lighting and shutting down procedures.
        - Common weld defects (slag inclusion, porosity, cracking, lack of penetration) and remedies.

        6. Auto‑Body Repair Work
        - Body Types:
          • Integral (monocoque/unibody) and composite body constructions; identification of major body sections (front engine compartment, passenger section, rear section).
        - Minor Repairs:
          • Basic methods of straightening panels: power jack alignment, pulling with sledge hammer or pull taps, use of pry bars and heat‑shrinking to restore original shape.
          • Tools for minor repair: hammers, hand dollies, spoons, spanners, screwdrivers, etc.
        - Major Repairs:
          • Basic alignment principles for damaged vehicles; quarter‑panel cutting and replacement; repair and alignment of lightweight body sections.

        7. Aluminium Panel Repairs
        - Steps involved in aluminium panel repair, including special precautions for softer, more ductile material.
        - Repair and alignment of damaged aluminium panels.
        - Aluminium deck‑lid (boot‑lid) repair procedures.

        8. Plastic Repairs
        - Precautions when working with plastics (heat sensitivity, fumes, correct tools and PPE).
        - Types of plastic resins: thermosetting and thermoplastic materials.
        - Repair of plastic parts by controlled melting and other simple techniques.
        - Introduction to glass‑fibre panel repair concepts.

        9. Spray Painting and Finishing
        - Spray Guns and Accessories:
          • Types of guns: pressure‑feed, siphon and gravity‑feed guns.
          • Parts of a spray gun (air cap, fluid needle, air valve, trigger, body, cup, etc.), their functions and maintenance.
          • Precautions in setting up and using spray guns to avoid defects and wastage.
        - Surface Preparation:
          • Cleaning, degreasing and abrading surfaces; rust and old‑paint removal; filling and sanding; masking areas not to be painted.
        - Paint Types and Codes:
          • Paints used on auto bodies (cellulose, synthetic enamel, acrylic, metallic, special effect paints).
          • Importance of manufacturer’s paint code and matching existing finishes.
        - Metallic Paints and Special Effects:
          • Special paints and variable agents (flakes, pigments) and their purposes.
          • Metallic spray techniques (wet and dry) and correct gun adjustment for even metallic distribution.

        10. Auto‑Body Workshop Business
        - Factors in Siting a Workshop:
          • Capital, accessibility, land, environmental considerations and proximity to customers and suppliers.
        - Basic Tools, Equipment and Facilities:
          • Essential items for starting and operating an auto‑body repair and spray‑painting shop.
        - Simple management ideas implied in the syllabus: record keeping, cost estimation and pricing, customer care and adherence to safety/environmental regulations.

        Guidelines for Candidates:
        - Take safety seriously: learn and observe all workshop rules, PPE requirements and hazardous‑waste procedures.
        - Become familiar with names, uses and maintenance of tools, machines and spray‑painting equipment through regular supervised practice.
        - Practise basic panel beating, alignment, filler application and surface preparation before attempting full spray jobs.
        - Study common paint and weld defects, their causes and remedies, so you can diagnose and correct problems on real jobs.
        - Use manufacturer’s manuals, paint guides and colour charts whenever possible and relate theory questions to real workshop situations.
        - Work past WAEC Auto Body Repairs and Spray Painting questions for all three papers to build speed, accuracy and confidence in both theory and practical components.`,
  },
  {
    subject: 'Auto Electrical Work',
    timeAlloted: 45 * 60,
    description: `Auto Electrical Work focuses on the electrical systems of motor vehicles, testing candidates’ understanding of core concepts and their ability to maintain, diagnose and repair battery, starting, ignition, charging, lighting and auxiliary systems safely and efficiently.

        Preamble & Objectives:
        - Derived from the Senior Secondary School Trade Curriculum in Auto Electrical Work; the syllabus is organized around six major themes: battery, starting, ignition, charging, lighting and auxiliary systems.
        - Assesses candidates’ ability to:
          • Understand key concepts and principles that govern automotive electrical systems.
          • Use appropriate tools, equipment and test instruments to carry out inspection, maintenance and repair operations on motor-vehicle electrical systems.
          • Apply and observe safety practices in the workshop and on vehicles to protect people, equipment and the environment.

        Examination Scheme:
        - Paper 1: Objective
          • 40 multiple-choice questions covering the whole syllabus.
          • Duration: 45 minutes for 40 marks.

        - Paper 2: Short-Structured / Theory
          • 6 short-structured questions; candidates answer any 4.
          • Duration: 1 hour for 60 marks.

        - Paper 3: Practical / Trade Test
          • 3 skill-based practical questions; candidates carry out any 2 in 2 hours for 90 marks.
          • A list of materials required for the test is sent to schools not less than two weeks before the examination.

        - Alternative to Practical (where facilities are inadequate):
          • 2 compulsory essay questions testing candidates’ knowledge of practical skills and procedures.
          • Duration: 2 hours for 100 marks.

        - Industrial Attachment:
          • Candidates are expected to undergo supervised industrial attachment during the long vacation between SS II and SS III, assessed by subject teachers and contributing 10 marks to continuous assessment.

        Major Syllabus Areas:

        1. Battery
        - Concept and Uses of Battery:
          • Meaning of battery; distinction between motor-vehicle battery and other types of batteries.
          • Uses of vehicle batteries as power source in starting, ignition, lighting and for ancillary work (e.g. soldering).
        - Types, Construction and Ratings:
          • Lead–acid and nickel–alkaline batteries; constructional details of a typical motor battery.
          • Battery ratings (voltage, ampere-hour capacity, cold-cranking amperes) and their applications.
        - Charging:
          • Safety rules in battery charging; preparation of electrolyte; correct cleaning and connection of terminals.
          • Charging modes, state of charge and precautions against over-charging.
        - Testing and Maintenance:
          • Testing electrolyte with hydrometer and interpreting specific gravity readings.
          • Cell voltage and polarity tests; use of appropriate tools and equipment.
          • Routine maintenance: electrolyte topping-up, terminal cleaning, recognition of over-charging symptoms and safe-keeping of idle batteries.

        2. Starting System
        - Purpose and Components:
          • Function of the starting system in cranking the engine.
          • Main components: battery, ignition switch, starter switch, solenoid, starter motor and flywheel.
        - Circuit Diagram:
          • Reading and drawing of starting-system circuit diagrams using conventional symbols.
          • Location and sequential arrangement of components in the vehicle.
        - Types of Starter Motor:
          • Axial and inertia type starter motors; pinion engagement mechanisms.
        - Repair and Servicing:
          • Dismantling and assembling starter motors; replacement of bushes and brushes.
          • Commutator soldering/repair and armature servicing.
          • Systematic troubleshooting and rectification of common starting faults.

        3. Ignition System
        - Purpose and Components:
          • Role of the ignition system in producing and distributing high-voltage sparks for combustion.
          • Components: ignition switch, battery, ignition coil, distributor, capacitor/ condenser, high-tension leads and spark plugs.
        - Circuit Diagram and Symbols:
          • Drawing and interpretation of ignition-system line diagrams with correct conventional symbols.
        - Ignition Coil:
          • Internal construction and operation of the ignition coil.
        - Types of Ignition System:
          • Conventional contact-breaker ignition and electronic ignition systems – basic features and advantages.
        - Timing and Faults:
          • Concept of ignition timing; timing faults such as retarded and over-advanced ignition.
          • Symptoms of ignition faults including hard starting, jerking, misfiring and backfiring.
          • Use of multimeter, scanner and test lamp in diagnosing ignition problems.

        4. Charging System
        - Purpose and Components:
          • Function of the charging system in restoring battery charge and supplying electrical loads.
          • Components: alternator, voltage regulator, battery, switch and connecting cables.
        - Alternator Construction and Operation:
          • Constructional details of an alternator; conversion of a.c. to d.c. (rectification).
          • Functions of main alternator parts and simple performance checks.
        - Circuit Diagram and Installation:
          • Drawing and reading of charging-system circuit diagrams (graphical and pictorial representation).
          • Removal and refitting of charging-system units following safe procedures.
        - Faults and Repairs:
          • Common faults such as bearing seizure, noisy operation and charging failure.
          • Brush and bearing replacement; diode testing, repair and replacement.

        5. Lighting System
        - Purpose and Classification:
          • Role of the lighting system in road safety and vehicle control.
          • Obligatory and non‑obligatory lights on a motor vehicle and their functions.
        - Headlamps:
          • Types and features of headlamps; correct setting and aiming of headlamps.
        - Circuit Diagrams and Troubleshooting:
          • Classification, drawing and reading of lighting-system circuit diagrams.
          • Diagnosis of faults such as bulb failure, non‑aligned headlamps, broken lenses, open and short circuits and their rectification.
        - Maintenance and Repairs:
          • Tools, equipment and procedures for inspection, maintenance and repair of lighting circuits and components.

        6. Auxiliary System
        - Concept and Components:
          • Meaning and importance of auxiliary systems in vehicle operation and driver comfort.
          • Components such as water temperature gauge, oil pressure gauge, fuel gauge, horn relay, wiper switch, screen washer pump, indicators and door switches.
        - Construction and Operation:
          • Constructional details and working principles of selected auxiliary-system components.
        - Maintenance and Repairs:
          • Use of appropriate tools and equipment for troubleshooting and repairing faults like horn failure, wiper malfunction and gauge inaccuracies.

        Facilities, Tools and Equipment (Typical Requirements):
        - Motor batteries and battery head moulder sets; complete auto-electrical tool boxes.
        - Personal protective items such as polythene gloves, goggles and aprons.
        - Electrical measuring instruments: ammeter, voltmeter, multimeter and high-rate discharge tester.
        - General workshop tools and equipment: spanners, hand drill, vice, bench/table, wire brush, bearing and pulley extractors, feeler gauge, soldering iron and solder/lead, emery cloth, wooden file, jumper cables, magnetic pick‑up, plastic trays.
        - Diagnostic and servicing equipment: hydrometer, screwdriver-type tester, battery charger, test lamp, cable stripper and insulation tape.

        Guidelines for Candidates:
        - Observe workshop and vehicle safety rules at all times, especially when handling batteries, rotating machinery, hot components and live circuits.
        - Practise reading and drawing circuit diagrams, identifying components on actual vehicles and relating theory to real systems.
        - Acquire confidence in using test instruments (multimeter, hydrometer, test lamps, scanners) for systematic fault diagnosis.
        - Participate actively in practical work and industrial attachment to develop hands-on competence in maintenance and repair operations.
        - Use past WAEC Auto Electrical Work questions to build familiarity with the style of objective, structured and practical questions and to improve speed and accuracy in the examination.`,
  },
  {
    subject: 'Auto Mechanical Work',
    timeAlloted: 45 * 60,
     description: `Auto Mechanical Work focuses on the construction, operation, maintenance and repair of the major mechanical systems of motor vehicles – engine, transmission, suspension, steering, lubrication, cooling, fuel and braking – as well as basic workshop management and enterprise.

        Examination Scheme:
        - Paper 1: Objective
         • 40 multiple-choice questions on the whole syllabus.
         • Duration: 45 minutes for 40 marks.

        - Paper 2: Theory (Sections A & B – Composite Paper)
         • Total duration: 2 hours 15 minutes for 80 marks.
         • Section A: 10 short-structured questions to be answered in 30 minutes for 20 marks.
         • Section B: 5 essay questions; candidates answer any 4 in 1 hour 45 minutes for 60 marks.

        - Paper 3: Practical / Alternative to Practical
         • Practical Test: 2 compulsory questions in 2 hours 30 minutes for 80 marks. A list of materials is released to schools at least two weeks before the paper to aid preparation and procurement.
         • Alternative to Practical: where practical facilities or materials are not available, candidates answer 2 compulsory theory-based questions in 1½ hours for 80 marks, testing their understanding of prescribed practical skills and procedures.

        Major Syllabus Areas:
        1. Workshop Safety and Regulations
          - Meaning of safety and regulation in the auto-mechanics workshop.
          - Causes of workshop accidents and common unsafe practices.
          - Accident-prevention techniques and the use of safety devices such as sand buckets, fire extinguishers and other fire-fighting equipment.

        2. Tools and Equipment
          - Types and uses of hand tools for marking, measuring, holding and cutting.
          - Types and uses of basic workshop equipment (e.g. hand valve grinding tool, grinding machine, etc.).
          - Care and maintenance of tools and equipment for efficiency and long service life.

        3. Vehicle Layout
          - Types of motor-vehicle chassis and basic chassis layouts.
          - Identification, location and functions of principal components such as engine, transmission system and chassis members.
          - Simple chassis inspection and routine maintenance.

        4. Automobile Engine
          - Types of engine design (e.g. in-line, vee, opposed).
          - Identification of main engine components and simple line diagram of a multi-cylinder engine.
          - Types of cylinder liners and their applications.
          - Two-stroke and four-stroke cycles in spark-ignition (petrol) and compression-ignition (diesel) engines.
          - Advantages of the four-stroke cycle over the two-stroke cycle.
          - Basic engine servicing and maintenance procedures.

        5. Transmission System
          - Transmission system layout and conventional arrangement from engine to driving wheels.
          - Types of drives and components of the transmission system (e.g. clutch, gearbox, propeller shaft, differential, final drive).
          - Operation of a simple single-plate clutch.
          - Types and operation of gearboxes; introduction to automatic transmission systems.

        6. Suspension System
          - Types and functions of suspension systems.
          - Common suspension faults and their effects on vehicle handling and comfort.
          - Basic fault-finding, maintenance and repair of suspension components.

        7. Steering System
          - Components and functions of the steering system.
          - Types of steering gearbox.
          - Manual steering faults and simple repairs.
          - Principle and operation of power-assisted steering.
          - Basic steering geometry concepts and their effect on tyre wear and vehicle stability.

        8. Engine Lubrication System
          - Purpose and types of engine lubrication systems.
          - Concept of friction and its disadvantages in moving parts.
          - Component parts of a typical lubrication system (pump, filter, galleries, sump, etc.).
          - Quality and viscosity of lubricants, oil additives and their importance.

        9. Cooling System
          - Functions and types of engine cooling systems.
          - Components of water and air cooling systems.
          - Comparison of air and water cooling systems and simple maintenance operations.

        10. Fuels and Combustion
          - General layout and working principle of the fuel supply system.
          - Fuel system component parts and functions (carburettors, pumps, filters, tanks and lines) and their maintenance.
          - Types and properties of fuels used in motor vehicles.
          - Comparison of mechanical and electrical fuel pumps.
          - Types, functions and maintenance of manifold, air cleaner and muffler.

        11. Braking System
          - Concept of friction, operating principles and types of braking systems.
          - Layout of the braking system.
          - Faults and maintenance of mechanical and hydraulically operated braking systems.
          - Functions, advantages and disadvantages of anti-lock braking systems (ABS).

        12. Wheels and Tyres
          - Types and functions of rims and tyres.
          - Simple vulcanizing and tyre repair.
          - Regulations and good practice for tyre interchanging and correct inflation pressure.
          - Basic road-wheel alignment and balancing.

        13. Workshop Management and Enterprise
          - Basic concepts of management, planning, controlling, staffing and directing within an auto-mechanics workshop.
          - Managing workshop resources (labour, tools, parts, time and finance).
          - Concepts of authority and responsibility in an enterprise.
          - Types of enterprise in the automobile sector and the advantages and disadvantages of each in relation to self-employment.

        Facilities, Tools and Equipment (Typical Requirements):
        - Tool boxes with locks containing hammers, hacksaws with extra blades, comprehensive socket and spanner sets, emery cloth, plug spanners, Allen keys, feeler gauges, oil cans, grease guns, pliers and related hand tools.
        - Measuring tools such as tyre pressure gauges, vernier calipers, surface gauges, surface plates, vee blocks, micrometer screw gauges and dial gauge indicators with magnetic stands.
        - Machine tools including grinding machines, bench grinders and valve grinding machines.
        - Lubrication bay and tyre service equipment: compressor with spray gun and grease hose, wheel balancing machine, portable tyre inflator, vulcanizing machine, tyre changer and tyre repair kits.
        - General servicing and reconditioning equipment: jacks and axle stands, engine hoist, work benches with vices, timing light, compression gauges, valve and coil spring compressors, torque wrenches, piston ring compressors and diagnostic test equipment.
        - Other utilities: fire extinguishers, sand and water buckets, complete engine, gearbox and final drive units, used vehicle tyres and complete vehicle/engine charts for demonstration.

        Guidelines for Candidates:
        - Take workshop safety seriously, observing all rules and using personal protective equipment when working with tools, machines, hot components and vehicles.
        - Study the construction, operation and maintenance of each vehicle system (engine, transmission, suspension, steering, lubrication, cooling, fuel and braking) and relate diagrams to real components on vehicles.
        - Practise systematic fault-finding and simple repairs using appropriate tools and sequences.
        - Engage fully in practical work to develop good hand skills, accuracy, neatness and confidence.
        - Use past WAEC Auto Mechanical Work question papers for objective, short-structured, essay and practical components to build speed, accuracy and familiarity with examination demands.`,
  },
  {
    subject: 'Auto Mechanics',
    timeAlloted: 60 * 60,
    description: `Auto Mechanics covers the layout, operation, servicing and repair of the main systems of a motor vehicle – engine, fuel and exhaust, lubrication, cooling, transmission, wheels and tyres, braking, steering, suspension and electrical/electronic systems – together with safe workshop practice and basic motoring safety.

      Examination Scheme:
      - Paper 1: Objective
        • 40 multiple-choice questions drawn from the entire syllabus.
        • Duration: 1 hour for 40 marks.

      - Paper 2: Essay / Theory
        • 5 structured/essay questions.
        • Candidates answer any 4 in 1½ hours for 60 marks.

      - Paper 3: Practical
        • 2 practical tests, both of which must be carried out in 2 hours for 100 marks.
        • Schools provide materials locally; tasks involve measurement, inspection, servicing, adjustments and simple fault diagnosis on real components and systems.

      Major Syllabus Areas (Concept + Practice):
      1. Workshop Regulations and Safety
         - Basic safety rules for personnel, tools, equipment and the work environment.
         - Types and correct use of fire extinguishers (foam, dry powder, sand, water, wet blanket).
         - Identification and use of personal protective equipment (aprons, goggles, shields, gloves, safety boots).

      2. Basic Tools, Measuring Instruments and Equipment
         - Types, uses and care of hand tools and simple power tools used in auto workshops.
         - Use of common measuring instruments for lengths, clearances and wear.
         - Identification and safe use of basic workshop equipment such as jacks, hoists and air compressors.

      3. Layout of a Motor Vehicle
         - General layout of a conventional motor vehicle and function of main components.
         - Drive arrangements: front‑engine/rear‑wheel drive, rear‑engine/rear‑wheel drive, front‑engine/front‑wheel drive and four‑wheel drive.
         - Practical inspection of vehicle layout and identification of main units.

      4. Engine
         - Classification of engines into petrol (spark‑ignition) and diesel (compression‑ignition) and their main parts.
         - Arrangement and functions of major components: cylinder head and cover, cylinder block, crankshaft, flywheel, connecting rod, piston and rings, spark plug, fuel‑injection pump and injectors, valves and valve springs, oil seals, camshaft, push rods, rocker shaft and arms.
         - Two‑stroke and four‑stroke cycles in petrol and diesel engines; advantages and disadvantages of each fuel/engine type.
         - Single‑ and multi‑cylinder engines; crankshaft arrangements and firing orders for in‑line, vee and horizontally‑opposed engines.
         - Valve‑operating mechanisms, drives, valve timing and calculation of opening/closing periods.
         - Practical tasks: identification of components, decarbonisation of cylinder head, compression tests, bore and journal wear checks, valve adjustment and fault diagnosis.

      5. Fuel Supply Systems
         - Basic combustion concepts: elements of combustion, air–fuel ratios, normal and abnormal combustion.
         - Fuels and their properties (petrol and diesel) and suitability for different engines.
         - Petrol supply systems: layout and operation of gravity and force‑feed systems, simple and multi‑jet carburettors, air cleaners, mechanical and electrical fuel pumps – their merits, demerits and servicing.
         - Petrol‑injection systems including Electronic Fuel Injection (EFI): basic idea, advantages and limitations.
         - Diesel supply system: layout, elementary treatment of injection pumps and injectors, cold‑starting devices.
         - Practical work: inspection and dismantling/reassembly of pumps and carburettors, servicing of injectors, bleeding diesel systems and basic fuel‑system fault diagnosis.

      6. Exhaust System
         - Purpose and layout of the exhaust system; types of silencers and manifolds.
         - Practical inspection of exhaust and inlet manifolds, checking for leaks and obvious faults.

      7. Lubrication
         - Reasons for lubrication and types (boundary and film lubrication); major lubricated components.
         - Types of feed: splash, forced and petroil; wet‑ and dry‑sump systems.
         - Principle of operation of gear and rotor‑type oil pumps and the role of oil filters.
         - Lubricants used in engines, transmission, steering, suspension and doors; viscosity rating and SAE numbering.
         - Practical activities: changing oil and filters, servicing and testing pumps, comparing fresh and used oil, identifying lubricants, and using grease guns and oil cans; simple fault diagnosis.

      8. Cooling System
         - Basic heat transfer concepts relevant to engine cooling.
         - Water‑cooling systems: purpose, layout and functions of main components; thermo‑syphon and pump‑assisted systems; elementary pressurised cooling and thermostats (bellows and wax‑pellet types).
         - Air‑cooling systems: layout, components and comparison of air‑ and water‑cooled engines.
         - Practical work: identification and inspection of radiators and hoses, fan‑belt replacement, system flushing and fault diagnosis.

      9. Transmission System
         - Function and layout of the transmission system; manual vs automatic transmissions and their merits and demerits.
         - Clutch: functions, types and operating principles of single‑ and multi‑plate clutches; hydraulic and mechanical actuation; simple related calculations.
         - Introduction to automatic transmission, torque converter and fluid flywheel.
         - Gearboxes: types, layouts and operating principles of sliding‑mesh, constant‑mesh and synchromesh gearboxes; gear selectors and simple gear‑ratio calculations.
         - Propeller shaft and universal joint: functions, layout and types, including sliding joints.
         - Rear axle: purpose, arrangement and functions of main components – final drive, differential, half‑shafts, oil seals and hub bearings; methods of supporting axle shafts and their pros and cons.
         - Practical work: stripping and assembling a single‑plate clutch, adjusting/bleeding clutch units, inspecting gearbox teeth and bearings, checking propeller‑shaft alignment and universal‑joint wear, identifying rear‑axle components and diagnosing common faults.

      10. Wheels and Tyres
        - Types and construction of wheel rims (pressed steel, disc and wire‑spoke) and hub attachments.
        - Tyre types (tubed and tubeless), advantages and disadvantages, tyre sizes and markings.
        - Wheel balancing and correct wheel‑bearing adjustment.
        - Practical tasks: checking and adjusting wheel‑bearing clearance, removing and refitting road wheels, tyre fitting and inflation, tube and tyre patching, and wheel balancing.

      11. Braking System
        - Layout, functions and operation of braking systems: drum and disc brakes; mechanical and hydraulic actuation.
        - Brake linings, materials and attachment methods; servo‑assisted brakes; advantages and disadvantages of disc vs drum brakes.
        - Common brake faults and their symptoms.
        - Practical work: inspection of brake types, pad and shoe replacement, bleeding and adjustment, simple brake‑efficiency checks and fault diagnosis.

      12. Steering System
        - General layout and functions of the front axle and steering systems.
        - Steering geometry: Ackermann linkage, castor, camber, king‑pin inclination, toe‑in and toe‑out.
        - Types of steering gearboxes (rack‑and‑pinion and recirculating‑ball) and their uses.
        - Steering faults and their effects on tyre wear and vehicle control.
        - Practical activities: identification of components for different layouts, front‑wheel alignment, inspection of tyre‑wear patterns and basic steering‑fault diagnosis.

      13. Suspension System
        - Purpose, layout and types of suspension: rigid‑beam and independent systems.
        - Springs (semi‑elliptic leaf springs and coil springs) – advantages and disadvantages.
        - Dampers (shock absorbers) and their role in ride comfort and stability.
        - Practical work: identifying differences between rigid‑beam and independent suspension arrangements and diagnosing simple suspension faults.

      14. Electrical and Electronic Systems
        - Electrical fundamentals: A.C. and D.C. sources, simple circuits, Ohm’s law, series and parallel arrangements; basic components (relays, resistors, lamps, fuses, switches) and their functions.
        - Auto‑wiring systems: wire gauges and colour coding; earth and insulated‑return systems; jointing techniques, terminals, connectors and soldering.
        - Battery: purpose, construction and testing of lead–acid batteries; electrolyte composition; battery care and maintenance.
        - Ignition: layout and operation of the coil‑ignition system, main components and ignition timing; introduction to computerised/electronic ignition.
        - Starting system: layout, functions and main components; types of starter motor.
        - Charging system: purpose and layout of dynamo and alternator systems; main components and their operation; simple comparison of d.c. and a.c. generators.
        - Lighting system: layout, components and functions; types and ratings of fuses and bulbs; headlamp focusing.
        - Auxiliary units: instrument panel, horn, wiper and other basic accessories – layout, operation and testing.
        - Electrical faults and systematic diagnosis.
        - Practical work: setting up simple circuits and using meters, simple soldering and cable joining, battery examination and electrolyte preparation, timing and plug‑gap setting, inspection and testing of starting/charging/lighting components, bulb and fuse replacement and basic fault‑finding.

      15. Basic Auto Electronics and Electronic Fuel Injection
        - Idea of auto‑electronics and common electronic components (diodes, transistors, resistors, capacitors, LEDs, sensors/transducers, coils and motors) and their symbols and functions in simple circuits.
        - Transistorised and electronic ignition systems: basic operation, inductive and Hall‑effect triggering, merits and demerits.
        - Electronic fuel‑injection systems: purpose and types (single‑point and multi‑point injection) and identification of key components.

      16. Auto Air‑Conditioning
        - Purpose and basic layout of vehicle air‑conditioning systems.
        - Identification and functions of major components (compressor, condenser, evaporator, receiver‑dryer) and simple operating principles.
        - Practical inspection and identification of A/C components and pipe runs.

      17. Safe Motoring
        - Main causes of road accidents and essential principles of safe driving.
        - Application of the highway code, safety devices and vehicle inspection for roadworthiness.
        - Identification of mechanical faults and defective components that could cause accidents; interpretation of common road signs.

      Guidelines for Candidates:
      - Combine strong workshop safety habits with accurate use of tools, instruments and equipment.
      - Study each vehicle system (engine, fuel and exhaust, lubrication, cooling, transmission, braking, steering, suspension and electrics/electronics) as a complete unit, linking diagrams to real components on vehicles.
      - Practise routine servicing operations, adjustments and fault diagnosis systematically during practical sessions.
      - Make good use of recommended Auto Mechanics textbooks and the highway code, and work many past WAEC Auto Mechanics questions across all three papers to build speed, accuracy and confidence.`,
  },
  {
    subject: 'Auto Parts Merchandising',
    timeAlloted: 50 * 60,
     description: `Auto Parts Merchandising is a trade subject that combines basic technical knowledge of motor-vehicle parts, tools and test equipment with the commercial skills needed to store, display, price, sell and support auto parts professionally.

        Examination Scheme:
        - Paper 1: Objective
         • 40 multiple-choice questions drawn from the whole syllabus.
         • Duration: 50 minutes for 40 marks.

        - Paper 2: Essay / Structured (Composite – Sections A & B)
         • Total duration: 2 hours for 100 marks.
         • Section A (Technical Section): 3 questions on the technical part of the syllabus, including 1 compulsory; candidates answer 2 questions (the compulsory plus 1 other) for 40 marks.
         • Section B (Merchandising Section): 4 questions on the merchandising part; candidates answer any 3 for 60 marks.

        Technical Section (Automobile Parts, Tools, Test Equipment and Accessories):
        1. Safety in the Auto Parts Merchandising Shop
          - Meaning of safety and the importance of safe working habits in a parts shop.
          - Safety clothing and personal protective equipment: helmet, goggles, gloves, nose mask, safety boots and overalls.
          - Types of fire extinguisher (foam, dry powder, sand, water, wet blanket) and where they are used.
          - Common causes of accidents in an auto parts store/workshop and basic prevention measures.

        2. Engine Parts
          - Identification of major engine parts commonly sold as spares: pistons, piston rings, connecting rods, camshaft, crankshaft, valves, cylinder head and oil pump.
          - Sketching, simple location and functions of the above parts.
          - Understanding how correct parts choice affects engine performance and reliability.

        3. Transmission System – Clutch
          - Identification of clutch parts: flywheel, clutch plate, pressure-plate assembly and release bearing.
          - Functions of each clutch component and simple symptoms of wear/damage.

        4. Transmission System – Gearbox
          - Types of gearbox: manual (3-, 4- and 5-speed) and automatic transmission.
          - Functions of the gearbox in speed/torque variation and reversing.

        5. Transmission System – Propeller Shaft and Rear Axle Assembly
          - Types of propeller shaft and universal/sliding joints.
          - Functions of main transmission components.
          - Identification of basic rear-axle parts and their roles.

        6. Suspension System
          - Suspension types: normal beam (rigid) and independent suspension.
          - Parts of the suspension system – dampers (shock absorbers), springs (coil, torsion bar, leaf springs).
          - Basic functions of suspension parts in ride comfort, stability and tyre life.

        7. Cooling System
          - Types of cooling system: water cooling and air cooling.
          - Functions of the cooling system in heat removal and engine protection.
          - Main parts of water and air cooling systems: radiator, fan blade, fan belt, water pump, hoses and thermostat.

        8. Steering System
          - Types of steering: manual and power-assisted.
          - Parts of a typical steering system: steering gearbox, steering wheel, steering shaft and tie rods.
          - Functions of steering components and their relevance to safety.

        9. Braking System
          - Types of braking system: disc and drum.
          - Main parts: master cylinder, wheel cylinders, connecting pipes, brake pads and brake shoes/linings.
          - Basic functions of each part and the importance of genuine brake components.

        10. Lubrication System
          - Main parts: oil tank/sump, oil filter, oil cooler, oil seals, pressure switch and oil pump (types).
          - Types of lubricant: oil and grease; common oil grades (e.g. SAE 40, SAE 60, SAE 30/20W).
          - Quality, correct application and uses of lubricants and greases in different vehicle systems.

        11. Electrical Parts
          - Common electrical components: battery, fuse, electronic control unit/brain box, alternator, ignition coil, bulbs, cut-out/regulator.
          - Identification and basic functions of each component.

        12. Wheels and Tyres
          - Types of wheels: spoke wheel, alloy wheel and pressed-steel wheel.
          - Types of tyres: tube and tubeless.
          - Basic wheel and tyre size markings and their meanings.
          - Functions of wheels and tyres and implications for safety and performance.

        13. Tools, Test Equipment and Accessories
          - Types of tools and their uses:
            • General tools – spanners, hammers, mallets, Allen keys, screwdrivers, pliers.
            • Special tools – extractors, pullers, sledge hammers, lifters.
            • Distinction between general and special tools.
          - Care of tools: greasing, cleaning, correct packing and storage; effects of poor tool care.
          - Test equipment: spark‑plug cleaner, engine analyser, dwell‑angle tester, exhaust‑gas analyser, vacuum tester – types and basic functions.
          - Maintenance of test equipment: correct starting/stopping procedures and use of manufacturers’ manuals.
          - Accessories: meaning, importance, care and examples (steering covers, car radios, wheel covers, seat covers, car covers).

        Merchandising Section (Storage, Cataloguing, Sales and Business Skills):
        1. Storage, Cataloguing and Visual Presentation
          - Storage: meaning; processes and methods of storing parts; reasons for proper storage (protection from damage, theft, confusion and loss of value).
          - Cataloguing: meaning, functions and use of catalogues to locate auto parts (by make, model, part number, illustration).
          - Re-order quantity and stock level: meaning and importance; procedures for re-ordering parts and maintaining stock levels.
          - Simple stock-control calculations, including re-order level and basic FIFO/LIFO ideas.
          - Visual presentation: methods of grouping and arranging parts (by brand, manufacturer, vehicle model); advantages for customers and staff.
          - Inventory: meaning, types (daily, periodic, annual) and procedures (use of bin cards and records).
          - Product sourcing: meaning, ways of sourcing products and reasons for effective sourcing.

        2. Sales, Invoicing, Forecasting, Record Keeping, Fund Raising, Market Analysis, Value Pricing, Advertising and Customer Service
          - Sales: meaning; types (cash and credit/deferred sales); methods of selling (personal selling, door‑to‑door, trade fairs, open market, unit shops); steps in sales calls.
          - Invoicing: meaning, typical contents of an invoice and its uses/importance.
          - Forecasting: meaning, reasons/need for forecasting and advantages for stock planning and profitability.
          - Record keeping: types of basic records (sales/purchases journals, ledgers, invoices, receipts, cash book), processes for keeping them and their uses in control and decision-making.
          - Fund raising (sources of finance): meaning; types and sources of funds for starting or expanding an auto parts business; simple procedures for raising funds.
          - Market analysis: meaning of market, market analysis and market segment; concept and bases of market segmentation and how to apply them in auto parts business.
          - Value pricing: meaning, types and practical application when pricing parts competitively yet profitably.
          - Advertising: meaning; types and media (print, radio, TV, online, outdoor, point‑of‑sale); advantages for promoting an auto parts business.
          - Customer service: meaning, types and advantages; handling customer complaints and building long-term relationships.

        3. Warranty/Guaranty, Regulatory Bodies and Entrepreneurship
          - Warranty/guaranty: meaning and importance; procedures for claiming warranty or guaranty rights; ways in which warranty rights can be lost (misuse, poor maintenance, unauthorised repairs, wrong installation).
          - Regulatory bodies and quality control: reasons for regulation; meaning and types of quality control; ways to improve quality; examples of regulatory bodies (e.g. SON, Consumer Protection Council/Board, Society of Automotive Engineers – SAE) and their functions.
          - Introduction to auto parts merchandising as a business: meaning of merchandising and auto parts merchandising; personal qualities of a successful merchandiser; ethical business behaviour and expected conduct of an auto parts seller (honesty, accuracy, fairness, respect for customers and compliance with regulations).

        Guidelines for Candidates:
        - Build solid familiarity with the names, appearances, functions and basic specifications of common vehicle parts, tools, test equipment and accessories.
        - Understand how storage, cataloguing, stock control and visual presentation make a parts business efficient and customer‑friendly.
        - Practise interpreting catalogues, invoices and simple records and performing basic stock and pricing calculations.
        - Study sales, customer service, warranty and regulatory concepts as they apply specifically to auto parts.
        - Use past WAEC Auto Parts Merchandising questions to become comfortable with both the technical and business sides of the subject and to improve speed and accuracy in the examination.`,
  },
  {
    subject: 'Basketry',
    timeAlloted: 50 * 60,
     description: `Basketry is a practical art and trade subject that develops candidates’ creative and manipulative skills in designing, producing, decorating and finishing baskets and related woven, coiled and knotted articles for cultural, functional, aesthetic and entrepreneurial purposes.

        Aims & Objectives:
        - Give candidates an understanding of the historical development and socio‑cultural and economic importance of basketry as an art and vocation.
        - Build creative thinking and practical skills in designing and producing a wide range of basketry forms to meet identified needs in society.
        - Develop competence in the making, decoration and finishing of basketry articles using appropriate tools, materials and techniques.
        - Equip candidates with basic entrepreneurial skills for planning, costing, pricing and running a small‑scale basketry enterprise.
        - Foster appreciation and evaluation of basketry works based on agreed criteria (materials, design, technique, finish, function and aesthetics).

        Examination Scheme:
        - Paper 1: Objective
         • 40 multiple‑choice questions on the entire syllabus.
         • Duration: 50 minutes for 40 marks.

        - Paper 2: Essay / Theory
         • 6 essay‑type questions.
         • Candidates answer 4 in 2 hours for 60 marks.

        - Paper 3: Practical Projects
         • 2 project options are set; candidates execute 1 project over 5 days, working 6 hours per day.
         • The practical execution carries 80 marks.
         • Question papers are released two weeks before the execution period for candidates to study, develop sketches and preparatory notes; these preparatory works carry 20 marks.
         • Total for Paper 3: 100 marks.

        Major Syllabus Themes:
        1. Meaning, Scope and Rationale of Basketry
          - Meaning and scope: coiled work, woven articles, frameworks, macramé and related crafts.
          - Types of articles: baskets, hats, mats, nets, couches, footwear, cots, rattles and similar items.
          - Basic characteristics of basketry tools and materials.
          - Creative combination of basketry with other materials (cowries, gourds, beads, etc.) to produce decorative and functional objects.
          - Rationale for studying basketry: creative use of natural and synthetic materials, skills acquisition for employment/further studies and contribution to tourism.

        2. Historical, Social, Cultural and Economic Importance
          - Origin and historical development of basketry in different cultures.
          - Social uses (e.g. entertainment, ceremonies, storage, everyday life).
          - Cultural significance: symbolism, identity and traditional practices.
          - Economic importance in agriculture, commerce, tourism and home‑based enterprises.

        3. Places, People and Basketry
          - Contributions of notable places and practitioners to the development of basketry.
          - Regional and local basketry traditions and specialities.

        4. Competencies, Attitudes and Communication Skills
          - Knowledge, creative thinking and practical skills (weaving, knotting, designing, finishing) required for employment.
          - Positive attitudes and values: tolerance, diligence, honesty, commitment, time‑management and effective communication.

        5. Materials: Types, Characteristics, Preparation and Care
          - Types and characteristics of materials: natural vs synthetic; flexible/pliable vs stiff/rigid.
          - Discovering material qualities and matching them to suitable articles and techniques.
          - Preparation techniques: drying, wetting/soaking, splitting, trimming and straightening.
          - Storage and care of materials using chemical and non‑chemical methods.
          - Environmental concerns and sustainable sourcing: preserving sources of materials, controlling bush burning, re‑afforestation and simple waste management.

        6. Tools and Their Uses
          - Categories of tools, their description and uses in cutting, splitting, measuring, weaving, coiling and finishing.
          - Drawing and labelling of basic basketry tools.
          - Improvisation of simple tools and equipment from local materials.
          - Care, maintenance and safety precautions when using tools and equipment.

        7. Elements and Principles of Design
          - Elements of design: dot, line, shape, form, space, texture, value and colour as seen in the environment and in basketry.
          - Creating elements in basketry using techniques such as knotting, weaving, coiling and waling (e.g. bead or knot as a dot, weave patterns for texture).
          - Colour theory: 12‑point colour wheel and basic colour harmonies; Ghanaian and local colour concepts and symbolism.
          - Principles of design: unity, balance, proportion, repetition, rhythm, contrast, emphasis and variety.
          - Applying principles through techniques such as spraying, scorching, weaving and pattern arrangement.

        8. Basic Design and Technology
          - Meaning of design and its role in basketry.
          - Idea creation and design development by drawing from memory, imagination and observation.
          - Selecting appropriate techniques, tools and materials to translate designs into finished articles.

        9. Designing Articles to Suit Specific Needs
          - Designing basketry articles (by hand or computer) to meet specific functional and aesthetic needs.
          - Making articles using weaving, knotting, plaiting, coiling, stitching and framing techniques.
          - Criteria for appreciation and evaluation: suitability of materials, structural soundness, choice of colour, quality of technique, decoration and finishing.

        10. Sustainable Development of Basketry
          - Exploring strategies for sustaining sources of raw materials (re‑afforestation, controlled harvesting, waste management and recycling).
          - Identifying challenges facing the basketry industry (competition from plastics, material scarcity, market access) and suggesting solutions.
          - Role of co‑operatives and collaboration in remaining competitive and improving livelihoods.

        11. Decoration and Finishing
          - Meaning of decoration and finishing.
          - Decorative processes: use of coloured and uncoloured weavers, weave variations, scorching, spraying and addition of complementary objects (beads, cowries, fabrics, etc.).
          - Finishing processes: wrapping, binding, singeing, tying, trimming, lashing, bending, gluing, sanding, varnishing, lacquering, embroidering, sealing, creating borders and edges.

        12. Entrepreneurial Skills and Business Practice
          - Building a portfolio of work (prints, photographs, CDs/DVDs and digital records).
          - Business plan: meaning, purpose and key steps in organising a basketry business (registration, capital, location, tools and materials, market survey).
          - Capital and funding: sources such as savings, bank loans, co‑operatives, grants and family support.
          - Exhibitions: types (general, specialised) and how they promote products.
          - Business promotion: billboards, internet and social media, print media, TV/radio, handbills and posters.
          - Packaging and handling: meaning, functions, types (primary, secondary, tertiary) and limitations; choosing packaging that protects work and supports branding.
          - Costing and pricing: variable and fixed costs, profit, and basic factors influencing price determination.

        Guidelines for Candidates:
        - Practise a wide range of basketry techniques (weaving, plaiting, coiling, knotting, stitching and framing) using both natural and synthetic materials.
        - Study local and international examples of basketry to understand history, symbolism, function and modern applications.
        - Keep design and project notebooks with sketches, colour studies, material tests and step‑by‑step processes.
        - Apply design principles consciously when planning and executing projects, and evaluate finished works using agreed criteria.
        - Develop basic entrepreneurial habits: accurate costing, neat packaging, good record keeping and effective communication with clients and collaborators.
        - Use past WAEC Basketry papers to gain familiarity with objective, essay and practical project requirements and to improve speed, accuracy and presentation.`,
  },
  {
    subject: 'Blocklaying, Bricklaying and Concreting',
    timeAlloted: 45 * 60,
    description: `Blocklaying, Bricklaying and Concreting is a core building trade that trains candidates to set out, construct, finish and maintain substructures, walls, floors and related elements safely and accurately, using blocks, bricks and concrete.

        Examination Scheme:
        - Paper 1 & Paper 2 (Composite Theory)
          • Taken together in one sitting in 1 hour 45 minutes for 100 marks.
          • Paper 1: 40 multiple-choice objective questions on the whole syllabus.
            - Duration: 45 minutes for 40 marks.
          • Paper 2: 5 short-structured questions; candidates answer any 4.
            - Duration: 1 hour for 60 marks.

        - Paper 3: Practical / Alternative to Practical
          • Practical Test:
            - 3-hour test worth 100 marks.
            - Work piece usually involves laying not more than 30 blocks; if bricks only, not more than 100 bricks.
            - For composite work (blocks and bricks), total materials do not exceed 20 blocks and 40 bricks.
            - A list of materials is sent to schools at least two weeks before the examination to aid procurement and preparation.
          • Alternative to Practical (where materials/facilities are inadequate):
            - Theory-based paper on practical skills.
            - 2 compulsory questions in 3 hours for 100 marks.

        Major Syllabus Areas:
        1. Workshop and Site Safety
           - Safety rules in the workshop and on site (scaffolding, electrical installations, excavation, handling tools and materials).
           - First aid and reasons for observing safety regulations.
           - Personal protective equipment (helmets, boots, hand gloves, nose masks, goggles, overalls).

        2. Basic Tools and Equipment
           - Identification, sketching and uses of common hand tools for blocklaying, bricklaying and concreting (trowels, hammers, builders’ squares, floats, spirit level, line and pins, etc.).
           - Simple maintenance: cleaning, sharpening where applicable, safe storage and handling.

        3. Site Preparation and Setting Out
           - Tools and equipment for site clearing and setting out.
           - Clearing and preparation of building sites.
           - Methods of setting out building lines and profiles.
           - Setting out tools and materials (profiles, pegs, battens, lines, tapes).

        4. Manufacture, Application and Properties of Blocks and Bricks
           - Basic manufacturing processes for sandcrete blocks and clay bricks.
           - Types of blocks and bricks and their uses.
           - Materials for blocks and bricks: sand, lime, cement, clay, laterite and water.

        5. Concreting Materials
           - Constituents of concrete: cement, fine and coarse aggregates, water.
           - Types of cement and their common uses.
           - Definitions and differences between fine and coarse aggregates.
           - Storage of cement and aggregates to prevent deterioration and contamination.

        6. Formwork for Construction Work
           - Functions and basic requirements of good formwork (strength, rigidity, accuracy, ease of striking and re-use).
           - Materials used for formwork (timber, steel, plywood) and their advantages.
           - Construction of simple formwork for floor slabs, beams, columns, arches, lintels and staircases.
           - Erection and striking of formwork in the correct sequence and with attention to safety.

        7. Concreting Operations
           - Concrete mix proportions (cement–aggregate ratio, water–cement ratio) for different classes of work.
           - Stages in concreting: batching, mixing, transporting, placing, compacting and curing.
           - Methods of transporting fresh concrete and precautions to avoid segregation and loss of materials.
           - Simple tests on concrete (slump and basic quality checks) and properties of good concrete (workability, strength, durability).
           - Construction joints in beams, columns, roofs and slabs and reasons for providing them.
           - Need for reinforcement in concrete; simple methods of reinforcing concrete elements and common types of reinforcement bars.
           - Casting and curing of in-situ and precast concrete elements.

        8. Substructure Construction Work (Including Ground Floors)
           - Types of soil; simple site and soil investigation and bearing capacity concepts.
           - Foundations: meaning, types and uses for different structures and ground conditions.
           - Ground floors: meaning, types, uses and methods of construction.
           - Damp-proof course (d.p.c.) and damp-proof membrane (d.p.m.): materials used, differences, positioning and methods of installation in walls and floors.

        9. Upper Floors
           - Functions and types of upper floors (solid, suspended, timber and concrete floors).
           - Methods of floor construction and basic floor finishes.

        10. Walls
            - Functions and types of walls (load-bearing, non-load-bearing, partition, retaining, etc.).
            - Common walling materials (blocks, bricks, stone, concrete).
            - Common bonds in brick and block walls and where they are used.
            - Mortar: types, mixing methods and typical mix ratios for different uses.
            - Differences between pointing and jointing; types of pointing and jointing and when each is applied.

        11. Openings in Walls
            - Functions and types of openings (doors, windows, arches, vents).
            - Materials used for lintels, beams and arches and their selection.
            - Windows and doors: functions, types, materials, methods of fixing and associated ironmongery (hinges, locks, bolts, handles).

        12. Stairs Construction
            - Types of stair (straight flight and simple variations) and basic terminology (tread, riser, landing, balustrade).
            - Construction principles of a straight-flight stair in block/brick and concrete work.

        13. Scaffolds and Scaffolding
            - Types of scaffolds and their components (standards, ledgers, braces, platforms, toe boards, guard rails).
            - Hoisting equipment and methods for moving materials vertically.
            - Safety regulations and good practice in erecting, using and dismantling scaffolds.

        14. Finishes
            - Types of finishes: internal and external, wet and dry (plastering, rendering, screeding, tiling, painting, etc.).
            - Types and characteristics of finishing materials and where they are used.
            - Uses, care and maintenance of common finishes.

        15. Construction of Drainage Systems
            - Principles of a good drainage system.
            - Types of drainage (surface and sub-surface) and basic components.
            - Differences between a sewer and a drain.
            - Simple sketches and understanding of septic tanks, soakaway pits, inspection chambers, manholes and cesspools.
            - Surface drainage and basic methods of laying drain pipes to specified gradients.

        16. Kerbs
            - Functions and types of concrete or stone kerbs.
            - Materials used for kerbs and simple methods of laying precast concrete kerbs.

        17. Business Opportunities in Building
            - Opportunities in the building industry for trained blocklayers/bricklayers and concrete workers (employment and self-employment).
            - Basic procedures for establishing a small construction or trade business.
            - Requirements for managing construction business: organisation, supervision, quality control and customer relations.
            - Simple estimating of quantities and costs of construction jobs.
            - Elementary principles and techniques of book-keeping for small building enterprises.

        Typical Tools and Equipment:
        - Hand tools for blocklaying, bricklaying and plastering: trowels, hammers, bolsters, cold chisels, builders’ and steel squares, spirit levels, gauges, floats, jointing tools, plumb rules, lines and pins.
        - Site preparation and concrete tools: spades, shovels, picks, wheelbarrows, headpans, hand rammers, gauge boxes, hand sieves and straight edges.
        - Equipment for mixing and placing concrete: concrete mixers, vibrators (poker and clamp-on), water hoses, Tyrolean and terrazzo machines, scaffolding sets and block-moulding machines.

        Guidelines for Candidates:
        - Practise accurate use of tools for setting out, laying and finishing blocks/bricks and for batching, mixing, placing and curing concrete.
        - Learn to interpret simple building drawings and translate them to site work with correct dimensions, levels, bonds and finishes.
        - Develop speed and accuracy in setting out, walling, finishing and small concrete works while maintaining safety and workmanship standards.
        - Understand the functions of substructure, walls, floors, openings, stairs, drainage and kerbs, and be able to describe how each is constructed.
        - Keep simple site records, measurements and cost estimates, and view the trade as a pathway to gainful employment or small business ownership.
        - Revise past WAEC Blocklaying, Bricklaying and Concreting papers to become familiar with objective, structured, essay and practical questions and to strengthen exam technique.`,
  },
  {
    subject: 'Book Keeping',
    timeAlloted: 50 * 60,
    description: `Book Keeping introduces candidates to the basic records and procedures that support small and medium-scale business operations, providing a foundation for further study in accounting and business.

      Aims & Objectives:
      - Develop understanding of simple business transactions, source documents and how records support day-to-day operations.
      - Build the basic skills needed to start and run a small business (recording, summarising and reporting transactions).
      - Help candidates appreciate the rules, concepts and functions of book keeping and how they feed into full accounting systems.
      - Prepare candidates for more advanced work in Financial Accounting and related business subjects.

      Examination Scheme:
      - Paper 1: Objective
        • 40 multiple-choice questions taken in 50 minutes for 40 marks.

      - Paper 2: Essay / Practice (Composite with Paper 1)
        • Total duration for Papers 1 & 2 together: 2 hours 50 minutes.
        • Section A – Theory of Book Keeping:
        - 4 questions on concepts and principles; candidates answer 2 for 20 marks.
        • Section B – Practice of Book Keeping:
        - 5 questions on recording and preparation tasks; candidates answer 3 for 60 marks in 2 hours.

      Major Syllabus Areas:
      1. Introduction to Book Keeping
         - Meaning, history, nature, importance and functions of book keeping in business.
         - Career opportunities and users of book-keeping information.
         - Qualities and values of a good book keeper (accuracy, honesty, confidentiality, orderliness).
         - Assets and liabilities: meaning, differences, classification and simple examples.
         - Business transactions: meaning, types (cash/credit, personal/impersonal) and parties involved.
         - Classification of accounts: personal (debtors, creditors) and impersonal (real, nominal).
         - Source documents: meaning, types (invoices, receipts, debit/credit notes, cheques, etc.) and their uses.

      2. Concepts and Conventions
         - Basic accounting concepts and conventions relevant to book keeping (e.g. business entity, going concern, cost, dual aspect).
         - Distinction between concepts and conventions and why they matter for recording.

      3. Books of Original Entry
         - Meaning, purpose and types of books of original entry (day books/journals, cash book, returns inwards/outwards books, petty cash book, etc.).
         - Simple formats, uses and preparation of entries from source documents.

      4. Ledger and Principles of Double Entry
         - Ledger: meaning, classification (sales, purchases, general), layout and uses.
         - Posting from books of original entry into ledger accounts.
         - Double-entry principle: meaning, history, rules (debit and credit) and simple illustrations of how every transaction affects at least two accounts.

      5. Cash Book and Petty Cash Book
         - Purpose, description and preparation of:
         • Single-column cash book.
         • Two-column (cash and bank) cash book.
         • Three-column (cash, bank and discount) cash book.
         • Petty cash book and simple imprest system.

      6. Bank Reconciliation Statement
         - Meaning and purpose of reconciling cash book with bank statement.
         - Common banking terms and documents.
         - Causes of differences (unpresented cheques, uncredited lodgements, bank charges, errors) and preparation of simple bank reconciliation statements.

      7. Trial Balance and Errors
         - Trial balance: meaning, functions and preparation from ledger balances.
         - Errors in book keeping: meaning, types and classification (errors disclosed and not disclosed by trial balance).
         - Simple correction of errors and use of a suspense account.

      8. Financial Statements of a Sole Proprietor
         - Trading account: purpose, format and preparation of a simple trading account; basic stock valuation ideas (FIFO and LIFO in outline).
         - Profit and loss account: purpose, format and preparation from adjusted trial balance.
         - Balance sheet: meaning, structure (assets, liabilities, capital) and preparation of a simple balance sheet for a sole trader.

      9. Adjustments to Financial Statements
         - Adjustments for prepayments and accruals of expenses and incomes.
         - Bad and doubtful debts; provision for doubtful debts.
         - Depreciation: meaning, reasons and simple methods (straight-line and reducing-balance) applied in final accounts.

      10. Control Accounts
        - Meaning and purpose of control accounts.
        - Preparation of simple sales ledger and purchases ledger control accounts to check the accuracy of personal ledgers.

      11. Single Entry and Incomplete Records
        - Meaning and limitations of single-entry systems.
        - Conversion of incomplete records to double-entry form and preparation of simple final accounts from incomplete records.

      12. Not-for-Profit Organisations
        - Purpose, content and preparation of:
          • Receipts and payments account.
          • Income and expenditure account.

      13. Manufacturing Accounts
        - Manufacturing activities and key terms (prime cost, factory overheads, work-in-progress, cost of production).
        - Preparation of manufacturing account and related trading, profit and loss accounts for simple manufacturing concerns.

      14. Partnership Accounts
        - Meaning and basic partnership terms (partnership deed, drawings, interest on capital, interest on drawings).
        - Preparation of partners’ capital and current accounts, profit and loss appropriation account and simple balance sheet.
        - Admission of a new partner and simple goodwill treatment.

      15. Joint Venture Accounts
        - Meaning, objectives and simple records for joint ventures.
        - Differences between joint venture and partnership.

      16. Departmental and Branch Accounts
        - Importance and differences between departments and branches.
        - Preparation of simple departmental accounts and basic branch accounts.

      17. Introduction to Company Accounts
        - Simple ideas on formation of companies and common terms (share capital, reserves, dividends).
        - Presentation of very simple company financial statements at an introductory level.

      18. Interpretation of Accounts
        - Reasons for interpreting financial statements.
        - Computation and basic meaning of simple ratios such as gross profit margin, net profit margin, return on capital employed, stock turnover, current/quick or acid-test ratios.

      19. Purchase of Business, Consignment, Hire Purchase, Contract and Co-operative Accounts
        - Reasons for purchase and sale of business and simple accounts prepared in such transactions.
        - Consignment: basic terms and preparation of simple consignment accounts.
        - Hire purchase: meaning and preparation of basic accounts in the books of the seller and hirer.
        - Contract accounts: meaning, purpose, terms and preparation of simple contract accounts.
        - Co-operative societies: meaning and objectives; introductory records.

      Guidelines for Candidates:
      - Master the language of simple business transactions, assets, liabilities, income and expenses and how they appear in books.
      - Practise writing up books of original entry, posting to ledgers, balancing accounts and extracting trial balances.
      - Work through many examples of cash books, bank reconciliations, control accounts, adjustments and simple final accounts.
      - Learn to interpret basic ratios and what they say about profitability, liquidity and efficiency.
      - Use past WAEC Book Keeping questions to build speed and accuracy in both objective and structured/practical sections and to strengthen exam technique.`,
  },
  {
    subject: 'Building Construction',
    timeAlloted: 45 * 60,
    description: `Building Construction is a trade subject that introduces candidates to the processes, drawings, components and services involved in putting up safe, functional and durable buildings, and to the roles of the construction team.

      Examination Scheme:
      - Paper 1: Objective
        • 40 multiple-choice questions on the entire syllabus.
        • Duration: 45 minutes for 40 marks.

      - Paper 2: Short-Structured / Essay (Composite with Paper 1)
        • 5 short-structured essay questions.
        • Candidates answer any 4 in 1 hour for 60 marks.

      - Paper 3: Practical (Building Drawing / Construction)
        • 2½-hour practical paper for 100 marks, split into sections by country.
        • Section A (Nigeria, Sierra Leone and The Gambia): Building Drawing.
        - 4 questions including a compulsory one.
        - Candidates answer the compulsory question and any 2 others.
        • Section B (Ghana): Construction questions.
        - 6 questions including a compulsory one.
        - Candidates answer the compulsory question and any 3 others.

      Major Syllabus Areas:
      1. Introduction to Building
         - Meaning of a building; classification and types (residential, industrial, commercial, institutional, etc.).
         - Basic awareness of national building codes, regulations and byelaw requirements that guide safe construction.

      2. Building Construction Process and the Construction Team
         - Definition, purpose and main procedures in building construction from site acquisition to completion.
         - Parties in the construction team: client’s team, contractor’s team and statutory personnel.
         - Functions and inter-relationships of team members (client, architect, builder, engineer, quantity surveyor, local authority, etc.).

      3. Safety Practices
         - Meaning of safety; rules and regulations in the workshop and on construction sites.
         - Common hazards and causes of accidents; methods of accident prevention.
         - Safety equipment and measures on site; first aid box contents and basic first-aid procedures.
         - Personal protective wears and safe working with potentially dangerous equipment and materials (electricity, scaffolding, formwork, cement, roofs, etc.).

      4. Basic Tools, Equipment and Machines
         - Identification and functions of common hand tools and equipment used in building work.
         - Simple care and maintenance to keep tools and equipment in good condition.

      5. Site Clearing and Levelling
         - Operations involved in clearing a building site (removing vegetation, debris, topsoil, etc.).
         - Tools and equipment for site clearing and levelling.
         - Basic site-levelling operations and their purpose.

      6. Site Organisation and Layout
         - Preliminary operations before main construction (site investigation, temporary works, services).
         - Procedure for planning site layout: positioning boundaries, hoardings, site huts, storage areas and access roads.

      7. Setting Out
         - Methods of setting out building lines (3:4:5 method, builders’ square method).
         - Tools and equipment used in setting out.
         - Step-by-step procedure for setting out a simple rectangular building.

      8. Building Drawing
         - Use of drawing tools and simple plane-geometry exercises related to building work.
         - Common building-drawing symbols and conventions.
         - Scales used in building drawings and their application on site.
         - Production drawings and interpretation of plans, elevations, sections and details.
         - Special details for building elements, including door and window schedules.
         - Simple scaling (enlargement, pictorials and perspectives) and introduction to AutoCAD / computer-aided drafting.

      9. Excavation and Earthwork
         - Classification, types and basic properties of soil.
         - Definition, functions and methods of excavation.
         - Tools, equipment and plant used for excavation.
         - Solutions to excavation problems (earthwork support, timbering, battering, dewatering) and safety rules during excavation.

      10. Foundations
        - Definition, purpose and functional requirements of foundations.
        - Types of foundation (strip, pad, raft, pile in outline) and factors influencing choice (soil type, load, site conditions).

      11. Ground Floors and Suspended Floors
        - Ground floors: types, functions, functional requirements and methods of construction; definition and issues associated with basements.
        - Suspended/upper floors: functions and methods of constructing concrete and timber upper floors.

      12. Concrete and Reinforcement
        - Definition and types of concrete; materials for concrete (cement, aggregates, water).
        - Cement: types, components, manufacture, properties and uses; awareness of alternatives (pozzolana, pulverised fuel ash, rice husk ash, etc.).
        - Aggregates: types and uses, including local alternatives such as palm-kernel shells and periwinkle shells.
        - Reinforcement: common types and uses.
        - Properties and characteristics of good concrete; basic process of concrete production and simple concrete tests.

      13. Walls
        - Definition, types and functions of walls (load-bearing, partition, retaining, etc.).
        - Walling materials: types, properties and locally sourced options.
        - Manufacturing processes for clay bricks, blocks and concrete blocks; standard sizes.
        - Mortar mixes for brick and block work and methods of wall construction including timber walls.
        - Wall openings: functions, methods of forming openings and functions of lintels and arches.

      14. Doors and Windows
        - Definition, functions and types of doors and windows.
        - Methods of fixing door and window frames into walls.

      15. Staircases
        - Definition, classification, types and uses of stairs.
        - Materials for constructing and finishing staircases.

      16. Plumbing Installations and Drainage Systems
        - Types of fittings in plumbing and sanitary works; materials used and their applications.
        - Types of sanitary appliances; soil and waste appliances and their installation.
        - Basic drainage terms, principles and functional requirements of good drainage.
        - Types of drainage systems and drainage materials (pipes and fittings).
        - Methods of laying and testing drain lines.
        - Description of septic tanks, soakaways, inspection chambers and manholes.

      17. Electrical Installation and Solar Energy Systems
        - Common electrical symbols, installation terms and materials.
        - Identification and uses of electrical wiring materials and basic types of wiring.
        - Solar energy systems: definition, functions and basic components.

      18. Roofs
        - Definition, purposes and types of roofs.
        - Functional requirements of roofs and materials used for roof structure and coverings.
        - Protection of roofs from wind, rain, lightning and other weather effects.

      19. Finishes
        - Floor finishes: types, characteristics, laying methods, uses, care and maintenance.
        - Wall finishes: types and characteristics; plastering and rendering (types and application methods).
        - Ceiling finishes: types, functions and materials used for ceilings.

      20. External Works
        - Fencing: types of fence, materials and methods of construction.
        - Gates: types, fixing and basic fabrication/production.
        - Access roads: types and materials used; simple methods of construction.
        - Landscaping: need for landscaping, materials and basic procedures.

      21. Business Organisation, Capital and Basic Book Keeping
        - Principles and management of business organisations in the building industry.
        - Types of business organisation (sole trader, partnership, limited company, etc.) and sources of capital.
        - Book keeping in small-scale building businesses and simple books of original entry.

      22. Contract Work
        - Contract work: meaning, types of building contract and common contract documents.
        - Parties to a building contract and their basic responsibilities.

      Guidelines for Candidates:
      - Link building-construction theory to real sites by observing and, where possible, participating in simple site activities.
      - Practise building drawing regularly (plans, elevations, sections, details) and learn to interpret drawings into actual construction steps.
      - Understand the sequence of operations from site clearing, setting out and foundation to walls, floors, roofs, finishes and services.
      - Pay close attention to safety rules, correct use of tools/equipment and quality workmanship.
      - Develop basic business awareness (simple recording, costing and contracts) as part of preparing for work or self-employment in the building industry.
      - Use past WAEC Building Construction papers to improve familiarity with objective, short-structured, drawing and practical questions and to refine exam technique.`,
  },
  {
    subject: 'Carpentry and Joinery',
    timeAlloted: 45 * 60,
    description: `Carpentry and Joinery is a building-trade subject that equips candidates with practical skills in working with timber, tools, machines and finishes to produce, assemble and install safe, functional and aesthetically pleasing woodwork items for buildings and furniture.

        Examination Scheme:
        - Paper 1: Objective
          • 40 multiple-choice questions covering the entire syllabus.
          • Duration: 45 minutes for 40 marks.

        - Paper 2: Short-Structured and Essay
          • Duration: 1 hour 30 minutes for 60 marks.
          • Section A: 5 short-structured questions; all are compulsory, answered in about 30 minutes for 20 marks.
          • Section B: 3 longer questions; candidates answer any 2 in about 1 hour for 40 marks.

        - Paper 3: Practical / Alternative to Practical
          • Practical Test: 2-hour practical test based on the prescribed syllabus; 2 questions are set and candidates answer 1 for 100 marks.
          • A list of required materials is supplied to schools at least two weeks before the examination to allow for preparation and procurement.
          • Alternative to Practical: where actual materials cannot be obtained, a 1½-hour written test of candidates’ practical knowledge is set, consisting of two compulsory questions for 100 marks.

        Major Syllabus Areas:

        1. Workshop and Worksite Safety (Preliminaries)
           - Workshop rules and regulations: safe behaviour, tool and machine handling, clothing and housekeeping.
           - Personal safety practices and use of safety devices (preventive and protective devices) on machines and tools.
           - Safety rules for specific machines and operations (sawing, planing, drilling, sanding, etc.).
           - Workshop layout: arrangement of benches, machines, storage and circulation routes for safe and efficient work.
           - First aid: types and uses of first aid materials and basic response to common workshop accidents.
           - Worksite safety: common hazards on site and safety precautions when working at heights or in confined spaces.
           - Worksite aids such as ladders, steps and scaffolding – safe use, inspection and maintenance.

        2. Tools, Machines and Maintenance
           - Hand Tools:
             • Types of woodwork hand tools: cutting tools, paring and chiselling tools, boring/drilling tools, striking tools, measuring and marking-out tools, holding and supporting tools.
             • Uses, care and routine maintenance of hand tools.
             • Simple sketches of common tools may be required.
           - Portable Power Tools:
             • Types of portable power tools e.g. jigsaws, portable circular saws, sanders, portable drills, routers.
             • Uses of portable power tools in carpentry and joinery operations.
             • Safety precautions and maintenance for portable power tools.
           - Machines and Maintenance:
             • Types of machines found in carpentry and joinery workshops (e.g. circular saw, band saw, surface planer, thicknesser, mortiser, spindle moulder).
             • Basic functions, safe operation and simple maintenance routines.

        3. Materials: Types and Uses
           - Timber:
             • Growth and structure of timber; distinction between softwoods and hardwoods.
             • Conversion and seasoning of timber; reasons for seasoning and methods used.
             • Common defects in timber (knots, shakes, warping, etc.) and their remedies.
             • Timber preservation: reasons for preservation and types of preservatives.
             • Veneers and manufactured boards (plywood, blockboard, particleboard, MDF, etc.) – characteristics and uses.
           - Adhesives:
             • Types of wood adhesives (e.g. animal glue, casein glue, urea-formaldehyde, PVA) and their characteristics.
             • Uses, mixing and application procedures, and safety precautions.
           - Ironmongery:
             • Types of ironmongery used in carpentry and joinery: screws, nails, bolts, hinges, locks, catches, handles, etc. (sketches may be required).
             • Fixing of hinges and locks; basic specifications for ordering nails and other fixings.

        4. Construction Principles and Techniques
           - Timber Preparation:
             • Sequence of timber preparation to size (planing surfaces to flatness, planing edges to squareness, marking out and cutting to size).
             • Sketching and developing working drawings for simple projects.
             • Interpreting simple working drawings of wood projects (orthographic views, dimensions, joints, assemblies).
           - Joints:
             • Common woodwork joints: halving, mortise and tenon, dovetail, housing, mitre, bridle, scarf, dowelled and others.
             • Construction, uses and relative strengths of joints.
             • Simple project work requiring specified joints (e.g. frames, small cabinets, stools).
           - Doors and Windows:
             • Types of doors (framed and panelled, ledged and braced, flush, sliding, folding, etc.) and windows (casement, sliding, louvred, fixed/light, etc.).
             • Basic constructional details and methods of fitting/installation.
           - Formwork and Centering:
             • Types of formwork used in concrete work; identification, construction and uses.
             • Centres for arches: design, construction and purpose; importance of proper timbering.
           - Wall Panelling, Cladding and Partitioning:
             • Simple panelling and cladding methods in timber; basic partition construction.
           - Stairs, Handrails and Balusters:
             • Basic stair components (treads, risers, strings, newels, balusters, handrails) and simple stair construction principles.
           - Scaffolding and Ladders:
             • Types and uses; safety and stability considerations.
           - Estimating and Costing:
             • Simple cost analysis for small projects (e.g. centre table, stools, doors), including materials and labour.
             • Projects may be presented in the form of working drawings for which candidates perform basic quantity and cost estimates.

        5. Finishes and Finishing
           - Abrasives:
             • Types and grades of abrasives (glasspaper, sandpaper, emery cloth, etc.).
             • Uses and selection of appropriate grades for different stages of surface preparation.
           - Finishes:
             • Types of wood finishes – varnishes, lacquers, paints, stains, waxes, oils, sealers.
             • Characteristics, applications and suitability of different finishes for interior and exterior work.
           - Finishing Process and Tools:
             • Stages of finishing: scraping, filling, sanding, staining, sealing, polishing and final coating.
             • Finishing tools and equipment, including spray guns and brushes.
             • Simple project work involving complete finishing of a piece of furniture or joinery item.

        6. Business Opportunities in Carpentry and Joinery
           - Furniture Making:
             • Production of items such as tables, chairs, doors, wardrobes, cabinets, beds, etc., for domestic, office and commercial use.
             • Quality control, simple packaging and presentation of finished products.
           - Material Supply and Merchandising:
             • Supply of timber, manufactured boards, ironmongery, adhesives and finishes.
             • Basic principles of buying, storage, stock control and sales.
           - Contract Work and Small-Scale Workshop:
             • Undertaking joinery and carpentry projects on contract for individuals, organizations and building contractors.
             • Description and layout of a standard small-scale workshop (space organisation, equipment, storage and safety facilities).
             • Simple feasibility studies and basic records for small carpentry/joinery businesses.

        Facilities and Equipment (Awareness Level):
        - Knowledge of the basic facilities and major equipment required in a standard carpentry and joinery workshop (e.g. benches, vices, saws, planes, chisels, clamps, power tools, machines, safety equipment and first-aid box).

        Guidelines for Candidates:
        - Observe and practise strict safety rules in both school workshops and worksites; know safety devices, first-aid materials and safe use of ladders and scaffolds.
        - Learn the names, uses and care of common hand tools, portable power tools and machines, and be able to sketch and label them.
        - Study the properties and uses of timber, manufactured boards, adhesives and ironmongery, and select suitable materials for given projects.
        - Practise setting out, joint construction and simple assemblies from drawings, building accuracy and neatness in workmanship.
        - Develop finishing skills by working through the full process from surface preparation to the final coat using different finishes.
        - Understand simple estimating and costing, and explore how carpentry and joinery skills can be turned into business opportunities.
        - Use past WAEC Carpentry and Joinery papers (including practical questions) to build speed, confidence and familiarity with exam and workshop expectations.`,
  },
  {
    subject: 'Catering Craft Practice',
    timeAlloted: 40 * 60,
     description: `Catering Craft Practice is designed to develop candidates' professional attitude, practical skills and technical knowledge for food production and service in hospitality and catering establishments.

        General Aims and Objectives:
        - Develop a professional approach to work, personal hygiene and teamwork in the catering industry.
        - Understand food commodities in terms of types, quality, cost and appropriate uses.
        - Apply methods of cooking to produce a variety of dishes and drinks for different occasions, establishments and individuals.
        - Understand dietary requirements and plan/prepare dishes that are nutritionally adequate.
        - Practise strict hygiene and sanitation in food handling, kitchen operations and personal conduct.
        - Acquire skills for producing pastry and confectionery products to acceptable standards.
        - Choose, use, care for and store kitchen equipment correctly and safely.
        - Observe safety precautions in catering environments and respond appropriately to accidents.
        - Develop simple enterprise skills in menu planning, costing and basic cost control.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple-choice questions on the whole syllabus.
         • 40 minutes for 40 marks.

        - Paper 2 (Short Essay):
         • 6 short-essay questions set; candidates answer any 5.
         • 50 minutes for 40 marks.

        - Paper 3 (Alternative to Practical):
         • 5 short structured questions testing practical and procedural knowledge.
         • Candidates answer all 5 questions.
         • 40 minutes for 40 marks.

        Major Syllabus Areas:

        1. The Concept of Catering and Hospitality Industry
          - Meaning of hospitality and catering; scope of the hospitality industry.
          - Components of the hospitality industry and types of catering establishments (hotels, restaurants, institutional catering, outdoor/mobile catering, schools, etc.).
          - Basic culinary terms commonly used in catering and a simple glossary of terms.
          - Safety precautions in catering: common causes of kitchen accidents, prevention, first aid and fire-fighting equipment.
          - Sanitation and hygiene: water and environmental sanitation, food safety practices, kitchen and personal hygiene.

        2. Food Commodities and Methods of Food Preparation
          - Study of major food commodities (meat, poultry, game, fish, eggs, vegetables, fruits, dairy products, pulses, cereals, herbs, flavourings, etc.) – identification, quality points, uses and storage.
          - Principles and methods of cooking: boiling, stewing, roasting, grilling, baking, frying and other methods as applied to different commodities.
          - Practical preparation of dishes and drinks from the various food groups.
          - Standard and local measurements used in recipes and portioning.

        3. Service of Foods and Drinks; Costing and Control
          - Food and beverage service personnel: types of staff and their functions.
          - Types of food service (e.g. silver service, waiter service, buffet, banquet, vending, gueridon, etc.) and types of covers and table laying/setting.
          - Types of alcoholic and non-alcoholic drinks and basic bar organisation.
          - Costing and control in catering: cost/sales concepts, simple dish costing and portion control.

        4. Kitchen Equipment and Their Uses
          - Types of kitchen equipment (large and small) and their specific uses.
          - Care, cleaning and maintenance of different types of kitchen equipment.

        5. Menu Planning and Special Menus
          - Meaning, scope and functions of a menu.
          - Principles and factors that influence menu planning for different clients and occasions.
          - Types of menu: table d'hote, a la carte and menus for special occasions (breakfast, lunch, dinner, tea, carte du jour, plat du jour, etc.).
          - Special menu planning and demonstrations for vegetarians, vulnerable groups, and people with different activity levels (manual and sedentary workers).
          - Food and table accompaniments used to complete meals.

        Guidelines for Candidates:
        - Build strong habits of personal and kitchen hygiene, sanitation and safety in all practical work.
        - Study food commodities and cooking methods closely and practise preparing a range of dishes and drinks.
        - Practise food and beverage service, table laying and clearing using correct procedures and terminology.
        - Learn to identify and use different kitchen equipment correctly, including care and maintenance routines.
        - Develop menu planning and simple dish-costing skills for different groups and occasions.
        - Use past WAEC Catering Craft Practice papers (including alternative-to-practical questions) to improve speed, accuracy and exam technique.`,
  },
  {
    subject: 'Ceramics',
    timeAlloted: 50 * 60,
     description: `Ceramics at WASSCE level is a vocational subject that trains candidates to design and produce functional and decorative ceramic wares, understand their cultural and economic relevance, and develop the skills needed to work safely in studios or run small-scale ceramic enterprises.

        Aims and Objectives:
        - Demonstrate knowledge of the history and development of ceramics and its role in cultural and economic life.
        - Explore, identify, prepare and use ceramic materials, tools and equipment effectively.
        - Practise healthy and safe studio habits (hygiene, ventilation, waste disposal and personal protection).
        - Design and develop creative ideas for ceramic forms and surface treatments.
        - Apply forming, decorating, glazing and firing processes to produce ceramic wares.
        - Interpret, evaluate and appreciate ceramic works critically.
        - Acquire basic entrepreneurial skills for planning and managing a small-scale ceramic industry.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple-choice questions on the entire syllabus.
         • 50 minutes for 40 marks.

        - Paper 2 (Essay):
         • 6 essay-type questions set; candidates answer any 4.
         • 2 hours for 60 marks.

        - Paper 3 (Practical Project):
         • Two project questions are set; candidates execute one within five days, working about six hours each day.
         • Practical execution carries 80 marks.
         • Question papers are issued two weeks earlier for candidates to study, design sketches and prepare notes; these preparatory works carry 20 marks.
         • Total for Paper 3: 100 marks.

        Major Syllabus Areas:

        1. Ceramics as a Vocation
          - Meaning and brief history of ceramics; ceramics as an art and a craft.
          - Career and business opportunities in ceramics.
          - Types of ceramic products and their uses: bricks, tiles, vases, bowls and other utilitarian and decorative wares.
          - Relationship of ceramic products to the culture of the people; indigenous pottery traditions (e.g. Ntonso, Pankrono, etc.).
          - Awareness of Ghanaian pioneer contemporary ceramic artists and their contributions.

        2. Raw Materials (Clay, Glazes and Other Raw Materials)
          - Clay: definition, formation, kinds and types (primary/kaolin, secondary/ball clay, earthenware, stoneware, etc.).
          - Prospecting, testing and evaluating clays (plasticity, shrinkage, firing temperature, etc.).
          - Preparation of clay and clay bodies (plastic, rough, etc.) and sources of raw materials.
          - Glazes, colouring oxides, stains and related materials for surface finishing.

        3. Tools and Equipment
          - Studio tools: types, uses and maintenance (modelling and trimming tools, sponges, cutting wire, callipers, knives, scrapers, kidneys, dippers, etc.).
          - Equipment: types, uses and maintenance (kilns and kiln furniture, potter's wheel, blunger, pug mill, jaw crusher, filter press, ball mill, dehumidifier, spraying booth and spraying machine, sackboard, etc.).
          - Simple design and construction of selected tools and equipment (forming and modelling tools, cutting wire, dippers, sackboards, rolling pins, simple kiln design ideas, etc.).

        4. Forming Techniques
          - Drawing and designing: idea generation and development; sketching, drawing, dimensioning and basic computer-aided design.
          - Handbuilding methods: pinching, coiling, slab-building and combinations of these techniques.
          - Wheel work: throwing and turning.
          - Other forming methods: casting, pressing and extrusion.

        5. Decoration and Finishing
          - Decorative processes: stamping, inlaying, incision, embossing, sgraffito, slip trailing, glazing, sprigging, spraying, painting, printing and related techniques.
          - Drying and firing: open firing, kiln firing, bisque firing and glost firing.
          - Glazing: preparation and application of glazes, firing schedules and common glaze defects and remedies.

        6. Establishment of a Small-Scale Ceramic Industry
          - Site selection and factors to consider: location, water and power supply, raw materials, markets, environmental factors, etc.
          - Financing, branding, registration, licensing, labour and quality control.
          - Entrepreneurial skills: managing a ceramic enterprise, simple records and operations.
          - Costing, pricing, packaging and marketing of ceramic products (market survey, valuing, labour cost, transportation, etc.).

        7. Exhibition
          - Definition, types and purposes of exhibitions.
          - Planning, organising and presenting works in exhibitions.

        8. Ceramics and Sustainable Environmental Issues
          - Energy conservation in ceramic production.
          - Afforestation, land reclamation and responsible use of raw materials.

        9. Professional Practices and Ethics
          - Healthy studio practices and safety.
          - Building and maintaining a portfolio of works.
          - Developing business plans, brochures and business cards.
          - Good professional conduct and concern for a sustainable environment.

        10. Museums and Galleries
          - Meaning, history and development of museums and galleries.
          - Functions and socio-economic importance of museums and galleries.
          - Basic administrative structure of a museum.
          - Ideas of authenticity and neutrality in museum practice.
          - Examples of museums and galleries and the nature of their activities.

        Guidelines for Candidates:
        - Study the history, cultural context and vocational opportunities in ceramics and relate them to contemporary practice.
        - Learn sources, preparation and testing of clays and glazes, and practise safe handling of raw materials.
        - Become familiar with studio tools and equipment, including basic maintenance and simple construction of tools.
        - Practise a range of forming, decorating, glazing and firing techniques to build confidence and craftsmanship.
        - Develop design and drawing skills and compile a portfolio of sketches, designs and finished works.
        - Explore costing, marketing and exhibition of ceramic works as preparation for self-employment or further professional training.
        - Use past WAEC Ceramics papers (objective, essay and practical projects) to improve speed, accuracy and exam technique.`,
  },
  {
    subject: 'Clerical Office Duties',
    timeAlloted: 50 * 60,
    description: `Clerical Office Duties introduces candidates to the principles, procedures, tools and communication skills needed to perform basic clerical and secretarial work in modern offices, and provides a foundation for further business studies.

        Objectives:
        - Understand the functions of the office and how information is received, recorded, processed, stored and safeguarded.
        - Apply knowledge of common office equipment, machines and materials to perform basic clerical tasks.
        - Communicate effectively in the office using appropriate written and oral forms (letters, memos, reports and meetings).
        - Demonstrate good personal and professional habits, including time management, hygiene, social skills and positive work attitude.
        - Develop problem-solving skills for handling routine clerical, secretarial and administrative issues.
        - Build a base for further studies and careers in business and administration.

        Examination Scheme (Composite Paper):
        - Paper 1 (Objective):
         • 50 multiple-choice questions on the entire syllabus.
         • 50 minutes for 30 marks.

        - Paper 2 (Case Study and Essay):
         • One compulsory case-study question based on office situations (22 marks).
         • Six other questions set; candidates answer any three (16 marks each).
         • Total of 70 marks; duration of 2 hours 10 minutes.

        Major Syllabus Areas:

        1. The Office and Its Functions
          - Meaning and functions of the office: receiving, recording, processing, giving and storing information, and safeguarding assets.
          - Main sections (reception, general office, stores, mail room/registry) and their roles.
          - Key departments (accounts, purchasing, sales, personnel/human resource, production, legal, administration, transport) and linkages.
          - Office personnel: office manager, supervisor, clerks, administrative assistants, secretaries, typists, receptionists, messengers; duties and relationships.
          - Professional image, personal attributes, social habits, personal hygiene, healthy lifestyle and time management.
          - Office layout: open vs closed offices, factors influencing choice and advantages/disadvantages.

        2. Organisation and Business Transactions
          - Principles of organisation: unity of objectives, span of control, authority and responsibility, delegation, unity of command, supervision, remuneration, accountability, esprit de corps.
          - Organisational structures: line, line-and-staff, staff, functional and matrix structures; organisational charts/organograms.
          - Business documents: letters of enquiry, price lists/catalogues/quotations, local purchase orders, orders, invoices, proforma invoices, delivery notes/waybills, debit and credit notes, statements of account, cheques/payment orders, receipts, purchase requisitions – their purposes, contents and use.
          - Methods of payment: cash, cheques, standing orders, credit transfers, money transfers – features, advantages and disadvantages.

        3. Payroll, Tax and Petty Cash
          - Payroll handling: terms such as wages, salaries, gross pay, net pay, allowances, basic pay and PAYE.
          - Methods of determining workers’ pay: time rate, piece rate, commission, flat rate, overtime and bonus.
          - Preparation of simple payrolls for wages and salaries.
          - Tax regimes: VAT, income tax, communication service tax, property tax, import/customs duty – basic concepts and importance.
          - Petty cash and the imprest system; completion of a simple petty cash book.

        4. Banking and Stock Procedures
          - Types of banks (commercial, development, merchant, rural/community, central bank) and their functions.
          - Types of accounts (savings, current, fixed deposit) and their features.
          - Cheques: features of a valid cheque, parties to a cheque, reasons for dishonour, types and crossing of cheques.
          - Stock procedures: meaning of stock, importance of stockkeeping, ordering procedures, duties of the storekeeper and requisition procedures.
          - Stock records and stocktaking (annual, periodic, perpetual); basic documents (bin cards, tally cards, purchase orders, etc.).
          - Stock control concepts: minimum stock, maximum stock, re-order level.
          - Simple stock valuation methods (cost price, average price, market price, lower of cost and market).

        5. Office Equipment, ICT and Mail Handling
          - General office machines and equipment: computers, photocopiers, duplicating machines, typewriters, calculators, cash registers, scanners, accounting machines, fax machines, perforators, collating machines, dictaphones, etc. – uses, care, advantages and disadvantages.
          - Basic computer and ICT concepts: components, peripherals, types of computers, uses, advantages and disadvantages; Internet, intranet, World Wide Web and service providers.
          - Mail-room equipment and materials: letter-opening machines, addressing machines, franking machines, shredders, scales, staplers, staple removers, guillotines, etc.
          - Mail handling procedures: incoming and outgoing mail, registers and records (mail registers, postage books, messenger’s receipt book).

        6. Communication, Meetings and Business English
          - Meaning, forms and channels of communication: verbal (oral and written), non-verbal and visual; vertical, horizontal and diagonal communication.
          - Barriers to effective communication (organisational, physical, psychological, socio-cultural) and ways to minimise them.
          - Business correspondence: letters (including form letters and circulars), memos and reports (short formal/informal, periodic/routine, special and statutory).
          - Meetings: types and purposes, requirements for a valid meeting (chairperson, secretary, tellers, scrutineers), common meeting terminology and preparation of meeting documents (notice, agenda, minutes).
          - Business English: proofreading, office abbreviations, spelling, word division and punctuation.

        7. Filing, Reference Sources and Postal/Courier Services
          - Filing and classification: reasons for filing, features of a good filing system; methods (alphabetical, numerical, subject, geographical, chronological, alpha-numeric, subject-numeric).
          - Filing systems: lateral, suspension, vertical and horizontal; general rules of filing.
          - Indexing and cross-referencing; electronic filing (including microfilming) and central vs departmental filing.
          - Reference books: dictionaries, encyclopaedias, almanacs, atlases, site plans, trade journals, telephone directories, post office guides and government publications (Hansard, white papers, gazettes, financial instructions, general orders).
          - Sources of official information (revenue offices, immigration, embassies, banks, etc.) and common personal/business documents (passports, visas, tax clearances, etc.).
          - Post office services: postage stamps, registered mail, recorded delivery, special and express services, private mail bags/boxes, business reply services, postal orders and money transfers.
          - Courier services (EMS and private couriers) and agency services rendered through the post office.

        8. Human Relations, Health, Safety and Entrepreneurship
          - Internal and external/public relations in the office; interpersonal skills and factors that promote good human relations.
          - Health hazards and accidents in the office: types, causes, prevention and safe practices; staff welfare services and benefits.
          - Pre-employment procedures: job advertisements, application letters and curriculum vitae, interview preparation and conduct.
          - Entrepreneurship and small-scale business: meaning and characteristics of entrepreneurs and small enterprises; roles and importance of small-scale businesses.

        Guidelines for Candidates:
        - Gain a clear picture of how a modern office works, including its structure, departments, personnel and information flow.
        - Practise completing business documents, simple payrolls, petty cash books, stock records and basic banking forms.
        - Build solid communication skills: writing letters, memos and reports, handling meetings and improving business English.
        - Learn to identify and use common office machines and ICT tools, and understand safe operation and basic maintenance.
        - Study filing and information storage methods, reference sources and postal/courier services as part of day-to-day office work.
        - Use past WAEC Clerical Office Duties papers (objective, case study and essay) to improve speed, application of knowledge and exam technique.`,
  },
  {
    subject: 'Clothing and Textiles',
    timeAlloted: 60 * 60,
     description: `Clothing and Textiles at Senior High School level trains candidates in clothing production, fabric science and clothing management, equipping them with employable skills to improve their own quality of life and contribute to the wider society.

        Objectives:
        - Identify and explore career opportunities in Clothing and Textiles and the basic requirements for each.
        - Demonstrate basic scientific knowledge of fibres and fabrics, including origin, construction, selection, use and care.
        - Explain the significance of clothing and select appropriate clothing and accessories for different occasions and figure types.
        - Select, use and maintain sewing equipment and tools safely and efficiently.
        - Apply clothing production skills (pattern work, freehand cutting and general construction processes) in making garments and household articles.
        - Design and decorate family clothing and simple household items creatively.
        - Repair, renovate, remodel and care for clothes effectively.
        - Develop entrepreneurial skills, sound work ethics and consumer awareness in Clothing and Textiles.
        - Apply techniques for promoting Clothing and Textiles products (modelling, exhibitions, fashion shows, etc.).

        Examination Scheme:
        - Paper 1 (Objective):
         • 60 multiple-choice questions on the whole syllabus.
         • 1 hour for 60 marks.

        - Paper 2 (Short-Structured Essay):
         • 6 short-structured essay questions set; candidates answer any 4.
         • 1 hour 30 minutes for 60 marks.

        - Paper 3 (Practical and Coursework):
         • Two sections: a practical test and an assessment of course work articles.
         • Duration: 2 hours 30 minutes, plus 30 minutes preparation time.
         • Practical test conducted by a visiting examiner; paper carries 100 marks in total, including up to 20 marks for pre-prepared articles and patterns from coursework.

        Major Syllabus Areas:

        1. Career Opportunities and Work Ethics in Clothing and Textiles
          - Reasons for studying Clothing and Textiles.
          - Career types: modelling, teaching, dressmaking/tailoring, textile chemistry, textile design and related fields.
          - Basic requirements for careers: knowledge, skills and academic qualifications.
          - Work ethics: meaning, components (rules, attitudes, behaviour, integrity, self-confidence, etc.), importance and benefits.

        2. Fibres, Yarns and Fabrics
          - Meanings of fibre, yarn and fabric.
          - Classification of fibres and fabrics; characteristics/properties and simple identification.
          - Fabric combinations (blends and mixtures).
          - Fabric construction (weaves, knitting, crocheting and other basic fabrication methods).
          - Common finishing treatments and their effects on fabrics.

        3. Clothing, Wardrobe Planning and Good Grooming
          - Concept and functions of clothing; classifications (clothes, accessories and cosmetics).
          - Wardrobe planning for different family members (babies, preschool children, teenagers, adults, etc.).
          - Factors to consider and benefits of planning wardrobes.
          - Good grooming: factors that promote neat, appropriate appearance; selection and correct use of clothes, accessories and cosmetics.
          - Cosmetics and health considerations.

        4. Sewing Equipment, Tools and Safety
          - Classification of sewing equipment by size (large and small) and by function (pressing, cutting, measuring, marking, etc.).
          - Selection/choice, factors affecting choice, use, care, correct handling and storage of tools and equipment.
          - Sewing machines: types, brands, advantages and disadvantages; factors affecting selection; main parts and their functions; setting, threading, routine care; common faults and simple remedies.
          - Safety rules in the use of sewing tools, equipment and machines.

        5. Basic Sewing Processes
          - Stitches: types, classification (temporary/permanent, decorative, etc.), uses and rules for working stitches.
          - Seams: types and classification, choice of seams for different fabrics and garments, rules for making seams.
          - Arrangement of fullness: gathers, pleats and similar devices; factors influencing choice and rules for working fullness.
          - Edge finishes: concept, reasons, edges that require finishing, types of finishes and points to consider when choosing suitable finishes.
          - Pockets: types, functions and basic methods of attachment.

        6. Garment Construction and Advanced Techniques
          - Essentials of garment construction: lining and interfacing (concept, importance, suitable fabrics and methods of fixing).
          - Fashion features: collars, necklines, sleeves, frills, belts and similar details – types, uses and basic working directions.
          - Patterns: types (commercial, freehand cutting, drafted and adapted patterns); taking body measurements; pattern making for simple garments (bodice, skirt, sleeve, shorts, etc.).
          - Pattern adaptation and simple alterations of basic blocks.
          - Layout, pinning, cutting out, assembling and fitting garments.

        7. Clothing Design and Figure Types
          - Elements of design (line, colour, texture, shape, etc.) and principles of design (balance, rhythm, emphasis, proportion, harmony, etc.).
          - Application of design elements and principles in clothing construction and selection.
          - Identification of common figure types for males and females and choice of suitable styles for different figures.

        8. Care and Maintenance of Clothes
          - Care labels and their meanings.
          - Stains: types, agents used for removal and basic stain-removal methods.
          - Laundry: basic steps in laundry work; washing simple fabrics such as cotton, linen and wool.
          - Dry cleaning: home sponging and commercial dry cleaning.
          - Repair, renovation and remodelling of garments; customizing clothes to extend wear and improve appearance.
          - Proper storage and packing of clothes for different seasons and purposes.

        9. Creative Fabric Crafts
          - Patchwork, appliqué and simple quilting.
          - Hand and machine embroidery.
          - Soft toys and simple stuffed items.
          - Crocheting, knitting and macramé as creative and income-generating crafts.

        10. Consumer Education and Entrepreneurship
          - Consumer and consumer education: meaning, rights of the consumer and enforcement bodies.
          - Principles of shopping for Clothing and Textiles products; budgeting vs impulsive buying; sources of products (markets, department stores, hawkers, etc.).
          - Entrepreneurship in Clothing and Textiles: meaning of entrepreneur, entrepreneurship and enterprise; characteristics, advantages and disadvantages of being an entrepreneur.
          - Requirements for Clothing and Textiles entrepreneurs (knowledge, skills, resources) and factors that promote success.
          - Types of Clothing and Textiles enterprises (tailoring/dressmaking, itinerant sewing, alteration services, etc.).
          - Steps in setting up and managing a small Clothing and Textiles enterprise (choosing a business idea, market research, business plan, day-to-day management).

        11. Organising Clothing and Textiles Exhibitions, Modelling and Fashion Shows
          - Meaning of exhibition, modelling and fashion show; simple types and purposes.
          - Factors to consider when organising events (venue, security, publicity, what to exhibit, target audience, etc.).

        Guidelines for Candidates:
        - Build strong foundations in fibre and fabric science and relate fabric properties to appropriate clothing uses and care.
        - Practise basic and advanced sewing processes regularly, including accurate body measurements, pattern work and machine skills.
        - Develop a sense of style, fit and good grooming when planning wardrobes and constructing garments.
        - Apply safe working habits, good work ethics and neatness in all practical work.
        - Explore creative fabric crafts and simple clothing-related business ideas as preparation for self-employment.
        - Use past WAEC Clothing and Textiles papers (objective, structured, essay and practical) to improve speed, accuracy and examination technique.`,
  },
  {
    subject: 'Cosmetology',
    timeAlloted: 40 * 60,
     description: `Cosmetology is a vocational subject that equips candidates with knowledge, skills and professional habits for caring for the hair, skin, face, hands and feet, and for managing or working in beauty-related enterprises.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple-choice questions covering the entire syllabus.
         • 40 minutes for 40 marks.

        - Paper 2 (Theory – Sections A and B):
         • Duration: 1 hour 20 minutes.
         • Section A: 1 compulsory question drawn from syllabus Sections A, B and G for 30 marks.
         • Section B: 3 questions drawn from syllabus Sections C, D, E and F; each carries 25 marks and candidates answer any 2.

        - Paper 3 (Practical):
         • A practical test consisting of 3 compulsory tasks based on syllabus content.
         • Duration: 1 hour for 80 marks.

        Major Syllabus Areas:

        1. Introduction to Cosmetology and Professional Ethics (Section A)
          - Meaning of cosmetology, cosmetics and cosmetician.
          - Main aspects of cosmetology (hair care, skin care, nails, make-up, etc.).
          - Professional ethics in cosmetology; examples of good and poor professional behaviour.
          - Qualities of a good cosmetologist (appearance, attitude, communication, confidentiality, reliability and interpersonal skills).

        2. General Safety Precautions and Hygiene (Section B)
          - Personal and client safety: meaning and general safety measures in the salon.
          - Personal hygiene: body cleanliness and oral hygiene.
          - Rules of personal hygiene and good grooming; grooming materials and healthy lifestyle habits.
          - Sterilisation of tools: definition, types and methods (moist and dry heat, steaming, boiling, UV light, chemical agents such as antiseptics and disinfectants).
          - First aid: meaning, contents of a first-aid box and basic treatment for common salon accidents (cuts, burns, scalds, etc.).

        3. Cosmetic Tools, Equipment and Materials (Section C)
          - General tools, equipment and materials used in cosmetology (for hair, face, nails, etc.).
          - Classification of tools, equipment and materials according to use and function.
          - Uses and basic maintenance of tools and equipment (cleaning, disinfecting, storage and handling).

        4. Hair Care, Hair Products and Barbering (Section D)
          - Types of hair (normal, dry, oily, dandruff/other scalp conditions) and general hair care practices (combing, brushing, washing, trimming, etc.).
          - General hair treatments: shampooing, conditioning, steaming, trimming, drying and styling; simple treatment plans for different hair types.
          - Scalp disorders and treatments; prevention of scalp problems and hair loss.
          - Hair loss: causes, prevention and basic nutritional/chemical treatment.
          - Hair tinting and colouring: meaning, temporary, semi-permanent and permanent colouring; tools and materials; basic procedures and safety.
          - Chemical hair relaxers: meaning, types (including cold waves, texturizers, etc.), tools and materials; procedure for relaxing hair and safety precautions.
          - Artificial hair: differences between wigs and hair pieces, types and reasons for wearing them; care and storage; tips for buying and basic procedures for fixing artificial hair and weaves.
          - Hair weaving: reasons for weaving; tools, materials and types/brands of hair pieces used; simple weaving techniques.
          - Hair barbering: meaning, barbering tools and materials and their maintenance; common hair diseases associated with barbering (bumps, lice, hair shaft damage, etc.); risks such as HIV transmission and ringworm; safety measures.

        5. Shampoos, Conditioners and Hair-Care Products (Section D)
          - Shampoos and hair conditioners: meaning and types.
          - Reasons for shampooing and conditioning the hair.
          - Techniques for shampooing and conditioning; client protection and comfort.
          - Safety precautions during shampooing and conditioning.

        6. Face and Body Care (Section E)
          - The skin: definition, basic types (oily, dry, normal, combination, sensitive) and general skin-care routines.
          - Skin-care products (cleansers, moisturisers, toners, lotions, creams, etc.) and their proper use.
          - Abuse of skin-care products and related problems.
          - Common skin problems (acne, eczema, ringworm, inflammation, etc.) and basic care/treatment advice.
          - Colour theory: primary, secondary, tertiary, neutral and intermediate colours; simple colour wheel/chart.
          - Facial make-up: types and purposes (beautification, correction, concealing defects); materials (foundations, powders, lipsticks, eye pencils, shadows, etc.) and step-by-step application.
          - Safety in applying facial make-up and maintenance/cleaning of make-up implements (brushes, sponges, masks, towels, etc.).
          - Facial defects (wrinkles, acne scars, pimples, etc.) and simple cosmetic treatments (creams, nutrition, facial exercises, etc.).
          - Lips and lipsticks: lip shapes, lip colours, types of lipsticks, techniques for applying lipsticks and lip colour.
          - Eye brows, lids and lashes: types and shapes of brows; materials used (tweezers, eye pencils, mascara, etc.); simple shaping and application techniques.

        7. Manicure and Pedicure (Section F)
          - Care of feet: effects of shoes on feet; correct shoe choice; massaging feet and trimming toenails.
          - Care of hands: massaging, oil application and trimming fingernails.
          - Nail disorders (white spots, bitten nails, brittle nails, etc.) and common nail diseases (ringworm, felon, ingrown nails, etc.).
          - Manicure and pedicure: definitions, tools and equipment (files, clippers, bowls, creams, etc.) and their maintenance.
          - Basic processes for manicuring and pedicuring and safety precautions.

        8. Entrepreneurship – Opening and Running a Beauty Salon (Section G)
          - Meaning of a salon and overview of salon services.
          - Conditions for establishing a salon: registration, licensing, compliance with local regulations and professional association requirements.
          - Salon equipment and layout: major equipment and materials needed for hair, skin and nail care.
          - Rules, regulations and laws governing salon business (health, safety, taxation, premises, etc.).
          - Media and methods for advertising salon services (radio, television, handbills, posters, newspapers, magazines, internet, etc.).

        Guidelines for Candidates:
        - Cultivate good personal grooming, hygiene and professional ethics in all practical work.
        - Practise hair-care, barbering, relaxing, colouring, weaving and wig work regularly, observing all safety and hygiene rules.
        - Build competence in basic skin care, facial make-up and nail care through repeated, supervised practice.
        - Learn the correct names, uses, maintenance routines and sterilisation methods for all salon tools, equipment and materials.
        - Explore simple plans for setting up and managing a small beauty salon and understand basic legal and advertising requirements.
        - Use past WAEC Cosmetology papers (objective, theory and practical) to strengthen exam technique, speed and confidence.`,
  },
  {
    subject: 'Crop Husbandry and Horticulture',
    timeAlloted: 60 * 60,
     description: `Crop Husbandry and Horticulture assesses candidates' knowledge and practical skills in the production, processing, storage, marketing and uses of arable, plantation and horticultural crops, as well as landscaping and related vocations.

        Aims of the Syllabus:
        - Understand the major factors (climatic, soil, biological and socio-economic) that affect crop production.
        - Demonstrate basic knowledge of the botany of arable, plantation and horticultural crops.
        - Apply principles and methods of propagating and cultivating a range of crops.
        - Manage crop-based enterprises effectively, including field operations, record keeping and simple economics.
        - Understand and apply methods for processing, preserving, storing, transporting and marketing crops and their products.
        - Identify and manage common problems associated with crop cultivation (weeds, pests, diseases and environmental stresses).
        - Appreciate the principles and practices of landscaping, home gardening, ornamental horticulture and interior decoration.
        - Explain the socio-economic importance and value chains of major crop groups.

        Examination Scheme:
        - Paper 1 (Objective):
         • 50 multiple-choice questions on the entire syllabus.
         • 1 hour for 50 marks.

        - Paper 2 (Essay):
         • 6 essay-type questions set; candidates answer any 4.
         • 2 hours for 80 marks.

        - Paper 3 (Practical / Alternative to Practical):
         • School candidates: practical paper with 4 questions; all must be answered in 2 hours for 60 marks.
         • Private candidates: alternative-to-practical written test of similar structure and weighting.

        Major Syllabus Areas:

        1. Cultivation of Field Crops (Arable Crops)
          - Factors affecting crop production: effects of solar radiation, temperature, rainfall, wind, soil (edaphic factors), biotic factors and inorganic elements on crop growth, development and yield.
          - Field crops: meaning, characteristics, common and scientific names, classification by use (cereals, grain legumes, roots and tubers, plantains, fibre, spice, sugar, oil, medicinal, aromatic, fumitory and masticatory crops) and by life cycle (annuals, biennials and perennials).
          - Distribution of field crops in West Africa and the influence of climate, soil, biological and socio-cultural factors on where crops are grown.
          - Breeding of field crops: selection of good planting materials, methods of crop improvement (introduction, selection, hybridisation) and techniques for maintaining improved varieties.
          - Seed production and marketing: factors influencing seed production, methods of production, processing, storage, marketing, qualities of good seed and factors affecting seed viability.

        2. Production of Major Field Crop Groups
          - Cereals (e.g. maize, millet, sorghum, rice): botanical attributes, classification, distribution, morphology (roots, leaves, flowers, seeds) and seed structure; land preparation; sowing/transplanting and cultural practices (supplying, thinning, weeding, fertiliser use, irrigation, pest and disease control); signs of maturity, harvesting, processing, storage, uses, value chains and marketing. Includes the life cycle of the stem borer (Busseola fusca).
          - Grain legumes (e.g. cowpea, groundnut, soya bean): classification and distribution; land preparation; sowing/transplanting and cultural practices; signs of maturity, harvesting, processing and storage; uses, value chains and marketing. Includes the life cycle of the bruchid (Callosobruchus maculatus).
          - Roots and tubers (cassava, yam, cocoyam, sweet potato, Irish potato): differences between root and tuber crops; species and varieties (including important yam and cocoyam species); morphology and anatomy of storage organs; climatic and soil requirements; planting materials (setts, minisetts, vine cuttings, suckers, bulbils, tissue-culture plantlets, etc.); land preparation (mounds, ridges), recommended spacing and orientation; cultural practices (staking, remounding, weeding, fertiliser use, pest and disease control); signs of maturity, harvesting methods (double and piece-meal), post-harvest handling, preservation, storage, processing and uses.
          - Cotton and sugar cane: botanical classification, morphology, environmental requirements and distribution in West Africa; land preparation, sowing/planting and cultural practices (including irrigation); pests and diseases and their control (including life cycle of cotton stainer, Dysdercus spp.); signs of maturity, harvesting, processing (e.g. ginning and grading of cotton; cane extraction and sugar processing stages) and uses/value chains.

        3. Production of Fruit and Tree Crops
          - Fruit crops: types (tropical and subtropical fruits such as citrus, pineapple, mango, banana, avocado pear, pawpaw, shea, etc.), scientific names, characteristics, environmental requirements and distribution in West Africa.
          - Nursery and field practices for fruit crops: land preparation; pre-nursery and nursery operations (budding, grafting, potting, soil sterilisation, shading, roguing, hardening, fertiliser application, pest and disease control, irrigation); transplanting methods (lining and pegging, planting at stake, etc.) and field maintenance (mulching, weeding, fertiliser use, pest and disease control).
          - Harvesting and post-harvest handling of fruits: signs of maturity, harvesting methods and equipment, precautions, sorting, grading, packaging, storage, pests and diseases and their control; marketing channels, quality and safety standards and fruit-crop value chains.
          - Tree crops: types and classification by use; scientific names, morphological characteristics and environmental requirements; causes and breaking of seed dormancy.
          - Establishment and management of tree crops: land preparation, nursery techniques (budding, grafting, potting, shading, weed control, fertiliser use, pest and disease control, irrigation), field planting and cultural practices.
          - Production of specific tree crops (cocoa, cashew, coconut, oil palm): classification, morphology, distribution; land preparation; nursery and field operations; pests (including cocoa capsids/mirids) and diseases; harvesting, handling, grading, processing (e.g. fermentation, drying, canning, coagulation, smoking) and storage; uses, nutritional and economic importance, value chains and marketing standards.

        4. Production of Vegetables
          - Types and characteristics of vegetable crops by use and life cycle; scientific names and economic parts of common vegetables (leafy, floral, fruit, pod/seed and storage-root vegetables).
          - Nutritional and socio-economic importance of vegetables.
          - Vegetable production systems: backyard/home gardens, market gardens, truck farming, production for processing, irrigated and dry-season vegetable farming, seed production and hydroponic systems.
          - Cropping patterns: crop rotation, intercropping, succession, relay and phased cropping – features, advantages and disadvantages.
          - Environmental requirements and criteria for site selection for vegetable enterprises.
          - Land preparation methods for vegetables (raised and sunken beds, ridges, flats), merits and demerits.
          - Nursery and field practices: seed-box/bed preparation, soil sterilisation, drilling, pricking out, hardening off, transplanting, supplying, irrigation/watering, thinning, mulching, staking, weed and pest management, fertiliser use.
          - Harvesting and post-harvest handling: signs of maturity, equipment, methods and precautions; trimming, washing, sorting, grading and packaging; processing and preservation methods (drying, curing, milling, pasteurisation, refrigeration, pickling, canning, etc.).
          - Quality and safety standards in vegetable production and marketing; vegetable seed production, storage, labelling, pricing and marketing; roles of different actors in vegetable value chains.

        5. Ornamental Horticulture and Landscaping
          - Ornamental plants: types by use and growth habit (outdoor bedding plants, shrubs, climbers, ground covers, water plants and indoor/house plants); scientific names, characteristics and functions.
          - Propagation of ornamentals: seed and vegetative methods; propagation media, containers, seed collection, cleaning, viability maintenance, nursing and planting at stake.
          - House plants: types, uses, selection and placement for specific indoor situations; tools, containers and management practices (light, air, watering, fertiliser use, pruning, pest and disease control, cleaning and polishing).
          - Establishment and management of lawns: characteristics and importance; common lawn grasses; planting materials, propagation methods, site preparation and maintenance (watering, weeding, spiking, etc.).
          - Establishment and management of hedges, beds and borders: common plants, importance and differences between beds, borders and hedges; site preparation, planting and routine care (watering, weeding, pruning, trimming).
          - Landscaping: meaning, importance (conservation, beautification, screening, green belts, windbreaks); landscape components (landform, plant materials, water, sculpture and stonework, boundaries and ground patterns); basic principles of landscape design (unity, scale, time, space, texture, light and shade, tone and colour, style, line, variety, balance, repetition, emphasis).

        6. Vocations and Enterprises in Crop Husbandry and Horticulture
          - Vocations and tasks in crop husbandry and horticulture (production, processing, input supply, produce marketing, exporting, importing, services, landscaping, etc.).
          - Educational qualifications, training and resources required for different vocations.
          - Importance of these vocations to individuals, communities and the national economy.
          - Basic enterprise development: factors and resources needed, development of a simple business plan, registration and management of small crop-based and horticultural enterprises.

        Guidelines for Candidates:
        - Participate actively in school farms and practical sessions, keeping farm diaries, practical notebooks and simple weed/plant albums.
        - Learn to identify common crops, weeds, pests and diseases and practise key field operations (land preparation, planting, weeding, fertiliser use, irrigation, harvesting and storage).
        - Practise nursery techniques, vegetable production, fruit and tree-crop establishment, and ornamental horticulture regularly.
        - Relate classroom theory on growth factors, crop botany and value chains to real farm and market situations.
        - Explore vocational and enterprise opportunities in crop production, processing, marketing and landscaping.
        - Use past WAEC Crop Husbandry and Horticulture papers (objective, essay and practical/alternative to practical) to build familiarity with exam formats, improve speed and strengthen application of knowledge.`,
  },
  {
    subject: 'Dyeing and Bleaching',
    timeAlloted: 40 * 60,
     description: `Dyeing and Bleaching is a vocational Art subject that develops candidates’ creative and technical skills in manipulating dyes, chemicals and resist techniques on fabrics to produce marketable, decorative and functional textile designs.

        Objectives:
        - Demonstrate knowledge and practical skill in the functional and productive manipulation of dyes and chemicals on fabrics.
        - Show creative ingenuity in visual representations through tie-dye, batik and printing.
        - Produce finished designed fabrics suitable for appreciation, exhibition and economic patronage.
        - Apply safety precautions and good studio practices when handling dyes, chemicals, heat and equipment.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple-choice questions drawn from the entire syllabus.
         • 45 minutes for 40 marks.

        - Paper 2 (Essay):
         • 6 essay-type questions set; candidates answer any 4.
         • 1 hour 40 minutes for 60 marks.

        - Paper 3 (Practical – Alternatives A, B or C):
         • Practical test of candidates’ skills in either Tie-dye, Batik (Wax or Starch Resist) or Printing.
         • Candidates choose and answer question(s) in ONE alternative only.
         • Work is executed on fabric using appropriate dye-resist materials and/or printing inks.
         • 30 minutes preparation time plus 4 hours working time; each alternative carries 100 marks.
         • Conducted and assessed on the spot by a WAEC-appointed visiting examiner; questions released at least 2 weeks before the test.

        Major Syllabus Areas:

        1. Introduction to Tie-Dye
          - Meaning and basic concept of tie-dye as a resist-dyeing technique.
          - Types of tie-dye designs and pattern effects.
          - Materials and tools for tie-dye: fabrics, threads/raffia/twine, rubber bands, buckets, sticks, protective gear, etc.

        2. Colour Manipulation and Motif Formation
          - Colour: definition and classes (primary, secondary, tertiary, neutral, etc.).
          - Formation of motifs for fabric designs (simple shapes, stylised natural forms, geometric and abstract motifs).
          - Using motifs to create impressions on fabric surfaces.
          - Creating repeat patterns (e.g. full repeat, mirror, half-drop, half-slide, block repeat and related arrangements).

        3. Safety and Precautions in Dyeing and Bleaching
          - Safety materials and personal protective equipment: masks, rubber gloves, aprons, cold water, petroleum jelly, etc.
          - Proper storage and labelling of dyes and chemicals.
          - Safe handling of electrical appliances, heat sources and boiling liquids.
          - Importance of safety precautions for personal health and environmental protection.

        4. Preparation of Dyes and Dye Baths
          - Meaning of dyes and basic dye chemistry at awareness level.
          - Types of dyes used in the syllabus: natural and synthetic; cold-water (reactive) dyes, warm/hot-water dyes (e.g. Dylon) and others.
          - Preparation and adjustment of dye baths (measuring, dissolving, adding salt/fixers as required).

        5. Dyeing Processes and Finishing
          - Dyeing procedures:
           • Plain/solid dyeing of fabrics.
           • Pattern dyeing using tying, pleating, folding, marbling and other resist techniques.
          - Finishing processes: oxidation, thorough washing, neutralising, starching, drying, ironing and simple packaging of dyed fabrics.

        6. Introduction to Batik
          - Meaning and concept of batik as a resist-dyeing technique.
          - Types of batik effects: freehand, splashing, crackle/cracking, blocking and others.
          - Materials and tools for batik: wax, fabrics, tjanting, foam, brushes, forms/blocks, starch (yam/flour), dyes, pots, heat sources, etc.

        7. Batik Techniques – Wax and Starch Resist
          - Wax resist method:
           • Preparation of fabric and wax; heating and handling of wax safely.
           • Wax application techniques (tjanting, brush, block, splash, etc.) and precautions.
           • Dyeing, oxidation, de-waxing and final finishing.
          - Starch resist method:
           • Meaning of starch and common sources (flour, yam, etc.).
           • Materials and tools for starch-resist work.
           • Preparation of starch paste of suitable consistency.
           • Safety and precautions in using hot starch and additives.
           • Application methods: stencilling, freehand drawing, screening and stamping.
           • Dyeing, oxidation, removal of starch and final finishing.

        8. Fabric Printing
          - Meaning of printing on fabric and how it differs from dyeing.
          - Basic printing techniques: relief processes (e.g. lino), intaglio awareness, silk-screen printing and related methods.
          - Tools, materials and equipment: blocks, lino sheets and cutters, screens and meshes, squeegees, printing tables, inks, Texfast/opaque inks, palettes, rollers, etc.
          - Motif formation and layout for print designs.
          - Production processes: preparing screens/blocks, mixing inks, printing on fabric, drying, fixing and finishing.

        9. Combined Tie-Dye and Printing; Bleaching
          - Introduction to printing on tie-dyed fabrics.
          - Techniques used to print on tie-dye: direct printing, discharge printing, resist printing, transfer printing, etc.
          - Materials and tools for combined tie-dye and printing and appropriate finishing steps.
          - Bleaching: meaning and purposes.
          - Types of bleaching: chlorine-based and oxygen-based methods.
          - Materials and tools for bleaching: fabrics, chemicals (e.g. chlorous/chlorine compounds, caustic soda, hydrosulphite, etc.), containers and protective gear.
          - Simple application of bleach on fabrics to create effects or remove colour, with strict attention to safety and fabric care.

        Guidelines for Candidates:
        - Practise tie-dye, batik and printing regularly, keeping samples and notes on methods, colour recipes and fabric types.
        - Use protective clothing and follow safety rules whenever handling chemicals, hot wax, boiling water and electrical appliances.
        - Experiment with motif creation, colour combinations and pattern repeats to develop personal design ideas.
        - Learn to prepare and finish fabrics neatly (washing, drying, ironing and packaging) for display or sale.
        - Explore how dyed, batiked and printed fabrics can be turned into products for income generation and small-scale enterprises.
        - Use past WAEC Dyeing and Bleaching papers (objective, essay and practical) to build confidence, speed and familiarity with exam expectations.`,
  },
  {
    subject: 'Edo',
    timeAlloted: 60 * 60,
    description: `Edo (Bini) at WASSCE level assesses candidates' ability to use Edo competently in speech and writing, and their understanding of Edo sound system, grammar, literature, customs and institutions.

        Aims and Objectives:
        - Communicate effectively in Edo by speaking, reading and writing accurately and fluently.
        - Become familiar with the sound system, grammatical structures and other core features of Edo.
        - Acquire the tools needed to appreciate Edo speech usage, oral and written literature, and cultural practices (customs and institutions).

        Examination Scheme:
        - Paper 1 (Objective – Language, Literature and Culture):
          • 60 multiple-choice questions covering the full syllabus.
          • 1 hour for 60 marks.
          • Section A – Language (30 items): comprehension (2 short passages), composition, sound system, grammar and translation.
          • Section B – Literature (22 items): basic principles of literary appreciation, oral literature (prose, poetry, drama) and written literature (prose, poetry, drama), largely based on the set texts.
          • Section C – Culture (8 items): Edo customs and institutions.

        - Paper 2 (Essay – Language, Literature and Culture):
          • 2 hours for 100 marks.
          • Candidates answer 6 questions in all: 3 from Language (Section A), 2 from Literature (Section B) and 1 from Culture (Section C).
          • All answers are written in Edo using the approved Edo orthography and metalanguage.

        Major Syllabus Areas:

        1. Language (Section A)
           - Composition:
             • Continuous writing of about 300 words in Edo within roughly 45 minutes.
             • Essay types include narrative, descriptive, argumentative, expository, dialogue, debate and letter writing.
           - Sound System (Phonology):
             • Edo vowels, consonants and syllabic nasals – production (place and manner of articulation) and basic phonetic/phonemic classification.
             • Syllable structure and common sound processes (e.g. vowel harmony, assimilation, elision).
             • Relationship between the sound system and Edo orthography.
             • Tones in Edo and basic tone notation.
           - Grammar:
             • Word structure and word formation.
             • Numerals (basic and derived forms).
             • Word classes: nouns, verbs, adjectives, adverbs, pronouns and others.
             • Grammatical categories: subject, object, complement, predicate, number, person, tense, aspect, mood, etc.
             • Phrases, clauses and the structure and types of sentences.
           - Comprehension and Translation:
             • Reading short Edo passages and answering questions to test understanding.
             • Translating simple passages or sentences between Edo and another language as specified in the exam instructions.

        2. Literature (Section B)
           - Basic Principles of Literary Appreciation:
             • Simple tools for analysing prose, poetry and drama (theme, plot, character, setting, style, tone, mood, etc.).
           - Oral Literature:
             • Study of selected Edo oral prose, poetry and drama texts (folktales, praise poetry, songs, enactments, etc.).
             • Appreciation of content, structure, performance features and cultural values embodied in oral texts.
           - Written Literature:
             • Study of the set Edo prose, poetry and drama texts.
             • Discussion of plot, characterisation, themes, style, language use and cultural relevance.
           - Questions on Oral and Written Literature in both Papers 1 and 2 are based mainly on the official list of set books for the examination period.

        3. Culture – Customs and Institutions (Section C)
           - Customs:
             • Traditional make-up and adornment practices.
             • Indoor and outdoor games.
             • Beliefs and practices surrounding pregnancy and its care.
             • Names and naming ceremonies.
             • Home training and social values: forms of greeting, respect for elders and superiors, cleanliness, tolerance, integrity, hard work, etc.
             • Edo foods and food-related customs.
             • Non-verbal communication in Edo culture.
             • Funerals and inheritance practices – traditional and modern approaches.
           - Institutions:
             • Traditional occupations and livelihoods.
             • Marriage customs and procedures.
             • Political and social structures: family organisation, age grades, chieftaincy, Obaship and related institutions.
             • Child care practices and beliefs.
             • Ancestors and ancestral veneration.
             • Beliefs about God (Osalobua/Osanobua), deities/divinities and other spiritual forces.
             • Modern religions (Christianity, Islam, etc.) and their impact on Edo society.
             • Economic life: advertising, money lending, cooperative ventures and communal labour.

        Guidelines for Candidates:
        - Use Edo actively in speech and writing in and out of class, paying attention to correct orthography, tone marks (where required) and grammar.
        - Practise phonology and grammar topics regularly by analysing words, sentences and short texts.
        - Read and revise all prescribed oral and written literature texts, noting key themes, characters, plots and stylistic devices.
        - Observe Edo customs and institutions in real-life contexts where possible, and relate them to the topics in the syllabus.
        - Attempt past WAEC Edo papers (objective and essay) under timed conditions to build speed, accuracy and confidence across Language, Literature and Culture components.`,
  },
  {
    subject: 'Efik',
    timeAlloted: 60 * 60,
    description: `Efik at WASSCE level tests candidates’ ability to use Efik correctly in speech and writing and their understanding of Efik sound system, grammar, literature and culture.

        Objectives of the Syllabus:
        - Enable candidates to speak, read and write Efik properly and communicate effectively in it.
        - Familiarise candidates with the sound system, grammatical structures and other core features of Efik.
        - Expose candidates to Efik culture – customs, institutions, worldview and related practices.
        - Provide tools for creative writing and for appreciating Efik language and literature.

        Examination Scheme (Composite Paper):
        - Paper 1 (Objective – Language, Literature and Culture):
          • 60 multiple-choice questions to be answered in 1 hour for 60 marks.
          • Section A – Language (30 items): comprehension (2 short passages), composition/letter writing, sound system, word and sentence structure, and translation.
          • Section B – Literature (22 items): basic principles of literary appreciation; oral literature (prose, poetry, drama) and written literature (prose, poetry, drama) based on the set books.
          • Section C – Culture (8 items): Efik customs and institutions.

        - Paper 2 (Essay – Language, Literature and Culture):
          • 2 hours for 100 marks.
          • Candidates answer 6 questions: 3 from Section A (Language), 2 from Section B (Literature) and 1 from Section C (Culture).
          • All answers are written in Efik using the standard orthography and approved metalanguage.

        Major Syllabus Areas:

        1. Language (Section A)
           - Composition and Letter Writing:
             • Continuous writing of about 300 words in Efik within roughly 45 minutes.
             • Essay types include narrative, descriptive, argumentative, expository, dialogue, debate and formal/informal letters.
           - Sound System (Phonology):
             • Efik vowels and consonants – production (place and manner of articulation) and basic phonetic/phonemic classification.
             • Syllable structure: how consonants and vowels combine in Efik words.
             • Sound processes such as vowel harmony, assimilation, elision, deletion and the distribution of key sounds in words.
             • Adaptation of loanwords into Efik.
             • Tone and intonation: high, low, falling and rising tones and their basic notation.
           - Grammar:
             • Word structure and word formation: roots, affixes (prefixes, suffixes), compounding, borrowing, reduplication and related processes.
             • Word classes: nouns, verbs, adjectives, adverbs, pronouns and others.
             • Grammatical categories: number, person, tense, aspect, negation and related categories.
             • Phrases and clauses: types and functions.
             • Structure and types of sentences (simple, compound, complex, etc.).
           - Comprehension and Translation:
             • Reading short Efik passages and answering questions to test understanding.
             • Translating simple sentences/passages between Efik and another language as specified by the exam, using correct orthography and meaning.

        2. Literature (Section B)
           - Basic Principles of Literary Appreciation:
             • Simple tools and terms for analysing prose, poetry and drama (theme, plot, character, setting, style, tone, mood, etc.).
           - Oral Literature:
             • Genres such as prose (folktales, legends, proverbs, riddles), poetry (songs, ballads) and traditional drama.
             • Study of three set books in oral literature, one for each genre; one question is set on each, and candidates answer any one.
           - Written Literature:
             • Prose, poetry and drama in written form, each represented by a set book.
             • One question is set on each written-literature text; candidates answer any one.
           - Questions in both Papers 1 and 2 (other than basic appreciation) are based on the official list of set Efik texts for the examination period.

        3. Culture – Customs and Institutions (Section C)
           - Customs (Edu Uwem):
             • Traditional occupations and forms of work (fishing, farming, trading, domestic work, etc.).
             • Good upbringing and its benefits: home training, discipline and moral values.
             • Food and food culture: common Efik dishes and their preparation/serving contexts.
             • Libation and other traditional practices.
           - Institutions (Nsionsio nka):
             • Family and kinship structures.
             • Worship and religious practices: beliefs about God (Abasi), deities (ndem) and related concepts.
             • Proverbs and their roles in communication and moral instruction.
             • Traditional entertainment and games.
             • Secret societies and other traditional institutions, with emphasis on their cultural roles.

        Guidelines for Candidates:
        - Use Efik regularly in speech and writing, paying attention to correct spelling, tone marking (where required) and sentence structure.
        - Practise phonology and grammar by analysing real examples from everyday speech, textbooks and past questions.
        - Read all prescribed oral and written literature texts carefully and revise key themes, characters, plots and stylistic features.
        - Observe Efik customs and institutions in family and community life and relate them to the culture topics in the syllabus.
        - Work through past WAEC Efik papers (objective and essay) under exam-like conditions to improve speed, accuracy and confidence across Language, Literature and Culture sections.`,
  },
  {
    subject: 'Electrical Installation and Maintenance Work',
    timeAlloted: 45 * 60,
    description: `Electrical Installation and Maintenance Work is a trade subject that assesses candidates’ knowledge and skills in basic electricity, domestic and industrial wiring, installation and repair of electrical machines, fault-finding, and small-scale business management in the trade.

        Aims and Objectives:
        - Understand and apply safety rules and regulations in electrical workshops and on sites.
        - Identify, select, use and maintain electrical installation tools, materials and accessories correctly.
        - Plan, install, inspect and test domestic and basic industrial wiring systems.
        - Install, test, maintain and repair common electrical machines and equipment, including simple rewinding operations.
        - Demonstrate basic entrepreneurship skills related to setting up and running a small electrical installation and maintenance business.

        Examination Scheme (Composite Paper):
        - Paper 1 (Objective):
          • 40 multiple-choice questions covering the entire syllabus.
          • 45 minutes for 40 marks.

        - Paper 2 (Essay and Practical):
          • Section A – Essay:
            - 3 essay questions set; candidates answer any 2 in 1 hour for 50 marks.
          • Section B – Test of Practical Work:
            - 2 compulsory practical questions drawn from the workshop practice and machine/maintenance sections of the syllabus.
            - 1 hour 15 minutes for 50 marks.
          • Total for Paper 2: 2 hours 15 minutes and 100 marks.

        Major Syllabus Areas:

        1. Workshop Safety Rules and Regulations
           - Common hazards and causes of accidents in electrical workshops.
           - Dangers arising from faulty equipment and tools, chemicals, water, poor joints, insulation failure and moving machines.
           - Precautions in handling electrical tools and materials; care and maintenance of tools.
           - First aid for electrical accidents: contents of a first-aid box and treatment for cuts, burns and electric shock; safe procedures for removing a casualty from live conductors and administering artificial respiration.
           - Statutory regulations: electricity supply regulations, factory acts, IEE regulations – aims, importance and basic applications.

        2. Electrical Installation Tools, Materials and Accessories
           - Measuring, marking and striking tools: types, uses and basic care.
           - Cables: types, sizes and preparation; armoured cables (reasons for armouring, parts, joints and terminations); data cables (computer, fibre-optic, coaxial) and their uses; other special cables (sheathed, screened/H-type, gas-pressurised, MICC, etc.).
           - Electrical accessories: switches, sockets, lamp holders, ceiling roses, joint boxes, plugs and other fittings; their uses and regulations guiding application.
           - Cells: primary and secondary cells, simple working principles and construction of a simple cell.

        3. Workshop Practice and Wiring
           - Working drawings and BS symbols: drawing and identification of standard electrical symbols (switches, sockets, lighting points, cooker outlets, etc.).
           - Generation, transmission and distribution: simple circuit diagrams and basic layout of systems.
           - Surface wiring:
             • Materials (cables, clips, nails, etc.) and tools (hammers, screwdrivers, pliers, etc.).
             • Factors affecting cable current rating (conductor type, ambient temperature, protective devices, etc.).
             • Wiring simple lighting and socket circuits, including a point of light controlled from two positions (two-way switch).
           - Conduit wiring:
             • Meaning, types (PVC, galvanised, surface, concealed), advantages and disadvantages.
             • Identification and uses of conduit systems.
             • Installation procedures: piping, wiring and fitting.
             • Conduit boxes and fittings (end box, angle, tee, 4-way, loop-in, junction box, knock-out box, couplers, etc.), tools (bending springs, bending machines, hacksaws, etc.) and materials (conduit pipes, boxes, adhesives, etc.).
           - Illumination:
             • Concept of illumination and related terms (illumination, luminous flux, luminous intensity).
             • Factors affecting illumination; types of lamps; simple calculations involving illumination.
             • Measurement of illumination using a photometer.
           - Soldering, Joints and Terminations:
             • Types of solders and fluxes; operation and maintenance of blowlamps; use of pots and ladles for larger cables.
             • Conducting materials (copper, aluminium, silver) – properties, advantages and disadvantages; heat transfer effects.
             • Insulating materials (PVC, rubber, paper, mica, bakelite, etc.).
             • Types of cable joints (tee, married, twist, telescope, scarf) and preparation of cables for joints and terminations.
             • MICC cable preparation and termination; tools, materials, advantages, disadvantages and relevant IEE regulations.

        4. Installation – Underground, Overhead and Machines
           - Underground cable installation:
             • Types of cables used; trench preparation; laying techniques; tools and materials; relevant IEE regulations.
           - Overhead line installation:
             • Tools, cables/wires and equipment; types of insulators and stay wires; drawing of lines and cross-arms; joints, terminations and electrical continuity.
           - Inspection and testing of domestic installations:
             • Types and sequence of tests (polarity, insulation resistance, earth continuity, ring circuit continuity, etc.).
             • Importance of inspection and testing; common instruments (insulation resistance tester/megger, bell set, ohmmeter).
           - D.C machines:
             • Types of DC motors and generators, main parts and construction; basic operation and applications.
           - A.C machines:
             • Types of AC motors and generators, identification, construction features, operation and applications.
             • Differences between AC and DC machines.
           - Installation of machines and starters:
             • Types of enclosures and their applications.
             • Starters: star-delta, direct-on-line, auto-transformer – selection and basic operating principles.
             • Tests for correct rotation, short circuit and earth faults; simple installation procedures for AC and DC machines.

        5. Rewinding and Maintenance of Electrical Machines
           - Tools and equipment for rewinding: identification and uses (screwdrivers, spanners, winding machines, crimping tools, mallets, etc.).
           - Winding drawings: simple wave and lap windings; interpreting diagrams and determining coil span per pitch/phase/pole; locating coil ends on commutator/slip rings for fixed brushes.
           - Rewinding of single-phase motors: conductor sizes, insulating materials, coil connection, continuity and earth tests.
           - Armature, commutator and slip rings: skimming, undercutting, checking commutator effectiveness.
           - Protective devices: fuses and circuit breakers (voltage and current operated) – working principles, applications and regulations for installation.
           - Maintenance of electrical equipment and machines:
             • Types of maintenance (preventive and corrective) and basic procedures.
             • Grades of lubricants and their uses.
             • Constructional features of common appliances (heaters, boilers, irons, etc.).
           - Dismantling, assembling and repair:
             • Safe dismantling procedures, reading nameplates and recording data.
             • Causes, signs and symptoms of faults; interpretation of circuit diagrams; basic troubleshooting (e.g. bad bearings, winding faults).
             • Inspection, performance testing and simple repairs.

        6. Entrepreneurship in Electrical Installation
           - Sources of funds and ways of sourcing funds for small businesses.
           - Simple proposal writing, budgeting and financial planning.
           - Basic management of a small electrical installation and maintenance outfit.
           - Government rules and regulations affecting business registration and operation.

        Guidelines for Candidates:
        - Observe strict safety practices in all workshop and site activities, and learn first-aid responses for electrical accidents.
        - Practise reading and drawing circuit and wiring diagrams, and carry out basic wiring, soldering, jointing and installation tasks.
        - Gain hands-on experience with inspection, testing, machine installation, simple fault-finding and basic rewinding.
        - Learn the names, uses and maintenance procedures for common tools, instruments, cables, accessories and protective devices.
        - Explore basic business concepts (costing, budgeting, registration) for starting a small electrical installation and maintenance service.
        - Use past WAEC Electrical Installation and Maintenance Work papers (objective, essay and practical) to build confidence, speed and familiarity with exam requirements.`,
  },
  {
    subject: 'Electronics or Basic Electronics',
    timeAlloted: 60 * 60,
    description: `Basic Electronics/Electronics equips candidates with foundational knowledge of electronic principles, components and circuits, and practical skills for building, testing and troubleshooting simple electronic systems, while also preparing them for further study or electronics-related vocations.

        Objectives:
        - Understand the basic concepts and principles that underlie electronic devices and systems.
        - Use simple electronic devices and components to build and test basic electronic circuits and systems.
        - Develop problem-solving skills through the design and analysis of electronic circuits.
        - Prepare for further work or training in electronics and related fields.
        - Apply entrepreneurial skills and sound work ethics in electronics-related activities.

        Examination Scheme:
        - Paper 1 (Objective):
          • 50 multiple-choice questions on the whole syllabus.
          • 1 hour for 50 marks.

        - Paper 2 (Short-Structured Theory):
          • 7 short-structured questions set; candidates answer any 5.
          • 1 hour for 50 marks.

        - Paper 3 (Practical or Alternative to Practical):
          • Practical: 2 experiments, both compulsory, to be carried out in 3 hours for 100 marks.
          • Alternative to Practical (where actual materials are unavailable): 2 compulsory theory questions testing practical skills, to be answered in 2 hours for 100 marks.

        Major Syllabus Areas:

        1. Electron Emission and Measuring Instruments
           - Electron emission:
             • Types of electron emission (thermionic, photoemission, secondary, field emission) and qualitative treatment.
             • Applications in devices such as diodes, triodes, tetrodes, pentodes and cathode-ray tubes.
           - Measuring instruments:
             • Concept and classification (analogue and digital).
             • Types and uses of basic instruments: multimeters, voltmeters, ammeters, ohmmeters, oscilloscopes, etc.
             • Simple principles of operation and protection of instruments.

        2. Semiconductors and Devices
           - Semiconductors:
             • Concept of semiconductors and common materials (silicon, germanium, etc.).
             • Doping and the formation of p-type and n-type semiconductors.
           - Semiconductor diodes:
             • Concept, types and ratings (voltage, current, power).
             • Basic principles of operation and applications.
             • Construction and testing of simple circuits using p-n junction diodes; I–V characteristics in forward and reverse bias (qualitative practical demonstration).
           - Transistors (BJT and MOSFET):
             • Meaning and basic structure; transistor biasing and applications (switch, inverter, amplifier).
             • Qualitative BJT characteristics; input, output and transfer characteristics; common configurations (e.g. common emitter).
             • Advantages of transistors over valves and of MOSFETs over BJTs.
           - Other semiconductor devices:
             • Thermistors, diacs, triacs, thyristors, etc. – qualitative principles and simple applications.
           - Integrated circuits (ICs):
             • Formation, functions and advantages over discrete components.
             • Basic circuit symbols and simple applications.
             • Awareness of RAM, ROM and EPROM.

        3. Circuit Analysis and Components
           - Electric current and materials:
             • Structure of the atom; conductors and insulators and their uses.
             • Direct and alternating currents; sources of d.c. and a.c.
           - Relationship between voltage, current and resistance:
             • Definitions of current, voltage, resistance.
             • Ohm’s law and simple calculations; construction of a simple circuit to demonstrate Ohm’s law.
           - Electric power:
             • Concept of electric power; $P=VI$ and related formulae; calculations in given circuits.
           - Circuit components:
             • Types of resistors, capacitors and inductors; symbols, units and basic ratings.
             • Colour coding and rating of resistors and capacitors; practical determination of fixed resistor values.
           - Electric circuits:
             • Simple circuit boards and arrangements: series, parallel and series-parallel.
             • Practical wiring and calculations for these circuit types.

        4. A.C. Circuits and Power
           - R–L–C circuits:
             • Concepts of capacitive reactance, inductive reactance and impedance.
             • RL and RC circuits; calculations involving $X_C$, $X_L$ and resonance frequency (qualitative and quantitative).
           - Generator principles:
             • Basic principle of operation of an a.c. generator.
           - Power in a.c. circuits:
             • Power, power triangle and power factor; importance and correction of low power factor (advantages and disadvantages).
             • Simple calculations involving power factor, Q-factor and bandwidth.

        5. Amplifiers
           - Voltage amplifiers:
             • Biasing methods; single-stage transistor amplifiers, especially the common-emitter amplifier.
             • Frequency response of an amplifier; effect and advantages/disadvantages of negative feedback.
           - Power amplifiers:
             • Classes (A, B, AB, C); applications, power gain, biasing methods and efficiency.
             - Push-pull amplifiers:
             • Qualitative treatment; matched and complementary pairs.
           - Operational amplifiers (op-amps):
             • Properties of an ideal op-amp; inverting and non-inverting amplifiers.
             • Types and simple applications (summing amplifiers, voltage followers, etc.).
             • Basic calculations involving inverting, non-inverting and summing configurations.

        6. Power Supplies and Rectification
           - D.C. power supply units:
             • Common sources: dry cells, solar cells, accumulators, etc.; rechargeable and non-rechargeable batteries.
           - Rectification and regulation:
             • Qualitative treatment of rectification and smoothing.
             • Voltage regulators: awareness of diac/triac/thyristor regulators, series regulators and transistorised electronic voltage regulators.
             • Functions of basic blocks in a regulated d.c. power supply.

        7. Oscillators, Multivibrators and Digital Basics
           - Oscillators:
             • Difference between positive feedback (oscillator) and negative feedback (amplifier).
             • Principles of oscillation; types such as Hartley, Colpitts, phase-shift, tuned-load and crystal oscillators.
             • Block diagrams and basic applications of oscillators.
           - Multivibrators (non-sinusoidal):
             • Types: monostable, bistable and astable; basic principles and uses.
           - Digital basics:
             • Number systems: binary, octal, hexadecimal; simple conversions between bases; addition and subtraction in binary.
             • Logic gates: AND, OR, NOT, NAND, NOR (combinational gates) – qualitative treatment using switches, truth tables and simple Boolean expressions.

        8. Communication Systems, Transducers and Sensors
           - Communication systems:
             • Electromagnetic waves: basic characteristics and relationship between velocity, frequency and wavelength.
             • Radio communication: modulation and demodulation, advantages of FM over AM (phase modulation by mention only).
             • Radio receivers: types; advantages of superheterodyne receivers over direct-input receivers.
             • Fault detection and simple repair in radio receivers; project work on constructing or designing a simple radio receiver.
             • Block diagrams of AM/FM transmitters, AM/FM superheterodyne receivers, and mono/colour TV receivers; functions of each block and signal flow; awareness of TV standards (NTSC, PAL, SECAM, etc.).
             • Modern communication methods: fibre optics, microwave, satellite, cellular phones, digital communication networks.
           - Transducers and sensors:
             • Meaning and principles of transducers and sensors.
             • Types and uses: acoustic, dynamic, electrostatic, electromagnetic, capacitive, pressure sensors, photoelectric and proximity sensors.
             • Thermistors as temperature-sensing devices; acoustic transducers (loudspeakers, microphones, earphones) – basic operation and applications.

        9. Control Systems and Electromagnetism
           - Control systems and servomechanisms:
             • Open-loop and closed-loop control circuits and simple principles of operation.
             • Qualitative understanding of servomechanisms and typical applications (e.g. doors, car systems, booths).
           - Magnetic and electric fields; electromagnetic induction:
             • Magnetic fields around current-carrying conductors; tracing lines of force.
             • Lenz’s and Faraday’s laws; self and mutual induction (definitions and simple applications).
             • Calculations involving energy stored in a coil.
             • Applications of electromagnetism in devices like electric bells, solenoids, loudspeakers, buzzers, moving-coil and moving-iron instruments, microphones and earphones.

        Guidelines for Candidates:
        - Build a solid grasp of semiconductor theory, basic circuit laws and a.c./d.c. principles before moving to more complex topics.
        - Practise assembling and testing simple circuits on breadboards or strip boards and interpreting circuit diagrams and data sheets.
        - Use appropriate measuring instruments (multimeters, oscilloscopes, etc.) safely and correctly.
        - Work through design-and-build tasks (small amplifiers, power supplies, radio projects) to strengthen problem-solving skills.
        - Explore basic entrepreneurial ideas in electronics repair, assembly and small project design.
        - Use past WAEC Basic Electronics/Electronics papers (objective, structured and practical/alternative) to improve speed, accuracy and familiarity with exam formats.`,
  },
  {
    subject: 'Fisheries (Alt A)',
    timeAlloted: 60 * 60,
     description: `Fisheries (Alternative A) is for candidates in Ghana and focuses on the economic, ecological and social importance of fisheries, sustainable exploitation of aquatic resources, and practical skills in fish farming, processing and fisheries-related entrepreneurship.

        Objectives:
        By the end of the course, candidates should be able to:
        - Explain the importance of fisheries in the socio-economic development of West African countries.
        - Recognize the dangers of overfishing and harmful fishing practices and suggest more sustainable alternatives.
        - Describe and apply key national regulations and traditional practices that govern fishing and resource use.
        - Distinguish between freshwater, brackish water and marine habitats, their characteristic species and resources.
        - Demonstrate basic skills in aquaculture (ponds, tanks, cages, aquaria) and fish husbandry.
        - Apply basic entrepreneurship and record-keeping skills in fisheries-related vocations and small businesses.
        - Explain the effects of water pollution and invasive species on fish stocks and habitats.
        - Describe and use basic fish preservation and processing techniques to maintain quality and value.
        - Show understanding of the basic biology, ecology, genetics and health of fish and other fishery organisms.
        - Apply simple fish health management, disease prevention and control measures in culture systems.

        Requirements (School Facilities & Exposure):
        - Schools should have at least an aquarium and a fish pond or concrete tank for routine practical work.
        - Teaching should be supported by visits to established fish farms, fisheries research institutes, landing sites and related enterprises.
        - Candidates are expected to keep detailed practical notebooks covering laboratory work, aquarium and pond observations, field trips and collected specimens.
        - Schools are encouraged to build and maintain an album of fishery organisms, fishing gear and craft, and fish-rearing facilities/equipment for teaching.

        Examination Scheme:
        - Paper 1 (Objective):
         • About 50 multiple-choice questions on the whole syllabus.
         • 1 hour for 50 marks.

        - Paper 2 (Essay):
         • 6 essay-type questions set; candidates answer 4.
         • 2 hours for 80 marks (20 marks per question).

        - Paper 3 (Practical / Alternative to Practical):
         • School candidates: practical paper based on hands-on activities in fisheries and aquaculture.
         • Private candidates: alternative-to-practical paper testing practical skills and interpretation.
         • 3 compulsory questions to be answered in about 2 hours for 60 marks.

        Major Syllabus Areas (Overview):

        1. Introduction to Fisheries and Fishery Organisms
          - Meaning and types of fisheries (culture/aquaculture and capture fisheries at subsistence, artisanal, commercial and industrial scales).
          - Importance of fisheries to food supply, employment, income generation and cultural life.
          - Identification and description of major fishery organisms (finfishes, crustaceans, molluscs and others) and their habitats.
          - Characteristics of freshwater, brackish water and marine environments and their typical species.
          - Invasive alien species in aquatic habitats, their characteristics, effects on fisheries and control/prevention measures.
          - Grouping of major fishery organisms under different habitat types.

        2. Fishing Activities, Gear and Craft
          - Major fish landing sites in the candidate’s country (beaches, harbours, lagoons, river banks, lake shores) and their facilities.
          - Activities at landing sites (unloading, fuelling, repairs, processing, marketing) and good sanitation practices, including waste and oil-spill management.
          - Classification, description and maintenance of active and passive fishing gear (nets, hooks, traps, etc.) and their merits/demerits.
          - Types and upkeep of fishing craft (canoes, trawlers, purse seiners) and accessories (engines, winches, navigation aids).
          - Fishing methods used in inland, coastal and deep-sea fisheries; recognition and control of harmful fishing practices.

        3. Fish Biology and Ecology
          - Identification and basic classification of common fishery organisms (including common and scientific names and higher taxa).
          - Fish measurements (lengths, weights) and external features (body form, fins, scales, lateral line, etc.).
          - Internal organs of bony fishes (gills, alimentary canal, heart and blood vessels, kidneys, gonads) and their functions.
          - Life processes in fishes: locomotion, feeding and digestion, circulation, respiration, excretion, reproduction and growth.
          - Environmental conditions in fish habitats (temperature, dissolved oxygen, salinity, pH, turbidity, light, nutrients, upwelling) and their effects on fish.
          - Ecological processes in aquatic systems: feeding relationships, predation, competition, food chains/webs, food pyramids, mortality and adaptation.
          - Aquatic pollution: causes, effects on fish and habitats, and prevention/control strategies.
          - Basic genetics as applied to fish (genes, chromosomes, genotype/phenotype) and simple applications to fish breeding and strain improvement.

        4. Aquaculture and Aquarium Work
          - Meaning, importance and current status of aquaculture in the candidate’s country (species farmed, farm types, facilities, production levels, challenges and prospects).
          - Constraints to aquaculture (expertise, cost of ponds and inputs, access to seed, feed and credit) and possible solutions.
          - Aquarium construction, stocking, daily management, record-keeping and cost tracking.
          - Introduction to fish farming: levels of intensity (extensive, semi‑intensive, intensive) and culture systems (monoculture, polyculture, integrated).
          - Fish production facilities: earthen ponds, cages, pens, concrete tanks, raceways and criteria for site selection.
          - Pond construction steps (site clearing, excavation, dyke/wall formation, fitting inlets/outlets, grassing) and routine pond management.
          - Stocking (species selection, fingerling sourcing, handling, packaging and transport) and maintenance (water level, leaks, predators, weeds, fertilization).
          - Water quality monitoring (pH, dissolved oxygen, turbidity, temperature, ammonia) and basic corrective measures.
          - Fish feeds and feeding: types of feeds and ingredients, simple diet formulation, feeding schedules and rationing.
          - Harvesting methods (partial/total harvest) and post-harvest pond preparation (draining, cleaning, refilling).
          - Common fish diseases, symptoms and basic prevention/treatment; link between environmental conditions and disease outbreaks.

        5. Fish Utilization, Processing and Preservation
          - Nutritive value of fishery organisms (proteins, fats, minerals, vitamins, water) and simple tests for nutrients.
          - Distinction between fish processing and fish preservation and the reasons for both (quality, shelf life, value addition).
          - General principles of processing and preservation: reducing microbes and moisture, slowing enzyme action and spoilage, protecting fats, etc.
          - Common processing operations (washing, scaling, gutting, filleting) and basic tools/equipment.
          - Traditional and modern preservation methods (smoking, drying, salting, frying, freezing, canning, pickling, irradiation, etc.) and associated equipment.
          - Packaging materials and methods for fresh and preserved fish for local and export markets.
          - Fish products (fillets, chunks, canned, smoked, dried, salted, marinated forms) and by‑products (oil, entrails, bones) and their uses.
          - Fish spoilage: signs, causes (microbial, enzymatic, oxidation), effects on quality, value, income and public health.

        6. Fisheries Management, Policy and Business
          - Meaning and goals of fisheries management (sustainable yields, spawning stock protection, conservation of habitats).
          - Management strategies: regulating number of fishing units, mesh sizes and catch quotas; closed seasons, protected areas and traditional practices.
          - Role of data (catch, effort, length/weight, age, gear type) and simple analysis for management decisions; seasonal patterns in catches and upwelling.
          - National policies and regulations on fisheries (subsidies, stakeholder roles, imports/exports, protection of juveniles and habitats).
          - International laws and conventions affecting fisheries (e.g. EEZ, conservation conventions and lists of endangered species) and their importance.
          - Budgeting and simple financial projections for fisheries enterprises (culture, capture, gear/craft manufacture, processing, marketing).
          - Pricing of fish and fish products in relation to demand, supply and quality.
          - Fish marketing: quality control, packaging, storage, transport, major markets and companies, and issues/solutions in marketing chains.
          - Supply and value chains in the fishery industry and responsibilities of different actors.

        7. Fishing Communities, Institutions and Careers
          - Important freshwater and marine fishing communities in the candidate’s country and their roles in the fishery sector.
          - Cultural festivals, taboos and other traditional practices that influence fishing effort, closed seasons and resource conservation.
          - Fisheries training, research and regulatory institutions, their objectives and major activities.
          - Job and business opportunities in the fisheries sub‑sector (teaching/research, fish farming, pond engineering, processing, marketing, gear/craft making, cold storage, input supply, etc.).
          - Steps and basic requirements for establishing fisheries enterprises (identifying opportunities, assessing demand, planning resources, preparing business plans, registration and management).
          - Role of fisheries extension services in farmer and fisher education, technology transfer and enforcement of regulations.

        8. Practical Skills and Fieldwork Emphasis
          - Drawing, labelling, identification and correct use/maintenance of common fishing gear and craft.
          - Identification and classification of freshwater, brackish water and marine fishes and other fishery organisms, including invasive species.
          - Dissection and observation of external and internal structures of representative fish and invertebrates.
          - Measurement of key environmental parameters in natural and artificial water bodies using basic water-test kits.
          - Construction and interpretation of food chains, food webs and food pyramids in aquatic systems.
          - Identification of characteristics of fresh and spoiled fish and of organisms associated with spoilage.
          - Practical work in fish processing, preservation, pond construction, feed formulation, fertilization and routine pond and stock management.

        Guidance for Candidates:
        - Build a strong conceptual foundation in aquatic habitats, fish biology and basic ecology before moving into management and business topics.
        - Engage fully in practical sessions (ponds, aquaria, field trips, landing-site visits) and keep detailed, organised practical notebooks.
        - Practise identifying common fishery organisms, habitats, gear and craft using local examples and photographs.
        - Work through past WAEC Fisheries papers and use the structure of Papers 1, 2 and 3 to guide revision, time management and practical preparation.
        - Explore simple business ideas (e.g. small-scale fish farming, processing and marketing) using realistic budgets and records to build entrepreneurial skills.
        - Keep up-to-date with local regulations, community practices and environmental issues (pollution, invasives, climate variability) that affect fisheries in your area.`,
  },
  {
    subject: 'Fisheries (Alt B)',
    timeAlloted: 40 * 60,
    description: `Fisheries (Alternative B) is for candidates in Nigeria and treats fisheries as a vocational trade for livelihood, emphasizing practical knowledge, skills and entrepreneurship across capture fisheries, aquaculture, post-harvest handling and small-scale fisheries business.

        Objectives:
        By the end of the course, candidates should be able to:
        - Explain the role of fisheries in the socio‑economic development of Nigeria and West Africa (food, employment, income, culture, health).
        - Demonstrate basic skills in both capture fisheries and culture fisheries (aquaculture).
        - Apply foundational entrepreneurial skills relevant to fisheries‑related vocations and micro‑enterprises.
        - Recognize and discuss the causes and effects of water pollution on fish, habitats and fisheries livelihoods.
        - Describe and use common fish processing, preservation and marketing techniques to maintain quality and value.

        Requirements (School Facilities & Learning Resources):
        - Each school offering Fisheries should have at least a small glass or plastic tank/aquarium and a pond or concrete tank for fish culture.
        - Teaching should be supported by visits to functional fish farms, fisheries research institutions, fishing companies and related agencies.
        - Candidates are expected to keep detailed practical notebooks covering laboratory work, observations in tanks/aquaria and ponds, field trips and collected specimens.
        - Schools should build and maintain an album of fishery organisms, fishing gear and craft, and fish‑rearing facilities and equipment for teaching and revision.

        Examination Scheme:
        - Paper 1 (Objective):
          • 40 multiple‑choice questions on the whole syllabus.
          • 40 minutes for 40 marks.

        - Paper 2 (Essay):
          • 6 essay questions set; candidates answer 4.
          • 2 hours for 80 marks.

        - Paper 3 (Practical / Alternative to Practical):
          • School candidates: practical paper based on hands‑on activities in fisheries and aquaculture.
          • Private candidates: alternative‑to‑practical paper testing practical skills and interpretation.
          • 4 compulsory questions to be answered in about 1½ hours for 60 marks.

        Major Syllabus Areas (Overview):

        1. Basic Concepts in Fisheries
           - Meaning of fishery and fisheries and the distinction between them.
           - Sub‑divisions of fisheries: capture fisheries (subsistence, artisanal, industrial) and culture fisheries (aquaculture).
           - Importance of fisheries (nutrition, employment, income generation, socio‑cultural roles, aesthetics, medicinal uses, etc.).

        2. Processes of Fish Production
           - Capture fisheries:
             • Common methods: hook and line, cast nets, set nets, traps, trawling, harpooning and related techniques.
             • Materials and tools used (hooks, cages, knives, traps, nets, etc.) and their uses.
           - Culture fisheries:
             • Desirable qualities of culturable species (hardiness, tolerance of varied water quality, acceptance of artificial feeds, ability to breed in captivity).
             • Identification of common culturable organisms (e.g. Clarias, Tilapia, Heterobranchus, shrimps and seaweeds) and basic culture facilities (ponds, tanks, raceways, cages, pens).

        3. Fish Culture Facilities, Systems and Water Quality
           - Fish ponds:
             • Types of ponds and tanks (earthen, concrete, plastic, fibre glass, etc.).
             • Main components: inlets, outlets, dykes/embankments/walls, monks and spillways.
           - Aquaculture systems:
             • Types of aquaculture: monoculture, polyculture and integrated fish farming.
             • Management systems: extensive, semi‑intensive and intensive.
           - Water quality control and monitoring:
             • Meaning of water quality and its importance for fish health and survival.
             • Key parameters (dissolved oxygen, pH, temperature, turbidity, conductivity) and typical acceptable ranges.
             • Simple monitoring methods (e.g. meters, test kits, chemical tests) and recognition of water pollution, its causes and basic prevention/control measures.

        4. Fish Feeds and Feeding
           - Fish feed and food materials:
             • Distinction between natural food (phytoplankton, zooplankton) and formulated feeds.
             • Common energy sources (e.g. cereal by‑products) and protein sources (e.g. oilseed cakes, fish meal) and their nutritive value.
           - Fish feeding:
             • Feeding regimes expressed as a percentage of body weight (e.g. 3–5% depending on size/age).
             • Ideal feeding periods, feeding frequencies and simple records.
             • Feeding methods such as broadcasting, spot/point feeding and use of feeders/automatic devices.

        5. Fish Pond Preparation and Management
           - Pond construction:
             • Site selection (topography, soil, water supply), layout, staking and excavation.
             • Construction steps for earthen and concrete ponds, including dyke building and structural integrity.
           - Pond preparation:
             • Tools used (hoes, spades, head pans, wheelbarrows, cutlasses, etc.).
             • Activities for new and old ponds: draining/flushing, repairing leakages or cracks, liming and fertilization, drying and cleaning.
           - Pond management practices:
             • Regular monitoring of water quality, observation of fish behaviour, checking for leaks and seepage.
             • Feeding routines, stocking time and stocking rates, predator and weed control.

        6. Fish Harvesting and Post‑Harvest Operations
           - Harvesting:
             • Fishing gear used for harvesting cultured fish and their construction, mending and maintenance.
             • Methods of partial and total harvesting from ponds and cages.
             • Types of fishing craft and their basic care.
           - Post‑harvest handling:
             • Basic processing and preservation methods and the distinction between them.
             • Handling fresh fish to maintain quality (sorting, washing, dressing, chilling, etc.).
             • Preservation techniques (e.g. smoking, drying, salting, freezing, sun‑drying) and associated equipment.
             • Packaging materials and methods (boxes, baskets, plastics, cartons, etc.) and simple marketing channels for fish.

        7. Fish Seed Production (Hatchery Work)
           - Broodstock selection and handling:
             • Differentiating male and female sexually mature fishes and identifying good breeders (vigour, size, lack of defects, breeding performance).
             • Proper handling and transport of broodstock (timing, duration, stress reduction).
           - Artificial breeding:
             • Concept of artificial breeding and reasons for its use (high‑quality seed, large seed output, reliable supply of fingerlings).
             • Key steps: broodstock selection and conditioning, inducement, stripping, fertilization and incubation.
             • Common equipment and materials (basins, happa nets, hatching troughs, tanks, syringes, microscopes).
           - Managing and nursing fish seed:
             • Maintaining good water conditions (aeration, cleanliness), feeding with natural food and gradually introducing formulated feed.
             • Removing dead/unfertilized eggs, protecting fry and fingerlings, grading and transferring to grow‑out ponds or tanks.

        8. Practical Fisheries and Skills Development
           - Measurement of environmental conditions (temperature, dissolved oxygen, pH, turbidity) using simple instruments.
           - Identification, use and maintenance of common fisheries tools and equipment (pumps, pelleting machines, aerators, secchi discs, etc.).
           - Identification of common culturable species in Nigeria, including their scientific names.
           - Identification and use of feed ingredients, fertilizers and liming materials.
           - Identification, description, use and basic maintenance of fishing gear and craft.
           - Identification of processed and preserved fish products, processing/preservation equipment and fish by‑products.
           - Identification and use of packaging materials for fish and awareness of basic marketing practices.

        Facilities and Equipment (Indicative):
        - The syllabus assumes access to essential water testing tools (e.g. DO meter, pH meter, thermometers, test kits), aquaria and nursery/demonstration ponds.
        - It also assumes basic gear and craft models (gill nets, cast nets, seines, traps, hooks, canoes/paddles), processing and preservation equipment (knives, balances, freezers, ovens, kilns, drying racks), and standard packaging materials.

        Guidance for Candidates:
        - Treat Fisheries as both a science and a trade: build sound understanding of concepts while practising hands‑on skills.
        - Engage fully in practical sessions, field trips and demonstrations, and keep well‑organised practical notebooks and records.
        - Regularly revise definitions, processes and simple quantitative ideas (e.g. stocking rates, feeding levels, basic water‑quality ranges).
        - Use local examples of fish species, farms, landing sites and markets to make concepts concrete.
        - Work through past WAEC Fisheries (Alternative B) papers, using the structure of Papers 1, 2 and 3 to guide exam preparation and time management.
        - Explore small, realistic fisheries business ideas (e.g. backyard ponds, simple processing and marketing) to build entrepreneurial confidence and skills.`,
  },
  {
    subject: 'Foods and Nutrition',
    timeAlloted: 60 * 60,
     description: `Foods and Nutrition focuses on the science of food and its relationship to health, alongside the practical skills needed to select, prepare, cook and serve wholesome meals for individuals, families and larger groups, and to apply these skills in the food and catering industry.

        Objectives:
        By the end of the course, candidates should be able to:
        - Demonstrate basic knowledge of foods, nutrients, nutrition and health.
        - Explain how good nutrition supports growth, health, work capacity and quality of life.
        - Identify and explore careers in foods, nutrition and catering and acquire foundational skills for further study or work.
        - Apply general principles of meal planning, food selection, preparation, cooking and service for different people and occasions.
        - Plan efficient, safe and hygienic kitchens for home, institutional and commercial food production.
        - Select, use, care for and store kitchen tools, equipment and appliances correctly.
        - Practise high standards of personal, kitchen and food hygiene and understand basic food safety laws and risks.
        - Apply principles of food processing, preservation and storage to minimize waste and maintain quality.
        - Use basic consumer‑education concepts when budgeting, shopping and evaluating food products and services.
        - Carry out simple research and experiments, adapt and improve local dishes and drinks, and document results.
        - Use acquired skills to set up or contribute to small enterprises in the food and catering sector.

        Examination Scheme:
        - Paper 1 (Objective):
         • 60 multiple‑choice questions on the whole syllabus.
         • 1 hour for 60 marks.

        - Paper 2 (Essay):
         • 6 essay questions set; candidates answer 4.
         • 1 hour 15 minutes for 40 marks.

        - Paper 3 (Practical):
         • Practical test of food preparation and service skills.
         • About 3 hours for 100 marks, usually assessed by a visiting examiner.

        Major Syllabus Areas (Overview):

        1. Nutrition and Health
          - Introduction to foods and nutrition: meaning, basic concepts, scope and importance for everyday life.
          - Careers associated with foods and nutrition; factors influencing career choice and desirable personal qualities.
          - Basic nutrients: types (carbohydrates, proteins, fats/oils, vitamins, minerals, water), functions, main food sources and deficiency problems.
          - Digestion, absorption and simple ideas on metabolism of carbohydrates, proteins and fats.
          - Use of simple food composition tables and basic physical/chemical tests to detect key nutrients in foods.
          - Meal management and planning: meal terms, reasons for planning, factors to consider (age, health, culture, income, time, equipment, etc.), general principles and sample meal patterns.
          - Special nutritional needs: planning meals for infants and children, adolescents, adults, the aged, pregnant and lactating women, vegetarians and people with specific health conditions (e.g. under/overweight, hypertension, diabetes, HIV & AIDS, invalids and convalescents).

        2. Food Laboratory, Equipment and Kitchen Management
          - Types of kitchens: traditional, modern domestic and institutional; simple layout and planning for efficiency, safety and hygiene.
          - Factors to consider in kitchen planning (space, workflow, ventilation, lighting, water and power supply, storage, waste disposal).
          - Cleaning agents and abrasives (local and commercial) and their appropriate uses.
          - Classes and types of kitchen tools, equipment and appliances (large and small); selection, correct use, care and storage.
          - Safety in the kitchen: common accidents, causes and prevention; contents and use of a basic first‑aid box and simple first‑aid procedures.
          - Sanitation: personal hygiene of the food handler; routine kitchen cleaning, waste disposal, pest control; safe handling of food and awareness of food‑borne diseases and simple food laws.

        3. Food Commodities
          - Animal foods and animal products: milk and milk products (e.g. cheese, yoghurt), eggs, meat, poultry, fish and other animal products – structure, selection and storage, simple preparation points.
          - Cereals and grains: main types, forms and uses; nutritional contribution, versatility and role in thickening and as a bulk energy source and fibre source.
          - Fruits and vegetables: classification, structure, nutrient value, storage and their importance in the diet.
          - Legumes and oilseeds: classification and examples; nutritional importance as protein and fat sources.
          - Fats and oils: types and origin (animal and vegetable fats/oils), uses in cookery and in the body, and simple health implications of excess or poor‑quality fats.
          - Starchy roots and plantain: common types, dishes and their roles in local food patterns.

        4. Food Storage and Preservation
          - Food spoilage: meaning, main causes and common agents of spoilage.
          - Food preservation: meaning, reasons (shelf‑life extension, safety, convenience, reduced waste), and general principles (controlling moisture, temperature, microorganisms and enzymes).
          - Preservation methods: heating, cooling, drying, smoking, salting, sugaring, fermenting and simple use of chemical preservatives, with examples of foods.
          - Food storage: importance of proper storage for both perishable and non‑perishable foods; storage areas and equipment (cupboards, shelves, refrigerators, freezers, granaries, barns, etc.) and basic guidelines for use.

        5. Food Preparation and Cookery
          - Principles of cooking: why food is cooked (safety, digestibility, palatability, appearance) and how cooking affects different commodities.
          - Methods of cooking:
           • Moist‑heat methods: boiling, stewing, steaming, poaching, etc.
           • Dry‑heat methods: baking, roasting, grilling, shallow and deep frying, etc.
           • Effects of heat on animal products, cereals, legumes, fats/oils and other commodities.
          - Transfer of heat: simple ideas of conduction, convection and radiation as they apply in cookery.
          - Common cooking terms and their meanings, as used in recipes and practical work.
          - Food additives: meaning, types (e.g. colours, flavours, preservatives), functions, safe selection and use, and a basic understanding of food adulteration.
          - Flour cookery and confectionery:
           • Types and uses of flours; other ingredients used with flour (fats, sugar, eggs, liquids).
           • Raising agents (steam, air, carbon dioxide) and their sources (e.g. whisking, baking powder, yeast).
           • Batters and doughs: types, uses and examples of dishes.
           • Biscuits and cakes: basic mixing methods (rubbed‑in, creamed, whisked, melted, sponge methods) and common products.
           • Pastries: main types (e.g. shortcrust, flaky, suet); principles of good pastry making and typical dishes.
           • Yeast mixtures: concept, ingredients, preparation, points to observe and examples (bread, rolls, doughnuts, etc.).
           • Icing and simple cake decoration: common icings (e.g. butter icing, royal icing), reasons for icing, preparation and application.

        6. Beverages
          - Classification of beverages into non‑alcoholic (e.g. tea, coffee, cocoa drinks, milk and egg drinks, fruit juices, herb/leaf infusions such as lemon grass) and alcoholic (e.g. wine, beer).
          - Nutritional contribution and uses of various beverages.
          - Basic principles for preparing and serving common beverages attractively and safely.

        7. Convenience Foods
          - Meaning and types of convenience foods (dried, canned, frozen, instant, ready‑to‑cook, ready‑to‑eat, etc.).
          - Selection and use of convenience foods; reading and interpreting labels.
          - Advantages and disadvantages of relying on convenience foods.
          - Preparation of simple dishes that make effective use of convenience products.

        8. Rechauffé (Leftover) Foods
          - Meaning of leftover and rechauffé dishes and reasons for sensible use of leftovers.
          - Guidelines for safe handling, storage and reheating of leftovers.
          - Examples of dishes that creatively and safely utilize leftover foods.

        9. Festive and Special‑Occasion Dishes
          - Meaning of festive/special‑occasion dishes and common occasions (religious and cultural festivals, weddings, birthdays, naming ceremonies, etc.).
          - Factors to consider when choosing dishes for special occasions (budget, facilities, number and type of guests, traditions).
          - Examples and preparation of suitable festive and special‑occasion dishes from local food culture.

        10. Art of Entertaining and Meal Service
          - Entertainment and parties: types of gatherings, reasons for entertaining and factors that contribute to successful events.
          - Table setting and etiquette: table appointments, covers for different meals, tray service, and good table manners.
          - Meal service styles: informal and formal service methods; factors influencing choice of service (type of party, facilities, staff, time, budget) and appropriate foods for each.

        11. Experimental Cookery and Local Food Research
          - Importance of research and experimentation in foods and nutrition.
          - Simple data collection on local dishes and beverages; adapting and developing new recipes from local ingredients.
          - Improving existing recipes in terms of nutritional value, time and energy use, cost and presentation.
          - Conducting small‑scale experiments, for example with local fruits, roots, cereals and legumes in new products.

        12. Consumer Education and Food Purchasing
          - Meaning of consumer education and basic consumer rights and responsibilities in relation to food.
          - Food budgeting: reasons for budgeting, steps and factors to consider when planning family or institutional food budgets.
          - Food purchasing: guidelines for effective shopping (quality checks, price comparison, seasonality), bulk buying, reasons for buying in bulk and its advantages and disadvantages.

        13. Self‑Employment and Small Catering Enterprises
          - Basic entrepreneurship concepts in catering: entrepreneur, enterprise and entrepreneurship.
          - Characteristics of a successful food entrepreneur and advantages/disadvantages of being self‑employed.
          - Steps in setting up simple catering enterprises (e.g. snack production, small restaurant or food service, outdoor catering).
          - Factors that influence the success of a catering business (location, quality, hygiene, pricing, customer care, record‑keeping).
          - Work ethics in foods and nutrition: punctuality, honesty, neatness, respect, teamwork and responsibility.
          - Food packaging: meaning, reasons for packaging food, desired qualities of packaging materials and common types of packaging for food products.

        Guidance for Candidates:
        - Build a strong foundation in basic nutrition concepts and links between diet and health before tackling more advanced topics.
        - Practise food preparation and service regularly, paying attention to hygiene, safety, timing and presentation.
        - Use local ingredients and dishes in practical work and experimental cookery to build confidence and creativity.
        - Keep well‑organised class notes, recipe files and practical notebooks, and review them alongside past Foods and Nutrition examination papers.
        - Observe food‑related businesses in your community (restaurants, street food vendors, caterers) to gather ideas on customer expectations, quality and entrepreneurship.`,
  },
  {
    subject: 'Forestry',
    timeAlloted: 60 * 60,
     description: `Forestry at Senior High School level introduces candidates to forests, forest ecology and wildlife, and develops the knowledge and practical skills needed for forest conservation, plantation establishment, forest‑based livelihoods and further study in forestry and related fields.

        Objectives:
        By the end of the course, candidates should be able to:
        - Appreciate the ecological, economic, cultural and environmental importance of forests and wildlife.
        - Demonstrate knowledge of basic forestry concepts, forest types, ecological zones and forest ecosystems.
        - Describe current forestry practices and apply basic skills in nursery work, plantation establishment and tending operations.
        - Explain how population growth, land‑use change and poor management contribute to forest degradation and deforestation.
        - Discuss the main causes and effects of forest degradation and outline measures for forest protection, rehabilitation and sustainable use.
        - Show understanding of key forestry regulations, policies, rights and the roles of forestry institutions and stakeholders.
        - Apply basic skills in forest measurement (mensuration), timber and non‑timber harvesting, and simple processing and marketing.
        - Identify opportunities for small‑scale income‑generating ventures in forestry (e.g. mushroom cultivation, beekeeping) and outline simple business steps.

        Requirements (School Facilities & Learning Experiences):
        - Schools offering Forestry are encouraged to maintain demonstration plots with a mix of indigenous and exotic tree species to support field skills.
        - Teaching should be supported by visits to forest reserves, national parks, zoos, plantations and forestry institutions/industries.
        - Students should keep practical notebooks and specimen albums capturing observations and activities from laboratory, nursery and field work.

        Examination Scheme:
        - Paper 1 (Objective):
         • 50 multiple‑choice questions.
         • 1 hour for 50 marks.

        - Paper 2 (Essay):
         • 6 essay‑type questions set; candidates answer 4.
         • 2 hours for 80 marks (20 marks each).

        - Paper 3 (Practical / Alternative to Practical):
         • School candidates: practical paper based on field and laboratory/nursery exercises.
         • Private candidates: alternative‑to‑practical test assessing practical understanding and interpretation.
         • 4 compulsory questions to be answered in about 2 hours for 60 marks.

        Major Syllabus Areas (Overview):

        1. Introduction to Forestry, Forest Ecology and Wildlife
          - Basic concepts of forest and forestry; differences between forestry and agriculture (land occupancy, time frame, risk, business aspects).
          - Functions of forests (environmental regulation, biodiversity, soil and water protection, climate moderation, cultural and economic roles).
          - Common forest plants (trees, shrubs, herbs, climbers, special plants) and typical forest animals (birds, insects, mammals, reptiles, amphibians, snails).
          - Forest ecology and ecosystems: forest environment, physical components, habitats (aquatic, terrestrial, arboreal) and typical plant/animal communities.
          - Food chains and food webs; producer–consumer–decomposer relationships; simple symbiotic associations and differences between saprophytes and epiphytes.
          - Ecosystem functions (regulating, supporting, cultural) and links between healthy/degraded ecosystems and human well‑being.
          - Ecological and vegetation zones: major vegetation types in West Africa, their characteristic plants and animals, and factors affecting vegetation distribution (climate, soils, biotic influences).
          - Broad forest types in relation to latitude (tropical, temperate coniferous, arctic); link between temperature, latitude and forest distribution; angiosperms vs gymnosperms.
          - Wildlife concepts and resources; importance of wildlife for socio‑economic development, tourism, research and ecosystem balance.
          - Examples of endangered, rare and common wildlife species and reasons for their status.
          - Types, examples and locations of wildlife reserves (national parks, game production reserves, wildlife sanctuaries, strict nature reserves) and basic management practices and protection from poaching.
          - Plant parts and functions; simple internal structure of leaf, stem and root (e.g. xylem, phloem, epidermis, cortex, pith) and use of external features (leaf, bark, buttress, stem form) for tree identification, including local/trade and scientific names.

        2. Forest Estates, Degradation and Regeneration
          - Concept of forest estates: reservation and constitution of forest reserves; role of reserve‑settlement authorities; internal and external boundary demarcation and admitted rights.
          - Differences between protected and non‑protected forests and general systems for managing public and private estates.
          - Major management problems (bushfires, illegal farming, illegal felling, chainsaw operations, illegal mining) and control measures.
          - Historical overview of forest cover and composition in the country; effects of population growth and exploitation on forest area and quality.
          - Measures to curb over‑exploitation (e.g. bans, species rating, stumpage‑fee systems, participatory forestry, approved yields and permits).
          - Deforestation: meaning, forms, indicators, main human causes and effects on soils, wildlife, economy and environment.
          - Control of deforestation through better farming practices, education, law enforcement, improved harvesting, rehabilitation, afforestation and reforestation.
          - Reforestation, afforestation and re‑afforestation: basic meanings, differences and purposes.
          - Natural regeneration concepts: soil seed banks, forest gaps, light‑ and shade‑demanding species, plant succession and forest gaps.
          - Factors influencing the choice of natural regeneration (topography, forest type, extent of damage, presence of seed‑bearing trees) and advantages/limitations compared with artificial regeneration.
          - Succession stages (primary, secondary, climax) with simple examples of light‑tolerant and shade‑tolerant trees.
          - Intervention methods to support regeneration: line and spot planting, agro‑forestry and taungya systems.
          - Agro‑forestry: meaning, examples of practices (agro‑silviculture, alley cropping, mulching, pruning, silvo‑pastoral and agro‑silvo‑pastoral systems), characteristics of suitable species and benefits (soil improvement, nutrient recycling, fuelwood, fodder, food, etc.).
          - Taungya: concept, background, conditions favouring its use, advantages and disadvantages.
          - Artificial regeneration (forest plantations): use of indigenous and exotic species in pure or mixed stands; reasons for and against exotics; and awareness of invasive alien species that threaten forest habitats.

        3. Plantation Establishment, Nursery Work and Forest Mensuration
          - Plantation development: purposes (wood supply, rehabilitation, species stocking, specific industrial needs) and differences between plantations and natural forests.
          - Factors affecting plantation development (resource needs, environmental degradation, species demand, site suitability, costs).
          - Planning and design of plantations: objectives, site selection and preparation, species choice, spacing, layout and cost considerations.
          - Nursery establishment: roles and types of nurseries (permanent and temporary); cultural practices such as bed preparation, seed collection, storage, pre‑treatment, sowing, pricking‑out, stumping/stripping, hardening‑off, transplanting, watering, weeding and shading.
          - Nursery tools and equipment (e.g. hoes, shovels, rakes, wheelbarrows, watering cans) and their correct use and care.
          - Nursery pests (e.g. snails, termites) and diseases (e.g. damping‑off, rust), signs of damage and basic control measures.
          - Costing nursery establishment: direct and indirect costs, cost control and simple record keeping.
          - Planting and tending operations: site clearing, pegging, methods of seedling planting, weeding (complete, line, spot), beating‑up, pruning and thinning, and effects of poor tending on tree growth and wood quality.
          - Operational costs in plantation management and the importance of accurate records.
          - Forest mensuration: meaning, importance and purposes (resource assessment, planning, pricing, monitoring change).
          - Measuring instruments and equipment (e.g. tapes, chains, clinometers, altimeters, compasses, ranging poles) and their basic use.
          - Parameters measured (height, diameter, girth, volume) and simple calculations in the metric system.
          - Enumeration of growing stock (e.g. full and sample enumeration) and basic sampling ideas (selective, systematic and random sampling).
          - Types and purposes of surveys (stock, strip and land surveys) and simple use of conventional symbols and keys.

        4. Harvesting, Utilisation and Marketing of Forest Resources
          - Timber harvesting:
           • Factors to consider and pre‑harvesting activities (stock survey, reconnaissance, field inspection, tree selection).
           • Basic procedures in natural forests and plantations: permit acquisition, yield approval, felling, extraction and measurement.
           • Log markings and their interpretation (ownership marks, locality and compartment codes, stock and tree numbers, reserve codes, log numbers).
           • Main harvesting, extraction and transportation processes (felling, cross‑cutting, hauling, loading, transport).
           • Harvesting equipment (chainsaw, hand saw, axe, cutlass) and key safety measures (protective gear, directional felling, staff training).
          - Harvesting of non‑timber forest products (NTFPs): examples (leaves for wrapping, medicinal plants, chewing sticks, mushrooms, snails, canes, rafters, bushmeat), common methods (hunting, gathering, trapping) and sustainability considerations.
          - General uses of forest products (construction, furniture, household items, food, medicine, crafts, fuel, etc.).
          - Wildlife harvesting: main techniques (hunting, trapping, baiting, gathering), sustainability issues, harmful practices (e.g. chemicals, fire, gin‑traps) and control measures.
          - Classification of forest products into direct (timber, NTFPs) and indirect (non‑tangible) benefits.
          - Contribution of forest products to the national economy (income, employment, exports, community livelihoods).
          - Demand for forest products: local vs foreign demand, factors affecting demand (taste, lifestyle, economic development, quality, availability) and related socio‑economic implications (revenue and resource pressure).
          - Marketing agencies and their roles in promoting and regulating forest product trade.
          - Forest‑based industries (sawmills, plywood and panel mills, furniture and craft industries): raw‑material sourcing, range of products (sawn timber, veneers, boards, artefacts) and contribution to employment and export earnings.
          - Very basic awareness of timber processing stages and main equipment (e.g. band mills, veneer machines).
          - Career and training opportunities in forestry and wood‑based industries.
          - Factors and resources for establishing small forest‑related enterprises (e.g. identifying opportunities, land, capital, materials and simple procedures).

        5. Forestry Institutions, Policies, Rights and Land Tenure
          - Structure and functions of the ministry responsible for forestry and of the Forestry Commission.
          - Roles of key implementing agencies (e.g. forest services, wildlife and timber‑industry development divisions).
          - Contribution of research and training institutions to forestry development.
          - Rights to forest resources and main instruments for granting them (e.g. contracts and permits for timber and NTFPs) and simple differences among these rights.
          - Illegal activities that violate rights (unlicensed felling, poaching, encroachment for farming or mining) and control measures.
          - Stakeholders in forest management (government agencies, traditional authorities and communities, NGOs, industries) and their roles in sustainable forestry.
          - Land tenure systems (communal, individual, leasehold, etc.), roles of traditional authorities and central government, land registration, and how tenure affects land use and forest management.
          - Forest and wildlife policies and laws: general aims (sustainable management, industrial development, training and research, stakeholder involvement) and reasons for enforcement.

        6. Income‑Generating Ventures and Practical Forestry
          - Mushroom cultivation: importance (nutritional, economic, medicinal, use of by‑products), basic fungal classification, differences between edible/poisonous and commercial/non‑commercial species, common cultivated species, nutrition and life cycle of mushrooms.
          - Methods and practices in mushroom production (e.g. local beds, improved beds, indoor/plastic‑bag production); main steps such as composting, filling bags or beds, sterilisation, inoculation, cropping, harvesting and basic processing, preservation and packaging.
          - Beekeeping: importance of beekeeping, simple apiary establishment (siting and baiting hives), members of the honey‑bee colony and their roles, routine management practices and precautions, common pests/diseases, signs of maturity, harvesting and processing of honey and wax, and basic marketing.
          - Practical wildlife and plant identification: recognising common wildlife and plants from observable features (tracks, droppings, body form, leaves, stems, crowns, flowers and fruits) and habitats.
          - Tools, equipment and machinery used in forestry and wildlife work: identification, correct use, maintenance and safety; advantages and disadvantages of different tools.
          - Nursery and plantation practicals: soil treatment, seed handling, sowing, pricking‑out, hardening‑off, transplanting, layout, thinning, pruning, weeding, beating‑up and documentation.
          - Simple forest mensuration exercises: measuring tree and stand characteristics and recording data.
          - Basic harvesting and processing exercises for timber and selected non‑timber forest products, including simple value‑addition ideas.

        Guidance for Candidates:
        - Combine classroom learning with careful observation during field trips, nursery work and plantation visits; record your findings in a well‑organised practical notebook.
        - Practise identifying trees, wildlife and forest products using local names and, where possible, scientific names.
        - Revise key concepts regularly (ecosystems, forest types, regeneration, mensuration, harvesting, policy) and relate them to real situations in nearby forests and communities.
        - Use past Forestry examination papers to familiarise yourself with the structure of Papers 1, 2 and 3 and to strengthen exam technique.
        - Pay attention to how forestry activities, policies and community practices in your area affect forest health and livelihoods, and think about realistic ways to support conservation and sustainable use.`,
  },
  {
    subject: 'Furniture Making',
    timeAlloted: 45 * 60,
     description: `Furniture Making equips candidates with the knowledge, hand skills and design thinking needed to work safely in a wood/furniture workshop, select and prepare materials, construct and finish furniture and upholstery items, and explore small‑scale business opportunities in the furniture industry.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple‑choice questions.
         • 45 minutes for 40 marks.

        - Paper 2 (Theory, Design and Drawing):
         • 2 hours for 60 marks.
         • Section A: 10 compulsory short‑structured questions (30 minutes, 20 marks).
         • Section B: 3 design/drawing questions; 1 is compulsory. Candidates answer the compulsory question (30 marks) and any one other (10 marks) in 1½ hours.

        - Paper 3 (Practical / Alternative to Practical):
         • Practical: one practical furniture‑making task in about 3 hours for 100 marks. A list of materials is released to schools in advance for preparation.
         • Alternative to Practical (where materials are not available): two compulsory theory‑based practical questions, both to be answered in about 2 hours for 100 marks.

        Major Syllabus Areas (Overview):

        1. Furniture Workshop and Safety
          - General workshop safety rules and regulations and why they matter.
          - Safe use of hand tools, workbenches and bench appliances.
          - Safe operation of woodworking machines and portable power tools.
          - Common causes and sources of workshop accidents and basic prevention measures.

        2. Furniture‑Making Materials
          - Timber as a material: basic idea of tree growth and wood structure; simple classification; parts and cross‑section of a log.
          - Conversion of timber (how logs are sawn into boards): common methods with simple advantages and disadvantages.
          - Seasoning of timber: meaning, need, methods and simple pros/cons.
          - Defects in timber: typical defects, their causes and basic classification, with emphasis on how they affect furniture quality.
          - Preservation of timber: reasons for preserving wood; characteristics, main types and applications of preservatives.
          - Manufactured boards: common types (e.g. plywood, blockboard, particle board, MDF), how they are produced in simple terms, their uses, advantages and limitations.
          - Wood adhesives: simple definition, preparation and application; common types, uses and desirable properties for furniture work.
          - Fasteners and fittings:
           • Nails – forms, basic uses and correct methods of driving.
           • Screws – main types, uses and simple driving procedures.
           • Cabinet fittings and hardware – examples (hinges, locks, handles, catches, runners, stays, etc.), their uses and basic fitting procedures.

        3. Woodworking Tools, Machines and Equipment
          - Hand tools: main groups (measuring/marking, cutting, boring, paring, holding, etc.), their uses and recognition by name and shape.
          - Woodworking machines: common types, what they do and key points for safe operation and maintenance.
          - Portable power tools: examples used in furniture making and basic operating principles and precautions.
          - Other tools and equipment used in a furniture workshop and general maintenance routines to keep tools/equipment in good condition.

        4. Furniture Design and Construction
          - Timber preparation: reading a cutting list, cutting timber to size, marking‑out and planing to final dimensions.
          - Woodworking joints: common joint types (e.g. butt, halving, mortise‑and‑tenon, dovetail, housing, mitre), their basic classification and principal uses.
          - Forms of furniture construction: frame construction and box/carcass construction and their typical applications.
          - Freehand sketching: simple proportionate sketches of common household furniture items (e.g. stools, tables, chairs, cabinets, beds).
          - Design fundamentals: basic elements (line, shape, form, colour, texture, proportion) and simple principles (balance, rhythm, emphasis, unity, etc.) as they relate to furniture.
          - Design stages: preliminary idea sketches, refinement and working drawings.
          - Planning for production: working rods, material selection and preparing a cutting list.
          - Construction procedures for different items of furniture: preparing materials, setting‑out and cutting joints, assembling components, checking for accuracy and applying basic finishes.

        5. Upholstery Work and Finishing
          - Meaning and forms of upholstery in furniture making.
          - Upholstery tools and machines used alongside woodworking tools.
          - Traditional and modern padding and stuffing materials and where they are used.
          - Design and construction of upholstery frames and simple upholstered items.
          - Padding, stuffing and fixing of covering materials (including basic sewing, stretching and tacking or stapling techniques).
          - Simple surface preparation and finishing of wooden furniture (e.g. smoothing, filling, staining, polishing or painting) as part of overall product quality.
          - Awareness of business opportunities in furniture and upholstery work (e.g. repairs, custom pieces, small production lines).

        6. Entrepreneurship in Furniture Making
          - Factors to consider when starting a small furniture business (location, tools and equipment, capital, labour, target market, product range, quality and pricing).
          - Managing a furniture workshop: simple organisation of space, workflow, stock and safety.
          - Estimating and costing: preparing basic estimates for furniture items (materials, labour, overheads) and simple methods of setting selling prices.
          - Record‑keeping and customer relations as foundations for a sustainable furniture‑making enterprise.

        Facilities and Equipment (Indicative):
        - The syllabus assumes access to a reasonably equipped furniture workshop, including standard hand tools, key woodworking machines, portable power tools, benches with vices, simple upholstery tools and a basic first‑aid box.

        Guidance for Candidates:
        - Practise correct tool handling, accurate marking‑out, joint cutting and assembly on simple projects before attempting more complex designs.
        - Develop confidence in freehand sketching and in interpreting and producing basic working drawings.
        - Pay close attention to safety rules and workshop discipline, especially when using machines and portable power tools.
        - Study examples of well‑made furniture to understand good proportion, joint choice, construction methods and finishing.
        - Use past Furniture Making examination papers to become familiar with the structure of Papers 1, 2 and 3 and to sharpen design/drawing and practical‑planning skills.
        - Observe local furniture workshops or artisans to learn about real‑world practices, customer expectations and viable small‑scale business models.`,
  },
  {
    subject: 'Garment Making',
    timeAlloted: 40 * 60,
    description: `Garment Making at senior secondary level develops basic sewing, pattern and small‑business skills so that learners can make well‑fitting garments for themselves and others and, over time, use these skills for self‑reliance and employment in clothing and fashion‑related ventures.

        Objectives:
        By the end of the course, candidates should be able to:
        - Demonstrate manipulative and practical skills needed to sew simple garments that are neat, durable and well‑finished.
        - Show interest and creativity in garment design and making, including appreciation of locally produced fabrics and self‑made articles.
        - Select, use and care for basic sewing tools, equipment and different types of domestic sewing machines.
        - Take body measurements, choose appropriate styles for different figure types and occasions, and make simple pattern adaptations.
        - Apply basic hand and machine sewing processes such as stitches, seams, seam finishes, facings and disposal of fullness.
        - Understand the basic concepts of garment design (elements and principles) and how age, figure type and other factors influence garment choice.
        - Appreciate entrepreneurship and small‑business management in garment making, including pricing, record‑keeping and customer relations.
        - Value the dignity of labour and the role of garment making in personal, family and national development.

        Examination Scheme:
        - Paper 1 (Objective):
         • 40 multiple‑choice questions.
         • 40 minutes for 40 marks.

        - Paper 2 (Short Essay):
         • 5 short‑essay questions set; candidates answer 4.
         • 45 minutes for 40 marks.

        - Paper 3 (Practical):
         • 4 short structured practical questions based on prescribed processes and skills.
         • All 4 questions to be answered in about 40 minutes for 40 marks.

        Major Syllabus Areas (Overview):

        1. Introduction to Garment Making and Design
          - Reasons for wearing clothing and basic functions of garments.
          - Identification of common clothing items and garments suitable for different occasions.
          - Concept of garment design: simple meaning, basic elements (line, shape, colour, texture, etc.) and principles (balance, rhythm, emphasis, proportion, harmony) in clothing.
          - Factors influencing garment design and choice (age, figure type, occupation, culture, climate, occasion).
          - Economic importance of garment design and making to the individual, family and community.
          - Entrepreneurship in garment making: meaning of small business, qualities of a good entrepreneur, advantages of self‑employment and key points to consider before starting a garment business.

        2. Sewing Tools, Equipment and Sewing Machines
          - Simple sewing tools and equipment: identification of common tools and their classification by function (measuring, marking, cutting, joining, pressing, fitting, storage).
          - Points to consider when choosing sewing tools and equipment (quality, cost, durability, suitability and safety).
          - Sewing machines: main types of domestic machines, their uses and basic parts and their functions.
          - Care and maintenance of simple sewing tools and machines: cleaning, oiling, safe storage and safe handling.
          - Common machine faults (e.g. skipped stitches, broken thread, uneven stitches) and simple remedies; effects of improper use of machine parts.

        3. Basic Sewing Processes
          - Basic stitches: temporary (tacking/basting) and permanent (running, backstitch, hemming, overcasting, etc.), their uses and simple procedures for making them.
          - Seams and seam finishes:
           • Meaning and purpose of seams.
           • Common seam types, where they are used and suitable seam finishes.
           • Points to consider when choosing seams and seam finishes (fabric type, garment area, appearance and durability).
          - Disposal of fullness: methods such as darts, pleats and gathers; simple procedures for forming and pressing them neatly.
          - Facings and interfacings:
           • Types and uses of facings and other edge finishes.
           • Types and basic functions of interfacings and where they are applied.
          - Openings and fastenings: common types (e.g. plackets, zips, buttons and buttonholes, hooks and eyes), their functions and where they are used on garments.

        4. Patterns, Freehand Cutting and Body Measurements
          - Patterns: simple idea of pattern drafting and its advantages when making garments.
          - Tools and equipment for pattern work and basic pattern markings and symbols.
          - Ways of obtaining patterns: awareness of draping, flat pattern work and simple computer‑aided approaches; emphasis on basic school‑level methods.
          - Freehand cutting: meaning and basic techniques involved in cutting simple garments without paper patterns.
          - Body measurements: importance of accuracy, main body parts to be measured and simple procedures for taking and recording measurements.
          - Drafting of basic blocks (bodice, skirt, sleeve, collar, trouser) in principle: measurements required and the idea of adapting basic blocks to create different styles.
          - Simple adaptation of patterns for blouses, skirts, sleeves, collars, trousers and basic garments such as gowns and jumpers using basic techniques (dart manipulation, slashing and spreading).

        5. Figure Types, Fabric Selection and Style Choice
          - Main figure types and common variations.
          - Choosing colours, textures and fabric designs/prints that flatter different figure types.
          - Choosing garment styles that solve or minimise figure problems (e.g. large bust, large hips, thin waist, short or tall figures).
          - Matching styles and fabrics to the occasion, weather and cultural expectations.

        6. Construction of Simple Garments
          - Unit method of sewing: understanding how garments are assembled in logical units/steps.
          - Application of basic processes (stitches, seams, fastening, facings, disposal of fullness) in constructing simple blouses/shirts and skirts.
          - Extension of the same principles to skirts, trousers, gowns and jumpers, using basic blocks or freehand methods.
          - Emphasis on accuracy, neatness, pressing during construction and simple fitting and finishing.

        7. Setting Up and Managing a Small Garment Business
          - Minimum requirements for starting a small garment venture (workspace, machines, basic tools, storage, pressing equipment, mirrors, forms, etc.).
          - Estimating the cost of minimum requirements and basic running costs.
          - Factors that contribute to success or failure of small garment businesses (location, quality, reliability, pricing, customer service, financial discipline).
          - Business finance: simple ideas on sources of finance, managing income and expenses and avoiding unnecessary debt.
          - Profit and price fixing: idea of profit margin and factors to consider when setting prices (cost of materials, labour, overheads, competition and market).
          - Customer relations: importance of maintaining good relationships, handling orders and alterations, and managing simple credit arrangements.
          - Record‑keeping: types of basic records (income, expenses, stock, orders, customers) and reasons for keeping them.
          - Business promotion: simple ways of promoting a small garment business (word of mouth, samples, local adverts, displays) and why promotion matters.

        Facilities and Equipment (Indicative):
        - The syllabus assumes access to at least a few sewing machines, cutting tables, pressing equipment, basic storage (cupboards/wardrobes), mirrors, dress forms and common sewing tools and materials for school‑level practice.

        Guidance for Candidates:
        - Practise basic stitches, seams, facings and fastenings repeatedly on samples before applying them to full garments.
        - Take and record body measurements carefully, and check fit during construction using trial fittings.
        - Build confidence in simple pattern use and freehand cutting, starting with basic blocks and straightforward styles.
        - Observe local tailors, seamstresses and small fashion businesses to understand real‑life work practices and customer expectations.
        - Use past Garment Making examination papers to become familiar with the style of objective, short‑essay and practical questions and to sharpen speed and accuracy.`,
  },
  {
    subject: 'General Agriculture',
    timeAlloted: 60 * 60,
     description: `General Agriculture provides a broad foundation in agricultural principles and practices, covering soils, crops, animals, farm power and mechanization, economics, agribusiness, extension and value chains so that learners can manage basic farm enterprises and progress into any agricultural sub-sector or further study.

        Objectives:
        By the end of the course, candidates should be able to:
        - Demonstrate understanding of key principles and practices in crop, animal, soil and farm management.
        - Carry out simple laboratory and field work, including experiments, projects and routine farm operations.
        - Use scientific skills such as observation, measurement, classification, data interpretation and basic calculations to solve agricultural problems.
        - Apply basic business and management skills to set up and run small agribusinesses.
        - Appreciate the importance of agriculture to the national economy and rural development.
        - Understand the agricultural value chain and how to maintain food quality and safety from production to market.

        Examination Scheme:
        - Paper 1 (Objective):
         • 50 multiple‑choice questions covering the full syllabus.
         • 1 hour for 50 marks.

        - Paper 2 (Essay – Sectioned):
         • 10 essay questions divided into 5 sections:
          – Section A: Introduction to Agriculture and Farm Mechanization
          – Section B: Soil Uses and Management
          – Section C: Crop Production
          – Section D: Animal Production
          – Section E: Agricultural Economics, Agribusiness and Extension.
         • Each section has 2 questions; candidates answer 1 from each section (5 questions total).
         • 2 hours for 80 marks (16 marks per question).

        - Paper 3 (Practical / Alternative to Practical):
         • 4 compulsory practical or alternative‑to‑practical questions.
         • 2 hours for 60 marks.

        Major Syllabus Areas (Overview):

        1. Introduction to Agriculture and Agricultural Education
          - Definition and branches of agriculture (crop and animal production, horticulture, mechanization, soil management, fisheries, forestry, agricultural economics, extension, etc.).
          - Role of agriculture in national development: food and nutrition, raw materials, employment, income/foreign exchange, linkages with industry and services.
          - Meaning, types and importance of agricultural education (formal, non‑formal and informal) and their roles in manpower development, leadership and rural development.
          - Major agricultural occupations and broad job descriptions and entry requirements (production, mechanization, processing, resource management, forestry, teaching, research and services).
          - Basic agricultural calculations and measurements: area, volume, percentages, plant population, yield per unit area, seed rate, fertilizer and pesticide rates, dressing percentage, etc.; comparison of indigenous and standard units.

        2. Land, Forestry and Sustainable Agriculture
          - Uses of land: agricultural (crops, animals, forestry, fisheries) and non‑agricultural (settlement, industry, recreation, etc.).
          - Land‑tenure systems common in West Africa (e.g. communal, freehold, leasehold, tenancy), with simple merits and demerits and their effects on agricultural production and investment.
          - Basic forestry concepts: what forests and forestry are; main features of forestry (long‑term land use, large area, risk, capital, business aspects).
          - Forest products (timber and non‑timber), their uses (construction, energy, medicine, food, crafts) and contributions to employment, income and foreign exchange.
          - Deforestation: meaning, causes and effects on soil, water, climate, biodiversity and the economy; simple forest management and conservation practices including game and wildlife conservation.
          - Concepts of sustainable agriculture and good agricultural practices (GAP) and examples of practices that maintain soil fertility, protect the environment and support food safety and quality.
          - Factors influencing GAP and sustainable production (social, technological, economic, political and physical factors including climate change).
          - Objectives and challenges of agricultural development in West Africa and the roles of governments and other agencies (ministries, research institutes, banks, NGOs, agro‑industries) in supporting it.

        3. Soils: Formation, Properties, Fertility and Conservation
          - Origin and formation of soils: main rock types (igneous, sedimentary, metamorphic), weathering processes (physical, chemical, biological), transportation and deposition of materials, and the role of organic matter.
          - Soil profile: horizons and simple description (colour, texture, structure, depth, porosity, organic matter); importance of understanding profiles for crop production.
          - Soil composition: mineral particles, organic matter, living organisms, water and air; roles of soil organisms and organic matter.
          - Physical and chemical properties of soils: colour, texture, structure, soil air and water, temperature, consistency and pH; importance of these properties for plant growth.
          - Land preparation methods: indigenous and mechanised methods and their effects on soil structure and fertility.
          - Plant nutrients: classification into macro‑ and micro‑nutrients; main functions and deficiency symptoms of key nutrients such as nitrogen, phosphorus, potassium, zinc and iron.
          - Nitrogen and carbon cycles as natural sources of nutrients.
          - Soil fertility and productivity: meaning, characteristics of a fertile soil and methods of maintaining/improving fertility (crop rotation, fertilizers, cover cropping, liming, mulching, fallowing, organic manures and compost).
          - Fertilizers: organic vs inorganic, simple types of chemical fertilizers and basic application methods (broadcast, banding, side‑dressing, foliar); factors affecting fertilizer use.
          - Soil and water conservation: types of soil water, erosion agents and types, factors influencing erosion, effects and conservation measures (agronomic, soil‑conditioning and tillage practices).

        4. Farm Mechanization, Power, Tools and Simple Surveying
          - Farm mechanization: meaning, objectives and importance; basic farm‑safety precautions when using machinery, electricity, chemicals, draught animals and tools; safe dress and simple first‑aid awareness.
          - Sources of farm power (human, animal, engines, solar, wind, water, electricity), with simple merits and demerits.
          - Farm machinery and implements: differences between machinery and implements; major field implements (ploughs, harrows, ridgers, planters, trailers) and simple ideas on use, care and maintenance.
          - Basic components and functions of internal‑combustion engines and small farm machines (e.g. power tiller, mower, mistblower, knapsack sprayer).
          - Harvest and post‑harvest tools and equipment (sickles, cutlasses, hoes, lifters, shellers, graters, mills, dehuskers) and simple storage structures (silos, barns, cribs, cold storage).
          - Irrigation and drainage: simple meanings, benefits and problems; common methods/systems (surface, sprinkler, drip, open and subsurface drainage) and their basic merits/demerits; basic equipment such as cans, pumps and pipes.
          - Elementary surveying and farmstead planning: simple purposes of surveying, basic instruments (tapes, chains, ranging poles, compasses, levels) and farmstead layout principles (topography, water, soil, wind, sun).

        5. Crop Production
          - Importance and classification of crop plants: benefits (food, feed, raw materials, income, foreign exchange) and classifications (by life cycle, botanical group, use, cultivation method).
          - Site selection and land preparation for crops; meaning and aims of tillage; primary and secondary tillage operations.
          - Plant propagation: seed and vegetative methods, with key activities (seed selection, testing, treatment, sowing, nursery work) and vegetative materials and techniques (corms, bulbs, tubers, runners, rhizomes, cuttings, budding, grafting, layering).
          - Cultural practices in crop production: seed‑bed preparation, fertilizer application, mulching, weeding, watering, pruning, thinning, staking and pest/disease control.
          - Plant protection: basic classification of crop diseases (pathogenic and non‑pathogenic), examples of common fungal, bacterial, viral, nematode and non‑pathogenic diseases and their general effects on yield and quality.
          - Crop pests and weeds: major field and storage pests and broad groups (insects, rodents, birds, nematodes); classification, importance, dispersal and general weed management methods.
          - Invasive alien species: simple concept, examples and their effects on agriculture and the environment.
          - General husbandry of selected crops: basic requirements and practices from land preparation through planting, cultural practices, pest/disease control, harvesting, storage and simple yield measurement for key field, vegetable, fruit and tree crops.
          - Basic principles of crop improvement: meaning, aims and simple methods (introduction, selection, controlled crossing) and basic ideas of producing improved varieties.
          - Ornamental plant production: identification, classification, importance and simple bed and border preparation.

        6. Animal Production and Fisheries
          - Importance, classification and distribution of farm animals in West Africa; main ruminant and non‑ruminant species and their uses.
          - Digestive systems of ruminants and non‑ruminants and the importance of main nutrients (carbohydrates, proteins, fats, minerals, vitamins and water) in animal feeding.
          - Classification of feedstuffs and simple preparation of common feeds (concentrates, roughages, silage, hay, fresh herbage); basic ration types (maintenance, production, balanced ration) and effects of malnutrition.
          - Reproduction in farm animals: major parts and functions of male and female reproductive systems, oestrus/heat and signs, basic reproductive processes and simple ideas on inbreeding and its effects.
          - Animal improvement: aims and simple methods (introduction, selection, breeding, artificial insemination) and their basic advantages/limitations.
          - General management practices: housing, feeding, breeding programmes, routine operations (weaning, castration, dehorning, debeaking, record‑keeping) and selection of breeding stock.
          - Animal health management: causes of diseases, broad types of diseases (viral, bacterial, fungal, protozoan), simple examples and general prevention and control (sanitation, vaccination, quarantine, nutrition, drugs and chemicals).
          - Parasites and pests of farm animals: ecto‑ and endoparasites, their general effects and simple control measures.
          - Husbandry of selected farm animals (ruminants, non‑ruminants and non‑ruminant herbivores) from birth to market and basic slaughtering and dressing concepts.
          - Introduction to fisheries: meaning and main types (capture and culture), simple comparison of small‑scale and larger‑scale fisheries.
          - Fish farming: basic meaning and benefits/problems, simple facilities (ponds, cages, tanks, raceways, pens), main site‑selection factors and very simple ideas on stocking, feeding, pond management, harvesting and preservation.

        7. Agricultural Economics, Agribusiness, Extension and Value Chains
          - Agricultural economics: meaning, scope and view of the farm as an economic unit; simple ideas about applying economic principles and farm management in agribusiness.
          - Principles of demand and supply and the simple idea of equilibrium price for agricultural products.
          - Factors of production (land, labour, capital, management) and basic functions of the farm manager; simple production‑function ideas and the law of diminishing returns.
          - Introduction to agribusiness: meaning, examples (on‑farm and off‑farm), agriculture‑related occupations and basic management tasks (planning, organizing, budgeting, record‑keeping, supervision).
          - Establishing and managing small agribusinesses: factors to consider, simple steps, sources of finance and basic business‑plan ideas.
          - Agricultural financing: main sources of farm credit, basic conditions for obtaining credit and the idea of subsidies; simple types of credit.
          - Farm records and accounts: types and importance of basic records; simple financial statements (income and expenditure, profit and loss, basic balance sheet) and their uses.
          - Marketing of agricultural produce: meaning, importance, basic marketing functions, channels, agents/agencies and common marketing problems.
          - Agricultural extension: meaning, objectives, importance, main agencies involved, characteristics of effective extension systems, common problems and major teaching methods (individual, group and mass).
          - Value chain approach: simple definition and characteristics, benefits of value‑chain development for competitiveness and food quality/safety, and basic ideas on standards along the chain.
          - Bodies responsible for food quality and safety assurance at local and international levels and examples of good practices along the value chain.

        Guidance for Candidates:
        - Link classroom learning with field, laboratory and project work; keep clear, dated records of your practical activities and observations.
        - Practise agricultural calculations regularly (plant population, yield, rations, fertilizer rates, simple accounts) to build speed and accuracy.
        - Use local farms, markets, forests, water bodies and agribusinesses as case studies for understanding topics such as land use, mechanization, marketing and value chains.
        - Study past General Agriculture examination papers to become familiar with the structure of the three papers and the balance between theory and practical questions.
        - Pay attention to how sustainability, food quality and safety, and agribusiness ideas apply to real agricultural activities in your community.`,
  },
  {
    subject: 'General Knowledge in Art',
    timeAlloted: 50 * 60,
    description: `General Knowledge in Art is designed to develop aesthetic awareness, perceptual skills and creative thinking through a wide range of art activities, art history and design studies, so that candidates better understand visual culture, appreciate art in society and can pursue further art‑related education or careers.

        Objectives:
        By the end of the course, candidates should be able to:
        - Observe, analyse and interpret visual forms in their environment and express ideas through drawing, design and other art activities.
        - Use, care for and maintain basic art tools, equipment and materials safely and effectively.
        - Recognise and evaluate the qualities, values and functions of different works of art.
        - Apply elements and principles of design in simple compositions and products.
        - Demonstrate knowledge of major periods, styles and structures in art history from prehistoric to modern times.
        - Appreciate their own cultural heritage and relate it to other world cultures.
        - Explain the meaning, significance and role of art in socio‑economic development, including tourism and small‑scale industries.
        - Understand indigenous art technologies, aesthetics, beliefs and values and how they influence contemporary practice.
        - Explore opportunities for self‑employment and entrepreneurship in visual arts.

        Examination Scheme:
        - Paper 1 (Objective):
          • 40 multiple‑choice questions on the entire syllabus.
          • 50 minutes for 40 marks.

        - Paper 2 (Essay):
          • 5 essay‑type questions set; candidates answer 3.
          • 1 hour 30 minutes for 60 marks.

        - Paper 3 (Practical – Drawing/Painting and Lettering/Product Design):
          • Section A – Drawing/Painting (compulsory): 2 questions set (still life and imaginative composition); candidates answer 1 in 3 hours for 50 marks.
          • Section B – Lettering/Calligraphy or Product Design: 2 questions set; candidates answer 1 from either area in 3 hours for 50 marks.
          • The paper is issued about two weeks before the examination to allow for planning and preparation.

        Major Syllabus Areas (Overview):

        1. Rationale for Visual Arts and General Knowledge in Art
          - Meaning of Visual Arts and of General Knowledge in Art as a broad, theory‑oriented art subject.
          - Scope of visual arts: two‑dimensional, three‑dimensional and digital/media‑based arts and related vocations.
          - Roles of visual arts in personal, cultural and national development: communication, identity, aesthetics, education, economic and social development.

        2. Basic Drawing: Tools, Equipment and Materials
          - Drawing as a foundation for visual expression: simple definitions, purposes and basic types of drawing.
          - Identification and exploration of drawing tools, equipment and materials (pencils, pens, brushes, papers, boards, erasers, charcoals, inks, etc.).
          - Preparation, use, care and maintenance of drawing media and support surfaces.

        3. Basic Design and Composition
          - Perspective: simple meaning, basic types (e.g. one‑point, two‑point) and their application in drawing.
          - Developing ideas from observation of natural and man‑made objects.
          - Drawing for communication: analytical study of objects and figures; simple visual communication sketches.
          - Design process: from identification of a problem or need, through idea generation and refinement, to final solution.
          - Product design: simple awareness of designing everyday products and packages.
          - Two‑dimensional and three‑dimensional design/drawing: basic distinctions and examples.
          - Computer as a tool in art: simple identification of parts of a computer and their uses in image creation, editing and presentation.
          - Portfolio building: importance and uses of a portfolio for documenting and presenting artworks.
          - Elements and principles of design: line, shape, form, colour, texture, space, value; balance, rhythm, emphasis, contrast, unity, proportion, etc., and their organization in compositions.
          - Perception: simple idea of how we see and interpret visual information.
          - Colour theory: basic definition and sources of colour; the twelve‑hue colour wheel; colour relationships and harmonies; colour mixing (tints, shades and gradation); and the psychological and symbolic aspects of colour.
          - Ghanaian and African colour concepts: associations of colours with objects, events and social meanings.

        4. Printmaking and Printing
          - Meaning of printing and printmaking as art processes.
          - Identification of basic tools, materials and equipment used in printmaking.
          - Overview of direct and indirect printing processes, including relief, intaglio, lithography, screen printing and simple photographic techniques.
          - Printing from natural and artificial surfaces and textures.
          - Printing as a means of communication and decoration.

        5. Lettering and Calligraphy
          - Meaning and uses of lettering in art and design.
          - Types of lettering: block (construction of block letters, upper and lower case, parts of letters), calligraphy and decorative styles.
          - Gothic (sans‑serif) lettering: simple features, functions and where it is commonly used.
          - Roman (serif) lettering: characteristics, significance and typical applications.

        6. Prehistoric Art
          - General idea of the Palaeolithic, Mesolithic and Neolithic periods.
          - Location and nature of prehistoric art (e.g. cave paintings, engravings, carvings).
          - Purposes and uses of prehistoric art, including beliefs about sympathetic magic in hunting and other aspects of life.
          - Tools, materials, techniques and terms associated with cave art.

        7. Ancient Egyptian Art
          - Purpose of Egyptian art, especially the idea of eternity and the afterlife.
          - Main characteristics and canons (conventions) of Egyptian sculpture, painting and architecture.
          - Influence and relevance of Egyptian art to later cultures and art traditions.

        8. Greek, Renaissance, Oriental and Oceanic Art
          - Greek art: simple overview of contributions to art from the Archaic, Classical and Hellenistic periods.
          - Renaissance art: meaning of the “rebirth”, period and origins; major artists, achievements, forms and styles; contributions to modern art.
          - Oriental art: beliefs, major forms and functions in Indian, Chinese and Japanese art and their influence.
          - Oceanic art: beliefs, forms and functions of Polynesian and Melanesian art.

        9. African Art
          - Influence of religious beliefs (animism, magic, myth, rituals, etc.) on African art.
          - Types and functions of indigenous African art forms (visual, performing and verbal arts).
          - Main characteristics of traditional African art and examples from major West African ethnic groups.
          - Development of contemporary African arts (visual, performing and verbal) and their uses.
          - Effects of religion, education, trade and politics as agents of change in African art.

        10. Art in National Development and Ghanaian Attitudes to Art
          - Indigenous Ghanaian art forms (e.g. Adinkra, kente, carving, pottery, textiles, jewellery) and their artists.
          - Contemporary Ghanaian art forms and artists and how they build on indigenous ideas.
          - Form, symbolism and functions of Ghanaian arts in social, political, educational and religious life.
          - Role of art in tourism and its contribution to national development.

        11. Art Appreciation and Modern Art
          - Concept of art appreciation and simple procedures for discussing artworks: identification, inventory, analysis of technical qualities and interpretation.
          - Creativity: meaning, the creative process and qualities of a creative person.
          - Basic ideas in aesthetics, art criticism and judgement and why they matter.
          - Introduction to modern art movements and philosophies (e.g. Impressionism, Post‑Impressionism, Neo‑Impressionism, Fauvism, Expressionism, Cubism, Surrealism and others) with simple notes on style, artists and impact.

        12. Entrepreneurship in Visual Arts
          - Examples of enterprises in visual arts (painting, sculpture, textiles, graphics, photography, crafts, etc.).
          - Steps in setting up a small art enterprise and factors to consider (capital, location, skills, market, support services).
          - Managing an enterprise: key functions of a manager; importance of good customer relations.
          - Costing and pricing: basic factors to consider when costing art products and setting prices.
          - Basic marketing and selling strategies, including advertising and promotion.
          - Business risks (direct and indirect) and simple ways of managing them.
          - Art exhibitions: simple meaning, types/levels and main things to consider when planning, preparing and mounting exhibitions.

        13. Professional Ethics and Business Documentation
          - Business plan: simple meaning, importance and steps in preparing a basic plan for an art enterprise.
          - Artist’s statement: definition, purpose and main features.
          - Business brochure and business card: meaning, importance and basic content.

        14. Museum and Gallery Studies
          - Meaning, history and development of museums and galleries.
          - Functions and socio‑economic importance of museums (education, preservation, research, tourism, identity).
          - Basic administrative structure of a museum and the idea of authenticity and neutrality.
          - Examples of museums and typical activities.

        Guidance for Candidates:
        - Build a strong foundation in drawing, design and colour by practising regularly with different tools and materials.
        - Visit museums, galleries, craft centres and art exhibitions where possible and keep notes or sketches of what you see.
        - Relate art history topics to timelines, cultures and major artists to make the stories and stylistic features easier to remember.
        - Develop a simple portfolio of your works and practise explaining your ideas and processes.
        - Use past General Knowledge in Art examination papers to become familiar with the types of objective, essay and practical questions set and to improve speed and accuracy.`,
  },
  {
    subject: 'Ghanaian Languages',
    timeAlloted: 180 * 60,
    description: `Ghanaian Languages at this level focus on building strong communicative competence in a chosen local language and deepening appreciation of the culture, values and institutions of the speech community, so that learners can use the language accurately and confidently in real‑life situations and contribute positively to national development.

        Aims and Assessment Objectives:
        The syllabus seeks to help candidates to:
        - Communicate clearly and coherently in the chosen Ghanaian language in speech and writing.
        - Arrange and present facts, ideas and opinions logically for different purposes (narration, description, argument, discussion, speech, letters, etc.).
        - Read and understand a variety of texts, evaluate information and select what is relevant to specific tasks.
        - Recognise implicit meanings, attitudes and shades of meaning in language.
        - Understand and apply the sound system (phonology) and structural patterns (syntax, word classes, agreement) of the language.
        - Appreciate the themes, characters, style and social attitudes expressed in oral and written literature.
        - Value the customs, institutions and artistic expressions of their own culture in relation to others.

        Examination Scheme:
        There are two papers, both of which must be taken.

        - Paper 1: Language Skill Development (3 hours, 120 marks)
          • Section A – Composition (40 marks):
            - Candidates write one essay of about 450–500 words from a choice of topics (narrative, descriptive, argumentative, discussion, speech, letter, etc.), often linked to customs and institutions.
          • Section B – Translation (20 marks):
            - A short English prose passage is translated into the Ghanaian language, testing grasp of meaning, register and style across different text types (narration, description, explanation, instructions, speeches).
          • Section C – Comprehension (20 marks):
            - A passage or verse in the Ghanaian language with questions on content, vocabulary and idioms, figures of speech, inferred meaning, summary and title/main idea.
          • Section D – Language Structure (40 marks):
            - Questions on phonology and syntax/grammar as outlined in the detailed syllabus.

        - Paper 2: Literature, Customs and Institutions (2½ hours, 80 marks)
          • Section A – Oral Literature / Customs & Institutions (20 marks):
            - One question chosen from two, testing knowledge and analysis of aspects such as marriage and divorce, naming and puberty/initiatory rites, chieftaincy and traditional administration, taboos and oaths, traditional judicial procedures, proverbs, riddles, puzzles, historical narratives, folktales, dirges, lyrics, lullabies, libation and drum/xylophone/horn texts, and occupational/war/play songs.
          • Section B – Written Literature (60 marks):
            - Divided into three parts (Prose, Drama and Poetry), each based on a prescribed set book studied over several years.
            - For each part, two questions are set on the book; candidates answer one question per part, demonstrating understanding of theme, plot/incidents, context, characterization, style, imagery and other literary devices.

        Major Syllabus Areas (Overview):

        1. Language Skills and Writing
          - Free composition in a range of types: narrative, descriptive, expository, argumentative, discussion, speech and letter writing.
          - Organisation of ideas into coherent paragraphs with appropriate use of figures of speech and accurate, mature language.
          - Writing related to customs and institutions (e.g. cultural practices, social issues, community life).

        2. Translation and Reading Comprehension
          - Translating English passages into the Ghanaian language, maintaining meaning, tone and suitable register.
          - Reading and responding to prose and verse passages: identifying main and supporting ideas, interpreting vocabulary and idioms, recognising figures of speech, drawing inferences and summarising content.

        3. Phonology (Sound System)
          - Vowel system: length, nasalisation, assimilation, elision and vowel sequences/harmony, as applicable to the chosen language.
          - Consonant system: assimilation, mutation, gemination (where relevant) and secondary articulations such as labialisation and palatalisation.
          - Tone: basic level tones and tone patterns, tone bearing units and the lexical and grammatical functions of tone; simple intonation patterns.
          - Syllable structure and types, and their role in pronunciation and spelling.

        4. Syntax and Grammatical Structures
          - Sentence types, functions and structure (simple, compound, complex, etc.).
          - Clauses: types, functions and structure.
          - Phrases: types, functions and structure.
          - Word classes and their roles in sentences (nouns, verbs, adjectives, adverbs, pronouns, conjunctions, postpositions, determiners, particles and auxiliaries as relevant in the language).
          - Word formation processes.
          - Direct and indirect speech.
          - Agreement (concord) within the sentence.

        5. Oral Literature, Customs and Institutions
          - Traditional institutions: marriage and divorce customs, naming, puberty and initiation rites, chieftaincy and traditional administration, traditional judicial procedures and the values they express.
          - Oral art forms: proverbs, riddles, puzzles, folktales, myths, legends, historical narratives, dirges, lullabies, lyrics, occupational and war songs, play songs, libation and drum/xylophone/horn texts.
          - Functions of these forms in education, moral instruction, conflict resolution, entertainment and the preservation of history and culture.

        6. Written Literature (Prose, Drama and Poetry)
          - Detailed study of a prescribed prose text: themes, plot, incidents, setting, characters, style and language.
          - Detailed study of a prescribed drama text: dramatic structure, conflict, characterisation, dialogue, setting, themes and stage directions.
          - Detailed study of a prescribed poetry text: themes, imagery, sound devices, tone, mood and structure.
          - Appreciation of how writers use diction, syntax, imagery and other stylistic devices to achieve particular effects.

        Guidance for Candidates:
        - Read widely in the chosen Ghanaian language, including textbooks, creative works and newspapers or magazines where available, and practise summarising what you read.
        - Practise writing different types of essays and letters, paying attention to clear organisation, appropriate vocabulary and accuracy.
        - Listen carefully to good speakers and performers in your language (storytellers, broadcasters, elders) to improve pronunciation, tone and expression.
        - Learn, practise and use proverbs, idioms and other oral forms appropriately to enrich your language.
        - Study the prescribed literature texts thoroughly, paying attention to themes, characters, plot, style and the cultural background.
        - Work through past Ghanaian Languages examination papers to become familiar with the structure and demands of Papers 1 and 2 and to improve speed and confidence.`,
  },
  {
    subject: 'Graphic Design',
    timeAlloted: 50 * 60,
     description: `The WASSCE Graphic Design syllabus develops both theoretical understanding and practical skills in Graphic Design as a vocation. It provides a foundation for further study at tertiary level and equips candidates with skills for self‑employment in areas such as illustration, cartooning, publishing and advertising.

        Aims and Objectives:
        - Develop candidates’ perceptual and analytical skills for solving visual communication problems.
        - Build manipulative skills in handling Graphic Design tools, materials and equipment safely and effectively.
        - Enable candidates to plan and execute design projects from idea generation through layout to finished artwork.
        - Introduce basic entrepreneurial skills for careers and self‑employment in Graphic Design.

        Scheme of Examination:
        The examination consists of three papers, all of which must be taken. Papers 1 and 2 are a composite paper taken at one sitting.

        - Paper 1: Objective Test (40 marks, 50 minutes)
         • About forty multiple‑choice questions sampling the entire syllabus: tools and materials, lettering, basic drawing and illustration, colour, communication design, reproduction processes, book design, paper craft, book binding, computer use in design and entrepreneurial concepts.

        - Paper 2: Essay (60 marks, 2 hours)
         • Six structured/essay‑type questions set on the main syllabus areas.
         • Candidates answer four questions, demonstrating understanding of concepts, processes, functions and applications of Graphic Design in society.

        - Paper 3: Practical Project (100 marks total)
         • Two practical project questions are set; candidates execute one within five days, working six hours each day (80 marks for execution).
         • The question paper is released two weeks before the execution period to allow for research, sketching and preparatory notes (20 marks for preparatory work).

        Main Syllabus Areas (Overview):

        1. Importance of Graphic Design
          - Meaning and scope of Graphic Design as a visual communication profession.
          - Roles of Graphic Design in society: information, education, persuasion, advertising, entertainment and cultural expression.

        2. Tools, Materials and Equipment
          - Identification, uses and care of basic Graphic Design tools and equipment.
          - Common materials and their properties (papers, boards, inks, colours, adhesives, etc.).
          - Safe handling, maintenance and storage practices.

        3. Lettering
          - Historical development of the alphabet and basic letter structures.
          - Types of lettering: block, serif, calligraphy, freestyle, display types.
          - Principles and practice of good lettering: proportion, spacing, legibility, alignment, emphasis and stylistic variety.

        4. Drawing and Illustration
          - Basic drawing: forms, shapes, proportion, composition and perspective.
          - Shading techniques: hatching, cross‑hatching, smudging/blending, stippling (pointillism).
          - Illustration techniques and media: pen and ink, pen and wash, flat colour, realistic and abstract painting, silhouette, engraving, photography.
          - Cartooning: types, importance and functions of cartoons in communication.

        5. Colour
          - Colour relationships: tints and shades, harmonious and complementary schemes, warm and cool colours, primary, secondary and tertiary colours.
          - Basic colour theory for effective design and legibility.
          - Simple ideas on colour symbolism and emotional/psychological effects.

        6. Communication Design
          - Visual communication: meaning, importance and basic concepts.
          - Design process: problem definition, research, idea development (thumbnail sketches), layout, refinement and final artwork.
          - Types of communication design media: posters, banners, packages, labels, signboards, signposts, book covers, billboards, handbills and related print/visual media.

        7. Reproduction Processes in Graphic Design
          - Historical development of printing and reproduction processes.
          - Relief printing and stencil printing: tools, materials and basic processes.
          - Other processes (e.g. lithography, gravure, photography) and modern electronic printing devices such as inkjet, laser and electrostatic printers.

        8. Book Design
          - Typography: typefaces, type styles and their appropriate uses.
          - Type indication methods and page layout principles (margins, columns, headings, body text, illustrations, white space and balance).
          - Basic ideas in cover and page design for readability and visual appeal.

        9. Paper Craft
          - Brief history and development of paper.
          - Types and sizes of paper and their uses in Graphic Design.
          - Methods of making decorative paper surfaces: marbling, comb patterns, doodling, wax resist, roller and twine patterns.

        10. Book Binding
          - Parts of a book and their functions.
          - Tools, materials and equipment for book binding and their uses.
          - Binding methods and styles; simple book repair and binding processes.
          - Care and maintenance of books.

        11. Computer Use in Graphic Design
          - Uses of appropriate computer software for designing visual communication items (e.g. posters, flyers, logos, book covers and layouts).
          - Basic digital workflows: scanning, image editing, typesetting and output.

        12. Entrepreneurial Skills in Graphic Design
          - Career opportunities: illustration, cartooning, publishing, advertising and related fields.
          - Employment avenues: advertising agencies, printing houses, ministries/departments, media organisations and self‑employment.
          - Setting up a Graphic Design enterprise: basic factors such as pricing, costing, marketing and client relations.
          - Building and maintaining a portfolio of works.

        13. Exhibition
          - Importance and functions of exhibitions in promoting Graphic Design work.
          - Types of exhibitions and factors to consider when planning them.
          - Organising, mounting and evaluating an exhibition of Graphic Design pieces.

        Guidance for Candidates:
        - Practise regular drawing, lettering and simple design projects to build confidence and speed.
        - Learn the correct names, uses and care of tools, materials and equipment.
        - Observe and analyse posters, packages, adverts and book covers in the environment to understand design decisions.
        - Experiment with both traditional and digital methods for creating and reproducing designs.
        - Keep a sketchbook and portfolio of completed works for assessment and future opportunities.
        - Study past Graphic Design examination questions to become familiar with the demands of Papers 1, 2 and 3 and to improve time management during the exam.
        `,
  },
  {
    subject: 'GSM Phones Maintenance and Repairs',
    timeAlloted: 45 * 60,
     description: `The WASSCE GSM Phones Maintenance and Repairs syllabus assesses candidates’ basic skills in maintaining mobile phones, including fault finding, systematic repairs and the ability to set up and manage a phone maintenance and repair enterprise. It combines theoretical knowledge with hands‑on competence in hardware, software and small‑business management.

        Aims and Objectives:
        The examination tests candidates’ knowledge and understanding of:
        - Basic concepts, acronyms and terminologies used in mobile telephony.
        - Tracing, testing, troubleshooting, maintenance and repair of mobile phones.
        - Analysing mobile phone PCB (Printed Circuit Board) circuits and components.
        - Applying different fault‑finding techniques for both hardware and software.
        - Using internet resources, data manuals, service manuals and troubleshooting guides.
        - Observing general safety precautions in mobile phone repair and maintenance workshops.
        - Requirements for setting up and successfully running a mobile phone maintenance and repair business.

        Scheme of Examination:
        There are two papers – Paper 1 and Paper 2 – taken as a composite paper at one sitting.

        - Paper 1: Objective Test (40 marks, 45 minutes)
         • About 40 multiple‑choice questions covering all aspects of the syllabus: concepts and terminologies, components and functions, maintenance and repairs, troubleshooting, safety and enterprise management.

        - Paper 2: Essay and Practical Test (100 marks, 1¾ hours)
         • Section A (Essay – 60 marks, 55 minutes): twelve short/structured essay questions based on the detailed syllabus; candidates answer any ten.
         • Section B (Test of Practical Work – 40 marks, 50 minutes): two questions testing practical skills in maintenance, repairs and troubleshooting; candidates answer both.

        Main Syllabus Areas (Overview):

        A. Introduction to Mobile Phones and Service Providers
          - Common mobile phone acronyms and terminologies (e.g. GSM, CDMA, MMS, SMS, call barring/forwarding, BSI, DCT, FPS, etc.).
          - Mobile phone bands (single, dual, tri and quad band) and their uses.
          - Types of mobile phones (straight, flip, slide, etc.) and their special maintenance needs.
          - Phone accessories and their functions: headsets/hands‑free, earpieces, Bluetooth devices, batteries, chargers, cables and others.
          - Types of menus and sub‑menus (phonebook, messages, calls, settings, applications, etc.) and their functions.
          - Major voice service providers and common service codes.

        B. Basic Computer and Internet Concepts
          - Introduction to computers: identification and functions of hardware (monitor, CPU, keyboard, etc.) and software (operating and application programs).
          - Introduction to the internet and web access platforms (WAP, GPRS, EDGE, Wi‑Fi, 3G, etc.).
          - Basic browsing activities such as downloading and uploading relevant resources for repairs and updates.

        C. Essential Components of Mobile Phones and Their Functions
          - Basic structure and block diagram of a mobile phone (power unit, memory unit, radio unit) and functions of each block.
          - Identification, description and functions of hardware components such as CPU, SIM socket, earpiece, keypad, buzzer, vibrator, microphones, displays and connectors.
          - Overview of software components (operating and application software) and their roles in phone operation.

        D. Maintenance and Repair of Mobile Phones
          - Common electronic components in mobile phones: resistors, capacitors, diodes, transistors, ICs, etc.
          - Preventive maintenance: reasons, advantages and procedures (cleaning, inspection, proper handling and storage).
          - Corrective maintenance: soldering, de‑soldering, re‑balling and replacement of faulty components.
          - Common tools and equipment for hardware repairs (e.g. precision screwdrivers, Allen keys, multimeters, oscilloscopes, soldering irons, solder suckers, tweezers, cutters, PCB holders) and their uses.
          - Software tools for repairs: unlocking software, flashing software, firmware installation and related utilities.
          - Safety in the mobile phone workshop: safety rules and regulations, safe use of tools and basic safety facilities (first‑aid box, fire extinguishers, safety charts).
          - Recognition of common hardware problems (damaged screens, charging ports, mouthpieces, earpieces, keypads, etc.).
          - Common software problems (phone lock codes, “contact service provider”, “invalid SIM”, “SIM card rejected”, hanging and restart issues, etc.).

        E. Troubleshooting
          - Basic troubleshooting skills: disassembling and assembling phones, testing and fault‑finding on displays, speakers, vibrators, ringers, charging ports and jacks, batteries, keypads and panels.
          - Use of relevant phone menus and diagnostic features for fault‑finding.
          - Identification of mobile phone ICs and electronic components on PCB layouts and schematic diagrams.
          - Tracing and fault‑finding in hardware: identifying faulty components by visual inspection, continuity and resistance tests, detecting open/short circuits and checking voltages at test points and IC pins.
          - Troubleshooting techniques: metering methods, signal tracing/injection, component testing and visual checks.
          - Troubleshooting SMD (Surface Mount Devices) and BGA (Ball Grid Array) components.
          - Software‑related fault‑finding: use of software codes, dealing with SIM‑locked/rejected phones, hanging and restart problems.
          - Flashing of mobile phones: flashing devices, flashing software and their uses; unlocking and resetting security/lock codes and counters.

        F. Setting Up and Managing a Mobile Phone Workshop
          - Basic requirements for setting up a mobile phone workshop: capital, personnel and factors influencing choice of location.
          - Facilities and equipment needed for an efficient workshop (tools, test instruments, workbenches, safety facilities and storage).
          - Managing a mobile phone business: simple risk analysis, costing, pricing, customer relations and estimation of return on investment.

        Supervised Apprenticeship (Industrial Training):
        - Candidates are expected to undergo a minimum cumulative total of about eight weeks of supervised apprenticeship in an accredited repair and maintenance centre to gain real‑life experience.

        Guidance for Candidates:
        - Learn and use correct mobile phone terminologies, acronyms and service codes.
        - Practise regular disassembly, assembly, testing and repairs of different phone types and models.
        - Observe strict safety precautions in the workshop and handle tools, components and batteries correctly.
        - Use computer and internet resources, data manuals and service manuals to support fault‑finding and repairs.
        - Keep a record or logbook of repaired phones, identified faults, steps taken and outcomes to build experience.
        - Seek opportunities for supervised apprenticeship/industrial training in recognised repair centres to consolidate practical skills.
        - Study past examination questions to become familiar with the structure and demands of Papers 1 and 2.
        `,
  },
  {
    subject: 'Health Education or Health Science',
    timeAlloted: 60 * 60,
     description: `The WASSCE Health Education syllabus is drawn from the Senior Secondary School Health Education curriculum and is aimed at empowering students with correct, adequate and timely information to help them acquire attitudes and practices that restore, maintain and promote positive health. Because Health Education is skill‑based and involves knowledge, beliefs, attitudes and practices, assessment includes both theory and practical work.

        Objectives:
        The examination assesses the extent to which candidates are able to:
        - Acquire basic knowledge of the human body and skills necessary for maintaining health.
        - Develop positive health practices in the school and community.
        - Identify the ecological relationship between humans and their environment as a basis for disease prevention.
        - Make observations and draw inferences from practical experiences with health implications.
        - Demonstrate readiness for further professional training in health‑related careers.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 are a composite paper taken at one sitting.

        - Paper 1: Objective Test (50 marks, 1 hour)
         • Fifty multiple‑choice questions covering the entire syllabus.

        - Paper 2: Short‑Structured and Essay (70 marks, 1 hour)
         • Section A: Six short‑structured questions; candidates answer all for 30 marks.
         • Section B: Three essay questions; candidates answer two for 40 marks.

        - Paper 3: Practical / Alternative to Practical (80 marks, 1¾ hours)
         • For school candidates, a practical test assessing skill‑based components of the syllabus.
         • For private candidates, an alternative test of practical work.

        Main Syllabus Areas (Overview):

        A. History and Development of Health Education
          - Meaning and settings of Health Education: home‑based, school‑based, community‑based, health‑facility‑based and workplace‑based programmes.
          - Meaning of health promotion and examples of health promotion activities.

        B. Human Anatomy and Physiology
          - Cells and tissues of the human body: types of cells (somatic and sex cells), cell division (mitosis and meiosis), cell differentiation and formation of tissues, organs and systems; growth and development, factors affecting them.
          - Sense organs: structure and functions of the eye, ear, skin, nose and tongue; accommodation, hearing and balance.
          - Major body systems: skeletal and muscular systems (bones, joints, muscle types and movement); circulatory system (heart, blood and lymph vessels, blood composition, circulation, blood clotting, blood groups and related disorders such as anaemia, sickle cell disease, leukaemia, hypertension and stroke); digestive system (organs, digestion, absorption, assimilation, metabolism and related disorders); excretory system (kidney and skin structure/functions, urine formation); respiratory system (organs, breathing mechanism, gaseous exchange, tissue respiration and associated disorders); nervous system (brain, spinal cord, nerves, reflex actions and related disorders); endocrine system (locations, hormones, effects of over‑ and under‑secretion); and homeostasis (physiological factors such as body temperature, blood sugar, water and electrolyte balance, pH and blood pressure).
          - Posture and postural defects: characteristics of correct posture, determination of correct posture, types of postural defects (flat foot, scoliosis, kyphosis, lordosis), causes, effects and roles of nutrition, exercise, orthopaedic and physiotherapy services.

        C. Personal Health
          - Meaning and importance of physical health.
          - Personal hygiene: care of body parts including teeth, mouth, hair, hands, nails and skin.
          - Promotion and maintenance of health: factors (nutrition, exercise, rest, hygiene, environment) and their roles.
          - Health screening: eye defects (myopia, hypermetropia, astigmatism, presbyopia, squint) and their correction; auditory defects (conductive and neurosensory impairment); common skin disorders; care of the eye, ear and skin; use of tools such as Snellen chart, tuning fork and audiometer.
          - Dental health: structure and functions of teeth, types of human teeth, common teeth diseases (dental caries, gingivitis) and dental hygiene.

        D. Community Health
          - Community health services: meaning, providers and types (orthodox services such as primary health care; traditional services such as bone‑setters and traditional birth attendants); advantages and disadvantages of traditional medicine.
          - School health programme: meaning, components (skill‑based health education, healthful school environment, school feeding services, school health and nutrition services, school‑home‑community relationships), goals and objectives; linkages and initiatives such as Health Promoting School (HPS), Child‑Friendly School (CFS), FRESH, Life Skills and Education for All.
          - Family health: maternal and child health, family size and budgeting.
          - Ageing and death education: features of the elderly, relating with the elderly, meaning of grief and death, common causes of death.
          - Epidemiology and vital statistics: definitions, components (birth, mortality and morbidity rates) and uses.

        E. Environmental Health
          - Housing: criteria for good housing (siting, ventilation, lighting, sanitation) and components of a standard house; effects of poor housing and sanitation.
          - Water supply: sources (rain, boreholes, rivers, wells, springs, ponds, pipe‑borne water), purification methods and uses.
          - Waste disposal: meaning, types (refuse and sewage), methods and associated health implications.
          - Pollution: meaning, types (air, water, soil and noise), sources of pollutants and health consequences.
          - Industrial and occupational health: meaning, industrial health services, occupational hazards and need for health care, rehabilitation and insurance schemes.
          - Pests and vectors: meaning, habitats, harmful effects and control measures.

        F. Nutrition and Food Nutrients
          - Nutrition: classes, sources and functions of food nutrients; caloric values and requirements in relation to age, occupation and health.
          - Balanced/adequate diets: definition, classification, nutritional problems and deficiency diseases; factors responsible for unbalanced diets and related effects.
          - Factors influencing feeding habits and food choices (availability, religion, education, age, beliefs, health condition, cost).
          - Role of water in nutrition.
          - Nutritional processes: ingestion, digestion, absorption, assimilation, metabolism and defecation.
          - Beverages: definition, types (especially non‑alcoholic) and effects on health.
          - Diets for different groups: children, male and female adolescents, athletes, pregnant women, adults and the aged.
          - Food hygiene, preservation and storage: principles of food hygiene, effects of methods of food preparation on nutrients, and preservation methods (smoking/drying, freezing/refrigeration, use of chemicals/sugar/salt, bottling/pickling/canning).

        G. Safety Education and First Aid
          - First aid: meaning, aims and principles; contents of a first‑aid kit.
          - Management of specific emergencies: cuts, wounds, bleeding, dislocations, fractures, burns and scalds, shock, asphyxia/suffocation and poisoning.
          - Agencies providing first‑aid services (e.g. Red Cross, clinics, emergency services).
          - Disasters: meaning, types and disaster‑relief activities.
          - Accidents: types, causes and prevention.
          - Disability and rehabilitation: meaning, types, prevention and rehabilitation services.

        H. Drug, Alcohol and Tobacco Education
          - Drug education terminologies: drug abuse, misuse, dependence, addiction, self‑medication and rehabilitation.
          - Classes of commonly abused drugs and ways of taking drugs into the body (oral, injection, inhalation, topical, rectal, etc.).
          - Consequences of drug abuse and self‑medication.
          - Behaviour‑altering chemicals: tobacco (components and effects of smoking), alcohol and other drugs and their effects on health.

        I. Communicable and Non‑Communicable Diseases
          - Meaning and classes of diseases.
          - Communicable diseases: definition, classification and examples – air‑borne/respiratory (common cold, measles, whooping cough, poliomyelitis, tuberculosis, cerebro‑spinal meningitis, diphtheria); water/food‑borne (dysentery, cholera, typhoid fever, schistosomiasis, guinea‑worm); insect‑borne (malaria, yellow fever, trypanosomiasis, filariasis); worm infections (roundworm, tapeworm, hookworm, guinea‑worm); animal‑borne diseases (rabies, leptospirosis); contact diseases (gonorrhoea, syphilis, athlete’s foot, ringworm, leprosy, scabies and AIDS). Factors necessary for occurrence: causative agent, susceptible host and route of transmission.
          - Non‑communicable diseases: deficiency diseases (scurvy, pellagra, rickets, kwashiorkor, anaemia, beriberi) and other conditions related to growth, heredity or normal disorders (cancer, diabetes, epilepsy, haemophilia, heart disease, hypertension, ulcers, mental illness, sickle cell disease, rheumatism, infertility, asthma, albinism, etc.).

        J. Family Life and Human Sexuality Education
          - Human reproduction: basic terms (reproduction, sex, gametes, ovulation, menstruation, fertilisation, pregnancy, gestation); structures and functions of male and female reproductive organs; conception, foetal development, childbirth and related disorders.
          - Human sexuality education: meaning, importance and life skills (assertiveness, negotiation and communication); sexuality, sexual and reproductive health rights, gender and equality.
          - The family: meaning, types, family size and roles of family members; family life challenges (widowhood, single parenthood, pre‑ and extra‑marital affairs, in‑law relations) and agencies promoting healthy family living.
          - Family planning and safe motherhood: meaning, need, birth‑control methods and elements of safe motherhood.
          - Population education: meaning of population and population education; census and its problems; implications of small and large populations.

        K. Emotional and Social Health
          - Interrelationship between emotional and social health.
          - Personality and personality traits; common personality problems and possible solutions.
          - Mental health: meaning, factors that promote mental health, types and causes of mental health problems, prevention, management and importance of good mental and social health.

        L. Consumer Health Education
          - Consumer health products and services; importance of checking labels and expiry dates.
          - Laws and agencies protecting consumers.
          - Factors influencing choice of products (authenticity, cost, availability, accessibility, advertisement).
          - Consumer health‑care services and different types of health services (traditional, orthodox, alternative and faith‑based).
          - Quacks and quackery: meaning, types (mechanical, electrical, drug, cosmetic and medical) and dangers.
          - Health insurance and advertisement of health products and services.
          - Nostrum: meaning, types (health tonics, miracle substances, magic potions, amulets and talismans) and reasons for avoiding them.

        Materials for Health Education Laboratory/Practical Work:
        - Models of organs and body parts, human skeleton and bones.
        - Contraceptive devices, reagents for nutrient tests and food samples.
        - Road‑safety signs, safety equipment, first‑aid box and kits, fire extinguisher and sand bucket.
        - Photographs and charts of health facilities, systems of the body, methods of sewage/refuse disposal, communicable and non‑communicable diseases, accident and disaster scenes, correct postures and postural defects.
        - Microscope, stethoscope, preserved worms, pests and vectors.
        - Materials for personal hygiene and food preservation.

        Guidance for Candidates:
        - Relate classroom learning to real‑life health practices at home, school and in the community.
        - Take practical sessions seriously, including simple measurements (pulse, temperature), observations and demonstrations.
        - Use charts, models and local examples to understand body systems, diseases and health programmes.
        - Practise and model good personal, environmental and nutritional habits.
        - Stay informed about common diseases, prevention strategies and available health services.
        - Attempt past Health Education examination questions to become familiar with the structure and demands of Papers 1, 2 and 3.
        `,
  },
  {
    subject: 'Home Management',
    timeAlloted: 60 * 60,
     description: `The WASSCE Home Management syllabus assesses candidates’ knowledge, understanding, appreciation and application of core concepts and skills for managing the home and contributing positively to family and community life. It focuses on management concepts, decision‑making, use of human and material resources, care of the home and family, consumer skills, sound health practices and self‑reliant skills for effective living.

        Aims and Objectives:
        The examination seeks to test how well candidates can:
        - Understand and apply basic management concepts (goals, values, standards and needs).
        - Use appropriate decision‑making processes in everyday home and family situations.
        - Identify and manage human and material resources available to the family.
        - Demonstrate basic skills in caring for the home, family members and the wider society.
        - Apply consumer skills in choosing, using and caring for goods and services.
        - Practise sound health, safety and sanitation measures in the home.
        - Develop self‑reliant and entrepreneurial skills for effective living and wealth creation.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 are a composite paper taken at one sitting.

        - Paper 1: Objective Test (50 marks, 1 hour)
         • Fifty multiple‑choice questions covering the entire syllabus.

        - Paper 2: Essay (50 marks, 1 hour 15 minutes)
         • Six essay questions set on the major syllabus areas.
         • Candidates answer four questions for a total of 50 marks.

        - Paper 3: Practical Test (100 marks, 3 hours)
         • A practical examination, conducted by a visiting examiner appointed by WAEC, assessing candidates’ skills in selected areas such as food preparation and service, clothing care and simple repairs, household care and organisation, and related management tasks.

        Main Syllabus Areas (Overview):

        1. Principles of Management
          - Meaning of Home Management and careers in Home Management.
          - The management process: planning, organisation, implementation and evaluation.
          - Application of the management process to specific home tasks (e.g. meal preparation, laundry, cleaning, budgeting).
          - Motivations for Home Management and factors that influence how families manage.
          - Family resources: definition, characteristics and kinds – human (time, energy, skills, work simplification as applied to the home), non‑human/material and community resources.
          - Decision‑making: meaning, process and its importance in family life.
          - Management of time and energy for individuals and families.

        2. Family Living
          - The family: meaning, types (nuclear, monogamous, polygamous, extended) and advantages/disadvantages of each.
          - The family life cycle: beginning, expanding, contracting and empty‑nest stages.
          - Functions of the family and roles/responsibilities of family members (mother, father, children, working mothers, single parents, etc.).
          - Family relationships (husband–wife, parent–child, sibling–sibling, nuclear and extended families); factors affecting relationships (communication, family values) and their effects on well‑being; conflict and conflict resolution.
          - Courtship and marriage: types of marriage; boy/girl relationships (meaning, guidelines for healthy relationships, problems of unhealthy relationships, consequences of indiscriminate sexual indulgence and need for sex education).
          - Choosing a life partner: factors such as blood group, genotype, HIV status; dating, courtship and engagement.
          - Preparation for marriage: readiness (where to live, maturity, finances), introduction to each other’s family and marriage ceremonies in different localities.
          - Parenthood: planning a family, reproduction, pregnancy, antenatal care, labour, delivery and post‑natal care (including immunisation).
          - Parenting: meaning, importance and responsibilities (providing needs, care and guidance).

        3. Feeding the Family
          - Classes of food and food nutrients; scientific study of nutrients and effects of heat on nutrients.
          - Special nutritional needs of infants, toddlers, adolescents, adults, pregnant and lactating women, the aged, convalescents, invalids and vegetarians.
          - Food storage and preservation methods.
          - Planning balanced meals for different groups of people.
          - Kitchen planning, hygiene and safety.
          - Food purchasing: wise buying practices and prevention of waste.
          - Preparation and serving of meals, snacks and beverages: cooking terms and methods; flour and flour mixtures; cooking equipment, utensils and tableware; table setting and service; entertainment and hospitality.

        4. Clothing the Family
          - Clothing: meaning, types and functions for individuals and families.
          - Household linen: types, selection, use and care.
          - Textiles and fabrics: origin of fibres; classification of fibres and fabrics.
          - Fabric construction methods such as weaving, knitting and crocheting.
          - Planning a wardrobe to suit age, occupation, climate, culture and budget.
          - Maintenance of clothing: simple garment repairs; laundry agents (water, detergents, fabric rinses, stiffening agents, bleaches) and their uses; laundry equipment and tools.
          - Laundry work: management of the family wash, stain removal, care of special garments and appropriate storage of clothing.

        5. Housing the Family
          - The family house: meaning of a house, types (storey building, bungalow, duplex, etc.) and classification (traditional and modern).
          - Choosing a family house: ways of acquiring a house (renting, mortgage, building, buying, inheritance, gift) and factors influencing choice (economic, social, psychological, site, ventilation, design); guidelines for selecting a suitable home.
          - Setting up a home: difference between a house and a home; equipment and furnishings in the home (large and small equipment; hard and soft furnishings; fixtures).
          - Home improvement and interior decoration: meaning and importance; elements and principles of art and design (colour, texture, line, proportion, rhythm, etc.) and their application; guidelines for interior decoration and simple flower arrangements.
          - Utilities in the home: water (sources, uses, purification and storage); light and electricity (meter reading, electricity bills); fuels (gas, kerosene, firewood, charcoal, electricity) – uses, storage, control, advantages and disadvantages.
          - Simple home maintenance and repairs: cleaning agents and materials (local and commercial); cleaning equipment and tools; care of surfaces (wood, tiles, formica, concrete, plastics); care of floors and floor coverings (carpets, linoleum, mats, rugs, terrazzo, parquet); care of household furnishings and ornaments (ceramics, metals, collage, etc.); cleaning of different functional areas in the home and bed making.
          - Safety in the home: importance, common household accidents, prevention and use of first aid.
          - Sanitation and waste disposal: meaning, importance, disposal of household waste and drainage; methods of disposal and pollution‑related health hazards; household pests (types and control).

        6. Wealth Creation and Management
          - Creativity and entrepreneurship: meaning, importance and relationship between creativity and entrepreneurship.
          - Entrepreneurial skills: creativity, management and communication.
          - Possible home‑based enterprises (e.g. fashion designing, hairdressing, fast‑food services, bead‑making and related ventures).
          - Money management: meaning, guidelines, processes and family budgeting (income, expenditure and steps in budgeting).
          - Wealth creation: meaning, types, advantages and ways of creating wealth (saving, investment, trading, etc.).
          - Capital market investment: meaning, advantages, guidelines and examples of investment opportunities (stocks, shares, treasury bills).

        7. Consumer Education
          - Consumer education: definition, importance and role in home management.
          - Rights and responsibilities of the consumer.
          - Consumer information: sources and uses.
          - Consumer legislation and agencies: types, functions and regulatory roles.
          - Principles of consumer education and wise consumption practices.
          - Consumer agents and environmental‑friendly consumption practices.
          - Market and market survey: meaning, types, uses/importance and procedures; purchasing practices, wise buying and prevention of wastage in the home.
          - Reading and using relevant Home Management textbooks and materials as recommended by the syllabus.

        Guidance for Candidates:
        - Practise applying the management process (plan–organise–implement–evaluate) to everyday home tasks.
        - Observe how real families manage time, energy, money, housing, food, clothing and relationships.
        - Develop practical skills in food preparation and service, clothing care and simple home maintenance.
        - Build basic budgeting, saving and simple record‑keeping habits.
        - Use markets, shops and available resources as “laboratories” for practising wise consumer behaviour.
        - Study past Home Management examination questions to become familiar with the structure and demands of Papers 1, 2 and 3.
        `,
  },
  {
    subject: 'Ibibio',
    timeAlloted: 60 * 60,
    description: `The WASSCE Ibibio syllabus is based on the senior secondary Ibibio curriculum developed by NERDC. It aims to develop candidates’ ability to speak, read and write competently in Ibibio, communicate effectively within their environment, appreciate their culture, customs and institutions, and acquire tools for creative writing and literary appreciation.

        Aims and Objectives:
        The syllabus is designed to enable candidates to:
        - Use standard Ibibio confidently in speech and writing for everyday communication.
        - Gain insight into Ibibio culture, customs and social institutions.
        - Acquire skills for creative writing and the appreciation of Ibibio literature.
        - Become familiar with the sound system, grammatical structures and other key features of the language.

        Scheme of Examination:
        The examination consists of two papers, Papers 1 and 2, which form a composite paper taken at one sitting. Rubrics are written in Ibibio, and all answers are to be written in standard Ibibio orthography using the appropriate metalanguage.

        - Paper 1: Objective Test (60 marks, 1 hour)
          • Sixty multiple‑choice questions in three sections – Language, Literature and Culture.

          Section A – Language (30 items):
          - Comprehension (two short passages).
          - Composition/letter‑writing concepts.
          - Sound system.
          - Grammar.
          - Translation.

          Section B – Literature (22 items):
          - Basic principles of literary appreciation.
          - Oral literature: prose, poetry and drama.
          - Written literature: prose, poetry and drama.
          (Questions, except those on basic principles, are based on the prescribed set books.)

          Section C – Culture (8 items):
          - Customs.
          - Institutions.

        - Paper 2: Essay / Composition (100 marks, 2 hours)
          • Also divided into three sections – Language, Literature and Culture.
          • Candidates answer six questions out of a total of thirteen, following detailed rubrics.

          Section A – Language:
          Part 1 – Essay/Letter writing
          - Five topics set from narrative, descriptive, argumentative, expository, dialogue, proverbial essays and letter‑writing.
          - Candidates write one essay of about 300 words (recommended time: about 45 minutes).

          Part 2 – Sound System
          - Two questions set; candidates answer one.
          - Topics include: vowels, consonants and syllabic nasal (production, place and manner of articulation, basic phonetic/phonemic classification); syllable structure; sound processes (e.g. vowel harmony); assimilation of loan words; tone and intonation (high, low, falling and rising tones).

          Part 3 – Grammar
          - Two questions set; candidates answer one.
          - Areas include: word structure and word formation (root, affixes, suffix, prefix, compounding, borrowing, reduplication); word classes (noun, verb, adverb, pronoun, adjective, conjunction, preposition); grammatical categories (number, person, tense, negation); phrases and clauses (structure and functions); sentence structure (simple, compound, complex) and sentence types by function (interrogative, declarative, negative); and correct use of punctuation marks.

          Section B – Literature:
          - Divided into Oral Literature and Written Literature.
          - Six questions set; candidates answer two – one on Oral Literature and one on Written Literature.

          Oral Literature
          - Prose: folktales, legends, proverbs, riddles.
          - Poetry: songs, ballads.
          - Drama: traditional drama.
          - Three set books are prescribed across these genres; one question is set on each, and candidates answer any one.

          Written Literature
          - Prose, poetry and drama based on three prescribed texts (one in each genre).
          - One question is set on each text; candidates answer any one.

          Section C – Culture:
          - Two essay‑type questions set on Ibibio customs and institutions; candidates answer one.

        Main Content Areas (Overview):

        1. Language
           - Effective use of Ibibio for oral and written communication in everyday situations.
           - Comprehension of prose passages and answering questions on content, vocabulary and inference in Ibibio.
           - Essay and letter writing in various forms (narrative, descriptive, expository, argumentative, dialogue, proverbial essays and formal/informal letters).
           - Sound system: inventory of vowels, consonants and syllabic nasals; syllable structure; tone and intonation patterns; key phonological processes.
           - Grammar: word classes, word formation processes, grammatical categories, phrases and clauses, sentence structure and punctuation.

        2. Literature
           - Basic principles of literary appreciation: plot, characterisation, setting, theme, style and figurative language.
           - Oral literature genres: folktales, legends, riddles, proverbs, songs, ballads and traditional drama; their forms, functions and performance context.
           - Written literature genres: prose, poetry and drama in Ibibio; close study of selected texts with attention to themes, characters, structure and style.

        3. Culture (Customs and Institutions)
           - Traditional occupations (mme ubok utom), such as fishing, farming, trading, palm‑wine tapping and crafts.
           - Family structure (ubon/ekpók): organisation of the family and kinship relations.
           - Religious institutions (usañ ukpono/isẹkpono): beliefs, deities and worship practices.
           - Traditional foods (ndidia/utem ndidia nte) such as staples and soups.
           - Funerals and mortuary rites.
           - Traditional festivals (mme usòrò/mbre Ibibio): types and significance.
           - Taboos (ibed) and their social functions.
           - Traditional education (edu uwem – ukpeep), coronation (uyad ntinya), maiden‑fattening (mboppo), rites of passage and age‑grade institutions.
           - Traditional societies and Ibibio worldview.
           - Ibibio naming practices and their cultural meanings.

        Prescribed Texts and References:
        - Selected oral literature, written literature, culture, grammar and reference texts are prescribed by WAEC to support detailed study of the language, literature and culture components.

        Guidance for Candidates:
        - Practise speaking Ibibio regularly in formal and informal contexts using standard orthography.
        - Read widely in Ibibio, especially the prescribed prose, drama and poetry texts, as well as culture and grammar books.
        - Write different types of essays and letters in Ibibio, paying attention to correct spelling, tone marking (where required), grammar and style.
        - Revise basic phonetics/phonology and grammar of Ibibio using examples from everyday speech and texts.
        - Learn and use proverbs, riddles, songs and other oral forms appropriately to enrich language use and cultural understanding.
        - Study past Ibibio examination questions to become familiar with the structure and demands of Papers 1 and 2 and to build speed and accuracy.
        `,
  },
  {
    subject: 'Information and Communication Technology (Core)',
    timeAlloted: 45 * 60,
     description: `The WASSCE Information and Communication Technology (Core) syllabus introduces candidates to basic ICT concepts and tools, and tests their ability to apply ICT knowledge and skills in education, everyday life and business contexts. It also prepares candidates for higher studies in ICT‑related fields.

        Aims:
        The examination is designed to assess how far candidates have:
        - Acquired knowledge and understanding of fundamental ICT concepts and terminology.
        - Developed the capability to use common ICT tools productively in learning and in simple business tasks.
        - Built a foundation for further study in ICT and related disciplines.

        Scheme of Examination:
        There are two papers, Papers 1 and 2, taken together as a composite paper at one sitting.

        - Paper 1: Objective Test (20 marks, 45 minutes)
         • Forty multiple‑choice questions based on the full syllabus.

        - Paper 2: Practical‑Oriented and Essay Questions (80 marks, 2 hours)
         • Four compulsory test‑of‑practical‑knowledge questions.
         • Six essay‑type questions, from which candidates answer any two.

        Main Syllabus Areas (Overview):

        1. Introduction to Basic ICT Concepts and Computers
          - Meaning of ICT and related terminologies.
          - Common ICT tools and their uses (e.g. mobile phones, computers, the Internet, television, radio).
          - Impact of ICT on education, social life and economic development.
          - Information processing cycle and distinction between data and information.
          - Basic types and uses of computers.

        2. Computer Hardware
          - Categories of computer hardware: system unit, peripherals and storage media.
          - Examples and basic functions of input, output, processing and storage devices.

        3. Computer Software
          - Types of software: system software and application software.
          - Open‑source versus proprietary software and common examples.

        4. Threats to Computers and Users
          - Meaning and categories of computer‑related threats.
          - Natural threats (e.g. earthquakes, heat, floods) and human threats (e.g. viruses, worms, intentional damage).
          - Health hazards to computer users: causes, effects and good ergonomic practices.
          - Basic remedies and preventive measures against threats and hazards (backups, antivirus, surge protectors, safe practices, etc.).

        5. Word Processing
          - Word‑processing packages: uses and importance for creating and editing documents.
          - Key concepts and common terminology (documents, cursor, formatting, clipboard, etc.).
          - Creating, saving and retrieving documents.
          - Editing and formatting text; inserting objects such as tables and graphics.
          - Printing documents.
          - Emphasis on speed and accuracy in typing (about 30–35 words per minute).

        6. Spreadsheets
          - Spreadsheet packages: uses and importance for calculations, data organisation and basic analysis.
          - Related concepts and terminology (workbook, worksheet, cell, range, formula, function, etc.).
          - Types of data (text, numbers, dates, formulas) and their uses.
          - Creating and saving workbooks.
          - Constructing and inserting formulae and functions.
          - Editing and formatting worksheets.
          - Printing worksheets and simple reports.

        7. Presentation Packages
          - Presentation software: uses and importance for preparing and delivering talks and lessons.
          - Creating and saving presentations.
          - Editing, formatting and inserting objects (text boxes, pictures, charts, tables, audio/video).
          - Slide shows and basic animation/transitions.
          - Use of master slides.
          - Factors to consider when preparing and delivering effective presentations (audience, content, layout, timing, clarity, etc.).
          - Printing presentation handouts and other outputs.

        8. The Internet and Online Communication
          - Meaning of the Internet; basic concepts, common terminologies and requirements for connection.
          - Features and uses of browser windows (address bar, navigation buttons, tabs, etc.).
          - Rules and regulations guiding responsible Internet use (netiquette).
          - Sending, receiving and managing e‑mail.
          - Internet‑related communication tools (chatting, downloading, uploading, search engines, social networks such as Facebook, etc.).

        Guidance for Candidates:
        - Practise regularly with real ICT tools (computers, mobile devices and common software) to build confidence and speed.
        - Apply ICT skills to schoolwork and simple real‑life tasks such as typing assignments, basic calculations and presentations.
        - Observe safe, healthy and responsible use of computers and the Internet.
        - Use textbooks, tutorials and past ICT examination questions to revise each topic and become familiar with the format and demands of Papers 1 and 2.
        `,
  },
  {
    subject: 'Information and Communication Technology (Elective)',
    timeAlloted: 60 * 60,
     description: `The WASSCE Information and Communication Technology (Elective) syllabus builds on core ICT knowledge to test candidates’ deeper appreciation of ICT concepts, practical skills for education and business, and readiness for higher studies in ICT and related fields.

        Aims:
        The syllabus is intended to:
        - Test candidates’ understanding and appreciation of key ICT concepts and technologies.
        - Assess their ability to apply ICT skills in educational, social and business contexts.
        - Verify their potential for further studies in Information and Communication Technology and related areas.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 form a composite paper taken at one sitting.

        - Paper 1: Objective Test (25 marks, 1 hour)
         • Fifty multiple‑choice questions based on the full syllabus.

        - Paper 2: Essay (30 marks, 1 hour)
         • Five essay‑type questions.
         • Candidates answer any three.

        - Paper 3: Practical Test (45 marks, 2 hours)
         • Three practical questions, all of which must be answered, assessing hands‑on competence with common ICT applications and tools.

        Main Syllabus Areas (Overview):

        1. Data Representation
          - Data types such as integers, real numbers and strings.
          - Number bases, with emphasis on binary, decimal and hexadecimal.
          - Units of data storage and their relationships (bit, byte, kilobyte, megabyte, etc.).
          - Media types and digital formats (e.g. digital video, digital sound, Voice over Internet Protocol – VoIP, voice‑recognition systems).

        2. Introduction to Information Systems
          - Meaning of an information system and basic components.
          - Types of information systems and their uses.
          - Attributes of good information (e.g. accuracy, timeliness, relevance).
          - Internal and external information sources, including intranet, extranet, memos, intercom, talking drum, mobile phone, etc.
          - Role of information in society.

        3. Introduction to Digital Technology Culture
          - The Internet: basic ideas, opportunities and risks.
          - Computer crime: examples and simple preventive measures.
          - Role and impact of ICT on everyday life (e‑business, e‑health, e‑mail, e‑learning, computer‑based training, computer‑assisted manufacturing, computer‑aided design, etc.).

        4. Word Processing (Advanced Use)
          - Creating, editing and formatting documents for personal, educational and business use.
          - Preparation of business documents such as memos and reports.
          - Using mail‑merge features for personalised mass communication.
          - Printing documents and managing layouts.

        5. Desktop Publishing
          - Creating, editing and formatting publications such as flyers, newsletters and simple brochures.
          - Applying layout principles (text, images, spacing) and preparing documents for printing.

        6. Spreadsheets (Advanced Use)
          - Creating, editing and formatting worksheets.
          - Sorting and querying data to extract information.
          - Creating graphs and charts to represent worksheet data visually.
          - Working with built‑in functions for calculations and basic data analysis.
          - Data security features such as the use of passwords to protect files.

        7. Hardware
          - External components (peripherals) and their functions.
          - Internal components of the computer and their functions.
          - Basic computer diagnostics and maintenance procedures.

        8. Software
          - System software (especially operating systems) and their functions.
          - Utility programs and their uses.
          - Types of application programs and common examples.
          - Software licensing considerations (e.g. proprietary, shareware, freeware, open‑source, site licences).
          - Software concepts and terminology: machine language; high‑level versus low‑level languages; fourth‑generation languages; language translators; source code; error messages; software portability; compilers, interpreters and assemblers.
          - Installation and upgrading of computer software (basic concepts and safe practices).

        9. Networking
          - Network concepts and benefits.
          - Types of networks (e.g. LAN, WAN, MAN, PAN).
          - Network topology (e.g. bus, star, ring, mesh) and simple characteristics.
          - Network architecture and configuration basics.
          - Communication of data on networks (media, devices and protocols at a basic level).
          - Data security on networks: threats and simple protection measures.

        10. Introduction to Programming
          - Flowcharts: symbols and construction of simple logical diagrams.
          - Algorithms and basic data structures for solving simple problems.
          - Program development life cycle (problem definition, algorithm design, coding, testing, documentation and maintenance).
          - Programming languages: generations and categories.
          - Web design fundamentals using HTML (HyperText Markup Language).
          - Practical knowledge of BASIC and HTML with examination questions limited to QBASIC constructs.

        11. Database Management Systems
          - Designing and creating simple databases (tables, fields, records and keys).
          - Working with queries to retrieve and manipulate data.
          - Using forms for data entry and interaction.
          - Producing reports from stored data.

        12. Application of ICT Tools in Education
          - Types of ICT tools used in teaching and learning.
          - Learning with ICT tools (e.g. computer‑based training, educational software, learning platforms).
          - Advantages and disadvantages of using ICT tools in education.

        Guidance for Candidates:
        - Practise extensively with word processors, spreadsheets, desktop‑publishing tools, databases and simple programming environments.
        - Relate each topic to real‑life applications in school, business and the wider community.
        - Pay attention to both theory (concepts, definitions and diagrams) and practical skills (hands‑on tasks and projects).
        - Use past ICT (Elective) examination questions to become familiar with the style and balance of objective, essay and practical items across Papers 1, 2 and 3.
        `,
  },
  {
    subject: 'Insurance',
    timeAlloted: 50 * 60,
     description: `The WASSCE Insurance syllabus introduces candidates to the basic concepts, principles, products and practice of insurance, and prepares them for further studies as well as informed participation in the insurance market as buyers, intermediaries or future practitioners.

        Scheme of Examination:
        There are two papers, Papers 1 and 2, taken together as a composite paper at one sitting.

        - Paper 1: Objective Test (50 marks, 50 minutes)
         • Fifty compulsory multiple‑choice questions covering the entire syllabus.

        - Paper 2: Case Study and Essay (80 marks, 2 hours)
         • Section A: One compulsory case‑study passage (about 200–250 words) with a compulsory question carrying 20 marks.
         • Section B: Six essay‑type questions; candidates answer four, each carrying 15 marks.

        Main Syllabus Areas (Overview):

        1. Introduction to Insurance
          - Meaning and basic concepts of insurance.
          - Historical development of insurance in the pre‑ and post‑independence era, including key legislation.
          - Insurance contracts: parties to the contract, essential features and basic contract principles.
          - Risk: meaning, effects of risk on society, classification of risks, and distinction between insurable and uninsurable risks.
          - Hazards: meaning and types, and their relationship to risk.
          - Perils: meaning and differences among perils, risks and hazards.
          - Functions of insurance (primary and secondary functions) and general benefits of insurance to individuals, businesses and the economy.

        2. Principles of Insurance
          - Insurable Interest: meaning, essential features and application in life assurance and property insurance.
          - Utmost Good Faith: meaning; duties of disclosure by both insured and insurer; breaches and their effects.
          - Proximate Cause: meaning and application in the settlement of claims.
          - Indemnity: meaning; methods by which insurers provide indemnity; application in property and liability insurance.
          - Subrogation: meaning; insurer’s rights and insured’s duties under subrogation.
          - Contribution: meaning, conditions necessary for contribution and simple applications.

        3. Documents Used in Effecting Insurance Contracts
          - Proposal forms: meaning, functions, uses and types of contracts where they are employed.
          - Cover notes: meaning, contents, functions/uses and contracts in which they are issued.
          - Certificate of insurance: meaning; use in motor, marine and employer’s liability insurance.
          - Policy: meaning, component parts, and parties to a policy document.

        4. Insurance Practice
          - Reinsurance: meaning; forms (facultative and treaty); functions and uses.
          - Insurance renewals: renewal notice (meaning, procedure, legal status); no‑claim discount (meaning and uses); long‑term agreement (meaning and uses).
          - Insurance claims: meaning; claims procedure (notification, documentation and claim forms, investigation, discharge voucher, payment); duties of the insured and the underwriter; roles of loss assessors and loss adjusters.
          - Insurance premium: meaning; factors considered in determining premium; reasons for loading premiums; return premiums (partial and full) and reasons for returning premium.

        5. Non‑Life Insurance Products
          - Motor insurance: meaning; types of policies (private car, commercial vehicle, motorcycle, agricultural/forestry vehicles, special types such as forklifts and tractors); scope of cover (Act only, third‑party only, third‑party fire and theft, comprehensive); ECOWAS Brown Card (meaning and uses).
          - Fire insurance: meaning; standard fire policy cover and special perils (earthquake, flood, riot and strike); types of property covered (buildings and contents, machinery and plant, stocks); typical buyers (individuals, corporate bodies, government agencies).
          - Theft and burglary insurance: meaning; properties covered (household property, stocks, machinery and plant); use in business and private premises; differences between theft insurance and burglary insurance.
          - Money insurance: meaning; what constitutes “money” (cash, cheques, postal orders); types of cover (in transit, in safe/strong room after business hours, in custody of staff such as cashiers).
          - Fidelity guarantee insurance: meaning; types of policies (named policy, position policy, blanket policy); meaning of bond; differences between commercial guarantees and bonds.
          - Engineering insurance: meaning; types (e.g. boiler, explosion, computer‑all‑risks); scope of cover (material damage and related liability).
          - Marine insurance: meaning; marine perils; main types of cover (hull, cargo and freight policies).
          - Aviation insurance: meaning; types of aviation policies (hull, cargo, freight, liability arising from aircraft operation, liability to passengers and non‑passengers); aviation risks; basic international regulations affecting aviation insurance (e.g. Montreal Convention, Hague Rules, Warsaw Convention).
          - Employer’s liability insurance: meaning; employees’ compensation policy; level of benefits and perils covered.
          - Public liability insurance: meaning; types such as personal liability and product liability policies.
          - Professional indemnity insurance: meaning; professionals who require it (doctors, lawyers, insurance brokers, accountants, architects, etc.).
          - Business interruption (consequential loss) insurance: meaning; causes of business interruption (e.g. machinery breakdown, fire incidents).
          - Personal accident insurance: meaning; risks covered (death, bodily injury, permanent disability, total temporary disability, medical expenses); forms (individual and group); benefits to insured; typical buyers of personal and group accident policies.

        6. Insurance Market (Operators)
          - Meaning of the insurance market.
          - Main operators: insurance companies, reinsurance companies, insurance intermediaries (brokers and agents), buyers (individual/private and corporate/public institutions), sellers and supporting services (loss assessors and loss adjusters).

        7. Insurance Regulators and Professional Bodies
          - Key regulatory and professional bodies and their basic roles/functions, such as:
           • Nigerian Insurance Association (NIA).
           • Nigerian Council of Registered Insurance Brokers (NCRIB).
           • Institute of Loss Adjusters of Nigeria (ILAN).
           • National Insurance Commission (NAICOM).
           • Chartered Insurance Institute of Nigeria (CIIN).

        8. Common Insurance Terminologies
          - Understanding frequently used terms such as: hazard, peril, concealment, disclosure, days of grace, ex‑gratia payment, extra premium, endorsement, excepted perils, subject‑matter of insurance, under‑insurance, return premium, surrender value, cover note and similar expressions.

        Guidance for Candidates:
        - Study the key principles, documents and procedures of insurance and relate them to real‑life situations.
        - Use case studies to practise interpreting policies, analysing risks and applying insurance principles.
        - Familiarise yourself with common non‑life products and the roles of different market operators and regulators.
        - Learn and be able to explain basic insurance terminologies accurately.
        - Work through past Insurance examination questions to build confidence with both objective and essay/case‑study formats.
        `,
  },
  {
    subject: 'Integrated Science',
    timeAlloted: 60 * 60,
     description: `The WASSCE Integrated Science syllabus (based on the Ghana Education Service SHS syllabus) develops an integrated body of scientific knowledge and skills across biology, chemistry, physics, agriculture and environmental science. It aims to raise scientific literacy and equip candidates with practical, problem‑solving abilities for life and work in a technological world.

        Aims and Objectives:
        The syllabus is intended to enable candidates to:
        - Solve basic problems in their environment through analysis, experimentation and practical work.
        - Appreciate the diversity and interconnectedness of living and non‑living things and the patterns of change in nature.
        - Adopt sustainable habits for managing the natural environment and resources.
        - Use common scientific appliances and gadgets effectively, understanding their basic operation and principles.
        - Explore, conserve and optimise energy as a vital resource.
        - Develop a scientific way of life based on observation, investigation and evidence.
        - Seek solutions to life problems recognising the interactions of science, technology and other disciplines.

        Practical Requirements:
        Candidates are expected to have:
        - Carried out rearing activities for at least one group of animals (e.g. poultry, small ruminants or small mammals).
        - Visited farms and science‑related institutions to observe scientific work and applications.
        - Kept practical notebooks with records of laboratory and field activities.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 form a composite paper taken at one sitting.

        - Paper 1: Objective Test (50 marks, 1 hour)
         • Fifty multiple‑choice questions sampling the whole syllabus.

        - Paper 2: Essay (80 marks, 1½ hours)
         • Six essay‑type questions; candidates answer four, 20 marks each.

        - Paper 3: Practical Test (60 marks, 2 hours)
         • Four questions testing practical skills in laboratory and field work; candidates answer all four.

        Main Syllabus Areas (Overview):

        A. Diversity of Matter
          - Introduction to Integrated Science: concept of integration across scientific fields; science as an interrelated body of knowledge; careers in science and technology; the scientific method (problem identification, hypothesis, experimentation, data collection, analysis and conclusion); safety precautions in the laboratory.
          - Measurement: basic and derived quantities and S.I. units; common measuring instruments and their use; sources of error; density and relative density experiments for regular and irregular objects.
          - Diversity of living and non‑living things: characteristics of living things; basic classification of living and non‑living things; biodiversity and its importance; simple classification schemes and historical contributions (e.g. Aristotle, Linnaeus, Mendeleev).
          - Matter: particulate nature of matter; elements (first twenty), compounds and mixtures; atoms, molecules, ions and atomic structure; ionic and covalent bonding; simple properties of ionic and covalent compounds; relative atomic mass, isotopes and the mole concept; simple solution preparation and dilution.
          - Cells: structure and functions of plant and animal cells and selected specialised cells (e.g. red blood cells, nerve cells, palisade cells, sperm cells, lymphocytes and phagocytes).
          - Rocks: types (igneous, sedimentary, metamorphic), their formation and characteristics; physical, chemical and biological weathering.
          - Acids, bases and salts: simple definitions (proton‑transfer concept), properties and uses; preparation and testing of common gases (H₂, CO₂, NH₃); simple classification tests for acids, bases and salts; preparation of salts; indicators, pH and determination of soil pH.
          - Soil conservation and fertility: soil and water conservation principles; soil nutrients (macro and micro), their functions and deficiency symptoms; maintenance of soil fertility; organic and inorganic fertilisers; causes and control of soil resource depletion.
          - Water: physical and chemical properties; hardness and softness, causes and methods of softening; water treatment for domestic/public use.
          - Metals and non‑metals: classification of materials; physical properties and uses of selected elements; alloys, their composition, uses and advantages.
          - Exploitation of minerals: examples (e.g. bauxite, gold, crude oil, kaolin); benefits, negative impacts and ways to minimise environmental effects.
          - Rusting: conditions for rusting, factors affecting rate and methods of prevention.
          - Organic and inorganic compounds: basic groups of hydrocarbons and simple functional groups; neutralisation and esterification; petrochemicals and crude‑oil refining; uses and environmental effects.

        B. Cycles
          - Air movement: land and sea breezes; trade winds; movement of air masses, storms and their effects (including spreading pollutants); precautions against storms.
          - Nitrogen cycle: processes, diagram and importance to plants and animals.
          - Hydrological (water) cycle: distribution of water on earth; processes in the cycle; sources and effects of water contamination; basic household and community water‑treatment and conservation methods.
          - Life cycles of pests and parasites: common pests/parasites of humans, animals and crops; life cycles of selected examples (e.g. tapeworm, guinea worm, Anopheles mosquito, weevils); control methods.
          - Crop production: general principles of crop production and practical production of selected crops (e.g. vegetables, cereals, legumes, roots/tubers), including harvesting, storage and marketing.
          - Farm animal production: main activities in farm‑animal production; ruminant and non‑ruminant production (e.g. cattle, goats, sheep, poultry, pigs, rabbits) and basic management practices.

        C. Systems
          - Skeletal system: major parts of the mammalian skeleton (axial and appendicular); types and functions of joints.
          - Reproduction and growth in plants: flower structure, pollination, fertilisation, fruit and seed formation; seed/fruit dispersal and germination; vegetative (asexual) reproduction methods and their importance.
          - Respiratory system: aerobic and anaerobic respiration; structure and function of the mammalian respiratory system; breathing mechanics; common respiratory disorders and their prevention; gas exchange in plants.
          - Food and nutrition: classes of food and their functions; malnutrition and diet‑related diseases; food fortification/enrichment, body mass index (BMI) and health benefits of water.
          - Dentition, feeding and digestion: structure and function of teeth; care of teeth; human digestive system and its functions.
          - Transport: diffusion, osmosis and plasmolysis with simple experiments and natural examples.
          - Excretory system: excretion versus egestion; excretory organs (lungs, skin, liver, kidneys); simple urinary disorders.
          - Reproductive system and growth in mammals: male and female systems; fertilisation, pregnancy, birth and care of the young; problems associated with reproduction; sexually transmitted infections (STIs); phases of growth and development.
          - Circulatory system: structure and functions of the human heart, blood and blood vessels; composition and functions of blood; common blood/circulatory disorders.
          - Nervous and endocrine systems: structure and functions of the central nervous system; causes/effects of damage; voluntary and involuntary actions and the reflex arc; major endocrine glands, hormones and effects of hormonal imbalance.

        D. Energy
          - Forms and transformation of energy: conservation of energy and efficiency; common energy conversions (e.g. solar to chemical, chemical to electrical, electrical to light/heat, potential to kinetic).
          - Solar energy: uses and simple applications (drying, heating, cooking); advantages over fossil fuels.
          - Photosynthesis: conditions, experiments to show necessity of light, chlorophyll and CO₂; conversion of light to chemical energy.
          - Electronics: conductors, semiconductors and insulators; simple behaviour of diodes and transistors; basic amplifier/switch ideas.
          - Electrical energy: static and current electricity; simple circuits; Ohm’s law; power and efficient use of appliances; generation and transmission (including transformers, fuses and earthing).
          - Sound energy: sources, properties, echoes, musical notes versus noise; parts and care of the human ear.
          - Light energy: reflection and refraction; structure and defects of the eye; dispersion and colours; electromagnetic spectrum and everyday applications.
          - Heat energy: nature and sources of heat; conduction, convection and radiation; temperature and thermometers; thermal expansion and change of state; evaporation and cooling.
          - Nuclear energy: radioactivity and radioisotopes; peaceful uses and hazards; protection and nuclear‑waste issues.

        E. Interactions of Matter
          - Ecosystems: basic ecological terms; natural and artificial ecosystems; biotic and abiotic components; ecological factors; food chains and food webs.
          - Atmosphere and climate change: layers of the atmosphere; human activities and atmospheric change; major pollutants, greenhouse effect, global warming, ozone‑layer depletion and acid rain.
          - Infection and diseases: pathogenic and non‑pathogenic causes; modes of transmission, symptoms, prevention and control of common diseases.
          - Magnetism: magnetic versus non‑magnetic materials; magnetic fields; magnetisation, demagnetisation and electromagnets.
          - Force, motion and pressure: basic types of force; Archimedes’ principle and flotation; distance, displacement, speed, velocity, acceleration and momentum; stability and centre of gravity; pressure in solids, liquids and gases.
          - Safety in the community: safe use of appliances, basic first‑aid methods; hazardous substances and common hazards; roles of health and regulatory organisations.
          - Variation and inheritance: chromosomes and genes; simple Mendelian inheritance; variation and its causes; sex determination and sex‑linked traits; blood groups, Rhesus factor and sickle‑cell anaemia.
          - Work and machines: work, energy and power; simple machines, mechanical advantage and efficiency; friction and its effects.
          - Endogenous technology and biotechnology: small‑scale industries and local technologies; genetic engineering and tissue culture and their applications.

        Guidance for Candidates:
        - Take all practical work seriously, keeping clear records in a notebook and relating experiments to theory.
        - Use everyday experiences (home, farm, community and environment) as sources of scientific examples and investigations.
        - Practise calculations involving units, measurements, density, energy, work, power, pressure and simple circuits.
        - Revise each topic using diagrams, flow charts, tables and past Integrated Science questions to become familiar with the style and demands of Papers 1, 2 and 3.
        `,
  },
  {
    subject: 'Jewellery',
    timeAlloted: 50 * 60,
     description: `The WASSCE Jewellery syllabus is rooted in Ghana’s cultural heritage and global jewellery practice. It develops learners’ creative potential, promotes indigenous and contemporary forms of jewellery using traditional and non‑traditional materials, and equips candidates with skills for design, production and small‑scale enterprise.

        Aims and Objectives:
        The examination assesses candidates’ ability to:
        - Demonstrate theoretical and practical knowledge in jewellery.
        - Design and produce jewellery items using available traditional and non‑traditional materials.
        - Understand how to establish, organise and manage a jewellery workshop for personal and national development.
        - Explain the history and development of jewellery globally, in Africa and in Ghana.
        - Show aesthetic awareness, cultural appreciation and ethical behaviour in jewellery practice.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 form a composite paper taken at one sitting.

        - Paper 1: Objective Test (40 marks, 50 minutes)
         • Forty multiple‑choice questions on the full syllabus.

        - Paper 2: Essay (60 marks, 2 hours)
         • Six essay‑type questions; candidates answer four.

        - Paper 3: Practical Projects (100 marks total)
         • Two practical project questions are set; candidates execute one over five days, working six hours each day (80 marks for execution).
         • Question papers are released two weeks before execution for study, sketching and preparatory notes (20 marks for preparatory work).

        Main Syllabus Areas (Overview):

        1. Jewellery as a Vocation
          - Rationale and importance of studying jewellery at senior secondary level.
          - Types and uses of jewellery (personal adornment, ceremonial, symbolic, economic, etc.).
          - Importance of jewellery as a field of study and vocation, including employment and entrepreneurship.
          - The jeweller and ethics of the profession (honesty, quality, safety, respect for culture and environment).

        2. History and Symbolism of Jewellery
          - Origins, indigenous and contemporary history of jewellery in the world and in Africa: types, values, materials and relevance.
          - History and development of jewellery in Ghana: origin; contributions to social, economic, cultural and political development; main producing and marketing centres; foreign influences.
          - Symbolism in Ghanaian culture: imagery, signs, ideas, adages, colours and their meanings in jewellery (e.g. Adinkra and other symbols).

        3. Materials, Tools and Equipment
          - Categorisation and types of tools, equipment and materials used in jewellery (metals, stones, beads, organic and synthetic materials).
          - Identification, sources, basic preparation and uses of key materials.
          - Storage, care and maintenance of tools and equipment to ensure safety and durability.

        4. Basic and Advanced Design
          - Application of elements and principles of design (line, shape, form, colour, texture, balance, rhythm, proportion, emphasis, harmony) in idea development.
          - Perception and symbolism in jewellery: using the senses and cultural symbols to generate and refine ideas.
          - Drawing for jewellery: sketching, orthographic and pictorial views of jewellery items.
          - Use of computer as a design tool (basic awareness of software such as Corel, Adobe packages, free internet resources) for concept development.

        5. Design and Technology Process (DTP)
          - Importance of DTP for critical thinking and problem‑solving.
          - Design and technology processes: identifying a need, researching, generating ideas, selecting, planning, producing, testing/evaluating and refining.
          - Simple project report writing: introduction, objectives, materials, methods, processes, outcomes and evaluation.

        6. Production, Decoration and Finishing of Jewellery Items
          - Metal preparation and alloy calculations: basic idea of alloying and selecting suitable metals/alloys.
          - Gemstones and other decorative materials: selection and basic usage.
          - Production processes (organic and inorganic): cutting, drilling, melting, bending, soldering, moulding, shaping, forming, buffing and related techniques.
          - Decoration and finishing: burnishing, embossing, tooling, incising, enamelling, glazing and other surface treatments.
          - Care, cleaning and storage of finished jewellery items.

        7. Packaging and Handling
          - Need for appropriate packaging to protect, present and market jewellery.
          - Types and qualities of effective packaging (strength, aesthetics, safety, environmental friendliness).
          - Materials and tools for packaging.
          - Designing and constructing simple packaging for jewellery pieces.

        8. The Jewellery Workshop
          - Organisation of the workshop: planning and layout; selection and arrangement of tools, equipment and materials; safety measures and housekeeping.
          - Setting up and managing a jewellery workshop: site and location; factors of production (capital, labour, raw materials, transport, skills, entrepreneurship).
          - Basic principles and elements of management and entrepreneurial skills: planning, implementation, assessment, motivation, job satisfaction and customer care.
          - Simple bookkeeping: keeping records of income and expenditure, taxes and other monetary obligations.

        9. Sustaining the Jewellery Industry
          - Strategies for sustaining the industry: sound mining practices, responsible sourcing, recycling, re‑afforestation and environmental care.
          - Challenges in the jewellery industry: material acquisition, customer confidence, quality control, compliance with hall‑marking and other regulatory requirements.

        10. Exhibition and Entrepreneurship
          - Building portfolios: importance, types (hard and soft copies), and key items to include for assessment and exhibition.
          - Exhibitions: importance, planning, organisation and mounting of jewellery displays.
          - Basic costing and pricing of jewellery items.
          - Business risks in jewellery enterprises and simple ways to manage them.

        Guidance for Candidates:
        - Observe and sketch traditional and contemporary jewellery in your community to build a visual vocabulary.
        - Practise design development from simple motifs to finished jewellery pieces, using both hand drawing and (where available) computer tools.
        - Work regularly with basic tools and materials to improve craftsmanship, safety and finishing quality.
        - Keep a portfolio of design sketches, photographs of finished work and project reports for use in assessment and exhibitions.
        - Study past Jewellery examination questions to become familiar with the demands of Papers 1, 2 and 3 and to manage time effectively in theory and practical work.
        `,
  },
  {
    subject: 'Leather Goods',
    timeAlloted: 60 * 60,
     description: `The WASSCE Leather Goods (Leather Craft/Leather Goods Manufacturing) syllabus focuses on the techniques, aesthetics and business of producing functional and decorative leather items. It develops candidates’ creative abilities, practical skills and entrepreneurial mindset in footwear, bags and other leather goods, while emphasising safe and efficient use and maintenance of materials, tools and equipment.

        Aims and Objectives:
        The examination assesses candidates’ ability to:
        - Explain the meaning and importance of leather craft as an art, technology and vocation.
        - Identify, select and prepare suitable leathers and related materials for various products.
        - Use appropriate tools, equipment and machines safely and effectively in leather goods production.
        - Design and produce footwear, bags and other leather goods using correct processes and techniques.
        - Apply decoration and finishing techniques to enhance function and aesthetics.
        - Demonstrate basic business, costing and marketing skills related to leather design and production.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 are taken at one sitting.

        - Paper 1: Objective Test (40 marks, 60 minutes)
         • Forty multiple‑choice questions covering the entire syllabus.

        - Paper 2: Essay (60 marks, 2 hours)
         • Six essay‑type questions; candidates answer four within two hours.

        - Paper 3: Practical – Leather Craft (Compulsory)
         • A practical paper that evaluates candidates’ ability to design and produce functional leather goods based on inspiration from the environment and culture.
         • The paper lasts four hours and consists of three alternatives (A, B and C); candidates attempt one only.
         • The question paper is released two weeks before the actual examination date to allow for preparatory sketches and notes.
         • The project is executed in the examination hall under supervision. Candidates must present preliminary sketches and notes explaining the rationale for their design and production.
         • Schools are required to provide necessary materials, tools and equipment for this paper.

        Main Syllabus Areas (Overview):

        1. Introduction to Leather Craft
          - Meaning of leather craft and its scope.
          - Importance of leather craft to personal, community and national development (employment, culture, fashion, export, etc.).
          - Preparation of leather (soaking, liming, fleshing, tanning, finishing at basic level).
          - Types of leather goods or products (footwear, bags, belts, wallets, purses, caps, accessories and other articles).

        2. Footwear Manufacturing
          - Components of footwear (upper, lining, insole, outsole, heel, fastenings, etc.).
          - Differences between men’s and ladies’ shoes in design, structure and styling.
          - Materials, tools and equipment for footwear design and production.
          - Pattern cutting for different shoe styles.
          - Clicking, skiving and folding processes and their role in footwear production.

        3. Leather Goods (Bags and Accessories)
          - Types of leather goods (handbags, briefcases, school bags, travel bags, wallets, belts and other accessories).
          - Functions of leather goods in daily life, work, fashion and culture.
          - Influence of social habits, fashion trends and price on the design and production of leather goods.
          - Design illustration and pattern making for various products.

        4. Materials, Tools and Equipment
          - Materials: leather, fabric, rubber, adhesives and related accessories.
          - Tools: knives and shears, hammers, mallets, punches, awls, bevellers, skivers, etc.
          - Equipment: sewing machines, sole trimmers, leather cutters, splitters, sanding, spraying/polishing machines and others.
          - Description, correct use and care of tools and equipment.
          - Safety measures in leather goods production to protect workers, equipment and products.

        5. Sewing Machines
          - Types and functions of sewing machines used in leather work (flat‑bed, post‑bed, zigzag, etc.).
          - Basic operation and routine maintenance of sewing machines.
          - Stitch formation and quality in leather work.
          - Methods of stitching appropriate to different products and seams.

        6. Leather Goods Decoration and Finishing
          - Techniques of decorating leather goods: stamping, stitching, carving, spraying, embroidery, appliqué, dyeing and related methods.
          - Meaning and importance of finishing in leather production.
          - Types of finishes and finishing processes used to protect and beautify products.

        7. The Business of Leather Design and Production
          - Choosing a suitable location for a leather business.
          - Sources of funding and basic financial planning.
          - Purchasing and supplies of materials and components.
          - Pricing and negotiation strategies.
          - Merchandising, advertising/publicity, marketing and selling of leather goods.
          - Fund management and simple record‑keeping for sustainability.

        Facilities, Tools and Equipment (Overview):
        - Footwear manufacture and repairs: flat‑bed and post‑bed sewing machines, zigzag machine, perforating, stamping and spraying machines, embossing and sole roughing/grinding machines.
        - Tools: pattern and cutting knives, awls, hammers, nail removers, sharpening stones, lasting pincers, clicking boards, eyelet pliers, tape rules, adhesives, sets of plastic lasts (adult, ladies, children), punching pliers, iron and expansion lasts, cutting boards, screwdrivers, pliers, scissors and zigzag scissors.
        - Bag manufacture and repairs: post‑bed and flat‑bed machines, guides and gadgets, skiving and splitting machines, plus scissors, zigzag scissors, gums/adhesives, tape rules, eyelet pliers and pattern knives.

        Guidance for Candidates:
        - Practise basic and advanced processes such as pattern cutting, clicking, skiving, stitching, assembling, decorating and finishing on a variety of leather products.
        - Observe and study footwear, bags and other leather goods in your environment to understand design, function, fashion and market preferences.
        - Use and maintain tools, machines and equipment safely, following recommended procedures.
        - Keep design sketches, patterns and records of completed work as evidence of learning and as preparation for the practical paper.
        - Study past Leather Goods examination papers to become familiar with the structure and demands of Papers 1, 2 and 3, and to manage your time effectively in theory and practical work.
        `,
  },
  {
    subject: 'Leatherwork',
    timeAlloted: 60 * 60,
     description: `The WASSCE Leatherwork syllabus is built around preparing animal hides and skins into leather (tanning) and using appropriate tools, equipment and techniques to produce a wide range of leather articles such as footwear, receptacles and other crafts. It provides pre‑vocational training within Visual Arts, developing self‑esteem, patriotism, cultural awareness, pre‑technical proficiency and entrepreneurial skills for national development.

        Aims and Objectives:
        The examination is designed to test candidates’ ability to:
        - Demonstrate knowledge of the theory and practice of leatherwork.
        - Use, care for and maintain leatherwork tools, materials and equipment correctly.
        - Apply a range of leatherwork techniques competently and safely.
        - Handle both natural and synthetic leathers appropriately in making products.
        - Cost, price and market leatherwork products in simple business contexts.
        - Identify career opportunities and pathways for entrepreneurial development in the leather industry.
        - Appreciate, analyse and interpret leatherworks as art and craft objects.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 form a composite paper taken at one sitting.

        - Paper 1: Objective Test (40 marks, 50 minutes)
         • Forty multiple‑choice questions on the whole syllabus.

        - Paper 2: Essay (60 marks, 2 hours)
         • Six essay‑type questions; candidates answer four within two hours.

        - Paper 3: Practical Projects (100 marks total)
         • Two practical project questions are set; candidates execute one within five days, working six hours each day (80 marks for execution).
         • Questions are sent to candidates two weeks before the execution period for study.
         • Sketches and preparatory notes made during the two‑week period are assessed for 20 marks, giving a total of 100 marks for the paper.

        Main Syllabus Areas (Overview):

        1. Introduction to Leatherwork
          - Meaning and scope of leatherwork.
          - History of leatherwork and reasons for studying it at Senior High School.
          - Clarification of the uses of leather in daily life, industry and culture.

        2. Leathers and their Use
          - Leather as a material: characteristics, types of leather, structure of skin and hide, chemical composition, sources and preparation.
          - Other leather materials such as synthetics and off‑cuts.
          - Uses of leather: containers, body items, household items, religious/magical items, industrial items, sports, defence and protective gear, music and entertainment, animal accessories.

        3. Materials, Tools and Equipment
          - Materials: definition, types and uses in leatherwork.
          - Tools and equipment: definition, sources and preparation.
          - Care, repair and maintenance of tools and equipment.

        4. Classification and Uses of Leather
          - Classification by source: hides (e.g. cattle, equine, deer), skins (e.g. goat, sheep, reptiles), kips (e.g. rabbit, squirrel, skunk).
          - Uses: hides for industrial and heavy items (industrial belts, boots, etc.); skins for footwear, garments, musical instruments; kips for fancy goods (gloves, headgear, etc.).

        5. Career Opportunities in Leatherwork
          - Leather production and tanning.
          - Leather technologist; leather art, decoration and restoration.
          - Leather product dealers and leatherwork tutors.
          - Dealing in leather accessories, equipment and tools.
          - Designing and producing leather items (cases, bags, shoes, upholstery, etc.).

        6. Designing
          - Definition of design in the context of leatherwork.
          - Elements of design (line, shape, form, colour, texture, space, etc.).
          - Principles of design (balance, rhythm, proportion, emphasis, unity, variety, etc.).
          - Design environment and sources of design ideas (natural and man‑made forms, culture, technology).
          - Design processes including computer‑aided designing and preliminary design sketches.

        7. Production of Leather Articles
          - Classification of leatherwork products.
          - Making of models and prototypes.
          - Decorative techniques used on leather.
          - Production techniques: cutting, stitching, assembling, moulding, lacing and related processes.
          - Finishing techniques to protect and beautify leather products.
          - Care and maintenance of leather articles, including appropriate packaging.

        8. Exhibition of Products
          - Meaning and types of exhibition.
          - Importance of exhibiting leather products for learning, marketing and appreciation.
          - Planning, preparing for and mounting exhibitions of leatherwork.

        9. Terminologies in Leatherwork
          - Key terms used in leatherwork practice and current developments in the industry.

        10. Entrepreneurship Skills
          - Areas of enterprise in leatherwork and how to explore them.
          - Siting and managing small leatherwork businesses.
          - Avenues for financial and technical support.
          - Costing, pricing and marketing of leatherwork products.

        Guidance for Candidates:
        - Study both the historical and practical aspects of leatherwork, linking concepts to real products in your environment.
        - Practise design, cutting, stitching, decoration, finishing and packaging on varied leather and synthetic materials to build confidence and skill.
        - Maintain leatherwork tools and equipment properly and observe safety rules in the workshop.
        - Keep sketchbooks, notes and records of completed projects as evidence of learning and as preparation for Paper 3.
        - Use past examination questions to familiarise yourself with the requirements of Papers 1, 2 and 3 and to improve time management in theory and practical work.
        `,
  },
  {
    subject: 'Machine Woodworking',
    timeAlloted: 60 * 60,
     description: `The WASSCE Machine Woodworking syllabus focuses on the safe and efficient use of woodworking machines to process timber, produce quality components and understand the business side of running a machine woodshop. It equips candidates with theoretical knowledge of timber and machinery, practical machining skills, and basic entrepreneurial abilities for the wood industry.

        Examination Scheme:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 are taken together as a composite paper.

        - Paper 1: Objective Test (40 marks, 1 hour)
         • Forty multiple‑choice questions covering the whole syllabus.

        - Paper 2: Short‑Structured Questions (60 marks, 1 hour)
         • Four short‑structured questions; candidates answer any three.

        - Paper 3: Practical Test or Alternative to Practical (100 marks, 2 hours)
         • One practical question of two hours’ duration testing candidates’ ability to carry out specified machine woodworking operations (100 marks).
         • A list of materials for the test is sent to schools not less than two weeks before the paper for procurement and preparation.
         • Where actual practical testing is not possible, an Alternative to Practical Test may be set: one question of two hours for 100 marks to assess understanding of practical work as prescribed in the syllabus.

        Main Syllabus Areas (Overview):

        1. Tree Growth, Structure and Types
          - Common West African trees, their characteristics and countries of origin.
          - Tree growth processes, parts of a tree and their functions.
          - Wood structure, including cross‑section of a tree and its implication for machining.

        2. Properties of Timber
          - Characteristics of softwoods and hardwoods.
          - Physical properties of common West African timbers relevant to machine woodworking (strength, density, stability, workability, etc.).

        3. Timber Processing: Conversion, Seasoning and Preservation
          - Concept and methods of timber conversion; common market sizes of timber.
          - Concept of timber seasoning, reasons for seasoning and methods (with advantages and disadvantages).
          - Types, characteristics and application of timber preservatives.

        4. Timber Defects
          - Types of natural and artificial defects (e.g. knots, shakes, splits) and their effects on machining and product quality.

        5. General Wood Machine Shop Safety
          - General machine shop safety habits.
          - Electrical and mechanical safety rules in a wood machine shop.

        6. Safety Equipment and Devices
          - Identification and uses of common machine shop safety equipment and devices (fire extinguisher, first‑aid box, sand bucket, guards, push sticks, goggles, ear protection, etc.).

        7. Safety in Operating Woodworking Machines
          - Specific safety rules and procedures for each major woodworking machine.

        8. Wood Machine Layout
          - Principles of safe and efficient machine shop layout.
          - Flow diagrams showing work movement and machine arrangement in a shop.

        9. Woodwork Machines
          - Parts, accessories, uses and basic maintenance of machines such as: cross‑cut saw, circular saw bench, surface planer, thicknesser, jig saw, sander, band saw, tenoner, mortiser, router, spindle moulder and woodturning lathe.

        10. Wood Machining Operations
          - Machine operations including cross‑cutting, ripping, grooving, surface planing, shooting, chamfering, bevelling, tapering, sanding, curve cutting, mitre cutting, tenoning, turning, rebating, mortising, shaping and moulding.

        11. Business Opportunities in Machine Woodworking
          - Identification of business opportunities in machine woodworking (production, maintenance, small‑scale workshops, supply of machined components, etc.).
          - Carrying out basic feasibility studies for machine woodworking enterprises.

        12. Funds Sourcing
          - Sources of funds for machine woodworking businesses (personal savings, bank loans, co‑operative associations, thrift societies and others).

        13. Operating and Managing a Wood Machine Shop
          - Basic considerations in setting up and managing a machine woodworking shop (premises, machines, labour, materials, safety, record‑keeping and customer relations).

        Practical Component (Summary):
        - Practical activities include bevelling and tapering, cross‑cutting, grooving, ripping, mitring, shooting and surface planing, thicknessing, curve cutting, moulding, sanding, turning, mortising, tenoning, cleaning and lubricating machine parts and accessories, chamfering and related operations.

        Facilities, Machines and Accessories (Overview):
        - A range of woodworking machines such as radial arm cross‑cutting machines, circular saws, surface planers, thicknessers, band saws, morticers, tenoners, routers, spindle moulders, lathes, sanders, veneer presses, dimension and powered saws, jig saws, spraying machines and drill presses are required.
        - Accessories include circular saw blades, mitre gauges, spray guns, morticing attachments, turning chisels, calipers, cutter blocks, oil cans, spanners, drill bits, grinding blocks, grease guns, band saw blades, mortising chisels, setting pliers, files and saw sets.

        Guidance for Candidates:
        - Master fundamental timber knowledge (growth, properties, processing and defects) and relate it to machining quality and safety.
        - Learn the names, parts, functions and safe operating procedures of all major woodworking machines and accessories.
        - Practise a wide range of machining operations under supervision, observing all safety rules and using appropriate protective equipment.
        - Understand basic shop layout, business opportunities, sources of funds and simple management practices for running a wood machine shop.
        - Use past examination questions and practical exercises to build confidence and time management skills for Papers 1, 2 and 3.
        `,
  },
  {
    subject: 'Marketing',
    timeAlloted: 50 * 60,
    description: `The WASSCE Marketing syllabus at Senior Secondary School level assesses candidates’ basic knowledge of marketing as both a business function and a career option. It covers core concepts, the marketing mix, consumer and organisational behaviour, marketing management, entrepreneurship and the role of marketing in the wider economy.

      Aims and Objectives:
      The examination is intended to test candidates’ ability to:
      - Demonstrate knowledge, skills and concepts needed to practise marketing as a career.
      - Set up and manage a simple market outlet or small marketing operation.
      - Explain and evaluate the roles of marketing in the economy.

      Scheme of the Examination:
      There are two papers – Papers 1 and 2 – which form a composite paper taken at one sitting.

      - Paper 1: Objective Test (40 marks, 50 minutes)
        • Forty multiple‑choice questions covering the entire syllabus.

      - Paper 2: Essay (80 marks, 2 hours)
        • Six essay questions, from which candidates answer any four.

      Main Syllabus Areas (Overview):

      1. Introduction to Marketing
         - Meaning of marketing.
         - Explanation of basic marketing terms: needs, wants, demands, product, exchange, transactions and markets.
         - Brief history of marketing in Nigeria.
         - Functions of marketing in business and the economy.

      2. Marketing Concepts
         - Meaning of marketing concepts.
         - Major concepts: production, product, selling, marketing and societal marketing concepts.

      3. Marketing Mix and Marketing Environment
         - Meaning of marketing mix.
         - Elements of the marketing mix (4 Ps: product, price, place and promotion).
         - Meaning of marketing environment.
         - Factors affecting the marketing environment: political, cultural, religious, economic, technological and social.

      4. Products
         - Meaning of a product.
         - Classification of products: industrial goods, consumer goods, services, primary and secondary products, and mineral products (oil and non‑oil).
         - Distinctions between classes of products.
         - Basic idea of product design and its role in marketing.

      5. Markets
         - Meaning of a market.
         - Classification of markets: consumer markets and organisational markets (industrial, reseller and government).

      6. Consumer and Organisational Behaviour
         - Meaning of consumer and organisational buying behaviour.
         - Factors influencing buying behaviour (economic, social, psychological, cultural and organisational).
         - The decision‑making process in logical order.

      7. Marketing Planning and Research
         - Meaning, process and importance of marketing planning and research.
         - Elements of marketing planning and the information required.
         - Reasons for marketing planning and research.
         - Use of feedback in improving marketing decisions.

      8. Pricing
         - Meaning of price and pricing.
         - Pricing strategies: haggling, cost‑plus, demand‑based and competition‑based strategies.
         - Application of pricing strategies in practical situations.
         - Determinants of price (cost, demand, competition, objectives, regulations, etc.).

      9. Advertising
         - Definition and functions of advertising.
         - Media: meaning, types (print, electronic, outdoor, online, etc.), advantages and disadvantages.
         - Basic production of simple advertisements (e.g. slogans, posters, radio/TV scripts) in principle.

      10. Sales Promotion
        - Meaning and functions of sales promotion.
        - Forms of sales promotion: price‑off, coupons, salesmen competitions, loyalty schemes, premium offers, trade‑in allowance, sampling, training schemes and merchandising incentives.

      11. Merchandising
        - Meaning of merchandising.
        - Elements of merchandising: packaging, branding and labelling.
        - Factors influencing merchandising decisions.
        - Functions of merchandising in marketing.

      12. Distribution
        - Definition of distribution.
        - Channels of distribution: meaning and types.
        - Factors influencing the choice of distribution channels.
        - Functions of channel members.

      13. Transportation
        - Meaning, modes, choice and importance of transportation in marketing.
        - Common documents used in transportation.
        - Factors affecting choice of mode of transport.

      14. Warehousing
        - Meaning, types and functions of warehousing.
        - Activities carried out in the warehouse.

      15. Market Unions and Facilitators
        - Market unions: meaning and types.
        - Roles of market unions in local markets.
        - Market facilitators: meaning, types and roles.

      16. International Marketing
        - Meaning, importance/reasons for engaging in international marketing.
        - Methods of entering international markets.
        - Factors influencing international marketing.
        - Basic guiding rules and regulations.

      17. ICT in Marketing
        - E‑marketing (electronic marketing): meaning, importance and uses.
        - Ethics and abuses associated with e‑marketing.

      18. Entrepreneurship in Marketing
        - Meaning of entrepreneurship and entrepreneur.
        - Sources of funds for starting marketing‑related businesses.
        - Factors affecting the location of a market outlet.
        - Management of market outlets: purchase and supply of goods and services.
        - Selling and bargaining skills.

      Guidance for Candidates:
      - Learn and apply marketing concepts to real‑life situations such as shops, markets, small businesses and online platforms.
      - Practise simple tasks like classifying products, sketching adverts, choosing distribution channels and applying pricing strategies.
      - Follow current trends in marketing, especially in e‑marketing and entrepreneurship, through local examples and case studies.
      - Study past Marketing examination questions to become familiar with the structure and demands of Papers 1 and 2 and to improve time management and writing skills.
      `,
  },
  {
    subject: 'Metalwork',
    timeAlloted: 60 * 60,
     description: `The WASSCE Metalwork syllabus is designed to test candidates’ skills in basic design, practical metalworking processes and entrepreneurship. It assesses knowledge of tools, equipment and materials, safe workshop practice and those aspects of creative thinking that can be expressed through planning and working mainly with metals. It also evaluates competence in fundamental manufacturing processes as a foundation for further study in science and technology.

        Aims and Objectives:
        The examination aims to test candidates’ acquisition of the knowledge and skills needed for further education and self‑development in science and technology. Candidates are expected to be able to:
        - Observe safe working practices in the metalwork workshop.
        - Demonstrate knowledge and understanding of tools, materials and equipment.
        - Apply basic processes for the care and maintenance of hand and machine tools.
        - Identify, analyse and evaluate problems and propose solutions.
        - Apply knowledge of processes and materials to the solution of metalworking problems.
        - Demonstrate basic skills of good craftsmanship.
        - Show awareness of career opportunities in metalwork.
        - Translate ideas into project designs and finished test pieces.

        Scheme of Examination:
        There are three papers – Papers 1, 2 and 3 – all of which must be taken. Papers 1 and 2 form a composite paper taken at one sitting.

        - Paper 1: Objective Test (40 marks, 1 hour)
         • Forty compulsory multiple‑choice questions on the entire syllabus.

        - Paper 2: Essay (60 marks, 1½ hours)
         • Five questions are set; candidates answer four. All questions carry equal marks.

        - Paper 3: Practical Test (100 marks, 3 hours)
         • A practical paper of three hours’ duration, with 10 minutes given beforehand to study the drawings.
         • Two questions are set; candidates answer one by making a specified test piece from supplied drawings.
         • Schools provide all necessary materials for the test.

        Main Syllabus Areas (Overview):

        1. General Safety in the Workshop
          - Potential sources of accidents and measures to avoid them.
          - Personal safety and appropriate protective clothing (goggles, apron, gloves, boots with hard toe caps, helmet).
          - Safety rules and precautions specific to a metalwork workshop.

        2. Metals
          - Description of metals, including physical and mechanical properties.
          - Types of metals:
           • Ferrous metals: pig iron, wrought iron, cast iron and steels.
           • Non‑ferrous metals: aluminium, zinc, copper, tin and lead.
           • Non‑ferrous alloys: brass, bronze, solders, duralumin – constituents and properties.
          - Uses of metals: construction of auto bodies and frames, building and bridge structures, and other engineering applications; selection of suitable metals for specific jobs.
          - Production of metals: common metal ores (e.g. copper pyrite, bauxite, hematite, calamine, galena, cassiterite); methods of extraction (open‑pit and underground mining); smelting and refining processes (blast furnace, Bessemer converter, open hearth, crucible, cupola, electric arc and high‑frequency induction, reverberatory furnace); forms of metal supply (billets, blooms and slabs).

        3. Hand Tools and Bench Work
          - Hand tools: types, uses, care and maintenance.
          - Measuring tools: linear and angular (steel rule, angle plate, scriber, etc.).
          - Work‑holding devices: bench vice, hand vice, G‑clamp, chucks, etc.
          - Cutting and shaping tools: cold chisels, hacksaws, files, taps and dies.
          - Striking tools and others: engineers’ hammers, mallets, screwdrivers, spanners, drifts and related tools.
          - Bench work operations: filing, chiselling, threading and sawing.
          - Maintenance routines such as oiling, cleaning and greasing of hand tools.

        4. Heat Treatment of Metals
          - Importance of heat treatment.
          - Heat treatment processes: annealing, case‑hardening, hardening, tempering and normalising.
          - Materials and equipment used: cooling media (coal, brine, water, air, oil, ash) and equipment (furnaces, pyrometers, ovens, quenching tanks, blacksmith’s forge, oxy‑acetylene flame).
          - Tempering colours and corresponding temperature ranges; safety precautions during heat treatment.

        5. Hand Forging
          - Principles and importance of forging.
          - Types of forging: cold, hot and drop forging.
          - Forging tools and equipment: anvil, hammers, top and bottom swages, hardies, fullers, tongs, hearth forge, etc.
          - Basic forging operations: fullering, upsetting, bending, drawing down, punching, flattening, cutting, twisting and swaging.
          - Practical forging of items such as chisels, centre punches, scribers, door bolts, hinges, hoes and pokers.
          - Safety precautions in forging.

        6. Foundry Work
          - Principles of casting: definition, importance and sand‑casting methods.
          - Materials and equipment for sand casting.
          - Processes: pattern making (single‑piece, split and flat‑back patterns), moulding, melting and pouring, dismantling and fettling.
          - Core making (box and sand cores).
          - Casting defects, their types and causes.
          - Safety precautions in foundry work.

        7. Metal Joining
          - Types of joints: temporary and permanent.
          - Temporary joining: common fasteners (bolts, nuts, pins, screws) – identification, uses and applications.
          - Soft soldering: tools, equipment, joint types and processes; design and making of simple artefacts involving soft soldering.
          - Hard soldering (brazing): tools, equipment, joint types, processes and procedures.
          - Gas and electric arc welding: equipment, operation, welding flames, basic welding techniques and safety.
          - Riveting: tools and equipment, types of rivets and joints, basic procedures.

        8. Sheet Metalwork
          - Selection of materials: low‑carbon steel sheet, galvanised sheet, tinplate, copper, aluminium and brass sheets; standard gauges.
          - Tools and equipment: stakes, hammers, mallets, snips, hand lever shears, folding bars, sand bags, slip rolls, etc.
          - Pattern development and cutting: methods and layout; cutting out patterns.
          - Basic fabrication processes: bending, raising, hollowing, sinking, expanding and contracting.
          - Joints and joining methods: lap joints, grooves, seams; self‑tapping screws, soft soldering and seaming.
          - Safety precautions in sheet metalwork.
          - Production of simple articles such as cans, funnels and bowls.

        9. Machine Tools and Processes
          - Drilling and reaming: description, tools, setting‑up; types of drilling machines (sensitive, pillar and radial); common drilling faults and remedies.
          - Grinding: description of grinding operations; types of grinding wheels; setting up grinding operations; grinding of single‑point tools (scribers, chisels, lathe tool bits).
          - Power sawing: description, tools and machines; setting up and using power saws to cut materials.
          - Centre lathe and turning operations: parts and functions (bed, headstock, tailstock, carriage, feed and thread‑cutting mechanisms); operations such as parting‑off, knurling, taper‑turning, drilling, boring, step turning and vee thread‑cutting; work‑holding devices (3‑ and 4‑jaw chucks, collets, faceplates, catch plates, mandrels, steadies and centres).
          - Shaping machines: types (swivel, tilting and universal table); parts (pedestal, ram, saddle, table, driving mechanism) and their functions; cutting‑tool materials and holders.
          - Cutting lubricants and coolants (soluble oil, straight cutting oil, soda solution) and their uses.
          - Care, maintenance and safety precautions for machine tools.

        10. Finishes and Decorative Processes
          - Types of finishes and decorative processes: polishing and buffing, spot‑facing, planishing, colouring, plating, etching, lacquering, pickling and enamelling.
          - Application of suitable finishes on metalwork projects.

        11. Design and Project Work
          - Identifying problems from real environments (market, classroom, lorry park, workshop, etc.) and defining them clearly.
          - Investigating and generating possible solutions (observation, interviews, visits, reading, sketches and models).
          - Selecting the best solution based on simplicity/complexity, availability of materials and cost.
          - Producing freehand sketches, working drawings, prototypes, testing and final production.
          - Preparing a simple design folio and realising the chosen artefact.

        Workshop Facilities, Tools and Equipment (Overview):
        - Safety equipment and materials: fire extinguishers, first‑aid box, buckets of sand, CO₂ extinguishers, wall charts, etc.
        - Workshop tools: cutting, measuring, marking‑out, driving, work‑holding and forging tools (hacksaws, chisels, snips, files, drills, calipers, squares, scribers, punches, hammers, vices, tongs, swages, anvils, etc.).
        - Workshop equipment: work benches, marking‑out tables, blacksmith’s hearth, foundry furnace, tool boxes, oil cans and basic computer hardware/software.
        - Machine tools: centre lathes, drilling machines, pedestal grinders, power hacksaws, folding machines, shears, rolling and milling machines, shaping machines, etc.
        - Welding equipment: arc‑welding and gas‑welding sets with accessories.
        - Materials: mild‑steel bars (round, flat, square, hexagonal), sheets and plates, galvanised and tinned sheets, and suitable non‑ferrous metals (copper, aluminium, brass).

        Guidance for Candidates:
        - Practise safe workshop habits at all times and use protective clothing and safety devices correctly.
        - Get hands‑on experience with a wide range of tools, machines and processes, from hand tools and forging to casting, welding, sheet‑metal fabrication and machining.
        - Keep a well‑organised record of design ideas, sketches, working drawings and completed projects to support Paper 3.
        - Link theory (properties of metals, processes, design and entrepreneurship) with practical tasks and real‑life engineering applications.
        - Work through past Metalwork examination questions to become familiar with the structure and requirements of all three papers and to improve speed and accuracy.
        `,
  },
  {
    subject: 'Mining',
    timeAlloted: 40 * 60,
     description: `The WASSCE Mining syllabus evaluates candidates’ knowledge of basic principles and practices in the mining industry, with a strong focus on developing interest in mining as a business and career. It provides opportunities for acquiring broad knowledge, practical attitudes and skills that can stimulate entrepreneurial interest and practice in mining.

        Aims and Objectives:
        The examination tests candidates’ ability to:
        - Explain the basic concepts associated with mining.
        - Acquire basic knowledge of rocks and minerals.
        - Recognise the special properties and values of gems and precious minerals.
        - Explain methods of extraction of minerals.
        - Explain how minerals are processed into useful products.
        - Appreciate the importance of health, safety and the environment in the mining industry.
        - Develop relevant skills and business attitudes for participation in mining.

        Field Work and Continuous Assessment:
        - Students are expected to undertake field work through excursions to mining‑related sites such as coal mines, oil derricks, petroleum refineries, comminution and tailings sites, oil‑spill sites and other mining/processing locations.
        - Field‑work reports form part of school‑based continuous assessment; scores from field work contribute significantly (around 40%) to the total continuous assessment marks forwarded to the examining body.

        Examination Scheme:
        There are two papers – Papers 1 and 2 – taken together as a composite paper in one sitting over a total duration of 2 hours 45 minutes.

        - Paper 1: Objective Test (40 marks, 40 minutes)
         • Forty multiple‑choice questions covering the whole syllabus.

        - Paper 2: Structured and Essay (100 marks, 2 hours 5 minutes)
         • Section A: Ten short‑structured questions on the entire syllabus (40 minutes, 40 marks). All questions are answered.
         • Section B: Five essay‑type questions from which candidates answer any three (1 hour 25 minutes, 60 marks).

        Main Syllabus Areas (Overview):

        A. Introduction to Mining
          - Meaning and major branches of mining.
          - Basic mining terminology.
          - Classification and importance of minerals.
          - Distribution of minerals in Nigeria; history of mining and mining activities in Nigeria.
          - Importance of minerals to humans and to the national economy.
          - Minerals markets and cartels; mine accounting; sources of finance for mining ventures.
          - Overview of the Nigerian Minerals and Mining Act (2007), mining titles and acquisition procedures.

        B. Basic Geology
          - Definition and scope of geology; relationship between geology and mining.
          - Structure of the Earth and relative abundance of elements in the Earth’s crust.
          - Economic mineral potentials of Nigeria.
          - Rocks and rock types: igneous, sedimentary and metamorphic rocks.
          - Rock‑forming minerals, their physical and chemical properties and the rock cycle.
          - Weathering and its effects.
          - Simplified geological and mineral maps of Nigeria.
          - Collection and collation of geological data; interpretation of topographical and geological maps.
          - Drawing simple geological cross‑sections.

        C. Stages of Development in Mining (Basic Concepts)
          - Classification of mining methods into surface and underground methods.
          - Choice of mining methods and reasons for selecting surface or underground options.
          - Prospecting stage: meaning and outline of prospecting techniques (geological, geophysical, geochemical, remote‑sensing).
          - Exploration stage: meaning and classification of exploration techniques (geological, geophysical, geochemical, remote‑sensing).
          - Meaning and purpose of feasibility studies and financial analysis in mining ventures.
          - Basic concepts of mine development, mineral‑processing and mill design, and unit operations in mining (e.g. drilling, blasting).
          - Need for mineral processing and its economic benefits; main unit operations in mineral processing.
          - Introduction to metal extraction processes (pyrometallurgy, hydrometallurgy, electrometallurgy).

        D. Mining Methods
          - Surface mining methods:
           • Open‑pit mines: features and examples of minerals mined by this method.
           • Open‑cast mining: features and typical minerals.
           • Quarrying and dimension‑stone production: meaning, products, uses, key unit operations and a simple flow sheet.
          - Underground mining methods:
           • Features of underground mines.
           • Basic concepts of methods such as square‑set, cut‑and‑fill, shrinkage, sub‑level, long‑hole, block‑caving, etc.
          - Sea‑floor, moon and asteroid mining (basic awareness).
          - Materials‑handling equipment: conveyor belts, trucks, pay‑loaders and related systems.

        E. Introduction to Mineral Processing
          - Meaning of mineral processing and key technical terms (comminution, concentration, mill accounting).
          - Properties of minerals relevant to processing, such as specific gravity and magnetism.
          - Main operations in mineral processing: comminution and concentration.
          - Products of mineral processing: concentrates, tailings, middlings; basic ideas of dewatering, auxiliary operations and tailings disposal.

        F. Gemology and Lapidary
          - Meaning of gemology and lapidary.
          - Machines and tools used in gemstone processing (faceting, grinding and polishing machines, etc.).
          - Meaning, properties and classification of gemstones (by shape, colour, size, refractive index, etc.).
          - Characteristics and methods of identifying quality gemstones.
          - Units of measurement for gemstones, pricing and marketing of gemstones.

        G. Health, Safety and Environment
          - Health hazards associated with mining.
          - Safety requirements and measures in mining operations.
          - Environmental impacts of mining and basic control measures.
          - Safety equipment and personal protective equipment (PPE) used in mining.
          - Common diseases linked to mining activities.
          - Administering simple first aid and using basic drugs/dressing materials.
          - Guidelines for accident reporting and environmental management.
          - Key issues in national mineral‑development policy, mining laws and regulations, environmental‑protection laws and national environmental policies.

        Facilities, Equipment and Materials (Overview):
        - Typical equipment includes spring balances, fire extinguishers, sand and water buckets, specific‑gravity test apparatus, models of open‑pit and underground mines, samples of rocks, minerals and gemstones, globes, microscopes, magnifying glasses, topographical and geological maps, periodic‑table charts, thermometers, electronic balances, beakers, cylinders and petri dishes.

        Guidance for Candidates:
        - Link classroom learning to field observations by paying attention during excursions and keeping clear notes.
        - Build a solid grasp of fundamental concepts in geology, mining methods, mineral processing, gemology and environmental management.
        - Use maps, models and simple diagrams to visualise geological structures, mineral deposits and mining methods.
        - Follow current issues in mining and the environment to better appreciate health, safety and regulatory aspects.
        - Practise with past Mining examination questions to become familiar with the format and to improve speed, accuracy and clarity of explanation.
        `,
  },
  {
    subject: 'Office Practice',
    timeAlloted: 60 * 60,
    description: `Office Practice focuses on the modern office as a centre for information, communication and coordination of business activities, and tests candidates’ understanding of office organization, procedures, equipment and documentation.

        Examination Scheme:
        - The examination is a composite paper made up of two papers taken at one sitting.
        - Paper 1: 50 multiple-choice objective questions to be answered in 1 hour for 50 marks.
        - Paper 2: 1 compulsory question on a simple case study and 6 other essay questions.
          • Candidates answer the case-study question and any four of the remaining questions.
          • Paper 2 carries 100 marks and lasts for 2 hours 10 minutes.

        Syllabus Focus:
        1. The Office
        - Meaning and definition of an office as a place where information is received, recorded, processed and safeguarded to support management decisions.
        - Functions of the office: receiving, recording, processing, storing and distributing information; coordinating activities; safeguarding assets and documents; providing services to management, other departments and the public.
        - Office layout: open, enclosed and combination layouts; factors influencing layout (lighting, ventilation, noise, workflow, supervision, security and safety).
        - Principles of office organisation: span of control, unity of command, unity of function, delegation, departmentalisation and clear lines of authority and communication.
        - Office procedures: routine procedures for opening and closing the office, handling visitors, documents and mail, use of office equipment and maintaining security and confidentiality.
        - Sections of the office and their inter-relationships (e.g. registry, accounts, personnel, purchasing, sales, stores, reception).

        2. Departments in an Organisation
        - Common departments: production, purchasing, sales/marketing, accounts/finance, personnel/human resources, transport, stores, public relations, research and development, administration.
        - Functions and responsibilities of each department and how they relate to the office.
        - Simple organisational chart: meaning, symbols and how charts show lines of authority and communication.

        3. Office Equipment
        - Meaning and importance of office equipment for achieving efficiency and accuracy.
        - Types of office equipment:
          • Manual: typewriter, perforator, stapling machine, duplicating machine, filing cabinets, guillotine, manual calculators, etc.
          • Electrical/Electronic: electric typewriter, photocopier, shredding machine, computer, scanner, facsimile (fax) machine, printers, duplicators, etc.
        - Uses of common office equipment and how they support office procedures (typing, copying, filing, communication, data processing).
        - Care and maintenance of office equipment: correct handling, cleaning, servicing, safe use and simple fault reporting.

        4. The Reception
        - Meaning of reception and reception area.
        - Duties of the receptionist: welcoming and directing visitors, answering enquiries, keeping visitors’ records, handling telephone calls and messages, controlling access to officials, basic security and protocol.
        - Documents handled by a receptionist: visitors’ book, appointment book, telephone message forms, call slips, identification passes and simple registers.
        - Qualities of a good receptionist: courtesy, tact, patience, good appearance, good communication skills, integrity, confidentiality and promptness.
        - Procedures for handling telephone calls: receiving and making calls, screening calls, taking and relaying messages, using appropriate telephone etiquette.

        5. Office Documents
        - Meaning and purpose of office documents as records of business transactions.
        - Types of office documents and their broad categories:
          • Administrative documents – memos, circulars, reports, minutes, notices, internal forms.
          • Sales documents – quotations, pro-forma invoices, invoices, delivery notes, receipts, credit and debit notes.
          • Stores documents – requisition notes, bin cards, stock cards, goods received notes, issue vouchers.
          • Purchasing documents – purchase requisition, purchase order, tenders, suppliers’ invoices.
        - Uses of office documents in recording, controlling and authorising transactions.
        - Basic preparation and completion of common office documents (headings, dates, references, signatures and distribution).

        6. Office Correspondence
        - Meaning of office correspondence as written communication within and outside the organisation.
        - Types of correspondence:
          • Internal: memos, notices of meetings, internal circulars.
          • External: business letters, minutes of meetings, reports, external circulars.
        - Features and formats of standard business letters and memos (headings, reference, salutation, body, complimentary close, enclosures and copies).

        7. Mail
        - Types of mail: personal, official, urgent and confidential mail.
        - Classification of mail into incoming and outgoing mail.
        - Procedures for handling incoming mail: receipt, opening (where authorised), date stamping, sorting, recording in mail register, distributing to departments/officers and filing where necessary.
        - Procedures for handling outgoing mail: drafting, typing, checking, signing, recording, enveloping, stamping/franking and dispatching.

        8. Filing
        - Definition and importance of filing as a method of arranging and storing documents for easy retrieval.
        - Systems of filing: vertical, lateral and electronic.
        - Methods of classification: alphabetical, chronological, numerical, geographical, alpha-numerical and subject.
        - Purposes of filing: preservation of records, easy reference and retrieval, legal evidence, planning and control.
        - Steps involved in filing a document and basic filing rules (cross-referencing, indexing, avoiding misfiling).
        - Filing equipment: filing cabinets, box files, lever-arch files, card-index systems, shelves and electronic storage.

        9. Communication
        - Definition and importance of communication in the office and in business.
        - Types of communication:
          • Verbal – oral (face-to-face, telephone) and written (letters, memos, reports, notices).
          • Non-verbal – visual (charts, diagrams, signposts, graphics), audio and audio-visual (radio, television, video conferencing).
        - Methods and media of communication:
          • Print: newspapers, magazines, directories, handbills, flyers, posters, notices.
          • Electronic: radio, television, telephone (fixed and mobile), audio and video conferencing, internet and email.
        - Communication service providers: examples such as national postal services, telecommunications companies, media houses, satellite stations, internet service providers and GSM operators.

        10. Meetings
        - Meaning and purpose of meetings in organisations.
        - Types of meetings: statutory, emergency, board, management, annual general meeting (AGM), staff and committee meetings.
        - Procedure for conducting a formal meeting: opening prayer, adoption of agenda, chairman’s opening remarks, reading and correction of previous minutes, adoption of minutes, matters arising, main business, any other business (A.O.B.), chairman’s closing remarks, adjournment/closing and closing prayer.
        - Common meeting terminologies: agenda, quorum, adoption, adjournment, resolution, minutes.
        - Roles of the chairman and secretary: preparing agenda and notices, conducting the meeting, maintaining order, recording minutes and ensuring follow-up on decisions.
        - Basic principles of writing minutes of meetings (accuracy, clarity, completeness and objectivity).

        11. Report Writing
        - Meaning and importance of reports as written accounts of events, investigations or activities for decision-making.
        - Types of formal reports: short reports, long reports, investigative reports and routine/periodic reports.
        - Parts of a formal report: title, terms of reference, introduction, findings, conclusions, recommendations and appendices.

        12. Information
        - Definition of information and how it differs from raw data.
        - Types and uses of information in the office and for management.
        - Sources of information: internal (records, reports, files, databases) and external (publications, media, internet, correspondents).
        - Storage of information: manual (files, card indexes, registers) and electronic (computers, external drives, cloud and other electronic media).
        - Qualities of good information: timeliness, accuracy, completeness, relevance, speed of delivery and usability.

        Guidelines for Candidates:
        - Understand how the office fits into the wider organisation and supports other departments.
        - Be familiar with common office equipment, documents, procedures and terminology.
        - Practise interpreting and completing basic office documents, mail registers, filing indexes and simple reports.
        - Develop good communication skills and professional attitudes expected in office environments (punctuality, confidentiality, courtesy and neatness).
        - Use past WASSCE Office Practice questions to build speed, accuracy and confidence in both objective and essay sections.`,
  },
  {
    subject: 'Painting and Decorating',
    timeAlloted: 60 * 60,
    description: `Painting and Decorating evaluates candidates’ knowledge, skills and understanding of pictorial and decorative painting, colour theory, techniques, costing and the socio-economic potentials of painting and decorating.

        Preamble, Aims and Objectives:
        - Provides the basis for assessing candidates’ knowledge and practical skills in painting and decorating.
        - Tests understanding of types of painting compositions and modern trends in painting and decorating.
        - Emphasizes application of painting and decorative arts to contemporary environments, aesthetic awareness and opportunities for job and wealth creation.

        Scheme of Examination:
        - Paper 1: 40 multiple-choice objective questions drawn from the entire syllabus, to be answered in 1 hour for 40 marks.
        - Paper 2: 6 essay-type questions; candidates answer 4 in 2 hours for 60 marks.
        - Paper 3: Decorative Painting practical, consisting of 2 alternatives; candidates attempt 1 in 3 hours for 100 marks. The question and requirements are released at least two weeks before the examination, but execution is done under WAEC supervision in the exam hall.

        Syllabus Focus:
        1. Introduction to Painting
        - Meaning of pictorial and decorative painting and the differences between them.
        - Tools and equipment used in pictorial and decorative painting.
        - Elements and principles of design as they apply to painting.
        - Functions of pictorial and decorative painting in homes, public spaces and commercial environments.

        2. Colour Theory
        - Meaning of colour.
        - Classes of colour: primary, secondary and tertiary.
        - Properties of colour: hue, tone and intensity.
        - Basic colour schemes: analogous, complementary and monochrome.
        - Improvisation of colours from local sources and materials.

        3. Techniques in Painting
        - Techniques in pictorial painting: dry painting, wet painting, glazing, impasto, fresco and tempera.
        - Techniques in decorative painting: flat painting, rag rolling, scumbling/combing, spraying, mural and mosaic.

        4. Types and Functions of Pictorial Painting
        - Major pictorial painting types: still life painting, nature painting and figure/life painting.
        - Differences between observatory (from direct observation) and imaginative painting compositions.
        - Functions and uses of pictorial paintings in education, decoration, communication and documentation.

        5. Types and Functions of Decorative Painting
        - Body decoration:
          • Meaning of body decoration.
          • Differences between traditional and modern body decorations.
          • Motifs, patterns and designs used for body decoration.
          • Materials used for body decoration and their safe application.
          • Functions and importance of body decorations in culture, identity and ceremonies.
        - Wall/interior decoration:
          • Meaning of wall and interior decoration.
          • Differences between traditional and modern wall decorations.
          • Motifs, patterns and designs for wall decoration.
          • Materials used for wall decoration (paints, finishes, textures, etc.).
          • Functions and importance of wall decorations in creating aesthetic and functional environments.

        6. Perspective
        - Meaning of perspective in drawing and painting.
        - Types of perspective (e.g. one-point, two-point, etc. – as required by the teaching syllabus).
        - Importance of perspective in achieving depth and realism in pictorial compositions.

        7. Prospects in Painting
        - Socio-economic value of pictorial and decorative painting to individuals and communities.
        - Careers and business opportunities in pictorial and decorative painting (studio work, mural design, interior/exterior decoration, sign writing, set design, etc.).

        8. Costing and Marketing of Paintings
        - Costing of pictorial and decorative paintings based on:
          • Location and size of work.
          • Materials and equipment involved.
          • Time invested.
          • Quantity and quality of work.
        - Exhibition of paintings: planning and processes, funding and organisation.
        - Marketing of paintings: pricing, negotiation, communication (advertising/publicity), outlets for marketing and selling of works.

        Decorative Painting Practical (Paper 3):
        - Assesses candidates’ ability to create aesthetic environments through practical decorative painting tasks.
        - Tasks are executed using suitable media such as poster colours, acrylics and related materials, following WAEC instructions and supervision.

        Guidelines for Candidates:
        - Build solid foundation in colour theory, design principles, tools and techniques for both pictorial and decorative painting.
        - Practise a variety of painting techniques (dry/wet painting, glazing, impasto, flat painting, rag rolling, scumbling, spraying, murals and mosaics).
        - Explore traditional and modern approaches to body and wall decoration using appropriate motifs, patterns and materials.
        - Develop basic skills in costing, planning exhibitions and marketing paintings as part of creative entrepreneurship.
        - Work regularly from observation and imagination, keeping sketches and practice pieces to prepare for both theory and practical papers.`,
  },
  {
    subject: 'Photography',
    timeAlloted: 60 * 60,
    description: `Photography assesses candidates’ knowledge of photography as a visual means of communication and their skills in creatively using photographic equipment, processes and digital tools, as well as their understanding of the socio-economic potentials of photography.

        Preamble & Objectives:
        - Understand the meaning and importance of photography as a medium of visual communication.
        - Trace the evolution and historical development of photography, with emphasis on Nigeria.
        - Demonstrate knowledge of the scope of photography and key terminologies.
        - Identify photographic equipment and explain their uses and basic care.
        - Show skills in taking, processing, presenting and preserving photographs.
        - Apply digital technology in capturing, editing and outputting photographic images.

        Scheme of Examination:
        - Paper 1: 40 multiple-choice objective questions on all syllabus topics, to be answered in 1 hour for 40 marks.
        - Paper 2: 6 essay questions, from which candidates answer 4 in 2 hours for 60 marks; covers introduction and importance of photography, theory of light, equipment and uses, history and appreciation, terminologies, digital photography, care/preservation and photo exhibition procedures.
        - Paper 3 (Photo Analysis and Processes / Equipment): 5 questions in 3 hours for 100 marks.
          • Section A: 2 compulsory questions on analysis of photographic images (about 50 marks).
          • Section B: 3 questions on processes and equipment; candidates answer 2 for about 50 marks.

        Syllabus Focus:
        1. Introduction to Photography
        - Meaning of photography and its functions/uses (recording events, visual communication in advertising, journalism, science, artistic expression, etc.).
        - Branches/areas of photography: fashion, advertising, photojournalism (documentary, photo-essay), forensic, nature, underwater, sports, architectural, events (weddings, birthdays, ceremonies), portraiture, still life and others.

        2. History of Photography
        - Origin of the camera and photography.
        - Development of photography in Nigeria.
        - Pioneers of photography:
          • Europe & America: Niépce, Daguerre, William Fox Talbot, Edwin Land, George Eastman, etc.
          • Nigeria: J. A. Green, Dotun Okubanjo, J. D. ‘Okhai Ojeikere, Sunmi Smart-Cole, Philip Trimmnel, Peter Obe, George Oshodi, Andrew Esiebo, Kelechi Amadi-Obi, Akintunde Akinleye, George Da Costa, etc.

        3. Terminologies in Photography
        - Key terms such as exposure, develop, print, enlarge, focus, aperture (f-number), shutter speed, perspective, image, intensity, light and composition, etc.

        4. Light in Photography
        - Sources of light:
          • Natural: sun, moon.
          • Artificial: flash, studio lighting and other artificial sources.
        - Basic properties of light relevant to photography: absorption, reflection, transmission, refraction and dispersion.

        5. The Camera
        - Principles underlying camera design using the pinhole camera (light-tight body, light inlet, viewing system, image formation).
        - Basic parts of an SLR/35 mm camera and their functions.
        - Categories of cameras: analogue and digital.
        - Types of cameras: compact, rangefinder, single lens reflex (SLR/35 mm), medium format (twin-lens and SLR), large format, instamatic, etc.
        - Lenses:
          • Optical principles (refraction, reflection, dispersion, diffraction) and difference between concave and convex lenses.
          • Focal length (normal, short and long) and its effects.
          • Relationship between lens and aperture.
          • Types and uses of camera lenses: normal/standard, wide-angle, telephoto and zoom.
        - Basic maintenance of cameras: keeping away from dirt and oil, protecting lenses from dust, using camera straps, careful handling and storage.

        6. Film
        - Nature of photographic film as light-sensitive material with specific chemical composition.
        - Types of film: black and white, colour.
        - Film formats: 110, 35 mm, 120/220, sheet film.
        - Film speed (ISO/ASA): meaning and examples of slow (e.g. 32–125), medium (e.g. 40–200) and fast (e.g. 400 and above) speeds.

        7. Taking Photographs
        - Processes involved:
          • Preparation: loading film, setting the camera, preparing the subject, setting up lighting (studio) or observing weather (outdoor), choosing appropriate aperture/shutter combinations.
          • Taking the photograph: handling the camera, composing, focusing and shooting.
        - Composition:
          • Meaning of composition.
          • Elements of composition (line, shape, space, colour, angle, centre of interest, background, closeness, form, texture, etc.).

        8. The Darkroom and Film Development
        - Layout and requirements of a darkroom: dry and wet areas, light-tight enclosure, good ventilation, water and waste outlets, flat working surfaces.
        - Tools and equipment: measures, containers, funnels, thermometers, timers, developing tanks, safelight, enlarger, dryer, trays/dishes, spiral spool, light box, etc.
        - Chemicals: developers, fixers, stop bath.
        - Film development processes: diluting developer, loading film into tank/using see-saw method, timing development, rinsing/stop bath, fixing, washing and drying negatives.
        - Safety measures: apron/lab coat, gloves, avoiding sudden movements, first aid for chemical burns, proper chemical storage.

        9. Printing Photographs
        - Materials and equipment: enlarger, safelight, printing paper, printing frame, developer, fixer, trays/dishes, dryer, guillotine/trimmer.
        - Printing process: diluting chemicals; producing contact prints; selecting exposure; exposing paper; developing, rinsing, fixing and final washing.
        - Drying and storage of photographs and negatives: using dryers or open air; storing negatives in paper packs/cellophane jackets and prints in albums or frames; safe storage of chemicals.

        10. Digital Photography
        - Similarities and differences between analogue and digital photography.
        - Development of digital photography and its impact.
        - Equipment for digital photography: digital cameras, computers and printers.
        - Software: common image-editing and management programs (e.g. Photoshop, PhotoEditor, PhotoPaint, Fireworks, Picasa, etc.).
        - Input/output devices and methods:
          • Input: scanners, digital cameras, CDs, flash drives, ZIP disks, etc.
          • Output: printers, computer monitors, internet and other display media.

        11. Exhibition of Photographs
        - Procedures for organising a photo exhibition: mounting and framing, acquiring exhibition space, publicity and effective display of photographs.

        Guidelines for Candidates:
        - Build strong foundational knowledge of photographic history, concepts, light, cameras, lenses and film.
        - Practise the full workflow from planning, shooting and composing through development/printing (or digital editing) to presentation and storage.
        - Learn and use correct photographic terminologies when answering theory and analysis questions.
        - Develop skills in analysing photographic images (content, composition, lighting, message) and in explaining processes and equipment.
        - Explore both analogue and digital workflows and study past WASSCE Photography questions to improve speed, accuracy and exam technique.`,
  },
  {
    subject: 'Physical Education',
    timeAlloted: 50 * 60,
    description: `Physical Education assesses candidates’ understanding of movement and physical activity, basic anatomy and physiology, sports skills, fitness, health, and sports administration, in line with the UNESCO view of physical activity as an inalienable right for every child.

        Preamble & Objectives:
        - Promotes educational, physical and psychological health and equips candidates with skills, abilities and physical/mental competencies for meaningful participation in society.
        - Tests understanding of movement activities and how they relate to concepts from biological, physical and social sciences.
        - Assesses skills in basic motor activities, fitness maintenance and self-awareness.
        - Builds appreciation of the role of movement and physical activity in human development and as tools for health and wellness promotion.

        Examination Scheme:
        - Paper 1: 50 multiple-choice questions in 50 minutes for 50 marks.
        - Paper 2: Short-essay theory paper (1 hour 20 minutes, 50 marks) with four sections:
          • Section A – Athletics, ball and racket games (3 questions; answer 2 for 20 marks total).
          • Section B – Foundations of physical education (2 questions; answer 1 for 10 marks).
          • Section C – Basic human anatomy and physiology in physical education (2 questions; answer 1 for 10 marks).
          • Section D – Sports administration and competitions (2 questions; answer 1 for 10 marks).
        - Paper 3: Practical paper on performance and skills (3 hours, 100 marks) in four sections:
          • Section A – Athletics (track and field) – 3 questions, answer 2 (25 marks each).
          • Section B – Ball games – 2 questions, answer 1 (20 marks).
          • Section C – Racket games – 2 questions, answer 1 (20 marks).
          • Section D – Gymnastics – 2 questions, answer 1 (10 marks); max 2 minutes allowed to respond to performance calls.

        Syllabus Focus:
        1. Principles and Philosophy of Physical Education
        - Philosophies and principles of early leaders in physical education; changes in the concept of physical education.
        - Education of the physical vs education through the physical.
        - Physical education ideologies: nationalism, patriotism and national ideologies and how sports support them.
        - Recreation: definition, importance, types (indoor/outdoor), examples (walking, jogging, swimming, cycling, music, table tennis, camping), differences between recreational activities and lifetime sports; safety in recreation.
        - Intramural and extramural activities and tournaments: definitions, benefits, types of tournaments and their advantages/disadvantages.
        - Traditional sports in West Africa: origins, types by country and values.
        - Greek festivals and the Olympic Games: ancient festivals (Isthmian, Pythian, Nemean, Olympian), ancient Olympic Games, and the origin and importance of the modern Olympic Games.

        2. National and International Sports Competitions
        - National sports championships: organization and administration by sports associations/federations and corporate bodies; the National Sports Festival.
        - Institutional sports: university, polytechnic, colleges of education games, National School Sports Federation.
        - All Africa Games: history, timing, and the role of the Supreme Council for Sport in Africa (SCSA).
        - International competitions: FIFA World Cup, Olympic/Paralympic Games, Commonwealth Games, All Africa Games, World Athletics Championships, World Swimming Championships – history, organisation and governing bodies.

        3. Basic Human Anatomy and Physiology in Relation to Physical Activity
        - Skeletal system: main parts and functions of the skeleton; bones and joints involved in movement; joints that permit different kinds of movement.
        - Nervous system: brain, spinal cord and nerves; simple reflex action and its relevance to movement.
        - Circulatory system: structure and function of the heart; effects of exercise on the heart; blood circulation, types of circulation and functions of blood.
        - Respiratory system: trachea, lungs and nose; functions; internal and external respiration; aerobic and anaerobic sports/activities.
        - Skeletal muscles: major muscles of the body, types of muscle contraction (isotonic, isometric), muscle cramps – causes, prevention and first aid.
        - Somatotype: basic body types and their relationship to physical activities and sports.
        - Posture: correct posture, common postural defects (kyphosis, scoliosis, lordosis, flat foot), causes and corrective measures.

        4. Practice of Athletics, Games and Dance
        - Athletics (track events): sprints (50 m, 100 m, 200 m, 400 m, hurdles and relays), starting techniques (bullet/bunch, medium, elongated), baton change (visual and non-visual), hurdles (high/low), middle distance (800 m, 1 500 m), steeplechase/cross-country, long distance (3 000 m, 5 000 m, 10 000 m, marathon), phases of running, rules, regulations and officiating.
        - Athletics (field events): throws (discus, javelin, shot put) and jumps (high, long, triple, pole vault) – equipment/specifications, techniques, rules, officiating and safety.
        - Ball games: football, basketball, volleyball, hockey, handball – basic skills, rules, regulations, officiating, safety and use of standard courts/fields.
        - Racket games: tennis, table tennis, badminton – court/table specifications, equipment, skills, rules, officiating, safety.
        - Traditional dances: types from different regions, costumes and basic dance steps.

        5. Physical Fitness, Health, Nutrition, Drugs and Sports
        - Physical fitness: definition, health-related and performance/skill-related components, examples, benefits of physical activity, exercise and fitness.
        - Health and personal hygiene: importance of health in physical education; care of the body; appropriate sportswear and its role in regulating body temperature.
        - Nutrition: classes of food nutrients, balanced diet, dietary considerations in sports, role of carbohydrates in performance.
        - Drugs in sports: meaning, types (stimulants, narcotics, hallucinogens, sedatives, ergogenic aids), drug use and misuse, effects on sports performance.
        - Sports injuries: common injuries (dislocation, sprain, strain, fracture, bruise, cramp), their causes, symptoms, management and prevention.
        - First aid: meaning, special conditions requiring first aid (heat stroke, heat exhaustion, drowning, shock, fainting, muscle fatigue), contents of a first-aid box, basic artificial respiration; safety precautions in physical education.
        - Corrective and adapted physical education: definitions, categories of people with special needs, corrective exercises, activities for special needs, and social/emotional issues.

        6. Sports Administration
        - Administrative structures and functions of sports at national, regional/state and local government levels.
        - Administrative structures and functions at institutional levels (schools, colleges, tertiary institutions), including organisational charts and organograms.

        Guidelines for Candidates:
        - Combine theoretical study of anatomy, physiology, fitness, competitions and administration with regular practical participation in athletics, games and dance.
        - Learn rules, regulations, basic techniques and safety precautions for major track, field, ball and racket events, and practise officiating roles.
        - Maintain good health, personal hygiene and appropriate sportswear habits to support safe participation.
        - Understand national and international sports structures and how competitions are organised and governed.
        - Use past WASSCE Physical Education questions to build familiarity with both theory and practical assessment demands.`,
  },
  {
    subject: 'Picture Making',
    timeAlloted: 50 * 60,
    description: `Picture Making tests candidates’ theoretical knowledge and practical skills across major two-dimensional picture processes such as drawing, painting, decoration, collage, mosaic, marquetry and printmaking, using both traditional and digital tools.

        Preamble & Objectives:
        - Provides opportunities for talent development and acquisition of skills in all areas of picture making for personal growth and nation building.
        - Assesses candidates’ competence in using local resources in picture making.
        - Tests understanding and appreciation of picture making as an art form and vocation.
        - Evaluates knowledge of the computer as a tool in picture making and awareness of career/job opportunities (e.g. muralist, illustrator, painter, portraitist, glass stainer, mosaicist, decorator, etc.).

        Examination Scheme:
        - Paper 1: 40 multiple-choice questions to be answered in 50 minutes for 40 marks.
        - Paper 2: 6 essay-type questions, of which candidates answer 4 in 2 hours for 60 marks.
        - Paper 3: 2 practical project questions; candidates execute 1 project over five days, working six hours per day.
          • Execution carries 80 marks.
          • Design sketches and preparatory notes, done in the two weeks before execution, carry 20 marks.
          • Total marks for Paper 3: 100.

        Syllabus Focus:
        1. Picture Making as a Vocation and its Importance
        - Awareness of colour and visual expression.
        - Career opportunities in picture making (muralist, illustrator, painter, portraitist, glass stainer, mosaicist, decorator, etc.).
        - Study of works of contemporary Ghanaian artists (e.g. Kofi Antubam, Prof. Asihene, Dr. S. K. Amenuke) and their contributions.
        - Social and economic importance of picture making in Ghana.

        2. Basic Design and Composition
        - Definition and planning of compositions.
        - Principles and components of composition; types of composition and what to compose.
        - Qualities of good composition in picture making.

        3. Drawing and Illustration
        - Exploration of drawing tools and materials (charcoal sticks, pencils, etc.): identification, selection, use and maintenance.
        - Types and purposes of drawing/illustration (observation, recording, expression, communication of ideas).
        - Drawing from natural and man-made objects, human figures and activities, and imaginative compositions.
        - Application of linear and aerial perspective.
        - Meaning, scope, types and purposes of illustration in picture making.

        4. Painting
        - Identification of tools and media: crayons, poster colours, acrylics, tempera, watercolour, oil colour, pastels, mixed media, etc.
        - Definition and explanation of painting.
        - Colour theory: study and mixing of colour, tones and colour behaviour on different surfaces, colour psychology and symbolism, and their application.
        - Painting media and techniques associated with each medium; exercises using different media.
        - Painting styles such as abstract and realistic approaches.

        5. Printmaking
        - Principles of printmaking as an aspect of picture making.
        - Basic processes and types such as block printing, pyrography, serigraphy (screen printing), intaglio and related techniques.

        6. Collage, Mosaic, Montage, Appliqué and Marquetry
        - Definitions and explanations of each process and their types.
        - Exploration of tools and materials, planning and stages of execution.
        - Production of simple collages, mosaics, montages, appliqué and marquetry works.

        7. Mural Painting, Frieze, Fresco and Panel Work
        - Concepts, scope and general characteristics.
        - Styles and types of murals (relief, incised, plain graphic, etc.).
        - Principles of composition and execution of mural painting.
        - Projects on mural painting using Ghanaian themes and contexts.

        8. Body Art
        - Study of body decoration: concept, scope, history and significance of body painting.
        - Examples from Ghanaian culture (e.g. dipo customs, other rites and ceremonies).
        - Types: scarification, tattooing, coiffure, costume and related forms of adornment.

        9. Colour
        - Definition, classification/types and basic concepts of colour.
        - Significance of colour in picture making and visual communication.

        10. History of Painting
        - Scope, key concepts/characteristics and major exponents.
        - Significance and contributions of painting to the development of art.

        11. Entrepreneurship in Picture Making
        - Introduction to marketing art products.
        - Basic concepts in costing, pricing and marketing of artefacts.
        - Survey, cost estimation, production planning, pricing and marketing strategies for picture making products.

        12. Exhibition, Studio, Gallery and Museum
        - Planning and mounting exhibitions; importance of exhibitions for artists and communities.
        - Maintenance and significance of studios, galleries and museums in preserving and promoting art.

        13. Digital Painting
        - Concepts, principles and components of the computer relevant to picture making.
        - Computer as a drawing and painting tool, using available software.

        Guidelines for Candidates:
        - Practise regularly across drawing, painting, printmaking, collage/mosaic and mural work, using locally available materials.
        - Study works of established artists and relate them to historical and contemporary Ghanaian contexts.
        - Develop basic entrepreneurial skills in costing, pricing, marketing and exhibition of artworks.
        - Build familiarity with digital tools for drawing and painting alongside traditional media.
        - Use past WASSCE Picture Making questions and timed practice to prepare for both theory and project components.`,
  },
  {
    subject: 'Plumbing and Pipe Fitting',
    timeAlloted: 60 * 60,
    description: `Plumbing and Pipe Fitting tests candidates’ knowledge of basic plumbing theory, materials, tools, water supply and drainage systems, sanitary installations, welding and simple business practices, as well as their practical skills in installing and maintaining systems safely.

        Examination Scheme:
        - Paper 1: 40 multiple-choice objective questions to be answered in 1 hour for 40 marks.
        - Paper 2: 5 short-structured essay questions; candidates answer 1 compulsory question and any other 3 in 1 hour for 60 marks.
        - Paper 3: Practical test of 1 hour 30 minutes for 100 marks, with 3 questions of which any 2 are attempted. A list of required materials is issued to schools at least two weeks in advance. If materials cannot be obtained, an alternative theoretical practical test (3 questions, answer 2 in 2 hours for 100 marks) may be used to assess practical skills.

        Syllabus Focus:
        1. Safety Rules and Regulations
        - Meaning of safety, rules and regulations in the workshop and on site.
        - Hazards in plumbing workshops and sites; causes and prevention of accidents.
        - Safety tools and equipment; safe working practices and safety measures.
        - Common safety signs and symbols.

        2. Hand Tools, Equipment and Maintenance
        - Classification of plumbing tools and equipment.
        - Types, sketches and uses of common tools and equipment.
        - Correct handling, care and maintenance of tools and equipment.

        3. Pipe Fitting Materials
        - Types, sketches, uses and storage of pipe fitting materials.
        - Conventional signs and symbols for fittings and materials and their interpretation.

        4. Types of Pipe
        - Classification of pipes by material and size.
        - Properties and uses of different pipe materials.
        - Factors influencing choice of pipe for particular jobs.

        5. Pipe Jointing
        - Meaning of pipe jointing.
        - Types and sketches of joints used in plumbing work.
        - Materials used in jointing (e.g. sealants, gaskets, solder, etc.).
        - Applications of joints for changes of direction, fixed ends, fitting installation and length adjustment.

        6. Sources and Quality of Water
        - Sources of water: rain, surface and underground water.
        - Basic characteristics of potable water (tasteless, odourless, colourless).
        - Classification of water into hard and soft types.

        7. Water Treatment
        - Types and methods of water treatment: sedimentation, filtration, sterilisation, chlorination, etc.
        - Methods of removing hardness from water.

        8. Water Distribution Systems
        - Meaning and components of a water distribution system (water main, communication pipe, service pipe).
        - Types of cold water distribution systems: direct and indirect.
        - Types of reservoirs and storage tanks.
        - Simple diagrams of direct and indirect cold water supply systems.
        - Types of hot water supply systems and basic diagrams.
        - Common defects in cold and hot water systems.

        9. Drainage Systems
        - Meaning and types of drainage systems and drains.
        - Basic installation steps: trenching, laying and testing of drains.
        - Common drainage fittings (bends, tees, Y-joints, etc.).

        10. Sanitary Appliances
        - Meaning of sanitary appliances.
        - Classification into soil and waste appliances.
        - Examples, descriptions and uses of soil and waste appliances.
        - Basic installation of sanitary appliances and their fittings (taps, valves, etc.).

        11. Sewage Systems
        - Meaning of sewage system and types (storm, domestic, industrial, etc.).
        - Uses, construction and sketches of inspection chambers and manholes.
        - Sewage collection points: soakaway, septic tank, cesspool and public sewer.

        12. Welding in Plumbing
        - Meaning of welding.
        - Common tools and equipment for basic welding in plumbing.
        - Types and simple methods of welding used with pipes and fittings.

        13. Business Organisation, Book-Keeping and Contracts
        - Business organisation: meaning, simple types relevant to plumbing businesses and sources of capital.
        - Types of capital: fixed and working capital.
        - Book-keeping: meaning; basic books/records for daily business transactions (profit and loss, expenditure, ledgers, receipts, invoices, etc.).
        - Contract work: meaning of contract; simple types (fixed-price/lump-sum and unit-price contracts); key contract documents (bill of quantities, quotations/estimates, drawings, specifications, agreements, variations); parties involved (client, contractor, host community, consultant, lawyer).

        Guidelines for Candidates:
        - Master safety rules, tools, materials and basic jointing methods before attempting complex systems.
        - Understand water sources, treatment, distribution and drainage as integrated systems.
        - Practise reading and sketching simple plumbing layouts, symbols and details.
        - Develop hands-on skills in cutting, jointing, fitting and simple welding using appropriate tools and safety precautions.
        - Build basic business awareness in record keeping, costing and simple contract arrangements for plumbing work.`,
  },
  {
    subject: 'Principles of Cost Accounting',
    timeAlloted: 60 * 60,
    description: `Principles of Cost Accounting tests candidates’ understanding of core costing concepts, cost buildup and classification, costing methods and techniques, budgeting and variance analysis, and the preparation and reconciliation of cost accounts for planning, control and decision making.

        Aims & Objectives:
        - Assemble, analyse and ascertain the cost of producing and procuring goods and services.
        - Develop skills in using cost accounting as a management tool for planning, control and decision making.
        - Promote awareness of economical use of resources through costing techniques.
        - Demonstrate knowledge of basic methods for presenting cost statements and cost information.

        Examination Scheme:
        - Paper 1: 50 multiple-choice objective questions to be answered in 1 hour for 25 marks.
        - Paper 2: 9 essay-type questions to be answered in 2 hours for 75 marks, split into:
          • Section A – Theory of Principles of Cost Accounting: 4 questions; candidates answer 2 (15 marks each).
          • Section B – Practice of Principles of Cost Accounting: 5 questions; candidates answer 3 (15 marks each).

        Syllabus Focus:
        1. Introduction to Cost Accounting
        - Meaning, objectives, functions and importance of cost accounting.
        - Differences among cost accounting, management accounting and financial accounting.
        - Basic costing terminology: cost, cost unit, cost centre, unit cost and related terms.

        2. Characteristics of a Good Costing System
        - Characteristics such as simplicity, relevance, accuracy and cost-effectiveness.
        - Qualities of good cost information (brief, timely, comparable, objective, etc.).

        3. Cost Classification and Types of Cost
        - Bases of classification: by behaviour, function, nature and other criteria.
        - Types of cost (direct, indirect, fixed, variable, semi-variable, period, product, historical, sunk cost, etc.).
        - Components of cost build-up: prime cost, overheads, factory/works cost, cost of production, cost of sales, total cost and profit.

        4. Elements of Cost
        - Explanation of material, labour and expenses as basic cost elements.
        - Grouping elements into direct and indirect costs (direct materials, direct labour, direct expenses, factory/production overheads, administration, selling and distribution overheads, etc.).

        5. Materials
        - Purchasing, receiving, storage and related documentation.
        - Stocktaking methods: periodic and continuous; perpetual inventory.
        - Inventory control concepts: minimum, maximum and re-order levels and Economic Order Quantity (EOQ) in principle.
        - Pricing of materials issues and stock valuation (FIFO, LIFO, simple average, weighted average, standard price) and their advantages/disadvantages.

        6. Labour
        - Meaning and types of labour and labour cost (skilled/unskilled, direct/indirect).
        - Methods of remuneration: time rate; piece rate (straight, differential, piece rate with guaranteed time rate); incentive schemes (premium bonus, overtime premium).
        - Methods of time-keeping and the concept of idle time.
        - Payroll procedures: preparation of wages analysis sheets; statutory and non-statutory deductions (with emphasis on computing individual bonus schemes).
        - Labour turnover: meaning, causes, costs and labour turnover ratio.

        7. Overheads
        - Definition and types of overheads.
        - Overhead analysis, collection, classification, allocation, apportionment and redistribution.
        - Overhead absorption: calculation of overhead absorption rates (e.g. direct wages %, direct material %, prime cost %, machine hour rate) and application of these rates.
        - Determination of under- and over-absorption of overheads.
        - Activity Based Costing (ABC): meaning, advantages and disadvantages, steps, differences from traditional product costing (limitations of traditional systems) and computation of product costs using ABC.

        8. Costing Methods
        - Job and Batch Costing: purposes, circumstances for use and ascertainment of job/batch cost.
        - Contract Costing: meaning, characteristics, preparation of contract accounts and contractee’s account.
        - Service (Operating) Costing: meaning, objectives, organisations using service costing and cost per unit of service.
        - Process Costing: where it is applied, key terminology (joint products, by-products, scrap, waste), and preparation of process accounts including normal loss, abnormal loss and abnormal gain.

        9. Costing Techniques
        - Marginal vs absorption costing: meanings, distinctions and key terms (fixed, variable, semi-variable cost; contribution; period vs product cost).
        - Preparation of income statements using marginal and absorption costing.
        - Break-even analysis: meaning, importance, assumptions and limitations.
        - Computation of break-even point in units and value, including segregation of costs using the high-low method.
        - Preparation and basic interpretation of break-even charts.

        10. Budgeting and Budgetary Control
        - Basic terminology: budgets, budget centres, budget period, budgetary control.
        - Budgeting process, administration and importance.
        - Preparation (where required) of selected functional budgets: cash sales, material usage, material purchase and production budgets.
        - Overview of other functional budgets (sales, production, capital expenditure) and preparation of cash and master budgets.

        11. Standard Costing
        - Meaning, importance and types of standards.
        - Computation of basic variances: material (price and usage) and labour (rate and efficiency).

        12. Cost Accounts
        - Interlocking cost accounts and preparation of cost ledger accounts.
        - Reconciliation of cost and financial profits.
        - Integrated cost accounts: preparation of ledger accounts and income statements where cost and financial accounts are combined.

        Guidelines for Candidates:
        - Build a clear grasp of cost concepts, classifications and elements before tackling methods and techniques.
        - Practise setting up simple cost sheets, job/contract/service/process accounts and overhead absorption calculations.
        - Work many numerical problems on marginal vs absorption costing, break-even analysis, budgeting and basic variances.
        - Use clear layouts for ledger accounts, cost statements and reconciliations, showing all workings.
        - Study recommended cost accounting texts and past WASSCE questions to strengthen both theory and computational skills.`,
  },
  {
    subject: 'Printing Craft Practice',
    timeAlloted: 45 * 60,
    description: `Printing Craft Practice evaluates candidates’ understanding of basic printing processes, workshop safety, composition, binding/finishing, screen and lithographic work, machine printing and the legal/industrial context of the printing trade, alongside practical skills in common printing operations.

        Examination Scheme:
        - Paper 1: 40 multiple-choice questions in 45 minutes for 40 marks.
        - Paper 2: 5 essay questions; candidates answer any 4 in 1½ hours for 60 marks.
        - Paper 3: Practical test of 1 hour with 2 questions; candidates attempt 1 for 100 marks. A materials list is issued at least two weeks in advance. Where actual practical is not possible, an alternative theoretical practical test (2 sets of questions, both to be answered in 2 hours for 100 marks) may be used to assess practical skills.

        Syllabus Focus:
        1. Introduction to the Art of Printing
        - Definition and explanation of printing as an art and industrial process.
        - Importance, products, advantages and uses of printing.
        - Overview of printing in Nigeria (e.g. Survey Press, Government Press, Railway Press, Daily Times and other press houses).
        - Brief history and copyright issues: early origins in China, Gutenberg; roles of Hope Waddell and Henry Townsend in Nigerian printing history.
        - Printing as a career: key occupations (lithographers, machine minders, compositors, finishers, etc.).
        - Main printing processes and image carriers: letterpress/relief, offset/lithography, gravure/intaglio, screen, stencil and flexography – basic descriptions, materials, tools and equipment.

        2. Workshop Safety and Maintenance
        - Types of technical workshops for printing (lithography, machine room, binding/finishing, composition room).
        - Safety precautions in printing workshops: ventilation/air conditioning, electrical and lighting safety, darkrooms, machine rooms, hot-metal areas.
        - Safe handling and maintenance of machines, tools and equipment; basic procedures and schedules.

        3. Letter Assembly and Composition
        - Hand composition: tools, facilities, principles, terminology, type structure and typefaces/typography.
        - Design for printing: concepts of basic design, layout and instruments used.
        - Mechanical composition: monotype and linotype systems, accessories and uses; differences between them.
        - Photo-composition: meaning, equipment, basic principles, components and advantages.

        4. Print Finishing and Binding
        - Print finishing: meaning, importance and processes such as cutting, folding, stitching, trimming, etc.
        - Binding materials, tools and equipment (threads, leathers, bone folders, guillotine, papers and boards).
        - Types of binding: book, pamphlet, padding, edition case, loose-sheet, perfect binding.
        - Processes and technical terms associated with each binding type; differences between perfect binding and other types; edition and loose-sheet binding – materials, operations and uses.
        - Warehouse: definition; identification and uses of printing materials (papers and boards, inks, chemicals, printing surfaces; sizes, substance/grammage); basics of stock and inventory control.

        5. Screen Printing
        - Origin and principles of screen printing.
        - Frame and stencil making: wooden frames, meshes (silk/organdie), hand-cut stencils with cardboard, block-out methods; stretching mesh on the frame.
        - Materials and tools: frames, mesh, joints, glue, nails, squeegees, wax, paints/inks (oil or water-based), sponges, cardboard.
        - Simple printing operations using stencils: attention to registration marks and multi-colour work.
        - Photographic screen making: coating the screen with sensitised solution, exposure, development; identification and use of materials (gelatin coating, dichromate, positives, tracing paper, opaque, light sources).
        - Image reproduction: transferring images from screen to substrates (paper, stickers, plastics, etc.) and use of enlargers and suitable films/papers.

        6. Lithography – Process Camera Work and Plate Making
        - Planning: work tickets and layout planning; definition and uses of layout plans (gripper margin, image area, folds, gutter margins).
        - Materials: negatives/positives, masking tape, brown paper; tools such as light tables and stripping knives; identification of emulsion/non-emulsion sides.
        - Film making/process camera work: definition and functions of the darkroom; equipment and materials (chemicals, artwork, funnels, trays, sinks, etc.); process cameras and digital colour separation machines.
        - Types of films (panchromatic, orthochromatic, laser films) and their characteristics; developers, fixers, stop baths and safelight effects.
        - Plate making: plate processors, exposure frames/vacuum frames, developing sinks; pre-sensitised plates, gum arabic; basic correction, preservation and safety procedures.

        7. Machine Printing
        - Offset (lithographic) machine printing:
          • Basic techniques of offset printing.
          • Spare parts and maintenance (lubrication, bearings, roller setting).
          • Brief history and configuration of the offset machine.
          • Plate fitting and adjustment for image position on the sheet.
          • Major units of a lithographic machine (feeder, printing, delivery, etc.) and their functions.
        - Relief (letterpress) printing:
          • History, principles and types of relief printing.
          • Main parts, maintenance and methods of imposition.
          • Types of image carriers and common tools/materials (chases, quoins, solvents, oils, etc.).
        - Gravure (intaglio) printing:
          • History, explanation, principles and types of gravure image carriers.
          • Typical products and advantages/disadvantages.
        - Flexographic printing:
          • Definition and concept of flexography and its image carrier.
          • Differences between flexography and other machine printing processes.
          • Importance of flexography for packaging (cellophane, pharmaceuticals, food packaging, etc.) and typical products.

        8. Legal and Industrial Aspects of Printing
        - Trade houses in the printing industry and their activities: employers’ associations, professional bodies, training institutions, examination bodies and relevant foreign trade houses.
        - Copyright: basic principles and relevance to printing and publishing.

        Guidelines for Candidates:
        - Gain familiarity with all major printing processes (relief, offset, gravure, screen, flexo) and their typical products.
        - Practise safe use and routine maintenance of tools, machines and materials in simulated or real workshops.
        - Develop hands-on skills in basic composition, binding/finishing and simple screen/lithographic work.
        - Learn to read and prepare simple layouts, work tickets and warehouse/stock records.
        - Study the organisation, legal context and career paths in the printing industry and use past WASSCE questions to build speed and confidence in both theory and practical components.`,
  },
  {
    subject: 'Radio, Television and Electronics Works',
    timeAlloted: 45 * 60,
    description: `Radio, Television and Electronics Works introduces candidates to workshop safety, basic electricity, electronic components and circuits, radio/TV communication systems, practical servicing skills and small-business opportunities in the electronics trade.

        Preamble & Objectives:
        - Provide insight into the world of radio, television and electronics works and their link to science and technology.
        - Improve attitudes towards routine maintenance and repairs of radio, TV and electronic equipment.
        - Test knowledge and understanding of safety, basic electricity, tools/instruments, devices/circuits, communication systems, workshop practice and entrepreneurship.

        Examination Scheme:
        - Paper 1: 40 multiple-choice questions in 45 minutes for 40 marks.
        - Paper 2: 6 short-structured questions; candidates answer any 4 in 1 hour for 60 marks.
        - Paper 3: Practical test of 2 hours for 90 marks, with 3 skill-based questions of which 2 are answered; materials list released at least two weeks before the exam. Where practical materials are unavailable, an alternative theoretical practical test of 2 compulsory questions in 2 hours for 100 marks may be used.
        - Industrial Attachment: supervised long-vacation attachment between SS II and SS III, assessed by teachers for 10 marks.

        Syllabus Focus:
        1. Workshop Safety Rules and Regulations
        - Sources and prevention of hazards (electric shock, wet floors, wrong tool handling, improper dressing, horseplay, etc.).
        - Safety checks when servicing radio receivers: preparing work areas, discharging capacitors, working safely on power lines and live circuits.
        - Safety precautions in television workshops: power supplies, picture tubes, high-voltage sections, component ratings and handling.

        2. Basic Electricity
        - Structure of matter: basic atomic structure and charge concepts.
        - Conductors, insulators and semiconductors: definitions and uses.
        - Current, voltage and resistance: definitions, units, symbols and simple relationships; attraction and repulsion of charges.
        - Electronic components: identification by name, type, symbol, value and rating (resistors, capacitors, inductors, diodes, transformers, transistors, ICs, etc.).
        - Resistors and capacitors: types, colour code, values/ratings; comparison of measured and coded values; simple capacitor testing.
        - Kirchhoff’s current and voltage laws: concepts and basic calculations.
        - Diodes and transistors: types, symbols, structure, testing, and configurations (CC, CE, CB).
        - Batteries: primary and secondary cells, symbols, types, differences between wet and dry cells and basic tests.
        - Ohm’s law: relationship between voltage, current and resistance; series and parallel resistors.
        - Electric power: definition, measurement and simple calculations.
        - Direct and alternating current: definitions, differences, uses and measurements.
        - Alternating waveforms: RMS, peak and average values, frequency and period for simple AC waveforms.

        3. Electronic Tools, Instruments and Fault-Finding Equipment
        - Electronic hand tools: types and uses (screwdrivers, diagonal cutters, soldering gun/iron, desoldering tools, pocket knife, wire stripper, soldering wick, etc.).
        - Measuring instruments: voltmeter, ammeter, ohmmeter, multimeter; basic AC/DC measurements; ohmmeter use for testing semiconductors; recognising faulty meters.
        - Fault-finding equipment: oscilloscope, signal tracer, digital frequency counter, logic probe, TV analyser and related tools – identification, uses and operation.

        4. Electronic Devices and Circuits
        - Meaning of electronics and electronic circuits.
        - Emission and photoelectric devices: concepts and applications; types of emission (thermionic, photoelectric, field, secondary).
        - Semiconductor devices: basic semiconductor theory; diodes for rectification – principles, characteristics and applications.
        - Power supply units: principles of operation, block/schematic diagrams, rectification types, filters and construction of simple stabilised low-voltage DC supplies.
        - Amplifiers: operation, construction and uses of Class A, B, C and AB amplifiers (quantitative treatment only).
        - RLC circuits: resistive, inductive and capacitive circuits.
        - Feedback: concept, types, advantages and effects of positive/negative feedback on gain, bandwidth, noise and distortion.
        - Oscillators and multivibrators: principles, types and basic circuits; astable, bistable and monostable multivibrators.

        5. Electronic Communication Systems
        - Electronic communication systems: definition, types, block diagrams, functions of stages and the concept of noise.
        - Electromagnetic spectrum: radio-frequency bands (VLF, LF, MF, HF, VHF, UHF, SHF, EHF) and applications in communication.
        - Transducers: definition, types and functions (loudspeakers, microphones, video cameras, CRT/LCD display units, etc.).
        - Modulation and demodulation: principles and types (AM, FM), waveforms and envelopes, modulation index/factor; role of carrier waves; basic demodulation methods.
        - Radio transmitters and receivers: functions, block diagrams and stages; TRF and superheterodyne receivers (AM and FM), with advantages and disadvantages.
        - Selectivity and sensitivity: definitions; function of tuners and identification of tuner stages.
        - Resonant circuits: series and parallel resonance, concepts of bandwidth and bandwidth ranges; simple resonance calculations and formula.
        - Satellite communication systems: basic elements, types, transmission/reception, antenna and applications.
        - Television transmitters: working principles and block diagrams.
        - Image and sound reproduction in TV receivers: scanning principles, video signals, FM detection.
        - Monochrome TV receivers: concept of television, structure, stages and processing of picture/sound signals.
        - Colour TV receivers: primary colours, colour TV systems/standards (PAL, SECAM, NTSC), colour signal components and basic transmission/reception principles.

        6. Workshop Practice and Maintenance
        - Soldering and desoldering: techniques, types of solder, fluxes (e.g. amber resin, NaCl solutions) and precautions.
        - Electronic repairs: dismantling and reassembling power supply units and RF/IF/detector/audio stages in radio sets; installation and maintenance of car radios.
        - Fault-finding and repairs in radios: using test instruments and logical troubleshooting; identifying faulty components and remedies; alignment of RF/IF stages.
        - Measuring instruments in practice: multimeter use for checking correct current, voltage and resistance in active/passive components and circuits.
        - Diagnosis and repair of black-and-white TVs: repair procedures, service manuals, circuit diagrams, symptoms and fault-clearing tools.
        - Diagnosis and repair of colour TVs: typical symptoms, fault-clearing at each stage, static/dynamic convergence, colour bar generators and signal testing.

        7. Entrepreneurship in Radio, Television and Electronics Works
        - Business management and finance: basic accounting practices, cost–benefit analysis, purchasing methods and business records (ledgers, repair orders, inventory sheets), sources of capital (banks, credit unions, etc.).
        - Customer relations: good personal appearance, understanding customer psychology, workplace relations and telephone courtesy.
        - Business opportunities: satellite installation, electronic specialist, radio/TV consultant or technician, sales and service craftsman, antenna and TV installation work.

        Guidelines for Candidates:
        - Combine theory with regular practical work on real or training panels, radios and TVs.
        - Practise safe workshop procedures consistently, especially around mains power, high-voltage sections and picture tubes.
        - Build competence in using multimeters, oscilloscopes and basic test equipment for troubleshooting.
        - Study block diagrams and circuit layouts of common radio and TV sets and understand the function of each stage.
        - Explore small-business aspects (costing, records, customer service) to prepare for self-employment or technical careers in electronics.`,
  },
  {
    subject: 'Refrigeration and Air Conditioning',
    timeAlloted: 60 * 60,
    description: `REFRIGERATION AND AIR-CONDITIONING – WASSCE SYLLABUS OVERVIEW

        Scheme of Examination:
        - The examination consists of three papers, all of which must be taken.
        - Papers 1 and 2 are composite and are taken at one sitting.

        Paper 1: Objective Test
        - 40 compulsory multiple-choice questions.
        - Duration: 1 hour.
        - Carries 40 marks.

        Paper 2: Essay
        - 5 essay questions are set.
        - Candidates answer any 4.
        - Duration: 1 hour 30 minutes.
        - Carries 60 marks.

        Paper 3: Practical / Alternative to Practical
        - Two practical questions are set; candidates answer one within 2 hours for 100 marks.
        - A cutting list / list of materials is sent to schools at least two weeks before the paper for procurement and preparation.
        - Candidates carry out the practical task in the presence of an examiner.
        - Where facilities are inadequate, an alternative to practical work approach may be adopted:
          • Two compulsory written questions testing practical skills and applications.
          • To be answered within 2 hours for 100 marks.

        General Practical Emphasis:
        - Tests real-workshop experience.
        - Assesses ability to identify and use tools and equipment correctly.
        - Assesses use of measuring instruments, fault-finding and troubleshooting on refrigeration and air-conditioning systems.
        - May require freehand sketches of tools, circuits and wiring diagrams.

        DETAILED SYLLABUS TOPICS

        1. Safety and First Aid in the Workshop
        - Workshop rules and regulations.
        - Safety devices and equipment: helmet, goggles, safety boots, hand gloves, overalls, aprons, sand bags, fire extinguishers, etc.
        - Accidents: causes and prevention techniques in the refrigeration and air-conditioning workshop.
        - First aid: materials (first aid box contents, charts, posters) and application of first aid measures.

          Practical Orientation:
          - Identification and safe operation of various types of fire extinguishers.

        2. Energy, Matter and Power
        (a) Energy
        - Definition and forms of energy.
        - Simple processes of energy conversion, e.g. electrical to mechanical, gas to vapour, mechanical to heat.
        - Meaning of internal energy.
        - Enthalpy of a fluid substance in relation to pressure, volume and temperature.
        - p–h diagram of a refrigerant.

        (b) Nature of Matter
        - States of matter: solid, liquid, gas/vapour.
        - Change of state of matter and its relevance to refrigeration.

        (c) Power
        - Meaning of power.
        - Calculation of power used by a compressor.
        - Units of power: watts and kilowatts.
        - Units of heat: joules and kilojoules.

          Practical Orientation:
          - Demonstration of conversion of mechanical to heat energy (e.g. rubbing palms together).
          - Demonstration of change of state, e.g. melting ice block to water.

        3. Temperature and Pressure
        (a) Temperature and its Measurement
        - Concept of temperature.
        - Different types of thermometers: gas, resistance, liquid-in-glass and thermocouple thermometers.
        - Temperature scale conversion:
          • Centigrade to Fahrenheit.
          • Celsius to Kelvin.
          • Fahrenheit to Rankine.
        - International practical temperature points, e.g.:
          • Boiling point of oxygen: –182.97 ºC (approx.).
          • Triple point of water: 0.01 ºC.
          • Boiling point of water: 100 ºC.

        (b) Pressure and its Measurement
        - Concept of pressure.
        - Types of pressure measuring devices: barometer, manometer, Bourdon-tube gauge, etc.
        - Pressure measurement and use of suction and high-pressure gauges.
        - Effects of temperature, pressure and volume in refrigeration and air-conditioning systems.
        - Gas laws and simple gas-law calculations:
          • Boyle’s law.
          • Charles’ law.
          • Dalton’s law.
          • Combined/general gas law, including simple calculations using:
            P1V1 = P2V2, P1/T1 = P2/T2, V1/T1 = V2/T2, P1V1/T1 = P2V2/T2.

          Practical Orientation:
          - Demonstration of the use of a barometer.
          - Demonstration of how suction and high-pressure gauges are used to assess system performance.

        4. Refrigeration and Air-Conditioning Systems
        (a) Introduction to Refrigeration
        - Basic vapour-compression refrigeration cycle:
          • Evaporation.
          • Compression.
          • Condensation.
          • Expansion.
        - Layout diagrams of typical refrigeration systems.
        - Types and applications of various forms of refrigeration (domestic, commercial, industrial, transport, etc.).
        - Principles of operation of different refrigeration systems.
        - Components and accessories: compressors, condensers, evaporators, expansion devices and accessories (relays, solenoid valves, capacitors, thermostats, etc.); functions and correct connections.

        (b) Refrigerants
        - Definition and types of refrigerants.
        - Chemical names and formulae of common refrigerants.
        - Properties and uses of refrigerants.
        - Refrigerant flow-control devices: thermostatic expansion valves (TEV), automatic expansion valves (AEV), capillary tubes, etc.
        - Operation principles of refrigerant flow controls.

          Practical Orientation:
          - Identification of refrigerants by cylinder colours.

        (c) Air-Conditioning Processes
        - Psychrometric properties of air.
        - Air-conditioning processes: heating and cooling, humidification and dehumidification as appropriate.
        - Latent and sensible heat; saturated and super-saturated vapour.

          Practical Orientation:
          - Demonstration of air-conditioning principles by sling psychrometer.
          - Demonstration of the difference between latent and sensible heat (e.g. methylated spirit on human skin).

        5. Compressors, Condensers and Evaporators
        (a) Compressors
        - Types of compressors used in refrigeration and air-conditioning.
        - Constructional features of compressors.
        - Working principles of different compressor types.

        (b) Condensers
        - Construction of condensers.
        - Types of condensers and their applications.
        - Calculation of heat load on condensers.
        - Types and functions of liquid receivers and service valves.
        - Principles of operation of liquid receivers and service valves.

        (c) Evaporators
        - Types of evaporators.
        - Construction and servicing of evaporators.
        - Working principles of evaporators.
        - Heat-load estimation on evaporators.

          Practical Orientation:
          - Demonstration of servicing of condensers and evaporators.

        6. Workshop Practices
        (a) Basic Tools Used in Refrigeration and Air-Conditioning Workshops
        - Identification and uses of basic tools: hacksaws, coil spring benders, reamers, tube cutters, etc.
        - Use of tools and procedures for servicing window-unit air-conditioners.

        (b) Fault Diagnosis and Troubleshooting
        - Different methods of diagnosing faults: systematic methods and observation.
        - Common faults in refrigeration systems and their symptoms: leakage, faulty units, faulty compressors, poor cooling, current leakage, etc.

        (c) Maintenance and Testing
        - Techniques of effective repairs in refrigeration and air-conditioning systems.
        - Techniques of troubleshooting in given systems.
        - Testing for leakages, polarity, insulation continuity, pressure and system efficiency.
        - Maintenance of compressors, evaporators, motors, condensers, valves, accumulators and contactors.

          Practical Orientation:
          - Servicing of a window air-conditioner.
          - Demonstration of testing for leakages, polarity, continuity and efficiency.
          - Demonstration of repair/maintenance procedures on compressors, evaporators, motors and condensers.

        7. Installation Processes
        (a) Piping, Ducting and Trunking
        - Concepts of piping, ducting and trunking.
        - Piping between condensers and evaporators.
        - Ducting, trunking and installation of air-conditioners.
        - Tools for piping/ducting/trunking: mallet hammers, flaring tools, silver tape, dot punches, bending springs, insulating materials (e.g. armaflex), hacksaws, PVC pipes, etc.

        (b) Motors and Generators
        - Types, functions, operation and applications of motors and generators in RAC systems.
        - Installation procedures for motors and generators.
        - Maintenance of motors and generators.

        (c) Cold Rooms
        - Working principles of cold rooms.
        - Cold-room installation procedures.
        - Factors affecting cold-room operation.

          Practical Orientation:
          - Installation of a split air-conditioner.
          - Servicing of motors and generators.

        8. Insulation Processes
        - Importance of insulation in refrigeration and air-conditioning systems.
        - Types of insulating materials: glass wool, fibreglass, polystyrene foam, wood, bitumen, ceramic, etc.
        - Functions and properties of insulation materials.
        - Procedures for insulating refrigeration and air-conditioning systems.

        9. Entrepreneurship
        (a) Business Concepts
        - Concept of business enterprise and types of business organisations: sole proprietorship, partnership, limited liability companies, etc.
        - Sources of finance: loans, mortgages, shares, overdrafts, savings, etc.

        (b) Marketing
        - Types and functions of marketing styles.
        - Meaningful marketing at the least cost.
        - Marketing protocol, customer relations and cost considerations.
        - Carrying out a commercial marketing survey to set up a refrigeration and air-conditioning business.

        (c) Basic Accounting
        - Principles of accounting.
        - Concept of business as a separate entity.
        - Simple records: ledgers, trial balance and balance sheet.

          Practical Orientation:
          - Preparation of basic accounting records such as ledgers and balance sheets.

        List of Facilities and Major Equipment/Materials (School/Training Workshop)
        - Complete RAC tool boxes.
        - Oxy-acetylene brazing sets.
        - Drilling and grinding machines (pillar, portable and table types).
        - Vacuum pump, recovery and recycling machine, spotter recovery pump.
        - Work benches, table vices and hand tools (hacksaws, pliers, screwdrivers, hammers, etc.).
        - Electronic leak detectors, electronic charging scales and halogen leak lamps/torches.
        - Manifold gauges and recovery cylinders.
        - Pipe benders, reamers, ratchet wrenches.
        - Various refrigerants (e.g. R134a, R600a, R717) as permitted by current regulations.

        Suggested Reading (Typical Texts):
        - Ananthanarayanan, P. N. – Basic Refrigeration and Air Conditioning.
        - Whitman, W. C., Johnson, W. M. – Refrigeration and Air-Conditioning Technology.
        - Rajput, R. K. – Refrigeration and Air-Conditioning.
        - Dossat, R. J. – Principles of Refrigeration.

        Exam Preparation Tips for Candidates:
        - Consolidate understanding of the vapour-compression cycle and air-conditioning processes with diagrams and p–h charts.
        - Develop strong workshop habits: safety, tool care, accurate measurement and neat workmanship.
        - Practise common service procedures on domestic refrigerators, freezers, window and split air-conditioners.
        - Work through calculations on power, temperatures, pressures and gas laws relevant to RAC work.
        - Use past WASSCE questions (Papers 1–3) to build confidence in objective, essay and practical/alternative-to-practical formats.
        - Relate classroom learning to real-life refrigeration and air-conditioning installations at home, school and workplaces.`,
  },
  {
    subject: 'Salesmanship',
    timeAlloted: 50 * 60,
    description: `SCHEME OF EXAMINATION
        There will be two papers, Paper 1 and Paper 2 – both of which will constitute a composite paper to be taken at one sitting.

        PAPER 1: Will consist of forty (40) multiple choice questions, all of which are to be answered in fifty (50) minutes for 20 marks.

        PAPER 2: Will consist of seven (7) essay questions out of which candidates will be required to answer any five (5) for eighty (80) marks. The Paper will last 2 hours.

        DETAILED SYLLABUS
        S/N CONTENTS   TOPICS AND NOTES

        1. PRINCIPLE OF SALESMANSHIP

        A. Introduction To Salesmanship:
            i. Definition of salesmanship.
            ii. History of salesmanship.
            iii. Development of salesmanship in Nigeria.
            iv. Importance of salesmanship.

        B. Concept Of Salesmanship:
            i. Explanation of basic terms in salesmanship:
            (a) Selling,
            (b) Consumer satisfaction,
            (c) Profit maximization.
            ii. The meaning and application of the concept of salesmanship.
            iii. The objectives of the salesman to:
            (a) the company,
            (b) himself,
            (c) customer.

        C. Characteristics Of A Salesman – Motivation, friendliness, confidence, persuasiveness and politeness.

        D. Functions Of A Salesman to:
            i. Customers,
            ii. Company,
            iii. Government,
            iv. Public or its environment.

        E. Salesmanship And Marketing:
            i. The relationship between salesmanship and marketing,
            ii. The differences between salesmanship and marketing,
            iii. The skills needed in salesmanship.

        F. Job / Career Opportunities In Salesmanship:
            i. Relevance of salesmanship as a course of study,
            ii. Self reliance / employment.

        G. Types Of Salesman:
            i. Industrial salesman,
            ii. Sales representative / marketing representative,
            iii. Retail salesman,
            iv. Wholesale salesman,
            v. Functions of each type.

        2. SALES MANAGEMENT

        A. Sales management:
            i. Definition of sales management,
            ii. Roles and techniques of a sales manager,
            iii. Sales decisions,
            iv. Factors affecting sales,
            v. Company objectives and sales force decision,
            vi. Roles of sales force.

        B. Sales forecasting:
            i. Definition of sales forecast,
            ii. Methods of sales forecasting,
            iii. Uses and users of sales forecasting,
            iv. Advantages and disadvantages of sales forecasting,
            v. Meaning of market survey,
            vi. Importance of market survey to salesman and producer.

        3. ADVERTISING AND SALES PROMOTION

        A. Advertising:
            i. Definition,
            ii. Types of advertising,
            iii. Types of advertising media,
            iv. Advantages and disadvantages,
            v. Reasons for advertising to the company and the salesman.

        B. Communication process:
            i. Definition,
            ii. Means,
            iii. Importance of communication to salesman,
            iv. Barriers in communication.

        C. Branding And Packaging:
            i. Definition of branding and packaging,
            ii. Types,
            iii. Importance,
            iv. Advantages and disadvantages.

        D. Promotion:
            i. Definition,
            ii. Types,
            iii. Sales incentives:
            (a) Gift items,
            (b) Discounts – trade / cash, seasonal, quantity,
            iv. Importance of promotion to salesman,
            v. Advantages and disadvantages of promotion.

        4. PRICING AND DEMAND

        A. Pricing:
            i. Definition,
            ii. Importance of price fixing,
            iii. Methods / strategies used in price determination,
            iv. Advantages and disadvantages of price fixing,
            v. Effect of price changes and customers' behaviour,
            vi. Problems of pricing.

        B. Demand:
            i. Meaning and types,
            ii. Factors affecting demand,
            iii. Laws of demand,
            iv. Effects of a change in demand,
            v. Market forces and pricing.

        C. Cost base:
            i. Definition of cost,
            ii. Types of cost,
            iii. Differences between types of cost,
            iv. Determinants of cost,
            v. Theory of cost,
            vi. Analysis / calculation of cost and profit.

        D. Discounts:
            i. Definition,
            ii. Types,
            iii. Importance and reasons for giving discounts to customers,
            iv. Differences between the various types of discounts.

        5. CONSUMER BEHAVIOUR

        A. Consumer:
            i. Definition,
            ii. Meaning of consumer market,
            iii. Meaning of consumer behaviour,
            iv. Factors affecting consumer buying behaviour,
            v. Types of buying decision.

        B. Decision process:
            i. Meaning,
            ii. Relationship between decision process and sales,
            iii. Methods of decision process,
            iv. Hierarchy of needs:
            (a) Psychological needs,
            (b) Sociological needs,
            v. Factors affecting consumer decision making,
            vi. Effect of consumer decision on sales,
            vii. Reasons for consumer decision,
            viii. Problems associated with consumer decision process.

        C. Environmental factors affecting consumer behaviour:
            i. Sociological factors:
            (a) Urban and rural community,
            (b) Family income,
            (c) Occupation,
            (d) Education,
            (e) Age,
            (f) Sex,
            (g) Race / nationality,
            (h) Religion / culture,
            ii. Characteristics of a conventional market,
            iii. Customer attitudes and behaviour.

        D. Corporate buying behaviour:
            i. Meaning of corporate / industrial buyer,
            ii. Factors that influence corporate / industrial buying behaviour:
            (a) Quantity,
            (b) Quality,
            (c) Sources,
            (d) Pricing,
            (e) Delivery time.

        E. Major sale influence:
            i. Product quality,
            ii. Price in relation to quality,
            iii. Factors that stimulate sales,
            iv. Promotion influencing sales,
            v. Corporate image,
            vi. Differences between price, product and promotion.

        6. DISTRIBUTION

        A. Channels of distribution:
            i. Definition of distribution and channels of distribution,
            ii. Various channels of distribution,
            iii. Advantages and disadvantages of each channel of distribution,
            iv. Problems,
            v. Factors affecting choice of channels of distribution.

        C. Retailer:
            i. Definition,
            ii. Functions,
            iii. Advantages of retailership to:
            (a) The producer,
            (b) The customer.

        D. Wholesaler:
            i. Definition,
            ii. Types,
            iii. Functions to:
            (a) The producer,
            (b) The consumer,
            iv. Advantages and disadvantages of wholesale trade.

        E. Warehousing:
            i. Meaning,
            ii. Types,
            iii. Advantages and disadvantages.

        F. Consumer co-operative societies:
            i. Definition,
            ii. Types,
            iii. History of consumer co-operative societies,
            iv. Functions of co-operative society to their members and the economy,
            v. Advantages and disadvantages.

        7. INTERNATIONAL TRADE

        A. Export trade and import trade:
            i. Definitions,
            ii. Types,
            iii. Advantages and disadvantages,
            iv. Role of a salesman in import and export trade,
            v. Differences between import trade and export trade,
            vi. Barriers to international trade.

        B. Basic concepts and documents used in import and export trade:
            i. Definition of documents,
            ii. Types of documents,
            iii. Uses of each document,
            iv. Examples of the documents,
            v. Explanation of the concepts:
            (a) Principle of comparative advantages,
            (b) Terms of trade,
            (c) Terms of payment,
            (d) Balance of trade and balance of payment,
            vi. Differences between balance of trade and balance of payment.

        8. ENTREPRENEURSHIP AND FINANCE IN SALESMANSHIP

        A. Concept of entrepreneurship in salesmanship:
            i. Definition of entrepreneur and entrepreneurship,
            ii. Differences between entrepreneurship and entrepreneur,
            iii. Types of entrepreneur,
            iv. Objectives of an entrepreneur,
            v. Roles and qualities of an entrepreneur,
            vi. The ways a salesman can be an entrepreneur,
            vii. Problems of entrepreneurship,
            viii. Advantages of entrepreneurship.

        B. Finance in salesmanship:
            i. Meaning of finance,
            ii. Uses and importance of finance,
            iii. Sources of finance to a salesman and an entrepreneur,
            iv. Definition of financial institutions,
            v. Types of financial institutions,
            vi. Role of financial institutions to salesman and entrepreneur.

        SUGGESTED TEXT BOOKS
            i. Basic Marketing – McCarthy Jerome E., William Perreault Jr.
            ii. Marketing – G. B. Giles (The M & E hand book services),
            iii. Consumer Behaviour – Prof. Achumba (University of Lagos),
            iv. Fundamentals of Selling – Charles M. Futrell.`,
  },
  {
    subject: 'Sculpture',
    timeAlloted: 50 * 60,
    description: `PREAMBLE

        Sculpture has been identified as one of the vocational subjects at the Senior High School Level because of its importance in the sustenance and development of the culture and arts of a society.

        OBJECTIVES
        The objectives of the syllabus are to test for the candidate’s:
            (i) knowledge in the history and development of sculpture,
            (ii) ability to explore, identify, select, construct, prepare, use and care for materials and tools,
            (iii) knowledge and experience in healthy studio practices,
            (iv) creative and manipulatory skills in designing and production of various forms of sculpture by employing the traditions and cultures of the society to arrive at original creations,
            (v) skills in the finishing and packaging of sculptures,
            (vi) capability to appreciate and critique works of sculpture,
            (vii) ability to plan and establish a small-scale industry.

        SCHEME OF EXAMINATION
        There will be three papers, Papers 1, 2 and 3 all of which must be taken. Papers 1 and 2 will be a composite paper to be taken at one sitting.

        Paper 1: Will consist of forty multiple-choice objective questions all of which must be answered within 50 minutes for 40 marks.

        Paper 2: Will consist of six essay-type questions. Candidates will be required to answer four questions within 2 hours for 60 marks.

        Paper 3: Will be two practical projects out of which candidates will execute one within five days, working for six hours each of the days. The paper will carry 80 marks.

        The questions will be sent to the candidates two weeks before the execution period for candidates to study. Designing of sketches and preparatory notes should also be done within the two weeks prior to the execution of the project. These will carry 20 marks. The total mark for the paper is therefore 100.

        DETAILED SYLLABUS

        Sculpture, as applied in this syllabus, includes the general knowledge of the history and development of the vocation, selection and use of tools and materials, health hazards and safety precautions, production of works, their finishing and packaging techniques and marketing strategies of the products.

        Candidates will be expected to respond adequately to questions drawn from all aspects of sculpture in their objective, essay and practical forms. Questions will be drawn from the following areas:

        1. Sculpture as a Vocation
            - Definition of sculpture, history and rationale for studying sculpture, career opportunities, the sculpture industry, methods of making sculpture (carving, modelling, casting, construction and assemblage, etc.).

        2. Exploration of Materials, Tools and Equipment
            (a) Carving – identification and uses of tools (gouges, mallets, chisels, saws, adze, etc.); methods of using various tools; care of tools to prevent rust and damage and promote longevity (clean and oil metal parts; keep tools in tool box). Equipment: table, bench vices, clamps, bench-screws, power tools, sprayers, grinders, drills, chain saw, etc. Care and maintenance: clean and oil regularly.
            (b) Modelling – identification and uses of modelling tools (spatula, calipers, frog / cutting wire, throne); methods of modelling (pinching, slabbing, coiling and hollowing); care and maintenance: prevent damage; clean, wash and dry tools. Equipment: modelling stand, modelling table, turn table.
            (c) Casting – identification and uses of casting tools (spatula, kidney, etc.); methods and materials: lost wax, sand casting, cold casting, plaster, cement, wax and slip casting, paper, etc. Equipment for casting: crucibles, tongs, safety equipment (gloves, goggles, masks, etc.).
            (d) Construction and assemblage – identification and uses of tools (hacksaw, wires, hammers, saw, chisels, pincers, rasps, files, etc.); methods of construction and assemblage (riveting, welding, tying, nailing, soldering, sewing, gluing, etc.). Equipment: soldering machine, arc-welding machine, goggles, etc.
            (e) Safety measures and precautions – Carving: wearing protective covering (glasses, respiratory aid, earplugs, boots, etc.), carve away from the body. Modelling: nose guard for working with dry clay, POP, cement, etc., boots, overall clothing. Casting: protective clothing for the body and face, boots, glasses, gloves. Construction and assemblage: protection from naked fire and electric wires, working on dry areas, wearing boots, avoid spillage and inhaling gases and dusty materials.
            (f) Sources of tools and equipment – e.g. blacksmith shops, carpentry workshops, hardware shops.

        3. Materials
            - Carving: ivory from elephants, whalebone, stone, wood from the forest, sawmills, timber markets and farms.
            - Modelling: clay from river banks, marshy areas, valleys, road construction sites, dug wells, environment and hardware shops; others from the environment and shops e.g. POP, cement and paper.
            - Maintenance: keep wet clay in bins or polythene; cement and POP in dry places; wax in a cool place; iron rods away from moisture; wood under shed or indoors.

        4. Preliminary Design
            - Meaning and importance of preliminary design: serves as a guide or gives an idea of the end product and also prevents waste of material and saves time.
            - Idea development: sketching and drawing, planning and organization of ideas (manual and computer-aided design), preparation of models for reference.

        5. Methods of Production
            - Production process: direct / indirect.
            - Carving (relief and in-the-round), engraving, whittling, chipping, chiselling, blocking, blasting, etc.
            - Modelling: pinching, coiling, hollowing, scooping, slabbing.
            - Casting: moulding, lost wax casting, cold casting, waste mould, piece mould casting, sand casting, repoussé layering.
            - Construction and assemblage: welding, soldering, riveting, hammering, joining, pressing, sewing, knotting, repoussé, filigree (quilling), etc.

        6. Lettering
            - Types of lettering: block, gothic, freestyle, italics, Roman, etc.
            - Methods of execution (manual and computer): stencilling, carving, embossing, moulding, reflective, scorching, etching, casting, forging, modelling, casting.
            - Application / uses: plaques, memorials, tombstones, ornamental inscriptions, epitaphs, medals, shields, labels.
            - Materials for lettering: paper, leather, stone, wood, POP, rubber, plastic, clay, etc.
            - Tools for lettering: chisels, lino cutters, gouges, spatula, knives, hammer, mallets, scooping tools, computer software, pencils, lettering pens, soldering rod, etc.

        7. Decoration and Finishing
            - Types of decoration: incision, engraving, scorching, painting, varnishing, burnishing, embossing, stamping, texturing, smoking, sand blasting, inlaying, etc.
            - Methods of decoration: cutting into surface with tools, pressing into surface, stamping on surface, etc.
            - Types of finishing: polishing, painting, varnishing, waxing, smoothing, sanding, texturing, etc.
            - Importance of decoration and finishing: adds value, preservation, protection, durability, easy identification, socio-economic importance (income generation, foreign exchange, export promotion, tourism, aesthetics, etc.).
            - Materials: brasso, paint, wax, cowries, bones, raffia, beads, straw, wood, etc.
            - Tools: gouges, knives, sprayers, brushes, pincers, files, abrasive papers, etc.

        8. Establishment of Small-scale Industry
            - Factors to be considered: types of raw materials, labour, transport and communication, location of market, siting of industry / factory, insurance, entrepreneurship, utility services, branding, quality control, capital, etc.
            - Socio-economic importance: income generation, promotion of tourism, foreign exchange earnings, recognition of sculpture, promotion of art and appreciation, etc.
            - Shortfalls in finishing: uneven / irregular surface textures, disproportionate figures, cracks and dents on works, unstable / imbalanced works, smearing / messy surfaces, etc.
            - Ethics of quality control: use of quality materials, appropriate subject matter, avoidance of indecent themes (vulgarism, pornography, derogatory and insulting signs and language), quality finishing, environmentally friendly materials, maintenance of a clean environment.

            - Packaging and handling:
            • Types of packaging: primary, secondary and tertiary.
            • Tools: knife, hammer, needle, saw, pliers, drills, staplers, scissors, etc.
            • Materials: plywood, plastic bags, fabrics, paper, glue, cardboard, foam, saw dust, sand, wood-shavings, styrofoam, adhesive tape, etc.
            • Techniques: boring, wrapping, nailing, gluing, tying, boxing, etc.
            • Importance: prevention of damage, shocks, hurt and damp conditions; facilitates easy and safe handling; adds value; aids identification.

        9. Costing, Pricing and Marketing
            - Marketing of art products including costing and pricing.
            - Basic marketing strategies: advertising by posters, handbills, television, radio, word of mouth, exhibitions, bazaars and fairs; effective decoration and finishing; attractive packaging; transportation.
            - Types of exhibition: general and specialised.
            - Planning exhibitions: factors such as type of exhibition (indoor / outdoor), theme, cost, publicity, expected number of guests, duration / time, mounting and layout, security, visitors’ book, sales, opening and closing, etc.
            - Mounting exhibits: consider colour scheme and balance, size, weight and height of exhibits, effect of light and ventilation, free movement for viewing, room decoration, creation of centre of attraction, invitations and publicity materials (posters, adverts, press releases, brochures, catalogues, sales list, etc.).
            - Importance of exhibitions: avenue for sales, advertisement of products, exposure of new technology, promotion of the artist, education, awareness creation and edification, etc.

        10. Managing Sculpture Enterprise
            - Functions of a manager: planning, organising, controlling, supervision, budgeting, bookkeeping and computer skills.
            - Attributes of a manager: perseverance, commitment, good temperament, courage to take risks.
            - Controlling business risk: direct risks (theft, fire, misuse / misappropriation of capital and profit) and indirect risks (lack of sales due to faulty and low quality products or wrong location, currency inflation, new taxes, changes in labour laws, etc.).
            - Measures of control: regular stock checks, employing an accountant / bookkeeper, increased vigilance and security; use of good quality electrical appliances, proper electrical connections, regular checks on fire extinguishers, etc.

        11. Appreciation and Criticism in Sculpture
            - Process of appreciation and evaluation in sculpture; terms in appreciating three-dimensional sculptures (content, form, subject matter, symbolism, materials and techniques, artist and style); critiquing works of art.

        12. Portfolio of Works
            - Methods of compiling works: photographs arranged and labelled in albums or slide holders in chronological / stylistic / thematic order; pictures of work stored electronically on CD, video tape, computer hard disk; photographs, drawings and sketches mounted on cardboards and labelled.
            - Uses of portfolio: reference and study, exhibition of works, self-evaluation.
            - Importance of portfolio: keeps record of works, tracks development of skills, source of inspiration, reveals weaknesses, develops creative ability and quality control, helps artist to be focused, supports further studies, builds self-esteem and confidence.

        13. Professional Practices
            - Developing a business plan.
            - Artist’s statement: definition and importance.
            - Business plan: definition, importance and preparation (steps involved).
            - Business brochure and card: definition and importance.

        14. Museum and Gallery Studies
            - Definition, history and development of the museum.
            - Functions and socio-economic importance of the museum.
            - Administrative structure of the museum.
            - Authenticity and neutrality of the museum.
            - Examples of museums and their activities.

        Ideal Studio
    - Indoor and outdoor areas; facilities such as water, adequate lighting, good ventilation, fire extinguishers, office, washroom, and separate sections for various types of work (carving, casting, welding, modelling); storage areas for tools, raw materials and finished works.`,
  },
  {
    subject: 'Shorthand',
    timeAlloted: 73 * 60,
    description: `SCHEME OF EXAMINATION
        Two passages will be dictated at 80 words per minute with an interval of 2 minutes. The syllabic intensity of the passages should be 1.35 – 1.40. Each passage will last for 3 minutes. After the last passage, 5 minutes will be allowed for candidates to read through their shorthand notes before the commencement of transcription.

        All transcription must be done on the typewriter. The use of English and Shorthand Dictionaries is allowed.

        The examination shall be of 1 hour 13 minutes duration:
        - 6 minutes for dictation,
        - 7 minutes for intervals,
        - 1 hour for transcription.

        DETAILED SYLLABUS

        (a) Business Matters
        One of the passages for dictation shall be business in nature e.g. letters, memoranda, reports and other related topics.

        (b) Literary Matters
        The other passage shall be literary and within the experience of the candidates. It shall not be technical or controversial.

        NOTES ON THE EXAMINATION

        (a) Candidates must provide their own typewriters, shorthand notebooks and other writing materials.

        (b) The shorthand notes may be written with either pen or pencil. The shorthand notes must be attached to the transcripts.

        (c) Specially marked typing papers will be supplied by the Council. No typing paper other than those supplied by the Council may be used.

        (d) Candidates shall not be allowed to make any carbon copy.

        (e) The shorthand readers shall be allowed access to the passages twenty minutes before the dictation commences. A preliminary passage of 2 minutes’ duration will be dictated before the commencement of the actual examination to enable candidates to become accustomed to the reader’s voice. The preliminary passage must not be transcribed and does not form part of the examination.

        (f) The candidate’s shorthand teacher may be appointed to dictate the passages. The invigilator must check the dictation and note errors or deviation from the total time allowed for each passage. A signed certificate of the actual time taken should be returned with the completed scripts.

        (g) Quarter‑minute indications are printed on each passage. Care should be taken to articulate the words very distinctly. Punctuation must be indicated by the inflexion of the voice and in no case dictated.

        (h) The reader is expected to leave the examination hall immediately after the dictation, taking away with him the shorthand passages. These should be returned to the Council or an official of the Council where one is available. On no account should the passages be left with either the invigilator or supervisor.

        READING LIST
        (1) First Course – Pitman
        (2) First Course Review – Pitman 2000
        (3) Pitman 2000 Phrase Book
        (4) First Dictionary – Pitman 2000 Shorthand
        (5) 700 Common Words – Pitman 2000
        (6) Office skills magazines
        (7) Journals
        (8) Speed Development – Pitman 2000 (Bryan Coombs)
        (9) Speed Builder – Bryan Coombs.`,
  },
  {
    subject: 'Social Studies',
    timeAlloted: 50 * 60,
    description: `PREAMBLE
        Social Studies is a study of the contemporary problems of society. The subject prepares the individual to fit into society by equipping him or her with knowledge about the culture or ways of life of the society, its problems, its values and its hopes for the future.

        The subject is multi‑disciplinary and draws its content from Geography, History, Sociology, Psychology, Economics and Civic Education. Essential elements of knowledge and principles from these disciplines are integrated into a single subject that stands on its own.

        As a subject, Social Studies helps students to understand their society better; investigate how their society functions; and develop a critical yet developmental mind that can transform society. By understanding social, cultural, environmental, political and economic issues in Ghanaian society, students are better able to examine institutions and ways of life with a critical and constructive outlook and contribute to faster growth and development.

        EXAMINATION OBJECTIVES
        The examination is aimed at testing candidates’:
        1. Understanding of the components of positive attitudes and values towards individual and societal issues.
        2. Knowledge of the components of society and their roles.
        3. Understanding of democratic values and how they foster national unity.
        4. Understanding of the importance of the institution of marriage and family and their importance to society.
        5. Understanding of positive self‑concept for the development of the individual, good interpersonal relationships and social harmony.
        6. Awareness of socializing agencies and their roles in forging national unity.
        7. Understanding of the physical and social environment and the need to preserve them.
        8. Awareness of socio‑economic problems in Ghana and how to solve them for sustainable development.
        9. Ability to cope with the problems of social change.

        SCHEME OF EXAMINATION
        There will be two papers, Papers 1 and 2, both of which must be taken.

        Paper 1:
        - 50 multiple‑choice objective questions.
        - Duration: 50 minutes.
        - Marks: 50.

        Paper 2:
        - Three sections (A, B and C) covering the following areas of the syllabus:
            • Section A: The Environment
            • Section B: Governance, Politics and Stability
            • Section C: Social and Economic Development
        - Section A will contain four questions; Sections B and C will contain three questions each.
        - Candidates will answer five questions in all, choosing at least one but not more than two questions from each section.
        - Each question will carry 20 marks.
        - Duration: 3 hours.

        DETAILED SYLLABUS

        SECTION A: THE ENVIRONMENT

        1.0 Self‑Identity
        1.1 Explanation of “self” and capabilities.
        1.2 False identity and its implications.
        1.3 Maslow’s Hierarchy of Needs Theory – its usefulness to the individual.
        1.4 Developing one’s capabilities.
        1.5 Self‑confidence: meaning and development.

        2.0 Adolescent Reproductive Health
        2.1 Explanation of the terms adolescence, reproductive health and reproductive rights.
        2.2 Characteristics of adolescence.
        2.3 Irresponsible adolescent behaviour and its implications.
        2.4 Safety measures for adolescent health.
        2.5 Fostering good gender relations.
        2.6 Chastity: meaning and benefits.

        3.0 Our Culture and National Identity
        3.1 Explanation of the concept of national identity.
        3.2 Identification of socio‑cultural practices in Ghana.
        3.3 Explanation of how socio‑cultural practices in Ghana promote or inhibit development and why some should be changed.
        3.4 The need for institutionalizing some socio‑cultural practices.
        3.5 Evaluation of the importance of national and traditional symbols in fostering national allegiance.
        3.6 Identification of elements of Ghanaian culture that bind Ghanaians together.
        3.7 Identification of Ghanaian values and traits.

        4.0 The Institution of Marriage
        4.1 Explanation of courtship and marriage.
        4.2 Purposes of marriage.
        4.3 Preparation towards marriage.
        4.4 Types of marriage in Ghana – processes and residence.
        4.5 Challenges in marriage.
        4.6 Causes and effects of broken marriages.
        4.7 Ways of sustaining marriages.

        5.0 Individual Obligations in the Family
        5.1 Explanation of family and types.
        5.2 Functions and importance of the nuclear and extended families.
        5.3 Roles of individual members of the family.
        5.4 Systems of inheritance.
        5.5 Importance of the family as an institution.
        5.6 Implications of abandoning the family.

        6.0 Responsible Parenting
        6.1 Differences between parenthood and parenting.
        6.2 Responsibilities of parents.
        6.3 Consequences of irresponsible parenting.
        6.4 Preventing and managing parent–child conflict.
        6.5 Ways by which children may cause their parents to be irresponsible.

        7.0 Socialization and Our Social Environment
        7.1 Explanation of socialization; agencies of socialization; agents of socialization.
        7.2 Roles of agents and agencies of socialization.
        7.3 Deviant behaviour and its implications for society.
        7.4 Challenges agents and agencies face in the socialization process.
        7.5 Factors that promote better interpersonal relationships among groups and individuals in Ghana.

        8.0 Our Physical Environment and Environmental Challenges
        8.1 Explanation of the terms physical environment and environmental challenge.
        8.2 How human activities in Ghana are influenced by the physical environment.
        8.3 Impact of human activities on the physical environment (positive and negative) and their effects on humans.
        8.4 Measures for protecting the environment.

        9.0 Education and Social Change
        9.1 Explanation of education, its forms and purposes.
        9.2 Explanation of social change.
        9.3 Impact of education on national development.
        9.4 Role of education in social change in Ghana.

        SECTION B: GOVERNANCE, POLITICS AND STABILITY

        10.0 National Independence and Self‑Reliance
        10.1 Explanation of political independence and self‑reliance.
        10.2 Challenges and responsibilities of political independence.
        10.3 Reasons why some Ghanaians misconstrue the implications of independence.
        10.4 How to overcome the challenges of political independence in Ghana.
        10.5 Application of the concepts of independence and self‑reliance to one’s life.

        11.0 Peace Building and Conflict Resolution
        11.1 Explanation of the concepts peace building and conflict resolution.
        11.2 Sources of conflicts and their effects.
        11.3 Identification of the effects of conflict on society.
        11.4 Conditions necessary for peace building.
        11.5 Processes of peace building.

        12.0 Leadership and Followership
        12.1 Meaning of leadership and followership.
        12.2 Qualities of a leader and a follower.
        12.3 Types of leaders.
        12.4 Leadership styles.
        12.5 Roles of leaders and followers in nation building.

        13.0 Our Constitution, Democracy and Nation Building
        13.1 Explanation of constitution, democracy and nation building.
        13.2 Features of democracy.
        13.3 Importance of the constitution in nation building.
        13.4 Role of democracy in nation building.
        13.5 Reasons why unity and development are more sustainable under democratic government.
        13.6 How to prevent autocratic rule.

        14.0 Rights and Responsibilities of the Individual
        14.1 Explanation of rights and responsibilities.
        14.2 Types of rights and responsibilities.
        14.3 Possible conflicts in the exercise of rights and responsibilities.
        14.4 Reasons for curtailment of rights.
        14.5 Need for citizens to perform their responsibilities.

        15.0 Ghana and the International Community
        15.1 Explanation of globalization and international community.
        15.2 Forms of co‑operation Ghana has with other nations and bodies (e.g. UNO, AU, ECOWAS, Commonwealth).
        15.3 Origin and objectives of the New Partnership for Africa’s Development (NEPAD).
        15.4 Benefits of NEPAD to Ghana.

        SECTION C: SOCIAL AND ECONOMIC DEVELOPMENT

        16.0 The Youth and National Development
        16.1 Meaning of youth.
        16.2 Challenges facing the youth in their efforts to contribute to national development.
        16.3 Avenues for youth training in Ghana.
        16.4 Avenues by which the youth can obtain employment.
        16.5 Benefits of financial security to the individual and the nation.
        16.6 Planning sustainable financial security for the future.
        16.7 Explanation of the work of some financial institutions in the country.
        16.8 Role of the youth in national development.

        17.0 Science and Technology
        17.1 Differences between science and technology.
        17.2 Role of science and technology in national development.
        17.3 Measures for promoting science and technology in Ghana.
        17.4 How science and technology are used in development and production in the world.
        17.5 Ways by which Ghana could promote development through increased application of science and technology.

        18.0 Resource Development and Utilization in Ghana
        18.1 Explanation of resource, development and utilization.
        18.2 Types of resources and the relationship among them in production.
        18.3 Measures for developing human resources.
        18.4 How human resources and other resources contribute to national development.

        19.0 The Role of the Individual in Community Development
        19.1 Explanation of community and community development.
        19.2 Identification of levels of community.
        19.3 Factors involved in community decision‑making.
        19.4 Need for vision, initiative and drive in community development.
        19.5 Explanation of what community development involves.
        19.6 Ways by which individuals can contribute to community development.

        20.0 Promoting National Socio‑Economic Development
        20.1 Explanation of the concept of development.
        20.2 Identification of components of national socio‑economic life.
        20.3 Characteristics of a buoyant economy.
        20.4 Sources of development problems in Ghana.
        20.5 Measures Ghana has taken to improve development and the standard of living in the country.
        20.6 Ways of improving the standard of living in Ghana.

        21.0 Sustainable Development
        21.1 Explanation of sustainable development and maintenance culture.
        21.2 Factors hindering sustainability of growth and development in Ghana.
        21.3 Measures to sustain growth and development.
        21.4 Measures to improve the quality of human resources and prevent their loss.
        21.5 Ways of ensuring sustainability in a person’s life.
        21.6 How to avoid incurring unmanageable debts.
        21.7 How to avoid falling victim to financial fraudsters.

        22.0 Population Growth and Development
        22.1 Structure of Ghana’s population and its social and economic implications.
        22.2 Social and economic implications of Ghana’s population structure.
        22.3 Measures for breaking the vicious cycle of Ghana’s rapid population growth.
        22.4 Efforts made towards implementation of Ghana’s 1994 population policy.
        22.5 Why it is difficult for Ghana to achieve the 4th and 5th Millennium Development Goals.
        22.6 Reasons for a person’s preference for an ideal family size.

        23.0 The World of Work and Entrepreneurship
        23.1 Explanation of work, productivity and entrepreneurship.
        23.2 Types of enterprises in Ghana.
        23.3 Qualities of an entrepreneur.
        23.4 Basic requirements for starting and sustaining a business enterprise.
        23.5 Contribution of local enterprises to economic development.
        23.6 Negative attitudes to work and their effect on productivity.
        23.7 Positive work attitudes that employers expect of workers.`,
  },
  {
    subject: 'Store Keeping',
    timeAlloted: 50 * 60,
    description: `PREAMBLE
        The subject Store Keeping is designed to equip candidates with the basic knowledge required in store record keeping and other store activities.

        AIM AND OBJECTIVES
        The examination in this subject is meant to test candidates’:
        (i) understanding and appreciation of store keeping, basic concepts, store organisation and layout,
        (ii) understanding of the functions of store keeping and warehousing,
        (iii) ability to relate concepts to the management of inventory and inventory control.

        SCHEME OF EXAMINATION
        There will be two papers, Papers 1 and 2, both of which will constitute a composite paper to be taken at one sitting.

        Paper 1:
        - 40 multiple‑choice objective questions,
        - to be answered within 50 minutes,
        - for 40 marks.

        Paper 2:
        - Six essay questions,
        - candidates will be required to answer any four within 2 hours,
        - all questions carry equal marks,
        - total marks: 80.

        DETAILED SYLLABUS

        1. Overview of Store Keeping

        1.1 Introduction to Store Keeping
        (i) Meaning and importance of store keeping,
        (ii) Components of store keeping:
            - warehousing,
            - requisition,
            - inventory and inventory control,
        (iii) Areas where store keeping is required.

        1.2 Career Opportunities
        (i) Definition,
        (ii) Job opportunities,
        (iii) Factors influencing choice of career in store keeping,
        (iv) Skills required for store keeping:
            - numerical,
            - administrative,
            - computer.

        1.3 Roles, Attributes and Qualification of a Store Keeper
        (i) Functions of a store keeper:
            - receipt of goods,
            - store requisition,
            - store issues,
            - counting and grading,
            - weighing and classification,
            - record keeping,
            - maintaining clean store environment,
        (ii) Attributes of a store keeper:
            - honesty,
            - quality consciousness,
            - flair for details, etc.,
        (iii) Qualification of a store keeper.

        2. Equipment and Facilities
        2.1 Types of store equipment,
        2.2 Uses of storage equipment,
        2.3 Care of storage equipment and facilities.

        3. Warehousing
        3.1 Meaning of warehousing,
        3.2 Forms of warehousing (room, cold room, silos),
        3.3 Factors for setting up a warehouse,
        3.4 Store keeping documents,
        3.5 Government regulations on warehousing (e.g. NAFDAC, SON).

        4. Requisitioning
        4.1 Meaning,
        4.2 Importance,
        4.3 Procedure,
        4.4 Documentation (requisition and issue notes),
        4.5 E‑requisitioning (use of computer).

        5. Inventory and Inventory Control
        5.1 Inventory:
        (i) Meaning,
        (ii) Uses of inventory,
        (iii) Inventory items,
        (iv) Types of inventory control (perpetual and periodic),
        (v) Store keeping terms and their computation:
            - stock‑out,
            - lead time,
            - maximum stock level,
            - minimum stock level,
            - economic order quantity.

        6. Stock Valuation
        6.1 Meaning,
        6.2 Reasons for stock valuation,
        6.3 Determination of stock values using:
        - LIFO method,
        - FIFO method.

        7. Identification of Store Items
        7.1 Need for identification of items,
        7.2 Classification of materials in store,
        7.3 Codification systems:
        - alphabetic,
        - numerical,
        - alphanumeric,
        - decimal.

        8. Safety and Environmental Issues

        8.1 Store Organisation and Layout
        (i) Meaning,
        (ii) Importance,
        (iii) Store layout,
        (iv) Factors influencing store layout.

        8.2 Safety and Safety Standards
        (i) Meaning of safety in store,
        (ii) Importance of safety at work,
        (iii) Methods of providing safety at the workplace:
            - first aid box,
            - fire extinguisher,
            - shielding of electrical installations,
            - safety wear (types and uses: gloves, boots, helmets),
            - Health and Safety at Work Act.

        8.3 Environmental Issues
        (i) Meaning and importance of environment,
        (ii) Characteristics of a good environment.

        9. Entrepreneurship
        9.1 Meaning of entrepreneurship,
        9.2 Importance of entrepreneurship,
        9.3 Entrepreneurship skills:
        - managerial,
        - accounting,
        - marketing,
        - promotion,
        9.4 Idea generation,
        9.5 Feasibility study,
        9.6 Writing a proposal on a type of business in store keeping.`,
  },
  {
    subject: 'Store Management',
    timeAlloted: 60 * 60,
    description: `PREAMBLE
        The syllabus is designed to assess candidates’ basic knowledge of store management, customer satisfaction and the understanding of entrepreneurial skills in managing a small or big store.

        AIM AND OBJECTIVES
        The examination therefore intends to test candidates’:
        (i) understanding of the rudiments of running a store and entrepreneurial skills,
        (ii) understanding of the concepts of wholesaling and retailing, basic business documents and how to handle business finance,
        (iii) ability to relate concepts learnt to control stock, keep records and manage business risk.

        SCHEME OF THE EXAMINATION
        There will be two papers, Paper 1 and Paper 2, which will be combined in a composite paper and taken at one sitting.

        Paper 1:
        - 50 multiple‑choice questions,
        - duration: 1 hour,
        - marks: 50.

        Paper 2:
        - Seven essay questions,
        - candidates will answer any five in 2 hours,
        - marks: 100.

        DETAILED SYLLABUS

        1. Introduction to Store Management

        1.1 Store Management
        (i) Meaning of store management,
        (ii) Importance of store management,
        (iii) Functions of a store manager,
        (iv) Characteristics of a store manager,
        (v) Customer services:
            - pre‑sale services,
            - after‑sale services.

        1.2 Functions of Management
        (i) Planning,
        (ii) Organisation,
        (iii) Leading,
        (iv) Staffing,
        (v) Controlling.

        1.3 Factors to be Considered in Establishing a Store
        - Market,
        - Finance,
        - Types of stock,
        - Transport,
        - Accessibility,
        - Security,
        - Government regulation,
        - Personnel.

        2. Concepts of Retailing and Wholesaling

        2.1 Retailing
        (i) Definition,
        (ii) Functions of a retailer.

        2.2 Wholesaling
        (i) Definition,
        (ii) Functions of a wholesaler.

        2.3 Warehousing
        - Meaning of warehousing,
        - Types of warehousing,
        - Importance of warehousing.

        2.4 Transportation
        - Definition,
        - Means of transportation,
        - Advantages and disadvantages of each means of transportation,
        - Factors that determine the choice of transport.

        3. Stock Control

        3.1 Introduction to Stock Control
        (i) Meaning of stock control,
        (ii) Importance of stock control,
        (iii) Types of stock records: stock list, bin card / store card, store requisition form and delivery notes.

        3.2 Methods of Stock Control
        (i) Manual method:
            - periodic, perpetual and annual stock‑taking,
        (ii) Computerised method,
        (iii) Levels of stock control:
            - maximum level,
            - minimum level,
            - reorder level,
        (iv) Issuing of stock:
            - FIFO and LIFO.

        3.3 Pricing
        (i) Definition of pricing,
        (ii) Types of pricing:
            - skimming,
            - mark‑up,
            - mark‑down,
            - penetration,
            - below or above mark pricing.

        4. Organisational Policy

        4.1 Organisational Policy
        (i) Definition,
        (ii) Employment policy,
        (iii) Decisions on:
            - customer services,
            - public relations.

        4.2 Organisational Standards
        (i) Definition of standards,
        (ii) Productivity standards,
        (iii) Standards for customer satisfaction.

        4.3 Organisational Culture
        (i) Definition of culture,
        (ii) Explanation of organisational culture:
            - attitude to customers,
            - relationship with co‑workers,
            - relationship with the boss,
            - dress code.

        5. Business Communication

        5.1 Overview of Communication
        (i) Definition of communication,
        (ii) Types of communication:
            - oral, written and non‑verbal,
        (iii) Business documents:
            - letters, memos, reports and circulars.

        5.2 Electronic Communication
        (i) Definition of electronic communication,
        (ii) Means of communication:
            - fax, telephone, email, radio messages,
        (iii) Word‑processing: using computers to write business letters.

        6. Business Law

        6.1 Overview of Basic Business Law
        - Definition.

        6.2 Law of Contract
        - Elements of contract,
        - Rights and obligations of employer and employee.

        6.3 Agency
        (i) Definition,
        (ii) Types of agents,
        (iii) Creation of agency,
        - Rights and obligations of principals and agents.

        6.4 Sale of Goods Act
        (i) Contract of sale,
        (ii) Formalities of sale.

        6.5 Government Regulation of Business
        - Meaning and uses of:
            (i) Patent rights,
            (ii) Copyright,
            (iii) Trade mark,
            (iv) Health and Safety at Work Act,
            (v) Consumer protection agency,
            (vi) Licence.

        6.6 Government Regulatory Agencies
        - NAFDAC,
        - NDLEA,
        - SON.

        7. Record Keeping

        7.1 Documentation
        (i) Definition of filing and indexing,
        (ii) Rules of filing,
        (iii) Filing and indexing methods.

        7.2 Source Documents
        (i) Meaning,
        (ii) Types:
            - invoices,
            - receipts,
            - cheques,
            - vouchers.

        7.3 Trade Documents
        (i) Documents used in foreign trade:
            - bills of lading,
            - certificate of origin,
            - consular invoice,
            - insurance certificate,
            - inspection certificate,
            - bill of exchange,
            - indent,
            - letter of credit,
            - proforma invoice,
        (ii) Documents used in home trade:
            - order,
            - enquiry,
            - waybill,
            - consignment note,
            - manifest,
            - invoice,
            - proforma invoice,
            - receipts,
            - credit and debit notes,
            - vouchers.

        8. Book Keeping

        8.1 Subsidiary Books
        - Meaning, types and uses:
            • purchases and sales day books,
            • returns outwards and inwards books,
            • cash books.

        8.2 Imprest System
        (i) Definition and description of the imprest system,
        (ii) Preparation of petty cash book.

        8.3 Final Accounts
        (i) Definition and purpose,
        (ii) Simple trading, profit and loss account and balance sheet.

        8.4 Sales / Turnover
        (i) Meaning,
        (ii) Calculation of rate of sales turnover.

        9. Risk Management
        9.1 Risk management:
        (i) Definition,
        (ii) Methods of risk management:
            - sound management practices,
            - reduction of risks (optimum stock level),
            - hedging,
            - risk transfer through insurance.

        10. Handling Finances of the Store

        10.1 Investments
        - Meaning,
        (i) Capital investment:
            - meaning,
            - investment in the capital market,
            - functions of the Stock Exchange,
            - advantages of shareholding,
        (ii) Money market instruments:
            - meaning,
            - treasury bills,
            - commercial papers,
            - bonds.

        10.2 Taxation
        (i) Meaning of tax,
        (ii) Importance of tax,
        (iii) Types of tax:
            - income tax,
            - import duties,
            - excise duties,
            - VAT.`,
  },
  {
    subject: 'Textiles',
    timeAlloted: 50 * 60,
    description: `PREAMBLE
        Textile is a component of the Visual Art programme at the Senior High School. It covers the rationale for studying the subject, tools / equipment and materials, processes from fibre preparation to fabric construction, decoration, finishing and care. The programme aims at developing patriotism, cultural awareness and skills acquisition for future employment.

        The aim of the syllabus is to help and guide examiners to identify suitable examinable domains in the textile field. Candidates’ acquisition of sound knowledge to achieve desired goals is of primary importance to the individual and the community at large.

        AIMS AND OBJECTIVES
        The examination syllabus intends to give candidates diversified channels through which they will involve themselves in experiments, observation, analysis and evaluation of findings.

        Candidates are therefore to respond to questions that examine:
        (i) the rationale for studying textiles,
        (ii) the development of textiles and careers in textiles,
        (iii) the development of positive attitudes and skills in producing indigenous and contemporary textiles,
        (iv) effective manipulative skills, using tools, materials and computer technology, and the safety measures to be taken,
        (v) the level of creativity as envisaged in design work for fashion, printing, weaving and dyeing of fabrics,
        (vi) skills for sustainable development and competencies in exploring the indigenous textile industry,
        (vii) elements of entrepreneurial skills such as costing, pricing and marketing of textile products,
        (viii) appreciation and patronage of indigenous and contemporary textile products,
        (ix) competencies in building portfolios and skills in mounting exhibitions in textiles.

        SCHEME OF EXAMINATION
        There will be three papers, Papers 1, 2 and 3, all of which must be taken. Papers 1 and 2 will be a composite paper to be taken at one sitting.

        Paper 1:
        - 40 multiple‑choice objective questions,
        - to be answered within 50 minutes,
        - for 40 marks.

        Paper 2:
        - Six essay‑type questions,
        - candidates will be required to answer four within 2 hours,
        - for 60 marks.

        Paper 3:
        - Two practical projects, out of which candidates will execute one within five days, working for six hours each day,
        - paper carries 80 marks.

        The questions will be sent to candidates two weeks before the execution period for study. Designing of sketches and preparatory notes should also be done within the two weeks prior to the execution of the project. These will carry 20 marks, bringing the total mark for the paper to 100.

        DETAILED SYLLABUS
        In this syllabus, Textiles covers general knowledge, identification and use of fibres, textile designing, production, sustainable developments, portfolio building and exhibition, and entrepreneurial skills.

        Candidates will be expected to respond comprehensively to questions drawn from all areas of textiles in objective, essay and practical forms. The scope of the syllabus covers the following areas:

        1. Introduction to Textiles
        (i) Meaning / rationale for studying textiles,
        (ii) Renowned Ghanaian textile artists,
        (iii) Development of textiles and careers,
        (iv) Textiles and cultural values,
        (v) Competencies, attitudes and behaviour expected of students for employment and in dealing with customers.

        2. Textile Fibres and Yarns
        (i) Identification and classification of fibres,
        (ii) Properties and suitability of fibres,
        (iii) Fibre processing (e.g. soaking, ginning, etc.),
        (iv) Yarn preparation – traditional and contemporary,
        (v) Types of yarn,
        (vi) Blends and mixtures,
        (vii) Yarn terminologies.

        3. Fabric Construction Processes
        (i) Point‑paper designing: plain, twill, satin / sateen and variations,
        (ii) Looms and accessories: types of looms (man‑power, traditional, broad loom and power looms e.g. shuttle and shuttle‑less looms) and accessories,
        (iii) Textile calculations: weight / cost of warp, weft and fabric,
        (iv) Weaving processes (traditional and broad loom): warping, beaming, heddling, reeding, tying‑up, bobbin winding, etc.; loom motions (primary, secondary and auxiliary motion),
        (v) Other fabric construction processes: knitting, crocheting and tapestry,
        (vi) Non‑woven fabrics.

        4. Fabric Decoration and Finishing Techniques
        (i) Preparation, care and maintenance of tools, materials and equipment,
        (ii) Basic drawing using elements and principles of design,
        (iii) Idea development / preliminary designing / ICT,
        (iv) Use of indigenous colour schemes, ideas, symbols and cultural motifs for designing,
        (v) Decorative techniques: dyeing, printing, appliqué and embroidery processes and equipment,
        (vi) Types of finishing techniques: mechanical, chemical,
        (vii) Care and maintenance of fabrics.

        5. Sustainable Development
        (i) Challenges and interventions in managing resources, marketing and distribution,
        (ii) Cultural values (traditional and contemporary),
        (iii) Healthy studio practices.

        6. Portfolio and Exhibition
        (i) Types of portfolios: hard‑copy and soft‑copy portfolios,
        (ii) Collection and building of portfolio,
        (iii) Meaning, types, purposes and processes of mounting exhibitions,
        (iv) Appreciating and patronising indigenous textiles.

        7. Entrepreneurial Skills in Practice
        (i) Preparation of a business plan,
        (ii) Planning of an enterprise,
        (iii) Key points of a business plan,
        (iv) Costing, pricing and marketing of textile products.`,
  },
  {
    subject: 'Tourism',
    timeAlloted: 40 * 60,
    description: `PREAMBLE
        The syllabus is designed to evaluate candidates’ knowledge of, and skills in, the potentials available in the tourism industry. It assumes that not less than three teaching periods or about 2 hours per week will be allocated to the subject during the senior secondary school course.

        AIM AND OBJECTIVES
        The examination will test candidates’ ability to:
        (i) explain concepts associated with tourism activities such as tourism, tourist, tourist attraction, tour operation, excursion, travel agency, etc.,
        (ii) acquire knowledge of tourist attraction centres and their locations,
        (iii) understand the financial, social and developmental benefits of tourism to a nation,
        (iv) explain tourism and cultural festivals of a nation,
        (v) acquire basic knowledge of the operations of travel agency and tour businesses,
        (vi) understand the roles and functions of public and private agencies in tourism,
        (vii) develop skills in tourism entrepreneurship.

        FIELD WORK
        Students should be exposed to field work through excursions to tourism industry sites such as airports, seaports, beaches, landforms, game reserves, national parks, museums, hotels, tour agencies, etc. They should be examined on this as part of their continuous assessment in schools. The scores for field work should account for 40 per cent of the total continuous assessment scores forwarded to the examining body.

        SCHEME OF EXAMINATION
        The subject will be examined as a composite paper made up of two papers (Papers 1 and 2) to be answered in 2 hours 30 minutes.

        Paper 1:
        - 40 multiple‑choice questions covering the entire syllabus,
        - to be answered in 40 minutes,
        - for 40 marks.

        Paper 2:
        - Two sections (A and B).
        - Section A: ten restricted‑response essay questions, all of which must be answered for 40 marks (candidates are advised not to spend more than 50 minutes here).
        - Section B: five essay‑type questions, of which candidates will answer any three for 60 marks in one hour.

        DETAILED SYLLABUS

        1. Concepts and Tourism Resources

        1.1 Concepts
        - Definition of tourism, tourist, visitor, tour, tour operation, excursion, recreation, leisure, hospitality, travel, etc.
        - Scope and history of tourism.

        1.2 Types and Forms of Tourism
        - Types: domestic and international tourism.
        - Forms: incoming / inbound, outgoing / outbound, long‑haul, short‑haul, business, eco / environmental, cultural, religious / pilgrimage, sports, youth, etc.

        1.3 Components of Tourism
        - Attraction,
        - Accessibility,
        - Amenities.

        1.4 Tourist Product
        - Definition of tourist product,
        - Characteristics of tourist products: heterogeneous, intangible, often immovable / location‑bound.

        1.5 Tourist Attractions in a Nation
        - Natural attractions: national parks, scenic features, beaches, coasts, landforms, etc.
        - Man‑made attractions: monuments, museums, palaces, etc.
        - Socio‑cultural attractions: historical monuments, festivals, carnivals, sports, etc.

        1.6 Tourism Statistics
        - Meaning of tourism statistics,
        - Inflow (arrivals) and outflow (departures),
        - Expenditure patterns,
        - Data collection,
        - Importance of tourism statistics.

        1.7 Benefits of Tourism
        - Economic (financial) benefits:
            • taxes,
            • levies,
            • balance of payments,
            • foreign‑exchange earnings, etc.
        - Social benefits:
            • cultural understanding,
            • job creation,
            • international co‑operation.
        - Developmental benefits:
            (a) Infrastructure: roads, electricity, water, communication;
            (b) Superstructure: buildings, flyovers, hotels, resorts;
            (c) Rural development: preservation of nature, land use and landscape enhancement.

        1.8 Festivals and Heritage in a Nation
        - Examples of festivals (contextual, e.g. Argungu International Fishing Festival, Osogbo Festival, New Yam Festival, etc.).
        - Heritage resources: world heritage sites, museums and monuments, art galleries, palaces, shrines, etc.

        2. Business and Entrepreneurial Skills in Tourism

        2.1 Reasons for Travel
        - Physiological factors: visit to friends and relations, holidays, health,
        - Economic factors: business / shopping, conferences / meetings, trade fairs / exhibitions,
        - Social factors: religion, sports, beautiful natural scenery,
        - Other factors: fashion fairs, education, self‑esteem and status.

        2.2 Travel Agency Business
        - Meaning of travel agency / travel agent,
        - Roles / functions of a travel agent: reservation, ticketing, visa procurement, foreign‑exchange procurement, travel advice, etc.,
        - Qualities of a travel agent: resourcefulness, marketing skills, honesty / integrity, good communication skills, timeliness, vision, managerial ability, good ICT skills, creativity, etc.

        2.3 Tour Operating Business
        - Meaning of tour operator,
        - Roles of a tour operator: packaging, marketing and executing tours,
        - Qualities of a tour operator: honesty, good public relations, good marketing skills, sound financial standing, good environmental knowledge, innovativeness, good ICT skills, etc.

        2.4 Support Services
        - Financial services: insurance, banking, bureaux de change,
        - Transport facilities and car‑hire services,
        - Souvenir shops (local crafts, artworks, etc.),
        - Accommodation, hotel and catering services,
        - Health services (first aid, clinics, hospitals, etc.),
        - Security services.

        3. Tourism Institutions and Marketing

        3.1 Public Agencies in Tourism
        - Identification and roles of public agencies (e.g. ministries of tourism / culture, tourism boards, local tourism committees, and national parastatals responsible for policy implementation, manpower development and heritage preservation).

        3.2 Private Agencies in Tourism
        - Identification and roles of private tourism associations (e.g. national tourism umbrella bodies, travel agencies’ associations, journalists / writers’ associations on tourism, tour operators’ associations, hotel associations, hospitality and tourism professional bodies).

        3.3 Tourism Marketing
        - Meaning of tourism marketing,
        - Tourism marketing strategies: segmentation, mass marketing, niche marketing (new / emerging markets),
        - Marketing tools in tourism:
            (a) Electronic media – television, radio;
            (b) Print media – newspapers, magazines, journals, publications;
            (c) Fairs and exhibitions – local and international;
            (d) E‑tourism – internet services, e‑banking, e‑reservation, electronic billboards, social media.

        4. Entrepreneurship, Service Delivery and Skill Acquisition in Tourism

        4.1 Entrepreneurship in Tourism
        - Meaning of entrepreneurship,
        - Meaning of cottage industries,
        - Examples of cottage‑industry activities relevant to tourism: leather works, ceramics and pottery, bread making, tie‑and‑dye, hair weaving, painting, cloth weaving, raffia weaving, etc.,
        - Typical products: shoes, bags, belts, pots, tiles, beads, ornaments, adire / kampala, woven cloths, caneware, etc.

        4.2 Service Delivery in Tourism
        - Meaning of service delivery,
        - Key service roles: tour guides, ticket retailers, cooks, waiters, photographers, cultural dancers, drivers, game guards, reservation officers, etc.

        4.3 Skill Acquisition
        - Definition of skills and skill acquisition,
        - Examples of skill‑acquisition centres and programmes in tourism / hospitality and culture (e.g. national and regional entrepreneurship and tourism‑training agencies, hospitality institutes, cultural‑orientation institutes, craft centres).

        SUGGESTED READING
        Examples include standard texts and references on travel and tourism geography, principles and practice, and introductory transport and hospitality studies from recognised authors and publishers.`,
  },
  {
    subject: 'Typewriting',
    timeAlloted: 165 * 60,
    description: `PREAMBLE / AIM
        The aim of the examination is to test candidates’ ability to produce mailable documents at 40 words per minute and to apply such skills in solving practical office problems.

        SCHEME OF EXAMINATION
        - Total time allowed: 2 hours 45 minutes, broken down as follows:
        • 5 minutes for warming up on the material supplied by the Council (preliminary passage; not part of the exam time),
        • 5 minutes for a speed and accuracy test,
        • 10 minutes for reading through the examination paper,
        • 2 hours 30 minutes for production tasks.

        - The speed and accuracy test:
        • Set at 40 words per minute,
        • Carries 10% of the total marks,
        • Syllabic intensity of the passage: between 1.35 and 1.40.

        - The production test:
        • Consists of a maximum of five tasks,
        • Starts after all scripts for the speed and accuracy test have been collected and initialled by the invigilator,
        • Total standard words for the whole examination (tasks plus accuracy test) will range between 1350 and 1450,
        • Total number of instructions attracting penalties will range between 40 and 50.

        DETAILED SYLLABUS
        Tasks may be presented in a mixture of manuscript, typescript and printed drafts with interpolations and manuscript correction signs.

        (a) Correspondence
        - Use of pre‑printed A4 and A5 headed paper.
        - Letters and memoranda with:
        • insets,
        • continuation sheets,
        • subject headings,
        • enclosures,
        • attention lines,
        • special markings such as “private”, “personal”, “confidential”,
        • indications for distribution of carbon copies.
        - Form letters and circular letters (with or without tear‑off portions).
        - Envelope addressing, including envelopes with special marks.

        (b) Manuscript
        - Typing manuscripts with or without inset matter.

        (c) Display Work
        (i) Headings
            - Main, sub‑, shoulder, paragraph and marginal / side headings.

        (ii) Paragraphing
            - Blocked, indented and hanging paragraphs.

        (iii) Paragraph Numbering
            - Arabic, Roman, decimal and alphabetical numbering.

        (iv) Display Work Items
            - Menus, advertisements and itineraries.

        (v) Meetings and Reports
            - Notice of meetings (with or without agenda),
            - Chairman’s agenda,
            - Minutes and reports.

        (vi) Business Forms and Cards
            - Completion of pre‑printed forms such as:
                • application and personal records forms,
                • invoices,
                • order forms,
                • delivery notes,
                • advice notes,
                • invitation cards and postcards.

        (vii) Tabulation
            - Tables with ruling, with or without leader dots,
            - Chronological and alphabetical arrangements.

        (viii) Literary Work
            - Short plays, poems and articles,
            - Footnotes.

        (ix) Financial Statement
            - Simple statements of account.

        SUGGESTED READING
        Typical texts include senior secondary school and West African–focused typewriting manuals and realistic office‑assignment books from established authors and publishers in business education and commercial typewriting.`,
  },
  {
    subject: 'Typewriting (2)',
    timeAlloted: 165 * 60,
    description: `PREAMBLE
        The aim of the examination is to test candidates’ ability to produce mailable documents at 40 words per minute and to apply such skills in solving practical office problems.

        SCHEME OF EXAMINATION
        (a) Total time allowed for the examination: 2 hours 45 minutes.
        (b) 5 minutes for warming up on the material supplied by the Council in the examination paper. The time taken for typing the preliminary passage will not be included in the time for the examination.
        (c) 5 minutes for a speed and accuracy test.
        (d) 10 minutes for reading through the examination paper.
        (e) 2 hours 30 minutes for production tasks.
        (f) The speed and accuracy test will be set at 40 words per minute and will carry 10 percent of the total marks. The syllabic intensity of the passage will range between 1.35 and 1.40.
        (g) The production test will consist of a maximum of five tasks and will start after all the scripts for the speed and accuracy test have been collected and duly initialled by the invigilator.
        (h) The total standard words for the examination (both tasks and accuracy test) will range between 1350 and 1450.
        (i) The total number of instructions attracting penalties will range between 40 and 50.

        SCOPE OF THE SYLLABUS
        Tasks may be presented in a mixture of manuscript, typescript and printed drafts with interpolations and manuscript correction signs.

        (a) Correspondence
        - Use of pre‑printed A4 and A5 headed paper.
        - Letters and memoranda with insets, continuation sheets, subject headings, enclosures, attention lines, including private, personal, confidential and indications of distribution of carbon copies.
        - Form letters and circular letters with or without tear‑off portions.
        - Envelope addressing, including envelopes with special marks.

        (b) Manuscript
        - Manuscripts with or without inset matter.

        (c) Display Work
            (i) Headings
            - Main, sub‑heading, shoulder, paragraph and marginal / side headings.

            (ii) Paragraphing
            - Blocked, indented and hanging paragraphs.

            (iii) Paragraph Numbering
            - Arabic, Roman, decimal and alphabetical.

            (iv) Display Work Items
            - Menus, advertisements and itineraries.

            (v) Meetings and Reports
            - Notice of meetings (with or without agenda),
            - Chairman’s agenda,
            - Minutes and reports.

            (vi) Business Forms, Cards, etc.
            - Completion of pre‑printed forms:
            • application and personal records forms,
            • invoices,
            • order forms,
            • delivery notes,
            • advice notes,
            • invitation cards and postcards.

            (vii) Tabulation
            - Tabulation with ruling, with or without leader dots, in chronological and alphabetical arrangements.

            (viii) Literary Work
            - Short plays, poems and articles,
            - Footnotes.

            (ix) Financial Statement
            - Simple statements of account.

        NOTES ON THE EXAMINATION
        (a) Facilities and scheduling: institutions running this course should provide sufficient typewriters and at least five periods of 40 minutes each per week. Qualified typewriting teachers should handle the subject.
        (b) Where the number of candidates exceeds the available typewriters, permission may be granted (on application to the Council) to hold the examination in two or three sessions on the same day.
        (c) Centres are responsible for effective segregation of candidates so that those who have completed an earlier session do not have contact with those in a later session for the duration of the examination.
        (d) Applications for multiple sessions, indicating the number of papers required and the suggested times, must be submitted when entry forms are returned to the appropriate national office.
        (e) The typewriting teacher should not act as Supervisor / Invigilator in his or her own school, but may be available to assist if a machine defect unexpectedly hampers a candidate.
        (f) An alternative paper (Alternative B) will be available to schools that cannot accommodate all candidates on the scheduled day, provided the Council receives at least six months’ notice.
        (g) The use of electric typewriters may be permitted; however, the Council will not accept responsibility for electrical / electronic problems. Supervisors and Invigilators must ensure memories are cleared and that candidates submit typed scripts of the speed and accuracy test.
        (h) Any satisfactory method of erasure is allowed, except in the speed and accuracy test where no corrections are permitted.
        (i) Dictionaries may be used in the examination.

        SUGGESTED STATIONERY FOR THE EXAMINATION
        - A4 plain: 9 sheets
        - A4 headed: 2 sheets
        - A4 memorandum: 2 sheets
        - A4 yellow flimsy: 4 sheets
        - A5 plain: 4 sheets
        - A5 headed: 2 sheets
        - A5 memorandum: 2 sheets
        - C6 envelopes: 2
        - Cards (approx. 6" x 4" / 152 mm x 100 mm)
        - Speed test sheet: 1 light blue / green paper
        - Carbon papers as required

        Candidates are instructed that no headed stationery other than that provided by the Council may be used.

        SUGGESTED READING
        Standard senior secondary and West African–oriented typewriting texts and office‑practice assignment books from recognised authors and publishers support the syllabus and provide graded drills, speed‑building passages and realistic business documents.`,
  },
  {
    subject: 'Upholstery',
    timeAlloted: 60 * 60,
    description: `SCHEME OF THE EXAMINATION
        There will be three papers, Papers 1, 2 and 3, all of which must be taken. Papers 1 and 2 shall be a composite paper to be taken at one sitting.

        Paper 1:
        - 40 multiple‑choice objective questions,
        - to be answered in 1 hour,
        - for 40 marks.

        Paper 2:
        - Four short‑structured questions,
        - candidates will be required to answer any three,
        - in 1 hour for 60 marks.

        Paper 3:
        - Practical test of 2 hours’ duration,
        - consists of one question,
        - for 100 marks.

        A list of materials for the test will be made available to schools not less than two weeks before the paper is taken, for material procurement and relevant preparations.

        ALTERNATIVE TO PRACTICAL TEST
        The Council may test candidates’ ability in practical work as prescribed in the syllabus in the event that materials for the actual practical test cannot be acquired. For this alternative test there will be one question to be answered in 2 hours for 100 marks.

        DETAILED SYLLABUS

        1. Introduction to Upholstery
        (a) Description of upholstery and upholstery articles,
        (b) Characteristics of upholstery workshops and articles,
        (c) Origin and styles of upholstery.

        2. Upholstery Workshop Safety
        (a) Safety: rules, regulations and precautions regarding clothing, habits, tools and materials; safety signs and posters,
        (b) Machine‑shop safety,
        (c) Safe use of machines, tools and devices,
        (d) Safe storage of tools, materials and equipment,
        (e) Accident: meaning, causes and management techniques,
        (f) Fire: meaning, types, sources, equipment and methods of handling,
        (g) First aid: meaning, equipment and administration.

        3. Upholstery Tools and Machines
        (a) Hand tools: types (e.g. hammers, needles, pairs of scissors / shears, etc.), identification, uses and maintenance,
        (b) Power tools: definition; types (staplers, buttoning machine, electric cutter, glue gun, etc.),
        (c) Machines: types (e.g. sewing machines, foam cutters, industrial machines); identification, parts, description, uses and specific maintenance.

        4. Upholstery Materials
        (a) Description,
        (b) Classification (e.g. platform, padding / stuffing, covering, fasteners and fittings, etc.),
        (c) Uses.

        5. Upholstery Design and Drawing
        (a) Drawing principles,
        (b) Drawing instruments: types and uses,
        (c) Lines: types and uses,
        (d) Construction of plane figures and solids (e.g. triangles, ellipses, pyramids, shaped blocks),
        (e) Methods of presenting drawings (e.g. freehand sketching, orthographic projections, pictorial drawing, etc.),
        (f) Design fundamentals (e.g. appearance, functionality, etc.),
        (g) Design stages: problem identification, alternative solutions, selection of best alternative, production of working drawings, cutting list, estimates, workshop rod, template, etc.

        6. Upholstery Construction and Repair
        (a) Types of upholstery construction: half upholstery, full upholstery,
        (b) Parts of upholstery works – frame, padding, covering (identification and selection of materials),
        (c) Upholstery construction: principles, processes, joints, standard sizes, parts relationships, setting out, cutting out, sewing, fixing, finishing, decorating,
        (d) Repair of faults in upholstery work:
            - types (e.g. stains, tears, breakages, etc.),
            - sources,
            - identification,
            - estimation,
            - repair.

        7. Maintenance and Repair of Upholstery Tools and Machines
        (a) Types of maintenance (routine, preventive, corrective, diagnostic, etc.),
        (b) Maintenance activities (cleaning, oiling, greasing, changing of parts, etc.),
        (c) Importance of maintenance and repairs,
        (d) Tools, machines, equipment and materials,
        (e) Housekeeping,
        (f) Maintenance records.

        8. Upholstery Workshop Management
        (a) Workshop environment: building, utilities, storage, facilities, etc.,
        (b) Workshop layout: types, facilities, conveniences, etc.,
        (c) Workshop personnel and their functions,
        (d) Control of tools, equipment and materials: procurement, storage, issuing,
        (e) Workshop records (e.g. job cards, tool cards, stock cards, etc.).

        9. Introduction to Entrepreneurship
        (a) Entrepreneurship: meaning and purposes,
        (b) Characteristics of entrepreneurs,
        (c) Business organisation: types and characteristics; forming a business organisation,
        (d) Sources of capital,
        (e) Careers and job opportunities in upholstery and related areas,
        (f) Feasibility studies: meaning and content.

        KEY TOOLS, EQUIPMENT AND MACHINES (SUMMARY)
        - Hand tools: hammers, pincers, scissors, needles, staplers, ripping tools, webbing stretchers, etc.
        - Equipment: full drawing sets (drawing boards, T‑squares, set squares, compasses, templates, sharpeners, etc.).
        - Power tools: jigsaws, electric staplers, glue guns, electric cutters, power screwdrivers, pressing irons, drills, routers.
        - Machines: manual and electric sewing machines, drilling machines, band saws, circular saws, ironing machines, buttoning machines, etc.

        TYPICAL MATERIALS (EXAMPLES)
        - Foam (e.g. 450 x 380 mm latex foam),
        - Soft synthetic leather (e.g. 750 x 560 mm),
        - Cord, sewing thread, calico, upholstery staples,
        - Plywood (e.g. 450 x 380 mm, 18 mm thick).

        SUGGESTED READING
        Standard upholstery and woodwork texts covering principles of upholstery construction, workshop management, materials technology and introductory management / entrepreneurship provide useful support and project ideas for the course.`,
  },
  {
    subject: 'Welding and Fabrication Engineering Craft Practice',
    timeAlloted: 60 * 60,
    description: `WELDING AND FABRICATION ENGINEERING CRAFT PRACTICE

        SCHEME OF EXAMINATION
        There will be three papers, Papers 1, 2 and 3, all of which must be taken. Papers 1 and 2 will be a composite paper to be taken at one sitting.

        Paper 1: Objective Test
        - 40 multiple-choice questions covering the entire syllabus.
        - Duration: 1 hour.
        - Marks: 40.

        Paper 2: Essay / Theory
        - 5 structured/essay questions set from the syllabus.
        - Candidates are required to answer any 4.
        - Duration: 1½ hours.
        - Marks: 60.

        Paper 3: Practical Test
        - 1 compulsory practical question on fabrication and/or welding operations.
        - Duration: 3 hours 10 minutes.
        - Marks: 100.
        - A list of required materials is sent to schools at least two weeks earlier for procurement and preparation.

        ALTERNATIVE TO PRACTICAL TEST
        Where it is not possible to conduct the actual practical examination, an Alternative to Practical paper may be set:
        - 1 written question designed to test candidates’ understanding of practical work as prescribed in the syllabus.
        - Duration: 3 hours.
        - Marks: 100.

        DETAILED SYLLABUS OUTLINE

        1. Workshop and Standard Workshop Practices
        1.1 Introduction to fabrication and welding practice.
        1.2 Safety precautions in the welding and fabrication workshop.
            - Types and causes of accidents in the workshop (fire, explosion, sharp objects, hazardous gases, etc.).
            - Accident prevention measures.
            - Types and causes of environmental pollution in and around the workshop.
            - Methods of preventing environmental pollution.
            - Safety facilities and protective wears (goggles, gloves, aprons, helmets, boots, ear protection, etc.).
        1.3 Workshop layout (fabrication and welding) – arrangement of machines, tools, storage and work areas for safe and efficient work.
        1.4 Standard welding codes and symbols used on drawings and in fabrication.
        1.5 First-aid administration in the workshop and its importance.

        Practical Emphasis:
        - Demonstration of the correct use of protective wears during welding and fabrication activities.
        - Demonstration of the use of first-aid facilities for minor workshop injuries.

        2. Properties and Selection of Metals
        2.1 Ferrous and non-ferrous metals:
            - Steel, aluminium, cast iron, copper, zinc, tin, alloy steels, etc.
        2.2 Properties of metals:
            - Ductility, hardness, toughness, malleability, fusion, tenacity, brittleness, elasticity and plasticity.
        2.3 Sheet metals (aluminium, mild steel, brass):
            - Concept of sheet metal and gauge sizes.
        2.4 Selection of suitable metals for specific fabrication and welding jobs based on properties, service conditions and cost.
        2.5 Heat treatment of metals:
            - Hardening, annealing, normalizing, tempering, case hardening, etc. and their purposes.

        Practical Emphasis:
        - Identification of ferrous and non-ferrous metals.
        - Simple demonstrations of heat treatment processes such as annealing, hardening and normalizing of metals.

        3. Tools and Equipment in Fabrication and Welding
        3.1 Identification of tools and equipment for fabrication and welding (hand tools, machine tools and accessories).
        3.2 Equipment set-up for gas and arc welding and basic fabrication operations.
        3.3 Job-holding devices for fabrication and welding (vices, clamps, jigs, fixtures, etc.).
        3.4 Measuring instruments, marking out and cutting tools.
        3.5 Identification of parts and accessories for gas and arc welding.
        3.6 Maintenance procedures for arc and gas (oxy-acetylene) welding equipment.
        3.7 Preparation of acetylene gas from calcium carbide.
        3.8 Types of electrodes, their compositions, applications, gauges and selection of appropriate electrodes for specific jobs.
        3.9 Equipment for fault detection and troubleshooting in fabrication and welding operations.

        Practical Emphasis:
        - Setting up oxy-acetylene equipment safely and correctly.
        - Demonstration of the use of measuring, marking out and cutting tools.
        - Preparation of acetylene gas from carbide under supervision.

        4. Operations and Techniques in Welding and Fabrication
        4.1 Types of welding (gas and arc welding) and explanation of their operating principles and differences.
        4.2 Description of a typical fabrication process from drawing to finished product.
        4.3 Types of joints, joint methods and their applications in welding and fabrication (butt, lap, tee, corner, edge joints, etc.).
        4.4 Classification and application of marking out techniques in welding and fabrication.
        4.5 Use of templates in fabricated and welded assemblies.
        4.6 Welding techniques and their applications (e.g. leftward and rightward methods, weaving, multi-pass welding).
        4.7 Techniques in fabrication work, including folding and its importance in producing stiff, neat and safe edges.

        Practical Emphasis:
        - Demonstration of various cutting techniques (hand and machine).
        - Students practise welding using both leftward and rightward methods.
        - Students undertake simple wire-edge and folding projects to develop fabrication skills.

        5. Fasteners
        5.1 Classification of fasteners into permanent and temporary types.
        5.2 Permanent fasteners:
            - Rivets: types, classes and applications.
        5.3 Temporary fasteners:
            - Bolts, nuts and screws: types, descriptions and uses.
        5.4 Uses of rivets, bolts, nuts and screws in fabrication work.

        Practical Emphasis:
        - Production of simple riveted joints.
        - Production of joints using bolts and nuts.

        6. Forging Process
        6.1 Introduction to forging and definition of forging.
        6.2 Forging tools and equipment: furnace, swages, fullers, flatters, tongs, anvils, hammers, etc.
        6.3 Basic forging operations:
            - Upsetting, drawing down, twisting, bending and forging an eye.

        Practical Emphasis:
        - Students to forge simple components such as an eye using appropriate tools and techniques.

        7. Preparation of Welding Surfaces and Environment
        7.1 Preparation of welding surfaces by cleaning with wire brushes, emery cloth, files, scrapers and grinding machines.
        7.2 Preparation of edges for welding (e.g. single V, double V, fillet preparations).
        7.3 Post-surface preparation:
            - Cleaning surfaces after welding with wire brush.
            - Oiling or other protective finishes to prevent corrosion/rusting.
        7.4 Defects in welding surfaces: causes and remedies.
        7.5 Welding environment:
            - Hazards such as awkward positions, poor ventilation, flammable materials, slippery (oily/greasy) floors, etc.
        7.6 Surface finishing for fabrication and welding:
            - Painting, metal spraying, galvanizing, oiling and other protective finishes.

        Practical Emphasis:
        - Preparation of single V-edges and other joint preparations for welding.

        8. Practical Work / Projects
        8.1 Marking out basic shapes (triangles, squares, rectangles) on sheet and plate metals.
        8.2 Cutting and bending of triangles, squares and rectangles.
        8.3 Soldering of sheet metals.
        8.4 Welding of steel using arc welding.
        8.5 Welding of steel using gas welding.
        8.6 Fabrication of ferrous and non-ferrous metals into required shapes.
        8.7 Suggested student projects (examples):
            - Name plate.
            - Trinket box.
            - Funnel.
            - Kitchen stool.
            - Car stopper.
            - Metal rake.
            - Scoop.
            - Hinges.
            - Charcoal stove, etc.

        9. Business and Entrepreneurship Opportunities
        9.1 Basic concepts:
            - Entrepreneurship, employer and employee.
        9.2 Types of enterprises:
            - Small-scale, medium-scale and large-scale enterprises.
        9.3 Factors to consider when setting up a workshop or enterprise:
            - Cost, site/location, weather, availability of materials, manpower, market, sources of power and transportation.

        Practical Emphasis:
        - Site visitations to existing fabrication and welding enterprises (small, medium or large scale) to observe real workshop organization and business practices.

        LIST OF FACILITIES AND MAJOR EQUIPMENT / MATERIALS (TYPICAL EXAMPLES)
        Schools offering this subject are expected to have, as far as possible, a well-equipped workshop with items such as:
        - Hammers (various types), chisels, punches, pliers, hacksaws and blades.
        - Try squares, straight edges, rules, scribers, dividers, snips, trammel drivers, hand nibbling machines, wire brushes and assorted files.
        - Vices (bench type), work benches, clamps (G-clamps, parallel clamps, toolmakers’ clamps, cramp folding machines, riveting pliers/sets, etc.).
        - Bending rollers, bench shears, power hacksaws, guillotines and folding bars.
        - Oxy-acetylene welding sets: oxygen and acetylene cylinders, regulators with flow meters, hoses, clips and allied accessories, blow pipes, water-to-carbide generators.
        - Electric arc welding sets: AC transformers, DC generators/rectifiers, electrode holders, electrode drying ovens, combined cutting and welding outfits.
        - Bench and pedestal grinding machines, bench grinding machines, double-ended buffers and polishers.
        - Drilling machines (pillar drilling machines), smith forges, anvils, swage blocks, chipping hammers, flatters, sledge hammers, mallets, magnetic clamps and mole grips.
        - Safety and protection equipment: hand gloves, aprons, welding goggles and plain goggles, hand shields and head caps, first-aid boxes, fire extinguishers and sand buckets.
        - Adequate supplies of electrodes of various gauges, sheet metals, structural sections and other consumables for practice projects.

        RECOMMENDED READING
        Standard welding, fabrication and metalwork texts that cover:
        - Fundamentals of welding and fabrication processes.
        - Workshop tools, machines and procedures.
        - Metal properties, selection and heat treatment.
        - Design, layout and execution of simple fabrication projects.
        - Basic entrepreneurship and workshop management in welding and fabrication.

        Examples of suitable references include recognised texts on welding and fabrication, metalwork technology, workshop processes and materials, and practical welding for senior secondary/technical school level.`,
  },
  {
    subject: 'West African Traditional Religion',
    timeAlloted: 50 * 60,
    description: `WEST AFRICAN TRADITIONAL RELIGION (W.A.T.R.)

        GENERAL AIMS
        By the end of the course, candidates should:
        - Gain insight into, and appreciation of, traditional religious beliefs and practices in West Africa and their impact on people’s lives.
        - Be able to work out practical ways of confronting contemporary challenges using acceptable traditional moral ideas and principles.
        - Understand how traditional principles can be applied in their daily lives.
        - Analyse the major traditional religious factors that influence the political, social and economic life of West Africans.

        SCHEME OF EXAMINATION
        There will be two papers, Papers 1 and 2, taken at one sitting as a composite examination.

        Paper 1 – Objective Test
        - 50 multiple-choice questions drawn from the entire syllabus.
        - Duration: 50 minutes.
        - Marks: 40.

        Paper 2 – Essay / Theory
        - Three sections (A, B and C), each with three essay-type questions.
        • Section A: General Introduction to West African Traditional Religion.
        • Section B: The Individual and the Community.
        • Section C: Contemporary Society.
        - Candidates answer four questions in all, selecting at least one question from each section.
        - Duration: 2 hours 10 minutes.
        - Marks: 60.

        DETAILED SYLLABUS

        SECTION A: GENERAL INTRODUCTION TO WEST AFRICAN TRADITIONAL RELIGION

        1. Introduction to the Study of West African Traditional Religion
        - Nature and characteristics of W.A.T.R.:
        • Belief in the Supreme Being.
        • Belief in divinities, ancestors and other mystical powers.
        - Reasons for, and purpose of, studying W.A.T.R.

        2. Terms Used to Describe West African Traditional Religion
        - Critical examination of labels such as:
        • Fetishism and paganism.
        • Animism and ancestral worship.
        • Polytheism and monotheism.
        • Totemism.
        • "Primitive" and related terms.

        3. Sources of West African Traditional Religion
        - Non-oral sources:
        • Traditional arts/crafts, shrines, sacred spaces and symbols.
        - Oral sources:
        • Names and attributes of God.
        • Theophorous names.
        • Proverbs and wise sayings.
        • Songs, dirges and praise poetry.
        • Myths, legends and drum language.

        4. Approaches to the Study of West African Traditional Religion
        - Thematic approach.
        - Comparative approach.
        - Historical approach.
        - Enumerative (descriptive) approach.

        5. The Structure of West African Traditional Religion
        - God in West African belief.
        - The ancestors.
        - The divinities.
        - Charms, amulets and other spiritual forces.

        6. Worship in West African Traditional Religion
        - Introduction to worship in W.A.T.R.
        - Main features:
        • Prayer and libation.
        • Sacrifice and offerings.
        • Spirit possession and other communal expressions of worship.

        SECTION B: THE INDIVIDUAL AND THE COMMUNITY

        7. Religious Personalities in West African Traditional Religion
        - Concepts and types of religious personalities (priests, priestesses, diviners, healers, custodians of shrines, etc.).
        - Processes for selecting religious personalities.
        - Functions and roles of religious leaders in community life.

        8. Festivals in West African Traditional Religion
        - Types of festivals (harvest, ancestral, purification, thanksgiving, etc.).
        - Significance of festivals for individuals and the community (religious, social, political and economic aspects).

        9. Religion and Medicine in W.A.T.R.
        - Relationship between religion and medicine.
        - Herbal medicine and the role of healers.
        - Interaction between traditional and orthodox healing systems.

        10. Concept of Time, Work and Wealth
        - Concept of time:
        • Understanding and measuring time in West African societies.
        • Concept of the future and continuity of life.
        • Importance of time in social, economic and religious activities.
        - Concept of wealth:
        • Traditional meanings and attitudes towards wealth.
        • Accepted ways of acquiring wealth.
        • Consequences of unjust or immoral acquisition of wealth.

        11. Concept of Man and Destiny
        - Traditional concept of the human person.
        - Concept of destiny and how it shapes human life and responsibility.

        12. Enemies of Life in West African Traditional Religion
        - Witchcraft and magic:
        • Meaning and nature of witchcraft and magic.
        • Acquisition of witchcraft power.
        • Role and perceived effects of witchcraft and magic in society.
        - Malevolent spirits and their influence on individuals and communities.

        13. Religion and Ethics in West African Traditional Religion
        - Main features of traditional ethics.
        - Sources of ethics in W.A.T.R. (divine commands, customs, taboos, elders’ counsel, etc.).
        - Moral values and virtues (honesty, hospitality, respect for elders, communal solidarity, hard work, etc.).
        - Taboos and sin in W.A.T.R.
        - Covenant and its role in maintaining moral order.

        14. Rites of Passage
        - Description of major rites of passage:
        • Birth and naming.
        • Puberty and initiation.
        • Marriage.
        • Death and funeral rites.
        - Importance of rites of passage for the individual and the community.

        SECTION C: CONTEMPORARY SOCIETY

        15. Traditional Political Institutions in West African Traditional Religion
        - Chieftaincy:
        • Structure of chieftaincy institutions.
        • Processes involved in selecting chiefs.
        • Importance of chieftaincy institutions for governance and social cohesion.
        - Asafo companies and similar traditional military/age-group organizations:
        • Background and functions.
        • Importance for defence, social control and development.

        16. Religion and Tourism in West African Traditional Religion
        - Religion and tourism: basic concepts.
        - Uses of free and leisure time.
        - Sacred personalities, places and objects as attractions for tourism.
        - Benefits and challenges of religious tourism.

        17. Religion and Stress Management in West African Traditional Religion
        - Meaning and types of stress situations in traditional society.
        - Causes and effects of stress.
        - Traditional religious means of managing and coping with stress (rituals, counselling, communal support, etc.).

        18. The Role of W.A.T.R. in Conflict Resolution and Management
        - Meaning and nature of conflict in traditional West African societies.
        - Causes and effects of conflicts.
        - Traditional mechanisms for conflict resolution and management (oaths, arbitration by elders, rituals of reconciliation, etc.).

        19. Religion and Reproductive Health in West African Traditional Religion
        - Traditional religious perspectives on reproductive health issues:
        • Hygiene, sexuality, promiscuity and abortion.
        - Circumcision rites and their implications for reproductive health.

        20. W.A.T.R. and Modernity
        - Impact of foreign religions (Christianity and Islam) on W.A.T.R.
        - Impact of W.A.T.R. on Christianity and Islam.
        - Impact of science and technology on traditional beliefs and practices.
        - W.A.T.R. and environmental conservation.
        - Impact of urbanization on W.A.T.R.

        21. New Religious Movements (NRM) in the Context of West African Traditional Religion
        - Examples of contemporary movements rooted in, or related to, W.A.T.R., e.g.:
        • Afrikania Mission (Ghana).
        • Godianism (Nigeria).
        - The future of West African Traditional Religion in a changing world.

        Note: The subject may not be taken together with Christian Religious Studies or Islamic Studies in some jurisdictions, in line with examination regulations.`,
  },
  {
    subject: 'Woodwork',
    timeAlloted: 40 * 60,
    description: `WOODWORK

        SCHEME OF EXAMINATION
        There will be three papers, Papers 1, 2 and 3, all of which must be taken. Papers 1 and 2 will be a composite paper to be taken at one sitting.

        Paper 1: Objective Test
        - 40 multiple-choice questions covering the Woodwork syllabus.
        - Duration: 40 minutes.
        - Marks: 40.

        Paper 2: Theory and Design
        - Two sections, A and B, to be taken in 2 hours 20 minutes.
        - Section A: Short structured questions in three parts:
        • Part I – for candidates in Ghana only.
        • Part II – for candidates in Nigeria, Sierra Leone and The Gambia.
        • Part III – for all candidates; two questions are set and all candidates answer one.
        - Section B: Design and drawing questions, answered within 1 hour 40 minutes for 40 marks.

        Paper 3: Practical Test
        - Duration: 3 hours.
        - Candidates are required to make a test piece from supplied drawings.
        - Marks: 100.

        Continuous Assessment
        - School-based assessment forms part of the final grade and should include marks for finished projects.
        - At least three specific projects are recommended for each candidate over the course.
        - Finished products should be kept intact for at least six months after the release of results.

        DETAILED SYLLABUS – THEORY AND DESIGN

        1. General Workshop Safety
        - Personal safety precautions.
        - General workshop safety regulations.
        - Safety devices and appliances: types and uses.
        - Hand tool safety: carrying, storing and handling hand tools.
        - Machine safety:
        • General machine shop safety.
        • Safety precautions in the use of portable power tools and machines.
        • Safety in machine operations.
        • Prevention of mechanical faults.
        - First aid: materials and administration in the workshop.

        2. Hand Tools
        - Types of hand tools.
        - Classification: geometrical tools; holding and supporting tools; impelling and percussion tools; cutting, boring, abrading and scraping tools.
        - Identification, description and sketching of common hand tools.

        3. Special Purpose Hand Tools
        - Types and uses of special planes: spokeshaves, rebate plane, plough plane, block plane, shoulder plane, etc.
        - Types and uses of special saws: bow saw, pad/keyhole saw, coping saw, fret saw, etc.
        - Types and uses of boring bits: expansion bit, forstner bit, countersink bit, auger bit, etc.
        - Shapers: scrapers, rasps, surforms, etc.
        - Identification, description and sketching of these tools.

        4. Portable Power Tools
        - Types: power drill, jig saw, spray gun, screw driver, sanders, router, power circular saw, etc.
        - Uses, care and safe operation.
        - Identification, description and maintenance.

        5. Woodworking Machines
        - Types: circular saw, crosscut saw, thicknesser, surface planer, mortiser, lathe, grinding wheel, drilling machine, etc.
        - Uses and safe operation.
        - Safety precautions including the use of guards, fences, push sticks, push blocks, gauges, etc.

        6. Maintenance
        - Types of maintenance: corrective, routine, predictive and preventive.
        - Reasons for maintenance.
        - Maintenance of hand tools: oiling, sharpening, repairing, proper storage.
        - Maintenance of machines: cleaning, oiling, servicing, replacing parts, etc.

        7. West African Timbers in Common Use
        - Timber growth and structure; classification (hardwoods and softwoods), parts of the tree and their functions.
        - Common West African timbers, e.g. iroko (odum), abura, mahogany, obeche (wawa), African walnut, afara, ebony, danta, emery, shedua, mansonia, cedar, afromosia (kokrodua), avodire, kusia.
        - Characteristics: surface, working and mechanical qualities; similarities and differences.
        - Specific uses of each timber.

        8. Timber Conversion
        - Explanation of timber conversion.
        - Methods of conversion:
        • Plain/through and through/live sawing.
        • Tangential/back/flat/rake sawing.
        • Quarter/radial/rift sawing.
        - Characteristics, advantages and disadvantages of each method.
        - Common market sizes: log, plank, scantling, board, batten, strip/lath, squares – identification, description and uses.

        9. Timber Seasoning
        - Meaning and reasons for seasoning timber.
        - Methods of seasoning:
        • Natural/open-air seasoning.
        • Artificial/kiln seasoning.
        • Water and chemical seasoning.
        - Advantages and disadvantages of each method.
        - Determination of moisture content:
        • Moisture meter method.
        • Oven-dry method.
        - Calculation of percentage moisture content.

        10. Timber Defects
        - Meaning of timber defects.
        - Types of defects:
        • Natural growth defects.
        • Felling defects.
        • Conversion defects.
        • Seasoning defects.
        • Defects caused by organisms.
        - Causes, prevention, remedies, description and sketching of common defects.

        11. Timber Preservation
        - Reasons for preserving timber.
        - Common timber preservatives and their specific uses.
        - Properties of a good timber preservative.
        - Methods of applying preservatives: brushing, dipping, spraying, etc., with advantages and disadvantages.

        12. Manufactured Boards
        - Types, structure, characteristics and uses of manufactured boards.
        - Advantages and disadvantages of each type.

        13. Timber Preparation
        - Selection of appropriate tools and machines for preparing timber.
        - Operational sequence:
        • Hand preparation.
        • Machine preparation.
        - Practical preparation of stock.

        14. Woodwork Joints
        - Classification of joints:
        • Widening joints: simple butt, dowel, tongued and grooved, loose tongue, rebated butt, etc.
        • Angle joints: mortise and tenon, dowelled butt, dovetails, housing, halving, etc.
        • Framing joints: mortise and tenon, bridle, plain mitre, dowelled butt, halving, etc.
        - Identification, description, sketching, construction and specific uses of joints.

        15. Wood Finishes and Finishing
        - Wood finishes:
        • Types: fillers, stains, paints, varnishes, lacquers, polishes, etc.
        • Properties, characteristics and uses of each.
        - Application of finishes:
        • Surface preparation.
        • Tools and methods: brushing, spraying, dipping, etc.
        • Stages and tools for each method.
        • Safety precautions.

        16. Wood Abrasives
        - Meaning and grades: coarse, medium and fine.
        - Identification, selection and uses, including specific applications of each grade.

        17. Wood Adhesives
        - Types:
        • Protein-based: animal glue, casein.
        • Synthetic resins: urea, phenol and melamine formaldehydes, epoxy resins, polyvinyl acetate (PVA).
        • Contact/rubber-based adhesives.
        - Characteristics, uses, preparation and application.
        - Safety precautions during application.

        18. Wood Fittings and Fasteners
        - Fittings: hinges, locks, handles, bolts, catches, etc.
        - Fasteners: nails, screws, bolts and nuts, corrugated fasteners, etc.
        - Identification, description, sketching, uses, application and fixing methods.

        19. Non-wood Materials
        - Types: glass, plastics, rubber, ceramics, metal, leather, etc.
        - Identification, description, characteristics, uses and other examples of each.

        20. Veneers and Veneering
        - Veneers: types and production.
        - Veneering:
        • Methods: hammer veneering, pressing.
        • Tools: veneer hammer, pressing iron, cramps, caul, etc.
        - Identification, description, sketching, uses and processes for each method.

        21. Wood Shaping and Bending
        - Shaping operations: rounding, moulding, bevelling, chamfering, tapering, carving, etc.
        - Bending methods: solid and laminated bending.
        - Identification, description, sketching, processes, techniques, tools and machines involved, and properties of wood suitable for each.

        22. Design and Drawing
        - Concept of design.
        - Design fundamentals and processes.
        - Freehand sketching.
        - Working drawings in first and third angle orthographic projections.
        - Cutting lists and bills of materials.
        - Basic draftsmanship skills, including correct sectional representation of materials.

        23. Project Design and Construction
        - Identification and analysis of given design problems (from customer needs, market surveys, situation analysis, etc.).
        - Designing solutions to meet identified needs.
        - Estimating the cost of projects.
        - Constructing items to meet design specifications.
        - Evaluating finished products against design purpose and specification.

        24. Upholstery (Woodwork Context)
        - Upholstery work as an aspect of woodwork.
        - Hand tools and machines: needles, scissors, hammer, webbing stretcher, sewing machine, buttoning machine.
        - Materials for framing, stuffing/padding, covering and decorating.
        - Processes and techniques: framing, padding, covering, finishing and decoration.

        25. Wood Turning
        - Wood lathe: parts and accessories.
        - Turning tools: chisels, gouges, callipers, etc.
        - Turning operations: faceplate turning, turning between centres and boring.
        - Suitable woods for turning: abura, ebony, mahogany, etc.
        - Typical projects: vase, bowl, candle holder, etc.

        26. Wood Carving and Sculpture
        - Carving: incised and relief carving; description, identification, application and processes.
        - Simple sculpture/ornament production.
        - Tools: chisels, gouges, knives, files, etc. – identification, sketching and uses.

        27. Surface Decoration
        - Types: inlaying, veneering, marquetry, lamination, laminated plastics, mouldings, etc.
        - Identification, description, processes, techniques and applications.

        28. Mass Production
        - Concept and principles of mass production.
        - Processes: market survey, design, production, quality assurance, sales/marketing, management, procurement, cost estimation, tooling up for production.
        - Basic mass production terms: templates, fixtures, trial run, departments, sections, prototype, quality control, etc.

        FOR CANDIDATES IN NIGERIA, SIERRA LEONE AND THE GAMBIA ONLY

        29. Entrepreneurship in Woodworking
        - Types of business organisation: sole proprietorship, partnership, cooperatives, etc. – characteristics, advantages and disadvantages.
        - Business opportunities in woodworking: merchandising, spray painting, upholstery work, wood turning, etc.
        - Business plans: format, content and sample plans.
        - Sources of funds: gifts, personal savings, loans, inheritance, cooperatives, etc.; benefits and risks.

        SUGGESTED READING (EXAMPLES)
        - Standard woodwork texts that cover woodwork theory and practice, design and drawing, upholstery, advanced woodworking and furniture making, and woodwork technology at senior secondary level.
        - Recommended authors include those commonly prescribed in WAEC syllabuses for Woodwork, such as Walton, Shaw, Green, Nnenji, Sackey and others.`,
  },
  {
    subject: 'Woodwork (GH)',
    timeAlloted: 40 * 60,
    description: `Placeholder – WAEC syllabus-based description for Woodwork (GH) will be added here.`,
  },
  {
    subject: 'Woodwork to I.E.D',
            timeAlloted: 40 * 60,
            description: `WOODWORK TO I.E.D

        PREAMBLE
        The Woodwork to I.E.D course at Senior High School level is designed to enable candidates gain knowledge in the art and craft of woodworking and acquire basic skills needed for technological growth. The knowledge and skills serve as a foundation for further development during and after school.

        It provides opportunities for students to demonstrate detailed knowledge of, and skills in:
        - Technical drawing and designing.
        - Practical work.
        - Methods and principles of construction.
        - Quality control, estimation and costing.

        AIMS
        Candidates are expected to demonstrate:
        - Creative ability, mental and practical skills in the use of hand and machine tools for constructing basic items using wood and related materials.
        - Good basic knowledge of design and competence in reading and interpreting working drawings.
        - Ability to plan and follow a logical sequence of work operations leading to successful completion of projects.
        - Functional skills that can provide a means of livelihood in woodworking.
        - Awareness of problems relating to wood and the wood industry.

        ASSESSMENT OBJECTIVES
        1. Knowledge and Understanding
        Candidates should be able to demonstrate knowledge and understanding of:
        - Terminologies used in woodwork.
        - Materials used in woodwork.
        - Care and maintenance of hand tools and machines.
        - Safety precautions in the workshop.
        - Principles of designing and drawing.
        - Methods and principles of construction.

        2. Application and Practical Skills
        Candidates should be able to:
        - Follow a given design brief to produce working drawings.
        - Interpret working drawings accurately.
        - Use tools, equipment and materials to carry out practical operations in correct sequential order.
        - Prepare surfaces and apply appropriate finishes.

        3. Evaluation and Appreciation
        Candidates should be able to:
        - Compare features of different items and make informed comments or judgements (contrast, justify, support or criticize a job).
        - Write appraisal reports on artefacts.

        STRUCTURE AND SCHEME OF EXAMINATION
        There will be three papers, Papers 1, 2 and 3, all of which must be taken. Papers 1 and 2 will form a composite paper taken at one sitting.

        Paper 1: Objective Test
        - 40 multiple-choice objective questions.
        - Duration: 40 minutes.
        - Marks: 40.

        Paper 2: Theory and Design
        - Two sections, A and B, taken within 2 hours 20 minutes.
        - Section A: Short structured questions in three parts (Parts I, II and III):
        • Part I – for candidates in Ghana only.
        • Part II – for candidates in Nigeria, Sierra Leone and The Gambia.
        • Part III – for all candidates; it will comprise two questions, of which all candidates answer one.
        - Section B: Design and drawing questions, all to be answered within 1 hour 40 minutes for 40 marks.

        Paper 3: Practical Test
        - Duration: 3 hours.
        - Candidates will be required to make a test piece from supplied drawings.
        - Marks: 100.

        DETAILED SYLLABUS

        A. PRACTICAL
        1. Practical Activities
        Practical activities will require the use of:
        - Common hand tools.
        - Portable power tools and basic woodworking machines.
        - A range of joints and shapes.
        - Nails, screws and other fasteners and fittings.
        - Solid wood, manufactured boards and suitable non-wood materials.

        2. Joints and Operations
        Candidates will work from dimensioned sketches, written descriptions or working drawings and should be able to construct the following joints:
        - Widening joints:
        • Plain/simple butt.
        • Dowelled joint.
        • Tongued and grooved joint.
        • Rebated butt joint.
        - Angle joints:
        • Box-like joints, e.g. common and lapped dovetail, pin joints.
        • Shelf-like joints, e.g. housing joints, pinned joints, dowelled joints, plain mitre joints.
        - Framing joints:
        • Mortise and tenon joints.
        • Bridle and lapped joints.
        • Mitre and dowelled framing joints.

        Candidates are also expected to perform the following operations:
        - Cutting operations: ripping and cross-cutting.
        - Planing operations: surfacing and thicknessing.
        - Shaping operations: chamfering, rounding and tapering.
        - Assembling and finishing:
        • Testing for squareness and parallelism (including the use of diagonals).
        • Trial assembly and cramping methods.
        • Preparation of surfaces and application of appropriate finishes.

        B. THEORY

        1. General Workshop Safety
        - Personal safety precautions.
        - General workshop safety regulations.
        - Safety devices and appliances: types and uses.
        - Hand tool safety: safe carrying, storing and handling.
        - Machine safety:
        • General machine shop safety.
        • Safety precautions in the use of portable power tools and machines.
        • Safety in machine operations.
        • Prevention of mechanical faults.
        - First aid: materials and administration.

        2. Hand Tools
        - Types of hand tools.
        - Classification of tools: geometrical; holding and supporting; impelling and percussion; cutting; boring; abrading and scraping.
        - Identification, description and sketching of common tools.

        3. Special Purpose Hand Tools
        - Types and uses of:
        • Planes: spokeshaves, rebate plane, plough plane, block plane, shoulder plane, etc.
        • Saws: bow saw, pad/keyhole saw, coping saw, fret saw.
        • Boring bits: expansion bit, forstner bit, countersink bit, auger bit, etc.
        • Shapers: scrapers, rasps, surforms, etc.
        - Identification, description and sketching.

        4. Portable Power Tools
        - Types: power drill, jig saw, spray gun, screw driver, sanders, router, power circular saw, etc.
        - Uses, care and safe operation.

        5. Woodworking Machines
        - Types: circular saw, crosscut saw, thicknesser, surface planer, mortiser, lathe, grinding wheel, drilling machine, etc.
        - Uses and basic maintenance.
        - Safety precautions: use of guards, fences, push sticks, push blocks, gauges, etc.

        6. Maintenance
        - Types of maintenance: corrective, routine, predictive and preventive.
        - Reasons for maintenance.
        - Maintenance of hand tools: oiling, sharpening, repairing, storing.
        - Maintenance of machines: cleaning, oiling, servicing, replacing parts.

        7. West African Timbers in Common Use
        - Timber growth and structure; classification (hardwoods and softwoods) and parts of the tree and their functions.
        - Common West African timbers: iroko (odum), abura, mahogany, obeche (wawa), African walnut, afara, ebony, danta, emery, shedua, mansonia, cedar, afromosia (kokrodua), avodire, kusia.
        - Surface, working and mechanical qualities; similarities and differences.
        - Specific uses of each timber.

        8. Timber Conversion
        - Explanation of timber conversion.
        - Methods: plain/through and through/live sawing; tangential/back/flat/rake sawing; quarter/radial/rift sawing.
        - Characteristics, advantages and disadvantages of each method.
        - Common market sizes: log, plank, scantling, board, batten, strip/lath, squares – identification, description and uses.

        9. Timber Seasoning
        - Meaning and reasons for seasoning.
        - Methods: natural/open-air, artificial/kiln, water and chemical seasoning.
        - Determination of moisture content: moisture meter method and oven-dry method.
        - Advantages and disadvantages of each method and calculation of percentage moisture content.

        10. Timber Defects
        - Meaning of timber defects.
        - Types: natural growth defects; felling defects; conversion defects; seasoning defects; defects caused by organisms.
        - Causes, prevention, remedies, description and sketching of defects.

        11. Timber Preservation
        - Reasons for preserving timber.
        - Common timber preservatives and their properties.
        - Properties of a good preservative.
        - Methods of applying preservatives: brushing, dipping, spraying, etc., with advantages and disadvantages.

        12. Manufactured Boards
        - Types, structure, characteristics and uses.
        - Advantages and disadvantages of each type.

        13. Timber Preparation
        - Selection of tools and machines for stock preparation.
        - Operational sequence: hand preparation and machine preparation.
        - Practical stock preparation.

        14. Woodwork Joints
        - Classification and applications:
        • Widening joints: simple butt, dowel, tongued and grooved, loose tongue, rebated butt, etc.
        • Angle joints: mortise and tenon, dowelled butt, dovetails, housing, halving, etc.
        • Framing joints: mortise and tenon, bridle, plain mitre, dowelled butt, halving, etc.
        - Identification, description, sketching, construction and specific uses.

        15. Wood Finishes and Finishing
        - Types of wood finishes: fillers, stains, paints, varnishes, lacquers, polishes, etc.
        - Properties, characteristics and uses of each.
        - Application of finishes: surface preparation, tools, and methods (brushing, spraying, dipping, etc.).
        - Stages, tools and safety precautions for each method.

        16. Wood Abrasives
        - Meaning and grades: coarse, medium and fine.
        - Selection and uses, including specific applications of each grade.

        17. Wood Adhesives
        - Types:
        • Protein: animal glue, casein.
        • Synthetic resins: urea, phenol, melamine formaldehydes, epoxy resins, polyvinyl acetate (PVA).
        • Contact/rubber-based adhesives.
        - Characteristics, uses, preparation and application.
        - Safety precautions during use.

        18. Wood Fittings and Fasteners
        - Fittings: hinges, locks, handles, bolts, catches, etc.
        - Fasteners: nails, screws, bolts and nuts, corrugated fasteners, etc.
        - Identification, description, sketching, uses, application and fixing.

        19. Non-wood Materials
        - Types: glass, plastics, rubber, ceramics, metal, leather, etc.
        - Identification, description, characteristics and uses.

        20. Veneers and Veneering
        - Veneers: types and production.
        - Veneering methods: hammer and press.
        - Tools: veneer hammer, pressing iron, cramps, caul, etc.
        - Identification, description, sketching, uses and processes.

        21. Wood Shaping and Bending
        - Shaping operations: rounding, moulding, bevelling, chamfering, tapering, carving, etc.
        - Bending methods: solid and laminated.
        - Identification, description, sketching, processes, techniques, tools and machines; properties of suitable woods.

        22. Design and Drawing
        - Concept of design.
        - Design fundamentals and processes.
        - Freehand sketching.
        - Working drawings in first and third angle orthographic projections.
        - Cutting lists and bills of materials.
        - Basic draftsmanship skills, including correct sectional representation.

        23. Project Design and Construction
        - Identification and analysis of design problems (from customer needs, market survey, situation analysis, etc.).
        - Designing solutions to meet identified needs.
        - Estimating the cost of designs.
        - Constructing projects to meet design specifications.
        - Evaluating products against design purpose and specification.

        24. Upholstery
        - Upholstery work as part of woodworking.
        - Hand tools and machines: needles, scissors, hammer, webbing stretcher, sewing machine, buttoning machine.
        - Materials for framing, stuffing/padding, covering and decorating.
        - Processes and techniques: framing, padding, covering, finishing and decoration.

        25. Wood Turning
        - Wood lathe: parts and accessories.
        - Turning tools: chisels, gouges, calipers, etc.
        - Turning operations: faceplate turning, turning between centres and boring.
        - Suitable woods for turning: abura, ebony, mahogany, etc.
        - Typical projects: vase, bowl, candle holder, etc.

        26. Wood Carving and Sculpture
        - Carving: incised and relief.
        - Simple sculpture/ornament production.
        - Tools: chisels, gouges, knives, files, etc. – identification, sketching and uses.

        27. Surface Decoration
        - Types: inlaying, veneering, marquetry, lamination, laminated plastics, mouldings, etc.
        - Identification, description, processes, techniques and applications.

        28. Mass Production
        - Concept and principles.
        - Processes: market survey, design, production, quality assurance, sales/marketing, management, procurement, cost estimation, tooling up for production.
        - Basic terms: templates, fixtures, trial run, departments, sections, prototype, quality control, etc.

        FOR CANDIDATES IN NIGERIA ONLY

        29. Entrepreneurship in Woodworking
        - Types of business organisation: sole proprietorship, partnership, cooperatives, etc. – characteristics, advantages and disadvantages.
        - Business opportunities in woodworking: merchandising, spray painting, upholstery work, wood turning, etc.
        - Business plans: format and content, including sample plans.
        - Sources of funds: gifts, personal savings, loans, inheritance, cooperatives, etc.; benefits and risks.

        SUGGESTED READING (EXAMPLES)
        - Standard woodwork texts that cover woodwork theory and practice, design and drawing, upholstery, advanced woodworking and furniture making, and woodwork technology at senior secondary level.
        - Authors commonly prescribed in WAEC woodwork syllabuses, including Walton, Shaw, Green, Nnenji, Sackey and others.`,
  },
];
